// API client for backend communication

import type {
  MintlifyToolsConfig,
  MintlifyAgentPrompt,
  MintlifyAgentPromptRevision,
  MintlifyAgentJob,
  MintlifyAgentJobDetail,
} from '../mintlify-shared/mintlify-shared-types';
import { downloadBlob } from './download-utils';
import { readNdjsonStream } from './stream-utils';

export interface ApiResponse<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    timestamp: string;
  };
}

export type ApiResult<T> = ApiResponse<T> | ApiError;

export interface User {
  id: string;
  email: string;
  displayName: string;
  groups: string[];
  preferences: { darkMode?: boolean };
  routeGroups: string[];
  // Managed-role tiers the user holds, keyed by role id (e.g. { 'account-modification': ['manager'] }).
  // Admins implicitly hold every tier of every role.
  managedRoles: Record<string, string[]>;
  buildInfo: {
    sha: string;
    shortSha: string;
    deployTag: string;
  };
}

export interface ManagedRoleTierSummary {
  id: string;
  label: string;
  description: string;
  routeGroup: string;
  userGrantCount: number;
}

export interface ManagedRoleSummary {
  id: string;
  label: string;
  description: string;
  tiers: ManagedRoleTierSummary[];
}

export interface ManagedRoleGrant {
  tupleId: string;
  userId: string;
  email: string;
  displayName: string;
  isBot: boolean;
  tierId: string;
  routeGroup: string;
  isBuiltin: boolean;
  createdAt: string;
}

export interface ManagedRoleDetail {
  id: string;
  label: string;
  description: string;
  tiers: Array<{ id: string; label: string; description: string; routeGroup: string }>;
  grants: ManagedRoleGrant[];
}

/**
 * Accent colour for a dashboard tile, named for the Kibo brand colour it renders
 * as (see global.css for the palette). Doubles as the CSS class name applied to
 * `.tool-card` in DashboardPage.css, so it is a closed union rather than a
 * string — mirrors `ToolTileColor` in src/tools/tool-registry.ts.
 */
export type ToolTileColor =
  | 'banana'
  | 'orange'
  | 'strawberry'
  | 'lime'
  | 'kiwi'
  | 'blueberry'
  | 'grape'
  | 'blue'
  | 'blackberry'
  | 'navy';

export interface Tool {
  id: string;
  identifier: string;
  name: string;
  description?: string;
  metadata: {
    url: string;
    linkType: 'external' | 'internal';
    sortOrder: number;
    isActive: boolean;
    icon?: string; // Icon identifier for the tool
    group?: string; // Grouping label for dashboard display
    color?: ToolTileColor; // Tile accent; omitted falls back to Kibo yellow
    status?: 'stable' | 'testing' | 'experimental' | 'in-progress';
    statusNote?: string;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ─── LinkedIn Post Generator ────────────────────────────────────────────────

export interface LinkedInAsset {
  id: string;
  name: string;
  url: string;
  type: string | null;
  pillar: string | null;
  subTopic: string | null;
  model: string | null;
  funnelStage: string | null;
  targetVertical: string | null;
  targetAudience: string | null;
  accessibility: string | null;
  freshness: string | null;
  tier: number;
  sendPriorityScore: number | null;
  keyStat: string | null;
  talkingPoints: string | null;
  namedBrands: string | null;
  guardrails: string | null;
  pairWithUrl: string | null;
}

export interface LinkedInGeneratorConfig {
  id: string;
  bannedPhrases: string[];
  bannedPatterns: string[];
  guidancePrompt: string;
  defaultTones: string[];
  hotNowBoosts: string[];
  updatedById: string;
  updatedAt: string;
}

export interface LinkedInVariant {
  variantIndex: number;
  tone: string;
  text: string;
  assetId: string | null;
  assetName: string | null;
  assetUrl: string | null;
  postId: string;
}

export interface LinkedInPost {
  id: string;
  tone: string;
  topic: string | null;
  targetAudience: string | null;
  authorContext: string | null;
  generatedText: string;
  postedAt: string | null;
  createdAt: string;
  asset: { id: string; name: string; url: string } | null;
}

export interface LinkedInFeedbackRow {
  id: string;
  postId: string;
  postText: string;
  tone: string;
  humanityScore: number;
  note: string | null;
  createdAt: string;
  userDisplayName: string;
  /** ISO timestamp when an admin discarded this feedback (soft delete), else null. */
  discardedAt: string | null;
}

export type LinkedInKnowledgeBaseCategory =
  | 'tone' | 'structure' | 'cliche' | 'emoji' | 'authenticity' | 'length' | 'other';

export type LinkedInKnowledgeBasePolarity = 'positive' | 'negative';

export interface LinkedInKnowledgeBaseRule {
  id: string;
  ruleText: string;
  category: LinkedInKnowledgeBaseCategory;
  polarity: LinkedInKnowledgeBasePolarity;
  frequency: number;
  lastSeenAt: string;
  sampleNote: string | null;
}

/** One person's usage of the LinkedIn Post Generator (admin/manager usage view). */
export interface LinkedInUsageRow {
  userId: string;
  displayName: string;
  email: string;
  tierId: 'user' | 'manager' | null;
  hasAccess: boolean;
  generations: number;
  generationsLast30Days: number;
  postedCount: number;
  feedbackCount: number;
  firstGenerationAt: string | null;
  lastGenerationAt: string | null;
  lastPostedAt: string | null;
}

export interface UserWithGroups extends Omit<User, 'groups'> {
  createdAt: string;
  /** True for system (bot) users — only ever present in the list when `type=bot`/`all`. */
  isBot: boolean;
  groups: Array<{
    id: string;
    slug: string;
    name: string;
  }>;
}

export interface Group {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

// System (bot) users. Non-authenticatable User rows managed at /admin/bots.
export interface BotConfigReference {
  feature: 'grafana-triage' | 'support-deflection' | 'canaries';
  label: string;
}

export interface BotSummary {
  id: string;
  email: string;
  displayName: string;
  isBot: true;
  createdAt: string;
  groups: Array<{ id: string; slug: string; name: string }>;
  createdBy: { id: string; email: string; displayName: string } | null;
  usedByCount: number;
}

export interface BotDetail extends Omit<BotSummary, 'usedByCount'> {
  googleId: string;
  updatedAt: string;
  usedBy: BotConfigReference[];
}

// ─── Scheduled chat prompts ───────────────────────────────────────────
// A user-scheduled first-message prompt that runs later, once or on a recurring
// cadence. At fire time the backend creates a normal origin:'user' chat owned by
// the scheduling user and runs chatGenerationWorkflow as a child with
// interactive:false — so a scheduled chat is indistinguishable from one the user
// typed by hand, with the same tools and permissions. See src/scheduled-prompts/.

export type ScheduledPromptCadence = 'one_time' | 'hourly' | 'daily' | 'weekly';
export type ScheduledPromptRunStatus = 'running' | 'ok' | 'cancelled' | 'error';

export interface ScheduledPromptRunSummary {
  id: string;
  threadId: string;
  status: ScheduledPromptRunStatus;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ScheduledPromptJobSummary {
  id: string;
  prompt: string;
  cadence: ScheduledPromptCadence;
  /** ISO datetime of the first fire, in the stored timezone. */
  startAt: string;
  /** IANA timezone name, e.g. "America/Chicago". */
  timezone: string;
  enabledCategories: string[];
  provider: string | null;
  model: string | null;
  isPrivate: boolean;
  isCollaborative: boolean;
  accountId: string | null;
  accountAssociationStatus: string | null;
  enabled: boolean;
  /** true → every fire appends to one rolling chat; false → each fire starts a fresh chat. */
  reuseThread: boolean;
  /** The pinned rolling-thread id when reuseThread=true (null until the first fire). */
  threadId: string | null;
  lastRunAt: string | null;
  lastRunStatus: ScheduledPromptRunStatus | null;
  lastRunThreadId: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledPromptJobDetail extends ScheduledPromptJobSummary {
  runs: ScheduledPromptRunSummary[];
}

/** A job summary augmented with owner identity, for the admin view (all jobs). */
export interface AdminScheduledPromptJobSummary extends ScheduledPromptJobSummary {
  owner: { id: string; email: string; displayName: string } | null;
}

/** A job detail augmented with owner identity, for the admin detail view. */
export interface AdminScheduledPromptJobDetail extends ScheduledPromptJobDetail {
  owner: { id: string; email: string; displayName: string } | null;
}

export interface ScheduledPromptCreateInput {
  prompt: string;
  cadence: ScheduledPromptCadence;
  startAt: string;
  timezone: string;
  enabledCategories?: string[];
  provider?: string;
  model?: string;
  isPrivate?: boolean;
  isCollaborative?: boolean;
  accountId?: string | null;
  accountAssociationStatus?: 'set' | 'none' | 'unknown';
  reuseThread?: boolean;
}

export interface ScheduledPromptUpdateInput {
  prompt?: string;
  cadence?: ScheduledPromptCadence;
  startAt?: string;
  timezone?: string;
  enabledCategories?: string[] | null;
  provider?: string | null;
  model?: string | null;
  isPrivate?: boolean;
  isCollaborative?: boolean;
  accountId?: string | null;
  accountAssociationStatus?: 'set' | 'none' | 'unknown' | null;
  enabled?: boolean;
  reuseThread?: boolean;
}

export interface Session {
  sid: string;
  deviceType: string;
  ipAddress: string | null;
  userAgent: string | null;
  lastActiveAt: string;
  createdAt: string;
  expiresAt: string;
}

export interface FeatureFlags {
  [key: string]: boolean;
}

export interface FeatureFlagDetail {
  key: string;
  name: string;
  description: string;
  category: 'ui' | 'experimental' | 'system';
  defaultEnabled: boolean;
  enabled: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface FeatureFlagAuditLogEntry {
  id: string;
  previousValue: boolean;
  newValue: boolean;
  changeReason: string | null;
  createdAt: string;
  changedBy: { id: string; displayName: string } | null;
}

export interface DebugUserProfile {
  id: string;
  email: string;
  displayName: string;
  groups: string[];
  description: string;
}

export interface DebugAuthStatus {
  enabled: boolean;
  profiles: DebugUserProfile[];
}

export interface GitHubOAuthSettings {
  id: string;
  githubUserId: number;
  githubUsername: string;
  githubAvatarUrl: string | null;
  scope: string | null;
  repositoryOwner: string | null;
  repositoryName: string | null;
  connectedAt: string;
  connectedBy: { id: string; displayName: string } | null;
}

export interface GitHubRepository {
  id: number;
  fullName: string;
  name: string;
  owner: string;
  description: string | null;
  visibility: 'public' | 'private' | 'internal';
  defaultBranch: string;
  htmlUrl: string;
}

export interface GitHubConnectionTestResult {
  success: boolean;
  repository?: GitHubRepository;
  recentCommits?: Array<{
    sha: string;
    message: string;
    author: string;
    date: string;
  }>;
  error?: string;
}

export interface GitHubConfig {
  configured: boolean;
}

// Zanzibar / KHRN Relation Tuples
export type TupleSubjectType = 'user' | 'group' | 'everyone' | 'userset';

export interface RelationTuple {
  id: string;
  khrn: string;  // formatted as "khrn:objectType:objectId"
  objectType: string;
  objectId: string;
  relation: string;
  subjectType: TupleSubjectType;
  subjectId: string;
  subjectRelation: string | null;
  isBuiltin: boolean;
  createdAt: string;
  createdById: string | null;
  createdBy: { id: string; email: string; displayName: string } | null;
}

export interface CreateTupleRequest {
  objectType: string;
  objectId: string;
  relation: string;
  subjectType: TupleSubjectType;
  subjectId: string;
  subjectRelation?: string;
}

// Credential Sets
export type CredentialSetScope = 'USER' | 'ADMIN' | 'SHARED';

export interface CredentialSetTypeSummary {
  identifier: string;
  name: string;
  description: string;
  icon?: string;
  fieldCount: number;
}

export interface CredentialSetSummary {
  id: string;
  typeIdentifier: string;
  typeName: string;
  name: string;
  description: string | null;
  scope: CredentialSetScope;
  isConfigured: boolean;
  configuredFields: number;
  totalFields: number;
  createdAt: string;
  createdBy: { id: string; displayName: string } | null;
}

export interface FireworksToolConfig {
  id: string;
  credentialSetId: string | null;
  credentialSetName: string | null;
  defaultModel: string | null;
  updatedAt: string;
  updatedBy: { id: string; displayName: string } | null;
}

export type ChatProvider = 'vertex' | 'fireworks';

export interface ChatModelDetail {
  id: string;
  provider: ChatProvider;
  modelId: string;
  displayName: string;
  inputPer1M: number;
  outputPer1M: number;
  contextLength: number | null;
  enabled: boolean;
  isDefault: boolean;
  sortOrder: number;
  syntheticCheckEnabled: boolean;
  updatedAt: string;
  updatedBy: { id: string; displayName: string } | null;
}

export interface ChatModelInput {
  provider: ChatProvider;
  modelId: string;
  displayName: string;
  inputPer1M: number;
  outputPer1M: number;
  contextLength?: number | null;
  enabled?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
  syntheticCheckEnabled?: boolean;
}

export interface ChatModelOption {
  id: string;
  provider: ChatProvider;
  modelId: string;
  displayName: string;
  inputPer1M: number;
  outputPer1M: number;
  contextLength: number | null;
  isDefault: boolean;
}

export interface ChatModelTestResult {
  ok: boolean;
  reply: string | null;
  error: string | null;
}

// Canary Health (/admin/canaries). Mirrors src/canaries/canary-schemas.ts.
export type CanaryKind = 'model' | 'tool';

export interface CanaryRunSummary {
  ok: boolean;
  durationMs: number;
  errorText: string | null;
  ranAt: string;
}

export interface CanaryProbeStatus {
  kind: CanaryKind;
  target: string;
  label: string;
  category: string;
  toolName: string | null;
  enabled: boolean;
  intervalSeconds: number;
  overridden: boolean;
  lastRun: CanaryRunSummary | null;
  recent: boolean[];
  passed24h: number;
  total24h: number;
  avgDurationMs24h: number | null;
}

export interface CanaryBotOption {
  id: string;
  displayName: string;
  email: string;
}

export interface CanaryOverview {
  probes: CanaryProbeStatus[];
  systemUser: CanaryBotOption | null;
  botOptions: CanaryBotOption[];
}

export interface CanaryProbeResult {
  kind: CanaryKind;
  target: string;
  label: string;
  category: string;
  ok: boolean;
  durationMs: number;
  error: string | null;
  ranAt: string;
}

export interface CanaryRunRow {
  id: string;
  ok: boolean;
  durationMs: number;
  errorText: string | null;
  ranAt: string;
}

// Alert pipeline test (/admin/alert-test). Mirrors src/alert-tests/alert-test-schemas.ts.
export type AlertTestSeverity = 'warning' | 'error';

export interface AlertTestTriggerResult {
  testId: string;
  severity: AlertTestSeverity;
  tier: 'notify' | 'page';
  loggedAt: string;
  reachesCloudLogging: boolean;
  logFilter: string;
}

export interface FieldRevisionEntry {
  id: string;
  version: number;
  hasValue: boolean;
  note: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: { id: string; displayName: string } | null;
}

export interface FieldDetail {
  key: string;
  name: string;
  description: string;
  required: boolean;
  sensitive: boolean;
  configured: boolean;
  activeVersion: number | null;
  revisions: FieldRevisionEntry[];
}

export interface ShareRuleDetail {
  id: string;
  businessGroup?: { id: string; name: string; slug: string };
  user?: { id: string; displayName: string; email: string };
  permission: string;
  grantedAt: string;
  grantedBy: { id: string; displayName: string } | null;
}

export interface CredentialSetDetail {
  id: string;
  typeIdentifier: string;
  typeName: string;
  typeDescription: string;
  typeIcon?: string;
  name: string;
  description: string | null;
  notes: string | null;
  scope: CredentialSetScope;
  isConfigured: boolean;
  fields: FieldDetail[];
  shareRules: ShareRuleDetail[];
  canEdit: boolean;
  createdAt: string;
  createdBy: { id: string; displayName: string } | null;
}

export interface CreateCredentialSetInput {
  typeIdentifier: string;
  name: string;
  description?: string;
  notes?: string;
  scope: CredentialSetScope;
}

// Temporal
export interface TemporalNamespace {
  name: string;
  state: string;
  description?: string;
  ownerEmail?: string;
}

export interface TemporalClusterInfo {
  connected: boolean;
  serverVersion: string | null;
  namespaces: TemporalNamespace[];
  error: string | null;
}

// Documentation
export interface DocSummary {
  slug: string;
  title: string;
  description: string;
  category: string;
}

export interface DocDetail extends DocSummary {
  content: string;
}

// Doc Index
export interface DocIndexStatus {
  id: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  startedAt: string | null;
  completedAt: string | null;
  pagesTotal: number;
  pagesIndexed: number;
  errorMessage: string | null;
  updatedAt: string | null;
}

export interface DocIndexPageSummary {
  id: string;
  url: string;
  path: string;
  title: string | null;
  wordCount: number;
  indexedAt: string;
}

export interface DocIndexPageDetail extends DocIndexPageSummary {
  content: string;
}

export interface DocIndexSearchResult {
  content: string;
  path: string;
  metadata: { title: string | null; url: string };
}

// Elasticsearch
export interface ElasticsearchEndpointSummary {
  id: string;
  identifier: string;
  name: string;
  url: string;
  credentialSetId: string;
  credentialSetName: string;
  isActive: boolean;
  rateLimitPerMinute: number;
  createdAt: string;
}

export interface ElasticsearchEndpointDetail extends ElasticsearchEndpointSummary {
  createdBy: { id: string; displayName: string };
  updatedBy: { id: string; displayName: string };
  updatedAt: string;
}

export interface ElasticsearchConnectionTestResult {
  success: boolean;
  clusterName?: string;
  status?: string;
  error?: string;
}

export interface ElasticsearchToolConfigDetail {
  id: string | null;
  globalEnabled: boolean;
  globalRateLimitPerMinute: number;
  llmCredentialSetId: string | null;
  llmCredentialSetName: string | null;
  llmModel: string | null;
  fieldGuide: string | null;
}

export interface ElasticsearchOutboundLogEntry {
  id: string;
  endpointId: string;
  endpointName: string;
  userId: string | null;
  userDisplayName: string | null;
  executedQuery: string;
  httpStatus: number;
  resultCount: number | null;
  elapsedMs: number;
  requestType: string;
  createdAt: string;
}

export interface ElasticsearchOutboundLogListResponse {
  items: ElasticsearchOutboundLogEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Web Search (Spider.cloud)
export interface WebSearchToolConfigDetail {
  id: string | null;
  globalEnabled: boolean;
  credentialSetId: string | null;
  credentialSetName: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface WebSearchResult {
  url: string;
  title?: string;
  description?: string;
  content?: string;
}

export interface WebSearchResultsResponse {
  results: WebSearchResult[];
}

export interface WebSearchOutboundLogEntry {
  id: string;
  userId: string | null;
  userDisplayName: string | null;
  requestType: string;
  query: string;
  httpStatus: number;
  resultCount: number | null;
  elapsedMs: number;
  createdAt: string;
}

export interface WebSearchOutboundLogListResponse {
  items: WebSearchOutboundLogEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Chat
export interface ChatThreadToolConfig {
  enabledCategories?: string[];
  personaContext?: {
    personaId?: string;
    personaName?: string;
    personaTaskId?: string;
    personaTaskName?: string;
    userPersonaTaskId?: string;
    userPersonaTaskName?: string;
    personaFields?: Record<string, unknown>;
    personaRenderedSystemPrompt?: string;
  };
}

export type ChatAccountAssociationStatus = 'set' | 'none' | 'unknown';

export interface ChatThreadAccountRef {
  id: string;
  orgName: string;
  ref: string | null;
}

/** One journal row as History shows it (src/journal/journal-schemas.ts). */
export interface JournalHistoryRow {
  id: string;
  at: string;
  kind: string;
  traceId: string | null;
  correlationId: string | null;
  causeId: string | null;
  actor: string | null;
  payload: unknown;
  refs: Array<{ kind: string; id: string }>;
}

/** How a chat turn ended, from its record (`chat_turns.outcome`); null while it runs. */
export type ChatTurnOutcome = 'completed' | 'cancelled' | 'failed' | 'timed_out' | 'interrupted';

/**
 * Kind/source of a chat thread. Mirrors the backend `ChatThreadOrigin` enum and
 * chat-origin-registry.ts. Humanized labels live in web/src/chat/chat-origin-registry.ts.
 */
export type ChatOrigin =
  | 'user'
  | 'slack'
  | 'a2a'
  | 'scheduled_job'
  | 'support_ticket'
  | 'grafana_alert'
  | 'incident'
  | 'site_draft'
  | 'doc_assistant';

export interface ChatThread {
  id: string;
  title: string | null;
  model: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  origin: ChatOrigin;
  user: { id: string; displayName: string };
  toolConfig: ChatThreadToolConfig | null;
  account: ChatThreadAccountRef | null;
  accountAssociationStatus: ChatAccountAssociationStatus;
  queueSkipped: boolean;
  // Set when the thread was started by an @-mention in Slack. UI renders a
  // small `Slack` badge when populated.
  slackChannelId: string | null;
  slackThreadTs: string | null;
  // Private threads are visible only to their owner. When viewed by a non-owner
  // (in admin/analytics surfaces that show counts but redact content), `title`
  // and other content fields are nulled out by the server.
  isPrivate: boolean;
  // Collaborative threads accept replies from anyone who can read them, and each
  // user turn records its own author. Never true alongside isPrivate.
  isCollaborative: boolean;
  // You have replied in this thread without owning it. Only ever true in the 'my'
  // view — the sidebar surfaces joined threads there regardless of their origin, so
  // an incident RCA you have worked on stays findable.
  isJoined: boolean;
  // The caller's own pin for this thread — pinned threads list first, per caller.
  isPinned: boolean;
  // The server's answer to "may this caller rename the thread?" — owner, or a
  // collaborator on a collaborative thread (same rule as canReply). The sidebar
  // and the thread header both render the rename button on it.
  canRename: boolean;
}

export interface ChatDocFeedbackTicket {
  id: string;
  jiraIssueKey: string;
  jiraIssueUrl: string;
  title: string;
  submittedAt: string;
}

export interface ChatThreadDetail extends ChatThread {
  messages: ChatMessageDetail[];
  docFeedbackTicket: ChatDocFeedbackTicket | null;
  // Whether the current user may post into this thread. Decided by the server so the
  // client never re-derives the write rule and cannot disagree with the endpoint
  // about who may type.
  canReply: boolean;
}

export interface ChatExternalResponse {
  text: string;
  verified: boolean;
  issues?: string[];
}

export interface ChatPendingPrompt {
  toolCallId: string;
  toolName: string;
  kind: 'text' | 'choice' | 'tenant-access' | 'tenant-write';
  prompt: string;
  // `{ options, allowCustom }` for 'choice'; `{ tenantId, method, path }` for the tenant
  // kinds; null for 'text'.
  options: ChatChoicePromptOptions | Record<string, unknown> | null;
}

/** The button labels of a choice prompt, plus whether a free-form answer is accepted. */
export interface ChatChoicePromptOptions {
  options: string[];
  allowCustom: boolean;
}

export interface KiboTenantAccessResult {
  hasValidToken: boolean;
  loginUrl: string | null;
}

/** Preflight for the tenant-access card: what to offer, and what to pre-select. */
export interface KiboTenantAccessEnvironments {
  hasValidToken: boolean;
  tokenEnvironmentLabel: string | null;
  suggestedEnvironmentId: string | null;
  suggestionReason: 'override' | 'rule' | null;
  suggestionDetail: string | null;
  environments: Array<{ id: string; label: string; description: string | null }>;
}

export interface KiboLoginEnvironment {
  id: string;
  label: string;
  loginUrlTemplate: string;
  description: string | null;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedById: string | null;
}

export interface KiboTenantLoginRule {
  id: string;
  rangeStart: number;
  rangeEnd: number;
  priority: number;
  environmentId: string;
  environmentLabel: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
  updatedById: string | null;
}

export interface KiboTenantLoginOverride {
  id: string;
  tenantId: string;
  environmentId: string;
  environmentLabel: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
  updatedById: string | null;
}

/** A successful login against an environment the rules did not suggest. */
export interface KiboTenantEnvironmentObservation {
  id: string;
  tenantId: string;
  environmentId: string;
  environmentLabel: string;
  suggestedEnvironmentId: string | null;
  suggestedEnvironmentLabel: string | null;
  observedHost: string | null;
  successCount: number;
  firstObservedAt: string;
  lastObservedAt: string;
  status: 'open' | 'applied' | 'dismissed';
}

export interface KiboSafePostPattern {
  id: string;
  method: string;
  pathPattern: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
  updatedById: string | null;
}

/**
 * Rules and overrides reference their environment by label, not id — ids differ
 * per database and an exported config has to import cleanly elsewhere.
 */
export interface KiboTenantLoginMappingExport {
  environments: Array<Omit<KiboLoginEnvironment, 'id' | 'updatedAt' | 'updatedById'>>;
  rules: Array<
    Omit<KiboTenantLoginRule, 'id' | 'environmentId' | 'updatedAt' | 'updatedById'>
  >;
  overrides: Array<
    Omit<KiboTenantLoginOverride, 'id' | 'environmentId' | 'updatedAt' | 'updatedById'>
  >;
  safePostPatterns: Array<Omit<KiboSafePostPattern, 'updatedAt' | 'updatedById'>>;
}

export interface KiboTenantLoginMappingImportResult {
  environmentsImported: number;
  rulesImported: number;
  overridesImported: number;
  safePostPatternsImported: number;
}

// ─── Projects ───────────────────────────────────────────────────────────

export interface ChatProject {
  id: string;
  slug: string;
  name: string;
  description: string;
  /** Where its files live in the filesystem, e.g. /projects/brand-guidelines. */
  mountPath: string;
  agentWrites: 'propose' | 'direct';
  archivedAt: string | null;
  createdAt: string;
  canEdit: boolean;
  /** False for a confidential project — the viewer@everyone grant was withheld. */
  openToEveryone: boolean;
}

// ─── Files (the VFS) ────────────────────────────────────────────────────

export interface VfsFileEntry {
  path: string;
  name: string;
  kind: 'file' | 'directory';
  sizeBytes: number;
  mediaType: string;
  updatedAt: string | null;
  /** No text representation — an image, or a format nothing can extract. */
  binary: boolean;
  /** A text view can be produced, so the preview offers an Extracted tab. */
  extractable: boolean;
  note?: string;
}

export interface VfsMountInfo {
  path: string;
  description: string;
  access: 'read-only' | 'read-write';
  writable: boolean;
}

export interface VfsSyncSource {
  id: string;
  path: string;
  origin: string;
  /** The Kibo GoogleDriveFolder id, or null if that connection was removed. */
  driveFolderId: string | null;
  externalName: string;
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncedAt: string | null;
  lastSyncedBy: string | null;
  lastSyncError: string | null;
  fileCount: number;
}

export interface VfsListing {
  entries: VfsFileEntry[];
  mounts: VfsMountInfo[];
  /** Frozen mirrors, so the tree can badge them and withhold upload up front. */
  syncSources: VfsSyncSource[];
  notice?: string;
}

export interface VfsFileContent {
  path: string;
  text: string;
  /** True when `text` is the extracted view of a document rather than the file. */
  extracted: boolean;
  warnings: string[];
  contentHash: string;
  mediaType: string;
}

export interface VfsUploadResult {
  path: string;
  contentHash: string;
  sizeBytes: number;
  created: boolean;
  /** Non-fatal extraction notes, including "we could not read the text of this". */
  warnings: string[];
}

// ============================================================================
// DOCUMENT EXPORT
// ============================================================================

export interface DocumentExportDocxRequest {
  markdown: string;
  fileName: string;
}

export interface DocumentExportXlsxRequest {
  markdown: string;
  fileName: string;
}

export interface DocumentExportCsvXlsxRequest {
  csv: string;
  fileName: string;
}

/**
 * A deck is exported by id because its .pptx only exists once the server renders
 * it; everything else sends the markdown the surface already holds.
 */
export type DocumentExportDriveRequest =
  | { source: 'markdown'; markdown: string; title: string }
  | { source: 'artifactDeck'; artifactId: string };

export interface DriveExportResult {
  fileId: string;
  webViewLink: string;
  googleUserEmail: string;
}

export interface GoogleDriveConnectionInfo {
  connected: boolean;
  googleUserEmail: string | null;
  connectedAt: string | null;
  lastUsedAt: string | null;
  /** The DRIVE_DOC_EXPORT flag — when false, no Drive item is offered anywhere. */
  exportEnabled: boolean;
}

export interface ChatAttachmentRef {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
}

export interface ChatMessageDetail {
  id: string;
  role: string;
  content: string;
  parts: Array<Record<string, unknown>> | null;
  externalResponse: ChatExternalResponse | null;
  attachments: ChatAttachmentRef[] | null;
  // Who typed this turn. Null for assistant turns, for system-composed user turns,
  // and for rows written before authorship was recorded — fall back to the thread's
  // `user` (already resolved to the origin label for ownerless threads).
  author: { id: string; displayName: string } | null;
  createdAt: string;
}

// Chat Attachments (Admin)
export interface ChatAttachmentAdminRow {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  deletedAt: string | null;
  messageId: string | null;
  threadId: string | null;
  user: { id: string; displayName: string; email: string };
}

export interface ChatAttachmentAdminDetail extends ChatAttachmentAdminRow {
  convertedContent: string | null;
}

export interface ChatAttachmentAdminList {
  rows: ChatAttachmentAdminRow[];
  total: number;
}

// Chat Tool Config
export interface ChatToolConfigDetail {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  defaultModel: string;
  titleModel: string | null;
  accountAssociationRequiredGroups: string[];
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface ChatPolicyResponse {
  accountAssociationRequired: boolean;
}

export interface AccountChatRow {
  threadId: string;
  title: string | null;
  isPrivate: boolean;
  ownerId: string;
  ownerName: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AccountChatsList {
  rows: AccountChatRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ChatSearchQueryParams {
  q: string;
  accountId?: string;
  ownerId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: 'relevance' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface ChatSearchRow {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  origin: ChatOrigin;
  user: { id: string; displayName: string; email: string };
  account: ChatThreadAccountRef | null;
  firstUserMessage: string | null;
  messageCount: number;
  rank: number;
  isPrivate: boolean;
}

export interface ChatSearchResult {
  rows: ChatSearchRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ChatSearchOwner {
  id: string;
  displayName: string;
  email: string;
}

export interface ChatAccountQueueRow {
  threadId: string;
  title: string | null;
  ownerId: string;
  ownerName: string;
  messageCount: number;
  createdAt: string;
  accountAssociationStatus: ChatAccountAssociationStatus;
  queueSkipped: boolean;
  account: ChatThreadAccountRef | null;
  firstUserMessage: string | null;
}

export interface ChatAccountQueueList {
  rows: ChatAccountQueueRow[];
  total: number;
  page: number;
  limit: number;
}

export type ChatAccountQueueAction =
  | { action: 'assign'; accountId: string }
  | { action: 'mark_no_client' }
  | { action: 'skip' }
  | { action: 'unskip' };

// Chat Tool Test Console
export interface ChatToolMetadata {
  name: string;
  category: string;
  description: string;
  inputSchema: Record<string, unknown>;
  available: boolean;
  unavailableReason?: string;
}

export interface ChatToolExecuteResult {
  result: unknown;
  durationMs: number;
}

// Tool-category drift audit (admin). Reports persisted categories the registry no
// longer knows; read-only, so an admin decides what each legacy value should become.
export interface ChatToolCategoryAuditRow {
  category: string;
  count: number;
  sampleIds: string[];
}

export interface ChatToolCategoryAuditStore {
  label: string;
  table: string;
  idLabel: string;
  unknownCategories: ChatToolCategoryAuditRow[];
}

export interface ChatToolCategoryAuditReport {
  knownCategories: string[];
  stores: ChatToolCategoryAuditStore[];
  totalUnknownRows: number;
}

// ── MCP Servers ───────────────────────────────────────────────────────────────
// Per-user MCP endpoints exposing the caller's own Hub AI tools to MCP clients.

export interface McpServerSummary {
  id: string;
  name: string;
  description: string | null;
  enabledCategories: string[];
  allowMutatingTools: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  activeConnectionCount: number;
}

/** One OAuth connection: a client, authorised by this user, against one server. */
export interface McpOAuthGrantSummary {
  id: string;
  /** Host of the client_id document, or of the redirect URI for a DCR client.
   * This is the identity to display — clientName is self-asserted. */
  clientHost: string;
  clientName: string;
  registrationType: string;
  serverId: string;
  serverName: string;
  scope: string;
  consentedAt: string;
  absoluteExpiresAt: string;
  lastUsedAt: string | null;
  createdIp: string | null;
  createdUserAgent: string | null;
}

export interface McpOAuthAdminGrantSummary extends McpOAuthGrantSummary {
  userId: string;
  userEmail: string;
  userDisplayName: string;
}

export interface McpOAuthClientSummary {
  id: string;
  clientId: string;
  clientHost: string;
  clientName: string;
  registrationType: string;
  redirectUris: string[];
  isBlocked: boolean;
  activeGrantCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface McpOAuthRedirectHostSummary {
  id: string;
  origin: string;
  label: string;
  isCustomScheme: boolean;
  createdAt: string;
}

export interface McpOAuthConfigView {
  accessTokenTtlSeconds: number;
  refreshAbsoluteSeconds: number;
  refreshIdleSeconds: number;
  updatedAt: string;
  updatedByEmail: string | null;
}

/** What the consent screen renders before the user approves a connection. */
export interface McpOAuthConsentRequest {
  requestId: string;
  clientHost: string;
  clientName: string;
  clientUri: string | null;
  registrationType: string;
  redirectUri: string;
  redirectIsLoopback: boolean;
  redirectIsCustomScheme: boolean;
  serverId: string;
  serverName: string;
  enabledCategories: string[];
  allowMutatingTools: boolean;
  userEmail: string;
  expiresInDays: number;
  idleTimeoutHours: number;
}

export interface McpServerDetail extends McpServerSummary {
  connections: McpOAuthGrantSummary[];
}

export interface McpServerInput {
  name?: string;
  description?: string | null;
  enabledCategories?: string[];
  allowMutatingTools?: boolean;
  isActive?: boolean;
}

/** A tool the server exposes right now, for this user, on this Hub. */
export interface McpExposedTool {
  name: string;
  description: string;
  category: string;
  readOnly: boolean;
}

export interface McpCategoryStatus {
  category: string;
  available: boolean;
  unavailableReason?: string;
  /** Whether an admin currently permits this category over MCP at all. */
  permitted: boolean;
}

/** The admin allowlist of tool categories permitted over MCP. */
export interface McpPolicyCategory {
  category: string;
  permitted: boolean;
  description: string;
}

export interface McpPolicy {
  categories: McpPolicyCategory[];
  updatedAt: string | null;
  updatedBy: string | null;
}

// ── MCP usage / observability types ───────────────────────────────────────────

/**
 * 'error' means the tool *returned* an error payload rather than throwing — the
 * failure mode that would otherwise look identical to success. 'denied' means the
 * client asked for a tool outside the server's allowlist.
 */
export type McpCallStatus = 'ok' | 'error' | 'exception' | 'denied';

export interface McpUsageToolCount {
  toolName: string;
  calls: number;
  errors: number;
}

export interface McpUsageSummary {
  totalCalls: number;
  distinctUsers: number;
  distinctServers: number;
  errorCalls: number;
  errorRate: number;
  topTools: McpUsageToolCount[];
  from: string;
  to: string;
}

export interface McpUsageUserRow {
  userId: string;
  displayName: string;
  email: string;
  calls: number;
  errors: number;
  toolCount: number;
  serverCount: number;
  lastUsedAt: string | null;
}

export interface McpUsageServerRow {
  serverId: string;
  serverName: string;
  isActive: boolean;
  ownerUserId: string;
  ownerName: string;
  ownerEmail: string;
  calls: number;
  errors: number;
  toolCount: number;
  lastUsedAt: string | null;
}

export interface McpUsageEventRow {
  id: string;
  userId: string;
  userName: string;
  serverId: string;
  serverName: string;
  grantId: string | null;
  clientName: string | null;
  toolName: string;
  inputArgs: unknown;
  status: McpCallStatus;
  errorMessage: string | null;
  durationMs: number;
  createdAt: string;
}

export interface McpUsagePage<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
}

// ── Signal analytics types ────────────────────────────────────────────────────

export interface SignalGroupRow {
  group: string;
  activeUsers: number;
  logins: number;
  pageViews: number;
  chatThreads: number;
}

export interface SignalAnalyticsSummary {
  activeUsers: number;
  totalLogins: number;
  totalPageViews: number;
  totalChatThreads: number;
  byGroup: SignalGroupRow[];
  byDay: Array<{ date: string; count: number }>;
}

export interface SignalUserRow {
  userId: string;
  displayName: string;
  email: string;
  groups: string[];
  logins: number;
  pageViews: number;
  chatThreads: number;
  dwellMinutes: number;
  lastSeen: string | null;
}

export interface SignalUserList {
  rows: SignalUserRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SignalUserEvent {
  id: string;
  type: string;
  page: string | null;
  resourceId: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface SignalUserEventList {
  events: SignalUserEvent[];
  total: number;
  page: number;
  limit: number;
}

export interface SignalAllEventRow {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  type: string;
  page: string | null;
  resourceId: string | null;
  groups: string[];
  createdAt: string;
}

export interface SignalAllEventList {
  rows: SignalAllEventRow[];
  total: number;
  page: number;
  limit: number;
}

// ─────────────────────────────────────────────────────────────────────────────

export type ChatAnalyticsInterval = 'day' | 'week' | 'month';
export type ChatAnalyticsMetric = 'conversations' | 'userMessages' | 'uniqueUsers' | 'cost' | 'tokens';

export interface ChatAnalyticsSeriesPoint {
  period: string;
  value: number;
}

export interface ChatAnalyticsTotals {
  conversations: number;
  userMessages: number;
  totalMessages: number;
  uniqueUsers: number;
  estimatedCost: number;
  totalTokens: { input: number; output: number };
  totalGenerationSeconds: number;
  feedbackThumbsUp: number;
  feedbackThumbsDown: number;
  feedbackWithText: number;
  totalComments: number;
}

export interface ChatAnalyticsPriorTotals {
  conversations: number;
  userMessages: number;
  uniqueUsers: number;
}

export interface ChatCostByOrigin {
  origin: ChatOrigin;
  estimatedCost: number;
  totalTokens: { input: number; output: number };
  conversations: number;
}

export interface ChatAnalytics {
  interval: ChatAnalyticsInterval;
  metric: ChatAnalyticsMetric;
  /** Origins the series/totals are scoped to (echoes the active filter). */
  origins: ChatOrigin[];
  series: ChatAnalyticsSeriesPoint[];
  totals: ChatAnalyticsTotals;
  prior: ChatAnalyticsPriorTotals | null;
  /** Per-origin cost breakdown spanning every origin, independent of `origins`. */
  costByOrigin: ChatCostByOrigin[];
}

export interface ChatConversationRow {
  id: string;
  origin: ChatOrigin;
  /** Null for ownerless system chats — render the origin label instead. */
  user: { id: string; displayName: string } | null;
  messageCount: number;
  createdAt: string;
  firstMessage: string | null;
  totalGenerationSeconds: number;
  estimatedCost: number;
  model: string;
  thumbsUp: number;
  thumbsDown: number;
  commentCount: number;
  isPrivate: boolean;
}

export interface ChatConversationList {
  rows: ChatConversationRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ChatMessageFeedback {
  id: string;
  messageId: string;
  threadId: string;
  userId: string;
  userDisplayName: string;
  rating: 1 | 5;
  content: string | null;
  createdAt: string;
}

export interface ChatComment {
  id: string;
  messageId: string;
  threadId: string;
  userId: string;
  userDisplayName: string;
  selectedText: string;
  startOffset: number;
  endOffset: number;
  content: string;
  editedAt: string | null;
  createdAt: string;
}

export interface ChatFeedbackListItem {
  id: string;
  messageId: string;
  threadId: string;
  userId: string;
  userDisplayName: string;
  rating: 1 | 5;
  content: string;
  messagePreview: string | null;
  createdAt: string;
}

export interface ChatFeedbackList {
  items: ChatFeedbackListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ChatToolCategory {
  category: string;
  available: boolean;
  unavailableReason?: string;
}

// Chat Tool Params
export interface ChatToolParamDetailEntry {
  key: string;
  label: string;
  section: 'toolDefaults' | 'executionLimits';
  toolName: string | null;
  paramName: string;
  codeDefault: number;
  codeMin: number;
  codeMax: number;
  hardFloor: number;
  hardCeiling: number;
  description: string;
  unit: string;
  effectiveDefault: number;
  effectiveMin: number;
  effectiveMax: number;
  hasOverride: boolean;
}

export interface ChatToolParamsDetail {
  params: ChatToolParamDetailEntry[];
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface ChatToolParamsUpdate {
  toolDefaults?: Record<string, Record<string, { default?: number; min?: number; max?: number }>>;
  executionLimits?: Record<string, number>;
}

// Code Repositories
export type CodeRepositorySyncStatus = 'PENDING' | 'SYNCING' | 'SYNCED' | 'ERROR';
export type CodeRepositorySource = 'GITHUB' | 'UPLOAD';

export interface CodeRepositorySummary {
  id: string;
  owner: string;
  name: string;
  fullName: string;
  defaultBranch: string;
  description: string | null;
  source: CodeRepositorySource;
  syncStatus: CodeRepositorySyncStatus;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastCommitHash: string | null;
  addedBy: { id: string; displayName: string };
  createdAt: string;
}

export interface CodeSyncConfigResponse {
  id: string | null;
  storageBasePath: string;
  vmUrl: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface CodeRepoSyncResult {
  id: string;
  fullName: string;
  success: boolean;
  error?: string;
}

export interface StorageDebugDirectoryEntry {
  path: string;
  size: number;
  isDirectory: boolean;
}

export interface StorageDebugRepoStatus {
  id: string;
  fullName: string;
  syncStatus: string;
  directoryExists: boolean;
  fileCount: number;
  totalSizeBytes: number;
}

export interface StorageDebugEnvironment {
  hostname: string;
  isCloudRun: boolean;
  cloudRunService: string | null;
  cloudRunRevision: string | null;
  nodeEnv: string | null;
}

export interface StorageDebugInfo {
  resolvedBasePath: string;
  basePathExists: boolean;
  directoryListing: StorageDebugDirectoryEntry[];
  diskUsage: string;
  mountInfo: string;
  gcsFuseMounted: boolean;
  repositories: StorageDebugRepoStatus[];
  environment: StorageDebugEnvironment;
  timestamp: string;
}

// Bucket Scan
export interface BucketScanResult {
  owner: string;
  name: string;
  fullName: string;
  fileCount: number;
  totalSizeBytes: number;
  isTracked: boolean;
  trackedRepoId: string | null;
  trackedSource: string | null;
}

export interface BucketScanResponse {
  basePath: string;
  results: BucketScanResult[];
}

// Code Search VM
export type CodeSearchVmEndpoint = 'search' | 'read-file' | 'find-files' | 'list-directory';

export interface CodeSearchVmHealth {
  vmUrl: string | null;
  configured: boolean;
  healthy: boolean;
  repoCount: number | null;
  latencyMs: number | null;
  error: string | null;
}

export interface CodeSearchVmTestResult {
  result: string;
  latencyMs: number;
}

// Salesforce
export interface SalesforceConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  instanceUrl: string | null;
  organizationId: string | null;
  /** The test connection: Support Lab tickets on the Salesforce desk only. */
  testCredentialSetId: string | null;
  testCredentialSetName: string | null;
  testInstanceUrl: string | null;
  testOrganizationId: string | null;
  /** The holding Account ("Kibo Admin - Unplaced") for Cases Hub cannot place, per connection. */
  unplacedAccountId: string | null;
  testUnplacedAccountId: string | null;
  syncStatus: string;
  syncDateFrom: string | null;
  syncDateTo: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastSyncCasesTotal: number | null;
  lastSyncCasesProcessed: number | null;
  summarySystemPrompt: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
  webhookUrl?: string;
}

export interface SalesforceWebhookLogEntry {
  id: string;
  requestIp: string;
  responseStatus: number;
  parsedCaseId: string | null;
  failureReason: string | null;
  rawXml: string;
  createdAt: string;
}

export interface SalesforceDeltaSyncRun {
  id: string;
  status: string;
  casesProcessed: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface SalesforceDeltaSyncStatus {
  watermark: {
    lastModifiedCursor: string;
    lastDeletedCursor: string;
    lastRunAt: string | null;
  } | null;
  recentRuns: SalesforceDeltaSyncRun[];
}

export interface SalesforceSyncStats {
  totalCases: number;
  openCases: number;
  closedCases: number;
  lastSyncedAt: string | null;
  syncStatus: string;
  lastSyncCasesTotal: number | null;
  lastSyncCasesProcessed: number | null;
  lastSyncError: string | null;
}

export interface SalesforceCaseSummary {
  id: string;
  sfCaseId: string;
  caseNumber: string;
  subject: string | null;
  description: string | null;
  status: string;
  priority: string | null;
  accountName: string | null;
  /** The join to a Hub `Account.salesforceAccountId`. */
  sfAccountId: string | null;
  sfUltimateParentId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  ownerName: string | null;
  origin: string | null;
  type: string | null;
  isClosed: boolean;
  isEscalated: boolean;
  sfCreatedDate: string;
  sfLastModified: string;
  lastSyncedAt: string | null;
  aiSummary: string | null;
  aiSummarizedAt: string | null;
}

export interface SalesforceActivityItem {
  type: 'comment' | 'email' | 'history' | 'attachment' | 'feed';
  id: string;
  timestamp: string;
  createdByName: string | null;
  commentBody?: string | null;
  isPublished?: boolean;
  emailSubject?: string | null;
  /** The text part where the email had one, the HTML part otherwise. */
  emailBody?: string | null;
  /** True when `emailBody` is markup because the email carried no text part. */
  emailBodyIsHtml?: boolean;
  emailFromAddress?: string | null;
  emailFromName?: string | null;
  emailToAddress?: string | null;
  emailCcAddress?: string | null;
  emailIncoming?: boolean;
  field?: string;
  oldValue?: string | null;
  newValue?: string | null;
  fileName?: string;
  contentType?: string | null;
  bodyLength?: number | null;
  /**
   * Set when the file's bytes live in Hub's own storage — the id to ask for a
   * download link. Null means the file is known but not archived.
   */
  archiveFileId?: string | null;
  /** Why the archive could not take this file. */
  archiveError?: string | null;
  feedType?: string;
  feedTitle?: string | null;
  feedBody?: string | null;
}

export interface SalesforceCaseDetail extends SalesforceCaseSummary {
  description: string | null;
  /**
   * `case` = the Salesforce Case.Description field; `feed_post` = the earliest
   * TextPost, substituted for a case that had no description of its own.
   */
  descriptionSource: 'case' | 'feed_post' | null;
  reason: string | null;
  sfClosedDate: string | null;
  customFields: Record<string, unknown> | null;
  activityFeed: SalesforceActivityItem[];
  rawData?: Record<string, unknown>;
}

export interface SalesforceConnectionTestResult {
  success: boolean;
  orgId?: string;
  username?: string;
  error?: string;
}

export interface SalesforceSyncResult {
  syncLogId: string;
  status: string;
  casesProcessed: number;
  error?: string;
}

export interface SalesforceCaseSummaryResponse {
  summary: string;
}

export interface SalesforceAccountSummary {
  id: string;
  sfAccountId: string;
  name: string;
  accountNumber: string | null;
  industry: string | null;
  website: string | null;
  customFieldCount: number;
  updatedAt: string;
}

export interface SalesforceAccountDetail extends SalesforceAccountSummary {
  customFields: Record<string, unknown> | null;
  rawData: Record<string, unknown> | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface SalesforceAccountSyncRun {
  id: string;
  status: string;
  accountsProcessed: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  triggeredById: string | null;
}

/** One run of the Salesforce → HubSpot client sync. */
export interface SalesforceCrmSyncRun {
  id: string;
  status: string;
  companiesProcessed: number;
  contactsProcessed: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  triggeredById: string | null;
}

/** Which declared client properties the HubSpot portal has, per object type. */
export interface SalesforceCrmPropertyStatus {
  objectType: string;
  present: string[];
  missing: string[];
}

// Salesforce file archive — copying Salesforce file bytes into Hub's storage
// before the Salesforce contract lapses.

export interface SalesforceArchiveScopeRule {
  label: string;
  parentObject: string;
  transport: string;
}

export interface SalesforceArchiveScopeStats {
  scopeId: string;
  total: number;
  archived: number;
  failed: number;
  remaining: number;
  /** Bytes Salesforce says the discovered files hold. */
  discoveredBytes: number;
  /** Bytes actually written into Hub's storage. */
  archivedBytes: number;
  lastArchivedAt: string | null;
  lastError: string | null;
}

export interface SalesforceArchiveScopeStatus {
  id: string;
  label: string;
  description: string;
  rules: SalesforceArchiveScopeRule[];
  stats: SalesforceArchiveScopeStats;
}

// ---- Ticket cutover -------------------------------------------------------
// Turning Salesforce intake off, and moving the cases that are still live.

export interface SalesforceCutoverRequirement {
  id: string;
  kind: 'field' | 'custom-setting' | 'custom-permission';
  apiName: string;
  sobject?: string;
  label: string;
  purpose: string;
  runbookStep: string;
  present: boolean;
}

export interface SalesforceCutoverAutomationCount {
  total: number;
  active: number;
  activeNames: string[];
}

export interface SalesforceCutoverSetupReadiness {
  requirements: SalesforceCutoverRequirement[];
  ready: boolean;
  automation: {
    /** Null means setup was unreadable, NOT that there are none. */
    validationRules: SalesforceCutoverAutomationCount | null;
    apexTriggers: SalesforceCutoverAutomationCount | null;
    flows: SalesforceCutoverAutomationCount;
    beforeSaveFlows: string[];
    setupUnreadable: string | null;
  };
}

export interface SalesforceCaseStatusAuditRow {
  status: string;
  isClosed: boolean;
  count: number;
  disposition: 'in-flight' | 'closed';
  basis: 'known-status' | 'closed-marker' | 'is-closed-flag' | 'open-by-default';
  /** True when the rule decided this status rather than the recorded vocabulary. */
  unreviewed: boolean;
}

export interface SalesforceCaseStatusAudit {
  rows: SalesforceCaseStatusAuditRow[];
  inFlight: number;
  closed: number;
  unreviewed: number;
  /** Cases whose status says finished while IsClosed says otherwise. */
  flagDisagreesWithStatus: number;
  accountsInScope: number;
}

export interface SalesforceMigrationFlagOutcome {
  accountId: string;
  accountName: string;
  salesforceAccountId: string | null;
  desired: boolean;
  written: boolean;
  skipReason: string | null;
  error: string | null;
}

export interface SalesforceMigrationFlagResult {
  considered: number;
  written: number;
  failed: number;
  skipped: Record<string, number>;
  outcomes: SalesforceMigrationFlagOutcome[];
}

export interface SalesforceCaseMigrationPreviewRow {
  accountId: string;
  accountName: string;
  salesforceAccountId: string;
  inFlight: number;
  alreadyMigrated: number;
  /** Non-null when the account has no tenant a migrated ticket could hang off. */
  blockedReason: string | null;
}

export interface SalesforceCaseMigrationPreview {
  rows: SalesforceCaseMigrationPreviewRow[];
  totalInFlight: number;
  totalBlocked: number;
}

export interface SalesforceUnlockedCase {
  sfCaseId: string;
  caseNumber: string;
  supportTicketId: string | null;
  lockError: string | null;
}

export interface SalesforceArchiveFailure {
  id: string;
  name: string;
  sfFileId: string;
  sfParentId: string;
  sfParentType: string;
  sfCaseId: string | null;
  byteSize: number | null;
  archiveError: string | null;
  attemptCount: number;
  updatedAt: string;
}

// Slack
export interface SlackConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  workspaceName: string | null;
  workspaceId: string | null;
  syncStatus: string;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  syncChannelsTotal: number | null;
  syncChannelsProcessed: number | null;
  syncMessagesProcessed: number | null;
  syncHistoryDays: number;
  hubBaseUrl: string | null;
  slackSystemPromptAddition: string | null;
  slackSystemPromptAdditionDefault: string;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface SlackChannelSummary {
  id: string;
  slackChannelId: string;
  name: string;
  topic: string | null;
  purpose: string | null;
  memberCount: number;
  isArchived: boolean;
  syncEnabled: boolean;
  lastSyncedAt: string | null;
  lastMessageAt: string | null;
  messageCount?: number;
}

export interface SlackChannelDetail extends SlackChannelSummary {
  slackCreatedAt: string | null;
  messageCount: number;
  participantCount: number;
  oldestMessageAt: string | null;
  newestMessageAt: string | null;
}

export interface SlackMessageItem {
  id: string;
  slackMessageTs: string;
  slackUserId: string | null;
  userName: string | null;
  text: string | null;
  threadTs: string | null;
  replyCount: number;
  reactions: { name: string; count: number }[] | null;
  files: { id: string; name: string; mimetype: string }[] | null;
  subtype: string | null;
  slackPostedAt: string;
  channelName?: string;
}

export interface SlackSearchResult {
  id: string;
  slackMessageTs: string;
  channelId: string;
  channelName: string;
  slackUserId: string | null;
  userName: string | null;
  text: string | null;
  /** The thread root when this hit is a reply, otherwise null. */
  threadTs: string | null;
  /** Null where the source cannot say — live search carries no reply count. */
  replyCount: number | null;
  slackPostedAt: string;
  rank: number;
}

/** One page of live Slack search results. Mirrors SlackSearchPage in slack-schemas.ts. */
export interface SlackSearchPage {
  results: SlackSearchResult[];
  total: number;
  /** 1-based, echoing the page actually served. */
  page: number;
  hasMore: boolean;
}

export interface SlackContextMessage {
  ts: string;
  text: string | null;
  userId: string | null;
  userName: string | null;
  threadTs: string | null;
  replyCount: number;
  subtype: string | null;
  postedAt: string;
  replies: SlackContextMessage[] | null;
}

export interface SlackMessageContext {
  channelId: string;
  windowSeconds: number;
  messages: SlackContextMessage[];
}

export interface SlackUserSummary {
  id: string;
  slackUserId: string;
  displayName: string | null;
  realName: string | null;
  email: string | null;
  avatarUrl: string | null;
  isBot: boolean;
  isDeleted: boolean;
}

export interface SlackConnectionTestResult {
  success: boolean;
  teamName?: string;
  teamId?: string;
  botUserId?: string;
  error?: string;
}

export interface SlackSyncResult {
  syncLogId: string;
  status: string;
  error?: string;
}

export interface SlackSyncStats {
  totalChannels: number;
  syncedChannels: number;
  totalMessages: number;
  totalUsers: number;
  lastSyncedAt: string | null;
  syncStatus: string;
}

// Atlassian
export interface AtlassianConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  cloudId: string | null;
  siteUrl: string | null;
  siteName: string | null;
  confluenceSyncStatus: string;
  confluenceSpaceKeys: string | null;
  confluenceLastSyncedAt: string | null;
  confluenceLastSyncError: string | null;
  confluenceSyncTotal: number | null;
  confluenceSyncProcessed: number | null;
  jiraSyncStatus: string;
  jiraSyncJql: string | null;
  jiraJqlGuide: string | null;
  jiraLastSyncedAt: string | null;
  jiraLastSyncError: string | null;
  jiraSyncTotal: number | null;
  jiraSyncProcessed: number | null;
  docsJiraProjectKey: string | null;
  docsJiraIssueType: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface AtlassianConnectionTestResult {
  success: boolean;
  siteName?: string;
  cloudId?: string;
  siteUrl?: string;
  error?: string;
}

export interface AtlassianTestQueryConfluenceResultItem {
  id: string;
  title: string;
  spaceKey: string;
  excerpt: string;
  url: string;
  lastUpdated: string;
}

export interface AtlassianConfluencePageByUrlResult {
  pageId: string;
  title: string;
  spaceKey: string;
  version: number;
  markdown: string;
}

export interface AtlassianTestQueryJiraResultItem {
  id: string;
  key: string;
  summary: string;
  status: string;
  priority: string;
  issueType: string;
  assignee: string | null;
  url: string;
}

export interface AtlassianSyncStats {
  confluencePages: number;
  confluenceSpaces: number;
  confluenceSpaceKeys: string[];
  confluenceLastSyncedAt: string | null;
  confluenceSyncStatus: string;
  confluenceSyncTotal: number | null;
  confluenceSyncProcessed: number | null;
  confluenceLastSyncError: string | null;
  jiraIssues: number;
  jiraProjects: number;
  jiraProjectKeys: string[];
  jiraOpenIssues: number;
  jiraLastSyncedAt: string | null;
  jiraSyncStatus: string;
  jiraSyncTotal: number | null;
  jiraSyncProcessed: number | null;
  jiraLastSyncError: string | null;
}

export interface AtlassianConfluencePageSummary {
  id: string;
  atlassianPageId: string;
  spaceKey: string;
  spaceName: string | null;
  title: string;
  version: number;
  atlassianUrl: string | null;
  authorName: string | null;
  lastUpdatedBy: string | null;
  atlassianUpdatedAt: string | null;
}

export interface AtlassianConfluencePageDetail extends AtlassianConfluencePageSummary {
  markdownContent: string | null;
  atlassianCreatedAt: string | null;
}

export interface AtlassianJiraIssueSummary {
  id: string;
  atlassianIssueId: string;
  issueKey: string;
  projectKey: string;
  projectName: string | null;
  issueType: string | null;
  summary: string;
  status: string;
  statusCategory: string | null;
  priority: string | null;
  assigneeName: string | null;
  reporterName: string | null;
  atlassianUrl: string | null;
  atlassianUpdatedAt: string | null;
}

export interface AtlassianJiraCommentDetail {
  id: string;
  authorName: string | null;
  authorEmail: string | null;
  bodyMarkdown: string | null;
  atlassianCreatedAt: string | null;
  atlassianUpdatedAt: string | null;
}

export interface AtlassianJiraIssueDetail extends AtlassianJiraIssueSummary {
  descriptionMarkdown: string | null;
  assigneeEmail: string | null;
  reporterEmail: string | null;
  labels: string | null;
  components: string | null;
  linkedIssues: unknown;
  devInfoPrs: unknown;
  devInfoBranches: unknown;
  atlassianCreatedAt: string | null;
  comments: AtlassianJiraCommentDetail[];
}

// Callouts
export type CalloutType = 'info' | 'warning' | 'success' | 'critical';

export interface ActiveCallout {
  id: string;
  title: string;
  content: string;
  type: CalloutType;
}

export interface CalloutSummary {
  id: string;
  title: string;
  content: string;
  type: CalloutType;
  page: string;
  startDate: string | null;
  endDate: string | null;
  status: 'active' | 'scheduled' | 'expired';
  dismissCount: number;
  createdAt: string;
  createdBy: { id: string; displayName: string };
  updatedAt: string;
  updatedBy: { id: string; displayName: string };
}

export interface CalloutPageOption {
  value: string;
  label: string;
}

export interface CreateCalloutInput {
  title: string;
  content: string;
  type: CalloutType;
  page: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface UpdateCalloutInput {
  title?: string;
  content?: string;
  type?: CalloutType;
  page?: string;
  startDate?: string | null;
  endDate?: string | null;
}

// Cloudflare
export interface CloudflareConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

// HubSpot
export interface HubSpotConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface HubSpotConnectionTestResult {
  success: boolean;
  portalId?: number;
  accountName?: string;
  uiDomain?: string;
  error?: string;
}

// ─── HubSpot Custom Channel (Support Tickets provider) ─────────────────────

export interface HubSpotStatusMapEntry {
  hubspot: string;
  portal: string;
}

// `stageMap` is HubSpot's server-generated pipeline stage ids keyed by the 13-stage
// machine — written by pipeline provisioning, not by hand. There are no priority
// maps: hs_ticket_priority is written outbound through a fixed code-declared map.
export interface HubSpotStatusMap {
  stageMap: HubSpotStatusMapEntry[];
  categoryMap: HubSpotStatusMapEntry[];
}

export interface HubSpotChannelConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  portalId: string | null;
  channelId: string | null;
  channelAccountId: string | null;
  inboxId: string | null;
  webhookBaseUrl: string | null;
  ticketPipelineId: string | null;
  propertiesProvisioned: boolean;
  oauthConnected: boolean;
  tokenValid: boolean;
  provider: string;
  attachmentsBucket: string | null;
  statusMap: HubSpotStatusMap;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

/**
 * Preflight for the whole HubSpot support pipeline. `skip` means the check could
 * not run because something it depends on failed — never treat it as a pass.
 */
export type HubSpotDiagnosticStatus = 'ok' | 'warn' | 'fail' | 'skip';

export interface HubSpotDiagnosticCheck {
  id: string;
  label: string;
  group: 'connection' | 'channel' | 'crm' | 'pipeline' | 'runtime';
  status: HubSpotDiagnosticStatus;
  detail: string;
  remediation?: string;
  durationMs: number;
}

export interface HubSpotDiagnosticsReport {
  status: HubSpotDiagnosticStatus;
  ranAt: string;
  durationMs: number;
  counts: { ok: number; warn: number; fail: number; skip: number };
  checks: HubSpotDiagnosticCheck[];
}

export interface HubSpotChannelStatusResponse {
  credentialSetConfigured: boolean;
  oauthConnected: boolean;
  tokenValid: boolean;
  channelRegistered: boolean;
  accountConnected: boolean;
  propertiesProvisioned: boolean;
  active: boolean;
  portalId: string | null;
  webhookUrl: string;
  lastReconcileAt: string | null;
  error?: string;
}

export interface HubSpotChannelInbox {
  id: string;
  name: string;
  type: string;
  isHelpDesk: boolean;
}

/** One custom channel registered under the app, and what makes it safe to remove. */
export interface HubSpotChannelSummary {
  channelId: string;
  name: string | null;
  webhookUrl: string | null;
  createdAt: string | null;
  isActive: boolean;
  /** Accounts connected in the CONNECTED portal; null when it could not be read. */
  connectedAccounts: number | null;
}

// Outcome of provisioning the Support Cases pipeline. `created` false means an
// existing pipeline with that label was adopted; `mapped` is the stage count
// written into statusMap.stageMap.
export interface HubSpotChannelPipelineResult {
  pipelineId: string;
  mapped: number;
  created: boolean;
}

// A HubSpot user that can author a conversation message. `actorId` is the
// `A-<userId>` form used as the deflection "Kibo AI Bot" sender.
export interface HubSpotChannelActor {
  actorId: string;
  email: string;
  name: string;
}

// ─── Looker (tenant order metrics) ─────────────────────────────────────────

export type LookerRange = 'week' | 'month' | 'ytd' | 'yoy';

// AI support triage / deflection
export interface SupportDeflectionConfigResponse {
  maxTriageQuestions: number;
  // Raw stored overrides (null = code default in force), mirroring the prompt fields.
  verifierModel: string | null;
  judgeModel: string | null;
  defaultVerifierModel: string;
  defaultJudgeModel: string;
  enhancementJiraProjectKey: string;
  engineeringJiraProjectKey: string;
  devopsJiraProjectKey: string;
  // Support Lab targets. Null = unset, and unlike the three above there is no
  // default to resolve against — so the page DOES have to model "unset" here.
  testEnhancementJiraProjectKey: string | null;
  testEngineeringJiraProjectKey: string | null;
  testDevopsJiraProjectKey: string | null;
  enhancementJiraIssueType: string;
  engineeringJiraIssueType: string;
  devopsJiraIssueType: string;
  // Null = the real target's type is used on the lab board too, which the page
  // shows as a placeholder so blank never reads as "no type".
  testEnhancementJiraIssueType: string | null;
  testEngineeringJiraIssueType: string | null;
  testDevopsJiraIssueType: string | null;
  // The process half (§08/§09C, decision 12). Always populated — the backend
  // resolves against its registry defaults, so the page never models "unset".
  slaWindows: Record<
    string,
    {
      hours: number;
      businessHours: boolean;
      // The exceptions to the note above: these have NO code default, because the
      // figures are contractual and unconfirmed. Absent = that tier is not tracked, and
      // the page has to say so rather than render a number nobody agreed to.
      resolutionHours?: number;
      updateCadenceHours?: number;
    }
  >;
  severityMatrix: Record<string, Record<string, string>>;
  calendar: { timeZone: string; holidays: string[]; workdayStartHour: number; workdayEndHour: number };
  /** False when `calendar` is the never-confirmed code default rather than a saved value. */
  calendarConfigured: boolean;
  sweepThresholds: {
    breachWarningPct: number;
    psStaleBusinessDays: number;
    jiraStaleDays: number;
    pendingClientBusinessDays: number;
    aiResolvedBusinessDays: number;
    triageTimeoutMinutes: { portal: number; email: number };
  };
  /**
   * The tool categories the agent researches with. Always populated — the backend
   * resolves null to the code default — so an empty array is an admin's real
   * choice of "no research tools", not "unconfigured".
   */
  deflectionCategories: string[];
  systemUserId: string | null;
  systemUserEmail: string | null;
  hubspotBotActorId: string | null;
  /** Empty = all tenants. An emergency scoping lever, not the normal rollout path. */
  tenantAllowlist: string[];
  systemPromptAddition: string | null;
  handoffTemplate: string;
  maxTurnsTemplate: string;
  enhancementTemplate: string;
  bugTemplate: string;
  updatedAt: string | null;
  /** The version these values come from — what the agent is running right now. */
  activeVersion: number | null;
  activeVersionId: string | null;
  flagEnabled: boolean;
  /** Problems that would break the agent at runtime; shown prominently when live. */
  configErrors: string[];
}

// ====== AI DEFLECTION TRACE TYPES ======
// Mirrors src/support-tickets/support-deflection-trace-schemas.ts.

/** One row in the cross-ticket trace list. */
export interface SupportDeflectionTraceRow {
  turnId: string;
  ticketId: string;
  ticketSubject: string;
  tenantId: string;
  ticketType: string;
  turnNumber: number;
  createdAt: string;
  /** The enforced verdict. */
  judgeVerdict: string | null;
  /** What the judge said before the cross-tenant guard could override it. */
  judgeVerdictRaw: string | null;
  verdictForcedBy: string | null;
  /**
   * Why it was dropped — a JudgeDropCategory, or null on a send or a pre-change row.
   *
   * The point of the vocabulary is that `leak` and `unsure` stop being the same
   * observable: one is the gate earning its keep, the other is the gate guessing.
   */
  judgeDropCategory: string | null;
  /** Whether each stage's output could be READ. Null on rows predating the columns. */
  verifierParseOk: boolean | null;
  judgeParseOk: boolean | null;
  crossTenantLeak: boolean;
  outcome: string | null;
  delivered: boolean;
  verifierSkipped: boolean;
  attempt: number;
  verifierModel: string | null;
  judgeModel: string | null;
  verifierLatencyMs: number | null;
  judgeLatencyMs: number | null;
  /** False once the spans have aged out; the verdicts survive regardless. */
  hasSpans: boolean;
}

export interface SupportDeflectionTraceList {
  rows: SupportDeflectionTraceRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SupportReactorDecisionRow {
  ruleId: string;
  fired: boolean;
  reason: string | null;
  eventType: string;
  createdAt: string;
}

export interface SupportDeflectionTraceDetail extends SupportDeflectionTraceRow {
  customerText: string | null;
  draftText: string | null;
  critiqueText: string | null;
  cleanedText: string | null;
  deliveredText: string | null;
  judgeReason: string | null;
  judgeReasonRaw: string | null;
  verifierSkipReason: string | null;
  errorText: string | null;
  decisionJson: unknown;
  chatThreadId: string | null;
  chatMessageId: string | null;
  eventId: string | null;
  ruleId: string | null;
  workflowId: string | null;
  workflowRunId: string | null;
  configVersion: number | null;
  configVersionId: string | null;
  rulesetVersionId: string | null;
  /**
   * The evidence was pruned. MUST be rendered as "expired" rather than as an empty
   * timeline — an absent stage and an expired one looking alike is the failure this
   * surface exists to prevent.
   */
  spansExpired: boolean;
  reactorDecisions: SupportReactorDecisionRow[];
}

/** One stage span, payload omitted. */
export interface SupportDeflectionStageSpan {
  /** A string: chat_debug_events.id is a BigInt and cannot serialize to JSON. */
  spanId: string;
  category: string;
  name: string;
  status: string;
  attempt: number;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  detailBytes: number;
  /** True when a payload string was clipped — a real anomaly, not routine. */
  truncated: boolean;
}

export interface SupportDeflectionStageSpanDetail extends SupportDeflectionStageSpan {
  detail: unknown;
}

// ---- Grafana alert triage ----------------------------------------------------

export interface GrafanaTriageConfigResponse {
  credentialSetId: string | null;
  credentialSetName: string | null;
  slackChannelId: string | null;
  slackChannelName: string | null;
  // Opsgenie thread-matching. opsgenieChannelId null = threading off (standalone posts).
  // opsgenieAppId is the stored override, null = registry default in force.
  opsgenieChannelId: string | null;
  opsgenieChannelName: string | null;
  opsgenieAppId: string | null;
  defaultOpsgenieAppId: string;
  systemPrompt: string | null;
  defaultSystemPrompt: string;
  // Raw stored overrides (null = code default in force), mirroring systemPrompt.
  verifierModel: string | null;
  judgeModel: string | null;
  defaultVerifierModel: string;
  defaultJudgeModel: string;
  systemUserId: string | null;
  systemUserEmail: string | null;
  updatedAt: string | null;
  activeVersion: number | null;
  activeVersionId: string | null;
  flagEnabled: boolean;
  configErrors: string[];
}

export interface GrafanaTriageConfigUpdate {
  credentialSetId?: string | null;
  slackChannelId?: string | null;
  slackChannelName?: string | null;
  opsgenieChannelId?: string | null;
  opsgenieChannelName?: string | null;
  opsgenieAppId?: string | null;
  systemPrompt?: string | null;
  verifierModel?: string | null;
  judgeModel?: string | null;
  systemUserId?: string | null;
}

export interface GrafanaTriageConfigVersionRow {
  id: string;
  version: number;
  note: string | null;
  createdAt: string;
  createdByEmail: string | null;
  isActive: boolean;
}

export interface GrafanaAlertGroupRow {
  id: string;
  groupKey: string;
  alertName: string | null;
  status: string;
  severity: string | null;
  priority: string | null;
  category: string | null;
  verdictSummary: string | null;
  notificationCount: number;
  firingAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  slackPermalink: string | null;
  hasSpans: boolean;
}

export interface GrafanaAlertGroupList {
  rows: GrafanaAlertGroupRow[];
  total: number;
  page: number;
  limit: number;
}

export interface GrafanaAlertGroupDetail extends GrafanaAlertGroupRow {
  labels: unknown;
  annotations: unknown;
  finalAnalysis: string | null;
  draftText: string | null;
  critiqueText: string | null;
  judgeVerdict: string | null;
  judgeReason: string | null;
  errorText: string | null;
  verifierModel: string | null;
  judgeModel: string | null;
  verifierLatencyMs: number | null;
  judgeLatencyMs: number | null;
  chatThreadId: string | null;
  workflowId: string | null;
  workflowRunId: string | null;
  attempt: number;
  configVersion: number | null;
  slackTs: string | null;
  slackResolvedTs: string | null;
  opsgenieAlertId: string | null;
  opsgeniePermalink: string | null;
  spansExpired: boolean;
}

export interface GrafanaTriageStageSpan {
  spanId: string;
  category: string;
  name: string;
  status: string;
  attempt: number;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  detailBytes: number;
  truncated: boolean;
}

export interface GrafanaTriageStageSpanDetail extends GrafanaTriageStageSpan {
  detail: unknown;
}

export interface GrafanaWebhookLogRow {
  id: string;
  authOk: boolean;
  responseStatus: number;
  failureReason: string | null;
  alertStatus: string | null;
  groupKey: string | null;
  createdAt: string;
}

export interface GrafanaWebhookLogList {
  rows: GrafanaWebhookLogRow[];
  total: number;
  page: number;
  limit: number;
}

export interface GrafanaWebhookLogDetail extends GrafanaWebhookLogRow {
  rawBody: string;
}

/** One entry in the config's version history. */
export interface SupportDeflectionConfigVersionRow {
  id: string;
  version: number;
  note: string | null;
  createdAt: string;
  createdByEmail: string | null;
  isActive: boolean;
}

export interface SupportDeflectionConfigUpdate {
  maxTriageQuestions?: number;
  verifierModel?: string | null;
  judgeModel?: string | null;
  enhancementJiraProjectKey?: string;
  engineeringJiraProjectKey?: string;
  devopsJiraProjectKey?: string;
  testEnhancementJiraProjectKey?: string | null;
  testEngineeringJiraProjectKey?: string | null;
  testDevopsJiraProjectKey?: string | null;
  enhancementJiraIssueType?: string;
  engineeringJiraIssueType?: string;
  devopsJiraIssueType?: string;
  testEnhancementJiraIssueType?: string | null;
  testEngineeringJiraIssueType?: string | null;
  testDevopsJiraIssueType?: string | null;
  slaWindows?: Record<string, { hours: number; businessHours: boolean }> | null;
  severityMatrix?: Record<string, Record<string, string>> | null;
  calendar?: { timeZone: string; holidays: string[]; workdayStartHour: number; workdayEndHour: number } | null;
  sweepThresholds?: {
    breachWarningPct: number;
    psStaleBusinessDays: number;
    jiraStaleDays: number;
    pendingClientBusinessDays: number;
    aiResolvedBusinessDays: number;
    triageTimeoutMinutes: { portal: number; email: number };
  } | null;
  /** Null = fall back to the code default; [] = no research tools at all. */
  deflectionCategories?: string[] | null;
  systemUserId?: string | null;
  hubspotBotActorId?: string | null;
  tenantAllowlist?: string[];
  systemPromptAddition?: string | null;
  handoffTemplate?: string | null;
  maxTurnsTemplate?: string | null;
  enhancementTemplate?: string | null;
  bugTemplate?: string | null;
}

/** The Jira routing targets an admin configures and can test. Mirrors JIRA_TARGET_PROJECTS. */
export const JIRA_TARGETS = ['enhancement', 'engineering', 'devops'] as const;
export type JiraTarget = (typeof JIRA_TARGETS)[number];

export const JIRA_TARGET_LABELS: Record<JiraTarget, string> = {
  enhancement: 'Enhancement (PEQ)',
  engineering: 'Engineering (EIN)',
  devops: 'DevOps / Cloud Engineering (CE)',
};

export interface SupportDeflectionJiraTestResponse {
  ok: boolean;
  projectKey: string;
  issueType: string;
  issueKey?: string;
  issueUrl?: string;
  error?: string;
}

/**
 * Whether a routing target would accept an issue, asked without creating one.
 *
 * `unknown` (Jira unreachable) and `unconfigured` (no lab project key set, a
 * supported state) are deliberately distinct from `invalid` — only the last is a
 * problem to act on.
 */
export interface SupportDeflectionTargetPreflight {
  target: string;
  isTest: boolean;
  projectKey: string | null;
  issueType: string | null;
  status: 'ok' | 'unconfigured' | 'invalid' | 'unknown';
  detail: string | null;
  availableIssueTypes?: string[];
}

export interface SupportDeflectionPreflightResponse {
  targets: SupportDeflectionTargetPreflight[];
  hasInvalid: boolean;
  jiraUnreachable: boolean;
}

export interface SupportDeflectionTurn {
  id: string;
  turnNumber: number;
  customerText: string | null;
  draftText: string | null;
  critiqueText: string | null;
  cleanedText: string | null;
  judgeVerdict: string | null;
  judgeReason: string | null;
  deliveredText: string | null;
  decisionJson: unknown;
  outcome: string | null;
  verifierModel: string | null;
  judgeModel: string | null;
  chatThreadId: string | null;
  errorText: string | null;
  createdAt: string;
}

export interface OpsGenieConfigResponse {
  credentialSetId: string | null;
  defaultTeam: string | null;
  enabled: boolean;
  /** Problems an admin must fix before paging can work. Empty = ready. */
  problems: string[];
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface OpsGenieTestResponse {
  ok: boolean;
  apiUrl: string;
  team: string | null;
  error?: string;
}

// ─── Outbound mail (Mail Outbox) ────────────────────────────────────────────

export type OutboundMailMode = 'capture' | 'deliver';
export type OutboundMailStatus = 'pending' | 'sending' | 'captured' | 'sent' | 'failed';
export type OutboundMailProvider = 'sendgrid' | 'hubspot_template';

export interface OutboundMailConfigProblem {
  /** `info` is a fact to show (capture mode); `warning` needs fixing. */
  severity: 'info' | 'warning';
  message: string;
}

/** Who Hub emails about support cases (GET/PUT /api/v1/admin/support-notifications/config). */
export interface SupportNotificationConfig {
  escalationEmails: string[];
  psEscalationEmails: string[];
  /** The unplaced-Case alert channel, without the #; null when unset. */
  slackChannelName: string | null;
  updatedAt: string | null;
}

export interface OutboundMailConfigResponse {
  mode: OutboundMailMode;
  sendgridCredentialSetId: string | null;
  sendgridCredentialSetName: string | null;
  fromAddress: string;
  fromName: string;
  redirectAllTo: string | null;
  /** Per-category override of `mode`; a category not listed follows `mode`. */
  categoryModes: Partial<Record<string, OutboundMailCategoryMode>>;
  /** Deliver only to these addresses or "@domain"s; anything else is redirected. Empty = anyone. */
  deliverOnlyTo: string[];
  /** False when no row exists and the capture-mode defaults are in force. */
  configured: boolean;
  problems: OutboundMailConfigProblem[];
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

/** How one kind of email is sent, overriding the environment's mode. */
export type OutboundMailCategoryMode = 'deliver' | 'redirect' | 'capture';

export interface OutboundMailConfigUpdateInput {
  mode?: OutboundMailMode;
  sendgridCredentialSetId?: string | null;
  fromAddress?: string;
  fromName?: string;
  redirectAllTo?: string | null;
  categoryModes?: Partial<Record<string, OutboundMailCategoryMode>>;
  deliverOnlyTo?: string[];
}

/** One outbox row without its bodies. */
export interface OutboundMailMessageSummary {
  id: string;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  status: OutboundMailStatus;
  mode: OutboundMailMode;
  provider: OutboundMailProvider;
  category: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  deliveredTo: string[];
  redirected: boolean;
  subject: string;
  attempts: number;
  error: string | null;
  providerMessageId: string | null;
  ticketId: string | null;
  traceId: string | null;
}

/** One outbox row in full. */
export interface OutboundMailMessageDetail extends OutboundMailMessageSummary {
  idempotencyKey: string;
  fromName: string | null;
  replyTo: string[];
  html: string | null;
  text: string | null;
  headers: Record<string, string>;
  providerRequest: unknown;
  providerResult: string | null;
  sourceId: string | null;
}

export interface OutboundMailListQuery {
  recipient?: string;
  category?: string;
  status?: OutboundMailStatus;
  provider?: OutboundMailProvider;
  mode?: OutboundMailMode;
  ticketId?: string;
  traceId?: string;
  since?: string;
  until?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface OutboundMailCategoryInfo {
  id: string;
  label: string;
  description: string;
}

export interface LookerToolConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  defaultLookbackWeeks: number;
  cacheTtlSeconds: number;
  maxConcurrent: number;
  minTimeMs: number;
  maxRetries: number;
  configured: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface WordpressToolConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  baseUrl: string | null;
  postRecencyMonths: number;
  indexPagePatterns: string[];
  configured: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface WordpressDraftContent {
  title: string;
  html: string;
}

export interface WordpressDraftCreated {
  id: number;
  link: string;
  editLink: string;
  status: string;
}

// ─── Company Enrichment ──────────────────────────────────────────────────────

export interface CompanyEnrichmentConfigResponse {
  id: string | null;
  spiderCredentialSetId: string | null;
  spiderCredentialSetName: string | null;
  fireworksCredentialSetId: string | null;
  fireworksCredentialSetName: string | null;
  builtwithCredentialSetId: string | null;
  builtwithCredentialSetName: string | null;
  modelId: string | null;
  scrapePageLimit: number;
  contentCharLimit: number;
  maxOutputTokens: number;
  activePromptId: string | null;
  activePromptVersion: string | null;
  hubspotGradeProperty: string | null;
  hubspotGroundTruthProperty: string | null;
  hubspotEnrichmentProperties: string[];
  swanGradeThreshold: string;
  swanCacheTtlDays: number;
  hubspotSwanSourceProperty: string | null;
  hubspotSwanSourceValue: string | null;
  /** HubSpot property name stamped on manual push to HubSpot from the UI. */
  hubspotManualSourceProperty: string | null;
  /** HubSpot property value stamped on manual push (defaults to "Kibo Hub Manual"). */
  hubspotManualSourceValue: string | null;
  /** Optional cap on enrichments performed per day (cache misses + forced). null = no cap. */
  swanMaxQueriesPerDay: number | null;
  configured: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface EnrichmentPromptResponse {
  id: string;
  version: string;
  promptText: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface EnrichOneResult {
  companyName: string;
  website: string | null;
  domain: string | null;
  hubspotCompanyId: string | null;
  kiboFitScore: number | null;
  capdbGrade: 'A' | 'B' | 'C' | 'D' | null;
  classification: Record<string, unknown> | null;
  hubspotData: Record<string, string | null> | null;
  scrapedContent: string | null;
  scrapedPages: string | null;
  fetchMethod: string | null;
  llmResponse: string | null;
  error: string | null;
}

export interface EnrichmentApiKeyInfo {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface EnrichmentApiKeyCreateResult {
  id: string;
  name: string;
  fullKey: string;
  keyPrefix: string;
  createdAt: string;
}

/** One row in the Swan API audit panel (list view — bodies omitted). */
export interface SwanAuditCallRow {
  id: string;
  domain: string;
  companyName: string | null;
  kiboFitScore: number | null;
  capdbGrade: string | null;
  qualifies: boolean | null;
  ipAddress: string | null;
  userAgent: string | null;
  status: string;
  errorMessage: string | null;
  latencyMs: number | null;
  cached: boolean;
  /** The HubSpot company id Kibo created on this call, if any. */
  hubspotCreatedCompanyId: string | null;
  /** Best-effort error from the Swan-path HubSpot auto-create. */
  hubspotWriteError: string | null;
  createdAt: string;
}

/** Full Swan audit row including request/response bodies (detail view). */
export interface SwanAuditCallDetail extends SwanAuditCallRow {
  apiKeyId: string | null;
  requestBody: unknown;
  responseBody: unknown;
}

export interface SwanAuditListResult {
  rows: SwanAuditCallRow[];
  total: number;
  page: number;
  limit: number;
}

export interface EnrichmentBatchSummary {
  id: string;
  name: string;
  source: string;
  status: string;
  totalCompanies: number;
  completedCount: number;
  errorCount: number;
  gradeACount: number;
  gradeBCount: number;
  gradeCCount: number;
  gradeDCount: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  approvedAt: string | null;
  syncedAt: string | null;
  errorMessage: string | null;
}

export interface EnrichmentResultRow {
  id: string;
  companyName: string;
  website: string | null;
  domain: string | null;
  companyType: string | null;
  hubspotCompanyId: string | null;
  kiboFitScore: number | null;
  capdbGrade: 'A' | 'B' | 'C' | 'D' | null;
  kiboFitReasoning: string | null;
  industryVertical: string | null;
  supplyChainRole: string | null;
  storeCount: string | null;
  syncedToHubspot: boolean;
  error: string | null;
}

/**
 * Built-in default HubSpot company properties pulled (read-only) and fed to the
 * classifier when the admin has not configured a custom set. Mirrors the backend
 * DEFAULT_HUBSPOT_ENRICHMENT_PROPERTIES in company-enrichment-schemas.ts.
 */
export const DEFAULT_ENRICHMENT_HUBSPOT_PROPERTIES: string[] = [
  'industry',
  'numberofemployees',
  'annualrevenue',
  'type',
  'country',
  'state',
  'city',
  'description',
  'lifecyclestage',
  'hubspot_owner_id',
  'founded_year',
  'num_associated_deals',
  'hs_total_deal_value',
  'hs_is_target_account',
  'hs_lead_status',
];

export interface EnrichmentHubspotProperty {
  name: string;
  label: string;
  type: string;
}

export interface VerifiedCompaniesResponse {
  count: number;
  companies: Array<{
    id: string;
    companyName: string;
    domain: string;
    verifiedGrade: string;
    source: string;
    verifiedAt: string;
  }>;
}

export interface ReferenceTestSummary {
  id: string;
  status: string;
  sampleSize: number;
  stratified: boolean;
  accuracy: number | null;
  gradeMatchCount: number | null;
  total: number | null;
  confusionMatrix: Record<string, Record<string, number>> | null;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export interface ReferenceTestDetail extends ReferenceTestSummary {
  results: Array<{
    id: string;
    expectedGrade: string;
    predictedGrade: string | null;
    predictedScore: number | null;
    match: boolean;
    error: string | null;
    verifiedCompany: { companyName: string; domain: string };
  }>;
}

export interface KpiWithYoy {
  value: number;
  priorValue: number;
  deltaPct: number | null;
}

export interface OrderSummaryResponse {
  range: LookerRange;
  startDate: string;
  endDate: string;
  totalCollected: KpiWithYoy;
  averageOrderValue: KpiWithYoy;
  validOrders: KpiWithYoy;
  paidOrders: KpiWithYoy;
  fulfilledOrders: KpiWithYoy;
  itemsPerOrder: KpiWithYoy;
}

export interface WeeklyTrendPoint {
  week: string;
  totalCollected: number;
  validOrders: number;
}

export interface WeeklyTrendResponse {
  range: LookerRange;
  current: WeeklyTrendPoint[];
  prior: WeeklyTrendPoint[];
}

export interface ChannelBreakdownRow {
  siteId: string | null;
  siteName: string | null;
  validOrders: number;
  totalCollected: number;
}

export interface CountryBreakdownRow {
  country: string | null;
  validOrders: number;
  totalCollected: number;
}

export interface ChannelBreakdownResponse {
  range: LookerRange;
  bySite: ChannelBreakdownRow[];
  byCountry: CountryBreakdownRow[];
}

export interface TopProductRow {
  productCode: string | null;
  productName: string | null;
  quantitySold: number;
}

export interface OrderItemDetailsResponse {
  range: LookerRange;
  topProducts: TopProductRow[];
  weeklyItemsPerOrder: { week: string; ipo: number }[];
}

// ─── Grafana (operational data) ────────────────────────────────────────────

export type GrafanaRangeHours = 1 | 6 | 24 | 168;

export interface GrafanaEndpointSummary {
  id: string;
  identifier: string;
  name: string;
  url: string;
  credentialSetId: string;
  credentialSetName: string;
  datasourceUid: string;
  isActive: boolean;
  skipTlsVerify: boolean;
  createdAt: string;
}

export interface GrafanaEndpointDetail extends GrafanaEndpointSummary {
  createdBy: { id: string; displayName: string };
  updatedBy: { id: string; displayName: string };
  updatedAt: string;
}

export interface GrafanaConnectionTestResult {
  success: boolean;
  status?: string;
  error?: string;
}

export interface GrafanaToolConfigResponse {
  id: string | null;
  defaultRangeHours: number;
  queryCacheTtlSeconds: number;
  maxConcurrent: number;
  minTimeMs: number;
  maxRetries: number;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface GrafanaDashboardConfigResponse {
  id: string;
  grafanaEndpointId: string;
  key: string;
  label: string;
  uid: string;
  excludedPanelIds: number[];
  displayOrder: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GrafanaPanelInventoryEntry {
  id: number;
  title: string;
  panelType: string;
  hasTenantVariable: boolean;
  excluded: boolean;
}

export interface GrafanaPanelInventoryResponse {
  key: string;
  uid: string;
  label: string;
  panels: GrafanaPanelInventoryEntry[];
}

export interface GrafanaDashboardSummary {
  key: string;
  label: string;
  displayOrder: number;
  enabled: boolean;
}

// ─── Grafana panel payloads (discriminated union by `kind`) ────────────────

export interface GrafanaSeriesPayload {
  kind: 'series';
  unit: string | null;
  series: Array<{ name: string; points: Array<[number, number | null]> }>;
}

export interface GrafanaStatPayload {
  kind: 'stat';
  value: number | null;
  unit: string | null;
  thresholdColor: string | null;
}

export interface GrafanaBarsPayload {
  kind: 'bars';
  unit: string | null;
  items: Array<{ name: string; value: number }>;
}

export interface GrafanaPiePayload {
  kind: 'pie';
  slices: Array<{ name: string; value: number }>;
}

export interface GrafanaTablePayload {
  kind: 'table';
  columns: string[];
  rows: Array<Array<string | number | null>>;
}

export interface GrafanaLogsPayload {
  kind: 'logs';
  lines: Array<{ time: number | null; level: string | null; message: string }>;
}

export interface GrafanaTextPayload {
  kind: 'text';
  mode: 'markdown' | 'html' | 'text';
  content: string;
}

export interface GrafanaTimelinePayload {
  kind: 'timeline';
  series: Array<{ name: string; segments: Array<{ from: number; to: number; state: string }> }>;
}

export interface GrafanaHeatmapPayload {
  kind: 'heatmap';
  unit: string | null;
  buckets: Array<{ x: number; y: string; value: number }>;
}

export interface GrafanaUnsupportedPayload {
  kind: 'unsupported';
  panelType: string;
}

export interface GrafanaErrorPayload {
  kind: 'error';
  message: string;
}

export type GrafanaPanelPayload =
  | GrafanaSeriesPayload
  | GrafanaStatPayload
  | GrafanaBarsPayload
  | GrafanaPiePayload
  | GrafanaTablePayload
  | GrafanaLogsPayload
  | GrafanaTextPayload
  | GrafanaTimelinePayload
  | GrafanaHeatmapPayload
  | GrafanaUnsupportedPayload
  | GrafanaErrorPayload;

export interface GrafanaDashboardPanel {
  id: number;
  title: string;
  panelType: string;
  grafanaUrl: string;
  payload: GrafanaPanelPayload;
}

export interface GrafanaDashboardResponse {
  key: string;
  label: string;
  uid: string;
  from: number;
  to: number;
  rangeHours: number;
  panels: GrafanaDashboardPanel[];
}

// Kibo Verification Tenants
export interface KiboVerificationTenant {
  id: string;
  credentialSetId: string;
  credentialSetName: string;
  displayName: string | null;
  baseUrl: string | null;
  enabled: boolean;
  displayOrder: number;
  notes: string | null;
  updatedAt: string;
  updatedById: string | null;
}

export interface KiboTenantNotesRevision {
  id: string;
  tenantId: string;
  notes: string;
  mode: string;
  createdById: string | null;
  createdAt: string;
}

// Google Drive
export interface GoogleDriveConfig {
  credentialSetId: string | null;
  pickerApiKey: string | null;
}

export interface GoogleDriveFolder {
  id: string;
  googleFolderId: string;
  folderName: string;
  folderUrl: string;
  googleUserEmail: string;
  cachedFileCount: number | null;
  lastListedAt: string | null;
  connectedById: string;
  connectedBy: { id: string; displayName: string };
  createdAt: string;
}

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size: string | null;
  modifiedTime: string | null;
  webViewLink: string | null;
  parents?: string[];
}

export interface GoogleDriveFileList {
  files: GoogleDriveFile[];
  nextPageToken: string | null;
  totalCount: number | null;
}

export interface GoogleDriveFileContent {
  fileId: string;
  fileName: string;
  mimeType: string;
  content: string;
  format: 'markdown' | 'text';
}

// DEV-ONLY: Atlassian token export/import shape — remove with import/export feature
export interface AtlassianDevTokenExport {
  client_id: string | null;
  client_secret: string | null;
  access_token: string | null;
  refresh_token: string | null;
  token_expires_at: string | null;
  cloud_id: string | null;
  site_url: string | null;
  site_name: string | null;
}

// DEV-ONLY: Google Drive folder export/import entry shape — remove with import/export feature
export interface GoogleDriveDevFolderExport {
  googleFolderId: string;
  folderName: string;
  folderUrl: string;
  googleUserEmail: string;
  googleUserId: string;
  scopes: string;
  refreshToken: string;
}

// Kibo
export interface KiboAuthStatus {
  authenticated: boolean;
  tenantId?: string;
}

export interface KiboTenantResult {
  data: unknown;
}

export interface KiboAuthConfig {
  credentialSetId: string | null;
  credentialSetName: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

// ============================================================================
// DOC FEEDBACK TYPES
// ============================================================================

export interface DocFeedbackSuggestions {
  title: string;
  aspect: string;
}

export interface DocFeedbackTicket {
  id: string;
  threadId: string;
  jiraIssueKey: string;
  jiraIssueUrl: string;
  title: string;
  aspect: string;
  notes: string | null;
  submittedById: string;
  submittedAt: string;
  submittedBy: { id: string; displayName: string };
  threadTitle: string | null;
}

export interface DocFeedbackTicketWithStatus extends DocFeedbackTicket {
  jiraStatus: string | null;
}

export interface JiraTransition {
  id: string;
  name: string;
}

// Local helper for the handful of helpers that need to fabricate an ApiError
// without going through fetchApi (e.g. raw `fetch()` paths that return Blob).
function localApiError(code: string, message: string): ApiError {
  return { success: false, error: { code, message, timestamp: new Date().toISOString() } };
}

async function fetchApi<T>(url: string, options?: RequestInit): Promise<ApiResult<T>> {
  // Only set Content-Type for JSON bodies — let the browser set it for FormData
  const headers: Record<string, string> = {};
  if (typeof options?.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
    credentials: 'include',
  });

  const data = await response.json() as ApiResult<T>;
  return data;
}

// ============================================================================
// ASSISTANT WIDGET TYPES
// ============================================================================

export interface AssistantSummary {
  id: string;
  name: string;
  slug: string;
  allowedDomains: string[];
  isActive: boolean;
  threadCount: number;
  createdAt: string;
  createdBy: { id: string; displayName: string };
}

export interface AssistantDetail {
  id: string;
  name: string;
  slug: string;
  publishableKey: string;
  previousKey: string | null;
  previousKeyExpiresAt: string | null;
  systemPrompt: string;
  model: string;
  allowedDomains: string[];
  allowedTools: string[];
  conversationStarters: string[];
  followupEnabled: boolean;
  followupCount: number;
  maxToolSteps: number;
  rateLimitPerVisitorPerHour: number;
  accentColor: string;
  greeting: string;
  placeholderText: string;
  headerSubtitle: string | null;
  launcherPosition: string;
  autoOpenDelayMs: number | null;
  isActive: boolean;
  credentialSetId: string | null;
  createdById: string;
  createdBy: { id: string; displayName: string };
  updatedById: string;
  updatedBy: { id: string; displayName: string };
  createdAt: string;
  updatedAt: string;
}

export interface AssistantCreateInput {
  name: string;
  slug: string;
  systemPrompt: string;
  model?: string;
  allowedDomains?: string[];
  allowedTools?: string[];
  conversationStarters?: string[];
  followupEnabled?: boolean;
  followupCount?: number;
  maxToolSteps?: number;
  rateLimitPerVisitorPerHour?: number;
  accentColor?: string;
  greeting?: string;
  placeholderText?: string;
  headerSubtitle?: string | null;
  launcherPosition?: string;
  autoOpenDelayMs?: number | null;
  credentialSetId?: string | null;
  isActive?: boolean;
}

export interface AssistantAnalytics {
  conversationsByDay: Array<{ date: string; count: number }>;
  totalConversations: number;
  totalMessages: number;
  uniqueVisitors: number;
  avgMessagesPerConversation: number;
  feedbackSummary: { thumbsUp: number; thumbsDown: number };
  totalTokens: { input: number; output: number };
  estimatedCost: number;
}

export interface AssistantThreadSummary {
  id: string;
  visitorToken: string;
  title: string | null;
  messageCount: number;
  rating: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantThreadDetail {
  id: string;
  visitorToken: string;
  title: string | null;
  rating: number | null;
  createdAt: string;
  messages: Array<{
    id: string;
    role: string;
    content: string;
    metadata: unknown;
    inputTokens: number | null;
    outputTokens: number | null;
    estimatedCost: string | null;
    createdAt: string;
  }>;
}

export interface AssistantFeedbackSummary {
  id: string;
  threadId: string | null;
  threadTitle: string | null;
  content: string;
  email: string | null;
  source: string;
  rating: number | null;
  isResolved: boolean;
  resolvedNote: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  isSpam: boolean;
  createdAt: string;
}

export interface AssistantFeedbackStats {
  total: number;
  unresolved: number;
  spam: number;
  bySource: { user: number; llm: number; 'widget-form': number };
}

// RFP Qualifier v2 types

export interface RfpPipelineToolCall {
  toolName: string;
  input: unknown;
  output: string;
}

export interface RfpSearchResultItem {
  id: string;
  type: 'qa' | 'doc';
  question?: string;
  answer?: string;
  title?: string;
  content?: string;
  sourceUrl?: string;
  score: number;
  finalScore: number;
  excerpt: string;
}

export interface RfpPipelineTestResult {
  answer: string;
  confidence: 'high' | 'medium' | 'low' | 'no_data';
  gaps: string[];
  sources: RfpSearchResultItem[];
  trace: RfpPipelineToolCall[];
  durationMs: number;
}

export interface RfpSearchToolTestResult {
  sources: RfpSearchResultItem[];
  formatted: string;
  durationMs: number;
}

export interface RfpConfig {
  id: string;
  credentialSetId?: string | null;
  model: string;
}

export interface RfpKbStatus {
  qaPairCount: number;
  docChunkCount: number;
  sourceCount: number;
  embeddedQaCount: number;
  embeddedDocCount: number;
}

export interface RfpKbGroup {
  id: string;
  name: string;
  description: string | null;
  sourceCount?: number;
  createdAt: string;
}

export interface RfpKbSource {
  id: string;
  sourceType: string;
  name: string;
  pathOrUrl?: string | null;
  status: string;
  itemCount?: number | null;
  groupId: string | null;
  groupName: string | null;
  createdAt: string;
}

export interface RfpKbImportResult {
  created: string[];
  replaced: string[];
  skipped: Array<{ id: string; reason: string }>;
}

export interface RfpSession {
  id: string;
  name: string;
  filename: string;
  uploadedById: string;
  uploadedByName: string;
  profileId?: string | null;
  totalQuestions?: number | null;
  verifiedCount: number;
  unverifiedCount: number;
  gapCount: number;
  needsReviewCount: number;
  status: string;
  hasOriginalFile: boolean;
  createdAt: string;
}

export interface RfpResult {
  id: string;
  sessionId: string;
  rowNumber: number;
  sheetName?: string | null;
  question: string;
  generatedAnswer?: string | null;
  editedAnswer?: string | null;
  sources?: unknown;
  /** Calibrated epistemic confidence 0..1 — independent of supportScore. */
  confidence?: number | null;
  confidenceBand?: 'high' | 'medium' | 'low' | 'no_data' | null;
  /** Fuzzy truth value 0..1 — 1.0 = Kibo fully supports, 0.0 = Kibo does not support. */
  supportScore?: number | null;
  supportBand?: 'yes' | 'partial' | 'no' | 'unknown' | null;
  gaps?: string[] | null;
  /** Per-step record of LLM tool calls (name + input args + truncated output). */
  toolTrace?: Array<{ toolName: string; input: unknown; output: string }> | null;
  /** Subset of `sources[].id` the model said materially shaped the answer. */
  usedSourceIds?: string[];
  verificationStatus?: string | null;
}

/**
 * What `GET /sessions/:id/results` returns: every field except `sources` and
 * `toolTrace`. Those two were 94% of the list payload (15.2 MB on a 234-row
 * session) and no list view renders them, so the server stopped sending them
 * in bulk. Use `api.getRfpResult` for the full row when you need either.
 */
export type RfpResultSummary = Omit<RfpResult, 'sources' | 'toolTrace'>;

export interface RfpResponseProfile {
  id: string;
  name: string;
  tone: string;
  length: string;
  wordLimit?: number | null;
  charLimit?: number | null;
  focusAreas: string[];
  pointOfView: string;
  citationStyle: string;
  customInstructions?: string | null;
  isDefault: boolean;
  strategy: 'standard' | 'marketing_analyst';
  applyLooksLikeQuestion: boolean;
  kbGroups: RfpKbGroup[];
}

export interface RfpProgressEvent {
  id: string;
  eventType: 'start' | 'sheet_detected' | 'question_done' | 'done' | 'error' | string;
  payload: {
    // start
    totalQuestions?: number;
    profileName?: string;
    strategy?: string;
    // sheet_detected — column-detection outcome per visible sheet
    questionCol?: number | null;
    answerCol?: number | null;
    questionConfidence?: number;
    answerConfidence?: number;
    questionCount?: number;
    skipped?: boolean;
    // question_done
    sheetName?: string;
    rowNumber?: number;
    question?: string;
    durationMs?: number;
    toolCallCount?: number;
    /** Per-tool call counts, e.g. `{ search_embeddings: 2, search_kibo_docs: 1 }`. */
    toolCallsByName?: Record<string, number>;
    confidence?: number;
    confidenceBand?: 'high' | 'medium' | 'low' | 'no_data';
    supportScore?: number;
    supportBand?: 'yes' | 'partial' | 'no' | 'unknown';
    verificationStatus?: 'verified' | 'unverified' | 'gap';
    error?: string;
    // error event
    message?: string;
  };
  createdAt: string;
}

export interface RfpExcelColumnInfo {
  index: number;
  name: string;
  letter: string;
  display: string;
}

export interface RfpExcelSheetInfo {
  name: string;
  rowCount: number;
  columns: RfpExcelColumnInfo[];
  preview: Record<string, unknown>[];
}

export interface RfpExcelFileInfo {
  sheets: RfpExcelSheetInfo[];
}

export interface RfpQaChunk {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  syntheticQueries: string[];
  hasEmbedding: boolean;
}

export interface RfpDocChunkDetail {
  id: string;
  title: string | null;
  content: string;
  sourceUrl: string | null;
  keywords: string[];
  syntheticQueries: string[];
  hasEmbedding: boolean;
}

export interface RfpSourceChunks {
  source: RfpKbSource;
  qaPairs: RfpQaChunk[];
  docChunks: RfpDocChunkDetail[];
}

// ─── Pricing Calculator Types ────────────────────────────────────────────────

export interface PricingGlobalSettings {
  uplift_pct: number;
  impl_discount_pct: number;
  offline_multiplier: number;
}

export interface PricingPriceTier {
  id: number;
  product: string;
  maxVolume: number;
  basePrice: number;
}

export interface PricingTierMultiplier {
  id: number;
  product: string;
  tier: 'good' | 'better' | 'best';
  multiplier: number;
}

export interface PricingRule {
  id: number;
  product: string;
  ruleType: 'trigger' | 'addon';
  key: string;
  label: string;
  tierForce: string | null;
  multiplier: number | null;
}

export interface PricingNfrOption {
  id: number;
  key: string;
  label: string;
  multiplier: number;
}

export interface PricingSlaOption {
  id: number;
  label: string;
  multiplier: number;
}

export interface PricingDrOption {
  id: number;
  label: string;
  multiplier: number;
}

export interface PricingUpliftOption {
  id: number;
  key: string;
  label: string;
  group: 'support' | 'agentic' | 'oms_addon';
  upliftPct: number;
  minUpliftPct: number;
  products: string;
}

export interface PricingInfraCost {
  key: string;
  price: number;
}

export interface PricingKeyValue {
  key: string;
  value: string;
}

export interface PricingDropshipTier {
  id: number;
  maxPartners: number;
  pricePerPartner: number;
}

export interface PricingConvoTier {
  id: number;
  maxConversations: number;
  pricePerConversation: number;
}

export interface PricingDealDiscountTier {
  id: number;
  minAcv: number;
  maxAcv: number | null;
  discountPct: number;
}

export interface PricingScheduleRow {
  year: number;
  onlineVolume: number;
  offlineVolume: number;
  onlineDiscountPct: number;
  offlineDiscountPct: number;
}

export interface PricingDropshipScheduleRow {
  year: number;
  tradingPartners: number;
  discountPct: number;
}

export interface PricingConvoScheduleRow {
  year: number;
  conversations: number;
  discountPct: number;
}

export type PricingCalculateInput =
  | {
      product: 'eComm' | 'OMS';
      customer: string;
      term: number;
      volType: 'Order Lines' | 'Orders';
      convFactor: number;
      implMonths: number;
      pooled: boolean;
      features: string[];
      nfrs: string[];
      slaSelection: string;
      drSelection: string;
      supportSelection: string;
      supportDiscountPct: number;
      agenticSelections: Array<{ key: string; discountPct: number }>;
      omsAddonSelections: string[];
      infra: { sandboxes: number; staging: number; perfMode: 'Ad-Hoc' | 'Dedicated'; perfCount: number; numSites?: number };
      schedule: PricingScheduleRow[];
    }
  | {
      product: 'Dropship';
      customer: string;
      term: number;
      schedule: PricingDropshipScheduleRow[];
    }
  | {
      product: 'Conversational';
      customer: string;
      term: number;
      schedule: PricingConvoScheduleRow[];
    };

export interface PricingImportResult {
  imported: boolean;
  counts: Record<string, number>;
}

export interface PricingClearResult {
  cleared: boolean;
  counts: Record<string, number>;
}

export interface PricingScheduleResult {
  year: number;
  volOn: number;
  volOff: number;
  feeOn: number;
  feeOff: number;
  netTotal: number;
  unitPrice: number;
}

export interface PricingDropshipScheduleResult {
  year: number;
  tradingPartners: number;
  partnerFee: number;
  platformFee: number;
  recurringSubtotal: number;
  discountAmount: number;
  netRecurring: number;
  onboardingFee: number;
  netTotal: number;
}

export interface PricingConvoScheduleResult {
  year: number;
  conversations: number;
  platformFee: number;
  usageFee: number;
  pricePerConv: number;
  netTotal: number;
}

export interface PricingBreakdownMultiplier {
  label: string;
  value: number;
  category: 'tier' | 'feature' | 'nfr' | 'sla' | 'dr' | 'uplift';
}

export interface PricingBreakdownInfraLine {
  label: string;
  detail?: string;
  annual: number;
}

export interface PricingBreakdown {
  tier: { final: string; multiplier: number; triggers: { feature: string; forces: string }[] };
  multipliers: PricingBreakdownMultiplier[];
  combinedMultiplier: number;
  infra: PricingBreakdownInfraLine[];
  infraAnnual: number;
}

export interface PricingResult {
  tier: string;
  schedule: Array<PricingScheduleResult | PricingDropshipScheduleResult | PricingConvoScheduleResult>;
  implFee: number;
  tcv: number;
  acv: number;
  explanation: string[];
  /** Structured derivation (eComm/OMS only); absent for Dropship/Conversational and older saved quotes. */
  breakdown?: PricingBreakdown;
}

export interface PricingQuoteCreate {
  customer: string;
  product: 'eComm' | 'OMS' | 'Dropship' | 'Conversational';
  tcv: number;
  acv: number;
  details: Record<string, unknown>;
  dealId?: string | null;
}

export interface PricingQuote extends PricingQuoteCreate {
  id: number;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  deal?: { id: number; dealName: string } | null;
  createdBy?: { id: string; name: string | null; email: string } | null;
}

export interface PricingDealCreate {
  dealName: string;
  quoteIds?: number[];
}

export interface PricingDealUpdate {
  dealName?: string;
  quoteIds?: number[];
}

export interface PricingDeal {
  id: number;
  dealName: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  totalAcv: number;
  discountPct: number;
  finalAcv: number;
  archivedAt: string | null;
  quotes: Pick<PricingQuote, 'id' | 'customer' | 'product' | 'acv' | 'tcv'>[];
}

export interface PricingTestResult {
  name: string;
  passed: boolean;
  details: string;
  expected: Record<string, unknown>;
  actual: Record<string, unknown>;
}

// ── Persona Chat Types ────────────────────────────────────────────────────────

export type PersonaFieldDefinition =
  | { key: string; label: string; description?: string; type: 'text'; required?: boolean; placeholder?: string }
  | { key: string; label: string; description?: string; type: 'textarea'; required?: boolean; placeholder?: string; rows?: number }
  | { key: string; label: string; description?: string; type: 'dropdown'; options: Array<{ label: string; value: string }>; required?: boolean; allowCustom?: boolean }
  | { key: string; label: string; description?: string; type: 'slider'; min: number; max: number; step: number; default?: number };

export interface PersonaTaskSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  systemPrompt: string;
  promptTemplate: string;
  fields: PersonaFieldDefinition[];
  aiHint: string | null;
  aiHintPlaceholder: string | null;
  sortOrder: number; // per-persona sort order from junction
  toolCategories: string[];
  createdAt: string;
  updatedAt: string;
  updatedBy: { id: string; displayName: string };
}

export interface AdminTaskPersonaAssignment {
  personaId: string;
  personaName: string;
  personaSlug: string;
  sortOrder: number;
}

export interface AdminTaskSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  systemPrompt: string;
  promptTemplate: string;
  fields: PersonaFieldDefinition[];
  aiHint: string | null;
  aiHintPlaceholder: string | null;
  sortOrder: number;
  toolCategories: string[];
  personaCount: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: { id: string; displayName: string };
}

export interface AdminTaskDetail extends AdminTaskSummary {
  personas: AdminTaskPersonaAssignment[];
}

export interface PersonaSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  baseSystemPrompt: string | null;
  sortOrder: number;
  availableToAll: boolean;
  groups: Array<{ id: string; name: string; slug: string }>;
  freeFormToolCategories: string[];
  tasks: PersonaTaskSummary[];
  createdAt: string;
  updatedAt: string;
  updatedBy: { id: string; displayName: string };
}

export interface UserPersonaTaskSummary {
  id: string;
  userId: string;
  userDisplayName: string;
  name: string;
  description: string | null;
  icon: string | null;
  systemPrompt: string;
  promptTemplate: string;
  fields: PersonaFieldDefinition[];
  aiHint: string | null;
  aiHintPlaceholder: string | null;
  sortOrder: number;
  toolCategories: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ChatInitPersonaTask {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  fields: PersonaFieldDefinition[];
  aiHint: string | null;
  aiHintPlaceholder: string | null;
  toolCategories: string[];
  promptTemplate: string;
}

export interface ChatInitPersona {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  freeFormToolCategories: string[];
  tasks: ChatInitPersonaTask[];
}

export interface ChatInitData {
  personas: ChatInitPersona[];
  myTasks: Array<{
    id: string;
    name: string;
    description: string | null;
    icon: string | null;
    fields: PersonaFieldDefinition[];
    aiHint: string | null;
    aiHintPlaceholder: string | null;
    toolCategories: string[];
    promptTemplate: string;
  }>;
  allToolCategories: string[];
  prompts: {
    checkAccuracy: string;
  };
}

export interface PersonaCreate {
  name: string;
  description?: string;
  baseSystemPrompt?: string;
  sortOrder?: number;
  availableToAll?: boolean;
  groupIds: string[];
  freeFormToolCategories: string[];
}

export interface PersonaTaskCreate {
  name: string;
  description?: string;
  icon?: string;
  systemPrompt?: string;
  promptTemplate: string;
  fields: PersonaFieldDefinition[];
  aiHint?: string;
  aiHintPlaceholder?: string;
  sortOrder?: number;
  toolCategories: string[];
}

export interface UserPersonaTaskCreate {
  name: string;
  description?: string;
  icon?: string;
  systemPrompt?: string;
  promptTemplate: string;
  fields: PersonaFieldDefinition[];
  aiHint?: string;
  aiHintPlaceholder?: string;
  sortOrder?: number;
  toolCategories: string[];
}

// The export payload is no longer modelled here: the file is uploaded verbatim
// and parsed server-side, so PersonaExportFormatSchema in
// src/personas/persona-schemas.ts is its single definition.

export interface PersonaImportPreview {
  tasksToCreate: string[];
  tasksToUpdate: string[];
  personasToCreate: string[];
  personasToUpdate: string[];
  warnings: string[];
}

export interface AdminUserPersonaTaskList {
  items: UserPersonaTaskSummary[];
  total: number;
  page: number;
  limit: number;
}

// ── A2A PROTOCOL TYPES ────────────────────────────────────────────────────────

export interface A2aApiKeyInfo {
  id: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

// ============================================================================
// SUPPORT TICKETS API (admin key management)
// ============================================================================

/** Ticket numbering: what the next case number will be. */
export interface TicketNumberInfo {
  /** Padded, exactly as the next ticket's id will read (e.g. "08834933"). */
  next: string;
  /** The same value unpadded, for prefilling the seed input. */
  nextValue: string;
}

export interface SupportKeyInfo {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  /** A Support Lab key: it addresses test tickets and only those. */
  isTest: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface SupportKeyCreateResult {
  id: string;
  name: string;
  fullKey: string;
  keyPrefix: string;
  isTest: boolean;
  createdAt: string;
}

export interface SupportPersonRef {
  email: string;
  name: string;
}

export interface SupportAgentRef {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface SupportAttachmentInfo {
  id: string;
  fileName: string;
  size: number;
  mimeType: string;
  scanStatus: 'pending' | 'clean' | 'blocked';
}

export interface SupportMessageInfo {
  id: string;
  ticketId: string;
  kind: 'message';
  authorRole: 'tenant-user' | 'tenant-teammate' | 'kibo-agent';
  authorName: string;
  body: string;
  // True when this message was generated by AI (vs a human Kibo agent). Drives
  // the distinct AI styling + "AI-generated" label.
  aiGenerated: boolean;
  createdAt: string;
  attachments: SupportAttachmentInfo[];
}

// A non-message timeline entry (status/priority/lifecycle change). `type` is an
// open string — render `summary` for any value so new event types need no UI change.
export interface SupportTicketEvent {
  id: string;
  ticketId: string;
  kind: 'event';
  type: string;
  summary: string;
  actor: SupportPersonRef | null;
  createdAt: string;
  data?: Record<string, unknown>;
}

export type SupportTimelineItem = SupportMessageInfo | SupportTicketEvent;

export interface SupportTicketSummary {
  id: string;
  subject: string;
  /** Derived from stage (+ the linked Jira issue's status once routed). */
  status: string;
  /** Rendered label for `status` — render this rather than mapping ids. */
  statusLabel: string;
  /** The severity the CLIENT reported. Their claim, not Kibo's assessment. */
  priority: string;
  category: string;
  requesterOrg: string;
  devAccountId: string;
  opener: SupportPersonRef;
  assignedAgent: SupportAgentRef | null;
  createdAt: string;
  updatedAt: string;
  unread: boolean;
  messageCount: number;
  /** Which Kibo team holds the case. Null until triage routes it. */
  teamHandling: string | null;
  jiraKey: string | null;
  jiraStatus: string | null;
  jiraFixVersion: string | null;
  /** The latest progress update — present only once a human approved it. */
  latestUpdate: string | null;
  // True while AI is composing a reply — show a loading indicator and keep polling.
  aiResponsePending: boolean;
  /**
   * Seconds the AI has been composing; null when it isn't (or when the start time
   * is unknown). Refines the indicator's wording on a long wait — see
   * `supportAiWaitState`. Not a substitute for `aiResponsePending`.
   */
  aiResponseWaitSeconds: number | null;
  /**
   * True when the ticket can be opened but not written to — the archived
   * Salesforce case history the mirror serves after the Salesforce portal is
   * switched off. False on every live ticket. Do not infer it from `status`: a
   * closed live ticket is still replyable and an archived one is not.
   */
  readOnly: boolean;
  /** Why, phrased for the reader. Null exactly when `readOnly` is false. */
  readOnlyReason: string | null;
  /**
   * Until when a closed case can still be reopened by replying (ISO time); null
   * while it is open, and on archived cases. Before then a reply reopens the
   * case; after it, the reply is a 409 and the customer opens a new ticket.
   */
  reopenableUntil: string | null;
  /** Contract 4.0: when the client escalated the case, or null. A case escalates once. */
  escalatedAt: string | null;
  /**
   * Contract 4.0: who closed a closed case, which says how it ended — `client`
   * (closed it or confirmed the fix), `kibo` (Kibo resolved it), `system` (closed
   * without a fix because nobody replied). Null while open.
   */
  closedBy: 'client' | 'kibo' | 'system' | null;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  timeline: SupportTimelineItem[];
}

// ---- Support Lab ----------------------------------------------------------
// The in-Hub simulator. A lab ticket IS a SupportTicket and a lab run reads back
// as SupportTicketDetail — the whole point is that the lab sees what a client
// sees — so the types below cover only what is extra: the picker, readiness, the
// run list, and reply verdicts.

export interface SupportLabTargetTenant {
  tenantId: string;
  displayName: string;
  tenantType: string;
  tenantRole: string;
  status: string;
  /** Null when the tenant has no dev account, which makes it unusable here. */
  devAccountId: string | null;
  devAccountLabel: string | null;
}

export interface SupportLabTarget {
  accountId: string;
  accountName: string;
  tenants: SupportLabTargetTenant[];
}

export interface SupportLabReadiness {
  ready: boolean;
  apiKeyConfigured: boolean;
  apiKeyPrefix: string | null;
  apiKeyUsable: boolean;
  personaDomainConfigured: boolean;
  personaDomain: string | null;
  systemUserConfigured: boolean;
  testJiraTargets: { engineering: boolean; devops: boolean; enhancement: boolean };
  blockers: string[];
}

export interface SupportLabHubSpotLink {
  url: string | null;
  reason: string | null;
}

export interface SupportLabVerdictOption {
  id: string;
  label: string;
  description: string;
  acceptable: boolean;
  expectsCorrectedText: boolean;
}

export interface SupportLabVerdict {
  id: string;
  /** Set when the verdict judges a reply the client received. */
  messageId: string | null;
  /** Set when it judges a draft the judge suppressed. Exactly one of the two. */
  turnId: string | null;
  verdict: string;
  correctedText: string | null;
  note: string | null;
  createdByName: string;
  createdById: string;
  createdAt: string;
}

/** The synthetic customer an operator acts as on one tenant. */
export interface SupportLabPersona {
  id: string;
  tenantId: string;
  email: string;
  displayName: string;
}

/**
 * One AI triage turn, as the gutter rail beside the portal frame needs it.
 *
 * `suppressed` and `deliveredText` are separate facts. A judge DROP is not
 * silence: the pipeline usually sends a canned handoff in the draft's place, so
 * the customer saw *something* — just not what the model wrote. `deliveredText`
 * is null in the genuinely-silent case, and neither fact is visible from the
 * thread alone.
 */
export interface SupportLabTurn {
  id: string;
  turnNumber: number;
  judgeVerdict: string | null;
  judgeReason: string | null;
  outcome: string | null;
  draftText: string | null;
  deliveredText: string | null;
  suppressed: boolean;
  errorText: string | null;
  createdAt: string;
}

export interface SupportLabEffect {
  id: string;
  actionId: string;
  ruleId: string | null;
  status: string;
  logText: string | null;
  errorText: string | null;
  chatThreadId: string | null;
  createdAt: string;
}

/**
 * The half of a case the client's portal deliberately hides: where it really is
 * in the pipeline, what severity Kibo bound it to, who holds it, and what the
 * automation engine did about it.
 */
export interface SupportLabPipelineState {
  ticketId: string;
  stage: string;
  stageLabel: string;
  portalStatus: string;
  portalStatusLabel: string;
  clientReportedSeverity: string;
  finalSeverity: string;
  aiDeterminedSeverity: string | null;
  severityOverrideReason: string | null;
  environment: string | null;
  teamHandling: string | null;
  supportTeam: string | null;
  routingReason: string | null;
  jiraKey: string | null;
  jiraStatus: string | null;
  jiraFixVersion: string | null;
  /** Why filing failed, for a case routed without an issue. */
  jiraFilingError: string | null;
  slaResponseDeadline: string | null;
  slaBreached: boolean;
  firstResponseAt: string | null;
  pendingDraft: string | null;
  aiDeflectionStatus: string;
  /** Why the AI stopped, when it stopped because something broke. */
  aiLastTurnError: string | null;
  hubspotThreadId: string | null;
  effects: SupportLabEffect[];
}

/** The changes feed the client polls. `cursor` is opaque — pass it back as-is. */
export interface SupportTicketChanges {
  tickets: SupportTicketSummary[];
  cursor: string;
}

export interface SupportUploadTarget {
  attachmentId: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

export interface SupportAttachmentDownload {
  downloadUrl: string;
  fileName: string;
  mimeType: string;
  expiresAt: string;
}

export interface SupportLabTicketSummary {
  labTicketId: string;
  ticketId: string;
  subject: string;
  stage: string;
  portalStatus: string;
  createdByName: string;
  createdById: string;
  personaEmail: string;
  tenantId: string;
  devAccountId: string;
  /** Null when the run targeted a hand-typed tenant rather than a picked account. */
  accountId: string | null;
  accountName: string | null;
  verdictCount: number;
  /** Of the judged replies, the share safe to send. Null = none judged yet. */
  acceptableRate: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportLabTicketListResponse {
  items: SupportLabTicketSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface SupportLabConfigResponse {
  apiKeyPrefix: string | null;
  personaDomain: string | null;
  updatedAt: string | null;
}

export interface SupportLabCreateTicketInput {
  tenantId: string;
  devAccountId: string;
  accountId?: string;
  subject: string;
  category: string;
  /**
   * Optional because the real client portal does not collect it. The lab offers
   * it as a deliberate override in the outer bar; omitting it is what a client
   * does, and is therefore the default a run should take.
   */
  type?: string;
  priority: string;
  body: string;
  attachmentIds?: string[];
}

/**
 * The run list's sortable columns. Mirrors `SUPPORT_LAB_RUN_SORTS` on the
 * server, which rejects anything else — the ids double as the table's column
 * ids, so a header sorts by naming the column it is.
 *
 * "Would send" is absent: it is a ratio over a run's verdict rows rather than a
 * column, so the database has nothing to order by.
 */
export const SUPPORT_LAB_RUN_SORTS = [
  'ticketId',
  'subject',
  'stage',
  'accountName',
  'tenantId',
  'createdByName',
  'createdAt',
] as const;
export type SupportLabRunSort = (typeof SUPPORT_LAB_RUN_SORTS)[number];

export interface SupportLabListParams {
  page?: number;
  limit?: number;
  sort?: SupportLabRunSort;
  dir?: 'asc' | 'desc';
  createdById?: string;
  accountId?: string;
  tenantId?: string;
}

/** An operator who has driven at least one run. */
export interface SupportLabRunCreator {
  id: string;
  name: string;
}

export interface SupportLabRunCreatorParams {
  accountId?: string;
  tenantId?: string;
}

// ---- Replaying a Salesforce case -------------------------------------------
// A convenience layer over the case mirror: pick a real case, get the intake
// fields filled in, review, submit. Nothing is recorded about where a run came
// from, so none of this is provenance — it only has to save typing and be
// honest about what it derived.

export interface SupportLabCaseTenantOption {
  tenantId: string;
  displayName: string;
  /** Non-null by construction — a tenant without one cannot be acted as. */
  devAccountId: string;
}

export interface SupportLabCaseResolvedTarget {
  accountId: string;
  accountName: string;
  tenants: SupportLabCaseTenantOption[];
}

/** One row of the picker. Unresolvable cases are listed too, and say why. */
export interface SupportLabCaseRow {
  caseId: string;
  caseNumber: string;
  subject: string | null;
  status: string;
  isClosed: boolean;
  priority: string | null;
  sfAccountName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  sfCreatedDate: string;
  resolved: SupportLabCaseResolvedTarget | null;
  /** Shown verbatim beside a disabled row. Null exactly when `resolved` is set. */
  unresolvedReason: string | null;
}

export interface SupportLabCaseListResponse {
  items: SupportLabCaseRow[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/** A derived intake value with its provenance. `from` is null iff `value` is. */
export interface SupportLabCaseDerivedField {
  value: string | null;
  from: string | null;
}

export interface SupportLabCaseAttachmentNotice {
  name: string;
  contentType: string | null;
  bodyLength: number | null;
}

/**
 * Everything the preview shows before the operator commits.
 *
 * The derivation readout belongs here, in the picker, and NOT in the intake
 * modal: the modal is a pixel-faithful replica of the client's own portal, and
 * a "prefilled from case 00042118" banner in it would break the one guarantee
 * the lab makes about what is inside the frame.
 */
export interface SupportLabCasePrefill {
  caseId: string;
  caseNumber: string;
  target: SupportLabCaseResolvedTarget | null;
  unresolvedReason: string | null;
  subject: string;
  body: string;
  bodySource: string;
  bodySourceReason: string;
  bodyTrimmedLines: number;
  priority: SupportLabCaseDerivedField;
  type: SupportLabCaseDerivedField;
  category: SupportLabCaseDerivedField;
  /** Named so the operator knows what the AI will not see. Never copied. */
  attachments: SupportLabCaseAttachmentNotice[];
  /** Context only — never sent as X-On-Behalf-Of. A run acts as its persona. */
  contact: { name: string | null; email: string | null } | null;
}

/**
 * The STAFF view of a case: the public ticket plus what decision 7 keeps off the
 * client's wire. Only ever returned by /api/v1/admin/support-tickets/*.
 */
export interface AdminSupportTicket extends SupportTicketSummary {
  /** Where the case ACTUALLY is. Several stages share one client-facing status. */
  stage: string;
  stageLabel: string;
  /** The binding severity — what drives SLA and paging. */
  finalSeverity: string;
  aiDeterminedSeverity: string | null;
  severityOverrideReason: string | null;
  environment: string | null;
  /** PS sub-tier. Only meaningful when teamHandling is 'ps'. */
  supportTeam: string | null;
  routingReason: string | null;
  slaResponseDeadline: string | null;
  slaBreached: boolean;
  firstResponseAt: string | null;
  /** The UNapproved draft, if one is waiting. Distinct from `latestUpdate`. */
  pendingDraft: string | null;
  /**
   * Why the AI's Jira filing failed, when it did. Non-null means the case is
   * correctly routed but carries no issue — distinct from a null `jiraKey`, which
   * is also what an outcome that files nothing looks like.
   */
  jiraFilingError: string | null;
  /**
   * Why the AI stopped, when it stopped because something broke. Non-null means a
   * human holds this because the AI failed — indistinguishable, from the stage and
   * the handoff message alone, from the AI choosing to hand it over.
   */
  aiLastTurnError: string | null;
  aiLastTurnErrorAt: string | null;
  /**
   * Opened through the Support Lab by a Kibo employee playing the client. Admin
   * surfaces see both halves of the estate; clients never see this one.
   */
  isTest: boolean;
}

/**
 * A timeline message on an ADMIN surface: the client's view plus whether the
 * message actually reached the HubSpot conversation.
 */
export interface AdminSupportMessage extends SupportMessageInfo {
  /**
   * true = in HubSpot, false = never got there, null = ticket isn't HubSpot-backed
   * so there is nothing to mirror. Publishing into HubSpot is non-fatal by design,
   * so `false` is the only signal that an AI reply the customer received is absent
   * from the thread an agent reads.
   */
  hubspotMirrored: boolean | null;
}

export type AdminSupportTimelineItem = AdminSupportMessage | SupportTicketEvent;

export interface AdminSupportTicketDetail extends AdminSupportTicket {
  timeline: AdminSupportTimelineItem[];
}

export interface AdminSupportTicketListResponse {
  items: AdminSupportTicket[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/** A case whose AI-drafted update is waiting on a human (decision 3). */
/** One thing the Salesforce desk needs from the org, and whether it is there. */
export interface SupportDeskCheck {
  id: string;
  label: string;
  ok: boolean;
  detail: string | null;
}

/** GET /api/v1/admin/support-tickets/desk */
export interface SupportDeskSummary {
  provider: string;
  openByDesk: Record<'hub_db' | 'hubspot' | 'salesforce', number>;
  salesforceChecks: SupportDeskCheck[];
  /** Where Support Lab-key tickets go; null = the same desk as `provider`. Salesforce means the test connection. */
  testProvider: string | null;
  /** The desk checks against the test connection, plus that it is a sandbox. */
  testSalesforceChecks: SupportDeskCheck[];
}

export interface SupportDraftQueueRow {
  ticketId: string;
  subject: string;
  tenantId: string;
  draft: string;
  finalSeverity: string;
  routedTo: string | null;
  jiraKey: string | null;
  updatedAt: string;
}

export interface SupportTicketListResponse {
  items: SupportTicketSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface SupportTicketListParams {
  /** The admin list filters by STAGE, not by the derived client-facing status. */
  stage?: string;
  devAccountId?: string;
  q?: string;
  page?: number;
  limit?: number;
  /** Support Lab tickets. Undefined = both real and lab. */
  isTest?: boolean;
}

// ---- Support automation workflows ----
export interface SupportRuleParamWire {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean';
  description?: string;
  default?: string | number | boolean;
}

export interface SupportRuleCatalogEntry {
  id: string;
  label: string;
  description: string;
  on: string[];
  actionIds: string[];
  params: SupportRuleParamWire[];
  defaultEnabled: boolean;
  enabled: boolean;
  configuredParams: Record<string, unknown>;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface SupportActionCatalogEntry {
  id: string;
  label: string;
  description: string;
  retryClass: 'idempotent' | 'mutating';
}

// ---- Support state vocabularies ----
// The internal reference for the four orthogonal state fields. Mirrors
// SupportVocabularies in src/support-tickets/support-tickets-schemas.ts.

export interface VocabularyFieldSummary {
  field: string;
  values: string;
  answers: string;
  writtenBy: string;
}

export interface VocabularyStage {
  id: string;
  label: string;
  description: string;
  portalStatus: string;
  portalStatusLabel: string;
  terminal: boolean;
  aiOwned: boolean;
  routedTo: string | null;
  jiraProject: string | null;
}

export interface VocabularyTerm {
  id: string;
  label: string;
  description: string;
}

export interface VocabularyFinalSeverity extends VocabularyTerm {
  rank: number;
  jiraPriority: string;
}

export interface VocabularyTransition {
  from: string;
  fromLabel: string;
  to: string;
  toLabel: string;
  on: string;
  triggerLabel: string;
  triggerDescription: string;
}

export interface SupportVocabularies {
  fields: VocabularyFieldSummary[];
  stages: VocabularyStage[];
  types: VocabularyTerm[];
  clientReportedSeverities: VocabularyTerm[];
  finalSeverities: VocabularyFinalSeverity[];
  aiDeflectionStatuses: VocabularyTerm[];
  portalStatuses: { id: string; label: string }[];
  transitions: VocabularyTransition[];
}

export interface SupportActionInvocationRow {
  id: string;
  ticketId: string;
  ruleId: string | null;
  actionId: string;
  status: string;
  logText: string | null;
  errorText: string | null;
  chatThreadId: string | null;
  createdAt: string;
}

export interface SupportActionInvocationList {
  rows: SupportActionInvocationRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SupportInvocationListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'actionId' | 'status';
  sortDir?: 'asc' | 'desc';
  ticketId?: string;
  ruleId?: string;
  actionId?: string;
  status?: string;
}

export interface SupportPortalChatRow {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportPortalChatList {
  rows: SupportPortalChatRow[];
  total: number;
  page: number;
  limit: number;
}

export interface SupportPortalChatListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface SupportRefItem {
  id: string;
  label: string;
}

export interface SupportReferenceData {
  statuses: SupportRefItem[];
  priorities: SupportRefItem[];
  categories: SupportRefItem[];
  agents: SupportAgentRef[];
}

export interface A2aConfigSummary {
  id: string;
  name: string;
  description: string | null;
  model: string;
  enabledCategories: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  activeKeyPrefix: string | null;
  threadCount: number;
}

export interface A2aConfigDetail extends A2aConfigSummary {
  systemPrompt: string | null;
  createdBy: { id: string; displayName: string };
  updatedBy: { id: string; displayName: string };
  activeKeys: A2aApiKeyInfo[];
}

export interface A2aConfigCreateInput {
  name: string;
  description?: string;
  systemPrompt?: string;
  model?: string;
  enabledCategories?: string[];
  isActive?: boolean;
}

export interface A2aConfigUpdateInput {
  name?: string;
  description?: string | null;
  systemPrompt?: string | null;
  model?: string;
  enabledCategories?: string[];
  isActive?: boolean;
}

export interface A2aKeyRotateResult {
  id: string;
  fullKey: string;
  keyPrefix: string;
  createdAt: string;
}

export interface A2aAnalytics {
  totalThreads: number;
  totalMessages: number;
  estimatedCost: number;
  messagesByDay: Array<{ date: string; count: number }>;
  recentThreads: Array<{
    id: string;
    title: string | null;
    messageCount: number;
    estimatedCost: number;
    lastActivity: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────

// ─── Chat artifacts (the side pane) ─────────────────────────────────────────
//
// A /work file the assistant authored that renders in the chat side pane. The
// kind list mirrors ChatArtifactKind in src/chat/chat-artifact-schemas.ts.

export type ChatArtifactKind = 'document' | 'slide_deck' | 'spreadsheet';

export type ChatArtifactSummary =
  | { wordCount: number }
  | { slideCount: number; layouts: string[] }
  | { rowCount: number; columnCount: number };

export interface ChatArtifact {
  id: string;
  threadId: string;
  kind: ChatArtifactKind;
  title: string;
  path: string;
  /** contentHash. Echoed back on save as the compare-and-set token. */
  revision: string;
  summary: ChatArtifactSummary | null;
  updatedAt: string;
  lastWriter: 'agent' | 'user';
  /** Someone else holds a live editing claim. */
  editingByOther: boolean;
}

export interface ChatArtifactWithContent extends ChatArtifact {
  content: string;
}

/** The out-of-protocol `artifact` stream event. */
export interface ChatArtifactEvent {
  type: 'artifact';
  action: 'created' | 'updated';
  artifactId: string;
  kind: ChatArtifactKind;
  title: string;
  path: string;
  revision: string;
}

// ─── Deck theme ─────────────────────────────────────────────────────────────
//
// The slide-deck brand tokens, editable at /admin/deck-theme. Colours are hex
// WITHOUT a leading '#', matching what pptxgenjs expects on the backend.

export interface ChatDeckTheme {
  name: string;
  slideBg: string; slideText: string; slideTextMuted: string;
  accent: string; accentText: string; accentSecondary: string;
  bandBg: string; bandText: string;
  surface: string; surfaceText: string; divider: string;
  invertBg: string; invertText: string; invertTextMuted: string;
  headingFont: string; bodyFont: string;
  fontSizes: Record<string, number>;
  showSlideNumbers: boolean;
  footerText: string;
}

export interface ResolvedChatDeckTheme {
  theme: ChatDeckTheme;
  /** Null when nothing is stored and the code default is in use. */
  version: number | null;
}

export interface ChatDeckThemeVersionRow {
  version: number;
  note: string | null;
  createdAt: string;
  isActive: boolean;
}

// ---- Google Meet -------------------------------------------------------------
// Mirrors src/google-meets/google-meet-schemas.ts. The status union is the honest
// answer to "what happened to this meeting" — see schema.prisma for what each means.

export type GoogleMeetingStatus =
  | 'creating'
  | 'waiting_for_participants'
  | 'in_progress'
  | 'awaiting_transcript'
  | 'completed'
  | 'abandoned'
  | 'no_transcript'
  | 'ended_by_user'
  | 'expired'
  | 'failed';

export type GoogleMeetingOrigin = 'debug' | 'api';

export type GoogleMeetAccessType = 'OPEN' | 'TRUSTED' | 'RESTRICTED';

/** Statuses the workflow will never move away from — the list stops polling on these. */
export const GOOGLE_MEETING_TERMINAL_STATUSES: GoogleMeetingStatus[] = [
  'completed',
  'abandoned',
  'no_transcript',
  'ended_by_user',
  'expired',
  'failed',
];

export interface GoogleMeetingSummary {
  id: string;
  title: string;
  purpose: string | null;
  status: GoogleMeetingStatus;
  origin: GoogleMeetingOrigin;
  failureReason: string | null;
  meetingUri: string;
  meetingCode: string;
  /** Null for ownerless API-triggered meetings. */
  createdByName: string | null;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  joinDeadlineAt: string;
  expiresAt: string;
  conferenceCount: number;
  participantCount: number;
  hasTranscript: boolean;
}

export interface GoogleMeetingParticipantView {
  id: string;
  displayName: string | null;
  kind: string;
  earliestStartTime: string | null;
  latestEndTime: string | null;
}

export interface GoogleMeetRecollectResult {
  captured: boolean;
  entryCount: number;
  meetingStatus: GoogleMeetingStatus;
}

/** What became of one conference's transcript. Mirrors the backend enum. */
export type GoogleMeetingTranscriptOutcome = 'pending' | 'captured' | 'no_speech' | 'never_started' | 'timed_out';

export interface GoogleMeetingConferenceView {
  id: string;
  conferenceRecordName: string;
  startTime: string;
  endTime: string | null;
  transcriptState: string | null;
  transcriptOutcome: GoogleMeetingTranscriptOutcome;
  /** When Google purges its own copy of the conference (~30 days). Re-collect works only before it. */
  expireTime: string | null;
  transcriptDocumentId: string | null;
  transcriptExportUri: string | null;
  smartNotesDocumentId: string | null;
  entryCount: number;
  participants: GoogleMeetingParticipantView[];
}

export interface GoogleMeetingDetail extends GoogleMeetingSummary {
  spaceName: string;
  workflowId: string;
  accessType: GoogleMeetAccessType;
  autoTranscription: boolean;
  autoSmartNotes: boolean;
  pollIntervalSeconds: number;
  transcriptGraceMinutes: number;
  maxMeetingDurationMinutes: number;
  joinDeadlineMinutes: number;
  endedByName: string | null;
  conferences: GoogleMeetingConferenceView[];
}

export interface GoogleMeetingTranscriptLine {
  id: string;
  speaker: string;
  text: string;
  offsetSeconds: number;
  startTime: string;
  languageCode: string | null;
}

export interface GoogleMeetingTranscriptView {
  meetingId: string;
  title: string;
  conferences: {
    conferenceId: string;
    startTime: string;
    endTime: string | null;
    lines: GoogleMeetingTranscriptLine[];
  }[];
}

export interface GoogleMeetStartResult {
  id: string;
  meetingUri: string;
  meetingCode: string;
  status: GoogleMeetingStatus;
  joinDeadlineAt: string;
}

export interface GoogleMeetStartRequest {
  title: string;
  purpose?: string;
  accessType?: GoogleMeetAccessType;
  joinDeadlineMinutes?: number;
}

export interface GoogleMeetConfig {
  credentialSetId: string | null;
  botAccountEmail: string | null;
  /** True only when an account has actually consented and the grant still works. */
  connected: boolean;
  joinDeadlineMinutes: number;
  maxMeetingDurationMinutes: number;
  pollIntervalSeconds: number;
  transcriptGraceMinutes: number;
  defaultAccessType: GoogleMeetAccessType;
  autoSmartNotes: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface GoogleMeetConfigUpdate {
  credentialSetId: string | null;
  joinDeadlineMinutes: number;
  maxMeetingDurationMinutes: number;
  pollIntervalSeconds: number;
  transcriptGraceMinutes: number;
  defaultAccessType: GoogleMeetAccessType;
  autoSmartNotes: boolean;
}

export interface GoogleMeetListParams {
  page?: number;
  limit?: number;
  status?: GoogleMeetingStatus;
  origin?: GoogleMeetingOrigin;
  search?: string;
}

// ---- Incidents ---------------------------------------------------------------
// Mirrors src/incidents/incident-schemas.ts. The unions are the wire contract, so
// a change to one has to be a deliberate change to the other; the backend's
// registry test asserts its half matches the database.

export type IncidentStage = 'declared' | 'active' | 'resolved' | 'followup' | 'closed' | 'dismissed';

export type IncidentSeverity = 'sev1' | 'sev2';

export type IncidentDismissKind = 'not_an_incident' | 'false_alarm' | 'test';

export type IncidentResponsibleParty =
  | 'internal_engineering'
  | 'customer_code'
  | 'third_party'
  | 'undetermined';

export type IncidentLinkKind = 'meeting' | 'chat_thread' | 'jira_issue' | 'slack_thread' | 'external_url';

export type IncidentActionCategory = 'process' | 'code' | 'documentation' | 'ui';

export type IncidentAccountSuggestionStatus = 'none' | 'pending' | 'confirmed' | 'rejected';

export type IncidentActorType = 'user' | 'slack' | 'system';

/** Stages the machine will never move away from — the detail page stops polling on these. */
export const INCIDENT_TERMINAL_STAGES: IncidentStage[] = ['closed', 'dismissed'];

export interface IncidentSummary {
  id: string;
  ref: string;
  title: string;
  stage: IncidentStage;
  severity: IncidentSeverity;
  responsibleParty: IncidentResponsibleParty;
  dismissKind: IncidentDismissKind | null;
  accountId: string | null;
  accountName: string | null;
  /** Null when nobody in the Hub declared it. */
  createdByName: string | null;
  createdAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
}

export interface IncidentEventRow {
  id: string;
  action: string;
  actorType: IncidentActorType;
  actorId: string | null;
  actorName: string | null;
  payload: Record<string, unknown> | null;
  at: string;
}

/**
 * One post on the incident feed.
 *
 * A deleted update still arrives, with an empty `body` and `deletedAt` set: the feed
 * renders a tombstone rather than a gap. Ordering comes from its `update_posted`
 * event, not from `createdAt` — an edit must not move a post.
 */
export interface IncidentUpdateRow {
  id: string;
  body: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
}

export interface IncidentLinkRow {
  id: string;
  kind: IncidentLinkKind;
  targetId: string | null;
  url: string | null;
  label: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  jiraStatus: string | null;
  jiraSummary: string | null;
  meetingStatus: string | null;
  meetingUri: string | null;
  /** For `meeting` links: what the bridge did. Optional for a browser running against an older server. */
  bridge?: IncidentBridgeSummary | null;
}

/** One bridge, summarised for the rail. Mirrors the backend type. */
export interface IncidentBridgeSummary {
  startedAt: string | null;
  endedAt: string | null;
  /** A conference is running now. */
  live: boolean;
  sessions: number;
  sessionsWithSpeech: number;
  transcriptLines: number;
  peopleJoined: number;
  transcript: 'pending' | 'captured' | 'none';
  noTranscriptReason: string | null;
}

/** The last sample the bridge monitor took. Present even when Slack pings are off. */
export interface IncidentAttendance {
  count: number;
  names: string[];
  at: string;
}

/**
 * A live reading of the Meet bridge, taken on demand rather than on the monitor's
 * ping cadence. `unavailable` is NOT `empty` — Meet being unreachable and nobody
 * being on the call must never render the same way.
 */
export interface IncidentLiveAttendance {
  state: 'live' | 'empty' | 'unavailable' | 'no_bridge';
  names: string[];
  at: string;
}

export interface IncidentDetail extends IncidentSummary {
  description: string;
  dismissReason: string | null;
  suggestedAccountId: string | null;
  suggestedAccountName: string | null;
  suggestedAccountConfidence: number | null;
  accountSuggestionStatus: IncidentAccountSuggestionStatus;
  dismissedAt: string | null;
  updatedAt: string;
  events: IncidentEventRow[];
  links: IncidentLinkRow[];
  updates: IncidentUpdateRow[];
  attendance: IncidentAttendance | null;
  /** Triggers the machine declares from the current stage — the action set to render. */
  availableTriggers: string[];
  /**
   * Whether the war room can be asked if this can be closed, and if not, why not.
   * Computed server-side; the card renders it rather than recomputing the rule.
   *
   * Optional because a browser can be running new JS against an old server during a
   * rolling deploy, and a missing field must render nothing rather than throw.
   */
  nudge?: IncidentNudgeState;
  /** The newest RCA run. Null before any; optional for the same rolling-deploy reason as `nudge`. */
  rca?: IncidentRcaRun | null;
}

/** Where one RCA run is. Mirrors `IncidentRcaRunState`. */
export type IncidentRcaRunState = 'queued' | 'waiting_for_transcript' | 'drafting' | 'ready' | 'skipped';

export const INCIDENT_RCA_OPEN_STATES: IncidentRcaRunState[] = ['queued', 'waiting_for_transcript', 'drafting'];

export interface IncidentRcaRun {
  id: string;
  state: IncidentRcaRunState;
  requestedByName: string | null;
  draftNowByName: string | null;
  meetingStatus: string | null;
  waitStartedAt: string | null;
  transcriptUsed: boolean | null;
  reason: string | null;
  createdAt: string;
  stateChangedAt: string;
  finishedAt: string | null;
  /** A finished run that was drafted without the transcript, whose bridge now has one. */
  transcriptNowAvailable: boolean;
}

export interface IncidentRcaRequestResult {
  /** False when a run was already open and the request changed nothing. */
  started: boolean;
  rca: IncidentRcaRun;
}

export type IncidentNudgeReason =
  | 'ok'
  | 'disabled'
  | 'terminal'
  | 'no-thread'
  | 'cooling-down'
  | 'not-quiet';

export interface IncidentNudgeState {
  available: boolean;
  reason: IncidentNudgeReason;
  /** Absolute instant, so the card never has to run a countdown against the poll. */
  availableAt: string | null;
  quietSince: string | null;
  lastNudgedAt: string | null;
}

export interface IncidentDecisionRow {
  ruleId: string;
  ruleLabel: string;
  fired: boolean;
  reason: string | null;
}

export interface IncidentEventDecisions {
  eventId: string;
  eventType: string;
  at: string;
  decisions: IncidentDecisionRow[];
}

export interface IncidentAutomation {
  incidentId: string;
  events: IncidentEventDecisions[];
}

/** Non-secret config bits the create dialog needs. Readable by any signed-in user. */
export interface IncidentMeta {
  autoStartInvestigation: boolean;
  defaultJiraProjectKey: string | null;
  warRoomConfigured: boolean;
  sev2Configured: boolean;
  warRoomChannelName: string | null;
  sev2ChannelName: string | null;
}

export interface IncidentEscalationTarget {
  entry: string;
  kind: 'email' | 'mention' | 'unknown';
  resolved: boolean;
  displayName: string | null;
  reason: string | null;
}

export interface IncidentPrerequisite {
  id: string;
  label: string;
  satisfied: boolean;
  fix: string;
}

export interface IncidentConfig {
  warRoomChannelId: string | null;
  warRoomChannelName: string | null;
  sev2ChannelId: string | null;
  sev2ChannelName: string | null;
  systemUserId: string | null;
  systemUserName: string | null;
  classifierModel: string | null;
  rcaModel: string | null;
  accountInferenceModel: string | null;
  attendancePingsEnabled: boolean;
  firstPingMinutes: number;
  secondPingMinutes: number;
  repeatPingMinutes: number;
  announceLinkUpdates: boolean;
  announceUpdates: boolean;
  autoStartInvestigation: boolean;
  investigationToolCategories: string[];
  rcaToolCategories: string[];
  nudgeQuietMinutes: number;
  nudgeCooldownMinutes: number;
  announceNudges: boolean;
  escalationMentions: string | null;
  defaultJiraProjectKey: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  prerequisites: IncidentPrerequisite[];
  escalationTargets: IncidentEscalationTarget[];
}

export type IncidentConfigUpdate = Omit<
  IncidentConfig,
  'systemUserName' | 'updatedAt' | 'updatedByName' | 'prerequisites' | 'escalationTargets'
>;

export interface IncidentCreateRequest {
  title: string;
  description: string;
  severity: IncidentSeverity;
  accountId?: string | null;
  /** Omit to take the configured default. */
  startInvestigation?: boolean;
}

export interface IncidentCreateResult {
  id: string;
  ref: string;
}

export interface IncidentListParams {
  page?: number;
  limit?: number;
  stage?: IncidentStage;
  severity?: IncidentSeverity;
  accountId?: string;
  search?: string;
  includeDismissed?: boolean;
}

export interface IncidentPatchRequest {
  title?: string;
  description?: string;
  responsibleParty?: IncidentResponsibleParty;
  accountId?: string | null;
}

export type IncidentJiraLinkRequest =
  | { mode: 'link'; issueKey: string; category: IncidentActionCategory }
  | {
      mode: 'create';
      projectKey: string;
      issueType: string;
      summary: string;
      description?: string;
      category: IncidentActionCategory;
    };

// ─── API Explorer ────────────────────────────────────────────────────

export interface ApiExplorerOperationSummary {
  service: string;
  method: string;
  path: string;
  operationId: string | null;
  summary: string | null;
  tags: string[];
  hasPathParams: boolean;
  parentCollectionPath: string | null;
  /** false = the body this PUT wants is NOT what the sibling GET returns. */
  putSchemaMatchesGet: boolean | null;
}

export interface ApiExplorerServiceSummary {
  service: string;
  title: string | null;
  operationCount: number;
  tags: string[];
}

export interface ApiExplorerCatalog {
  operations: ApiExplorerOperationSummary[];
  services: ApiExplorerServiceSummary[];
  generatedAt: string | null;
}

export interface ApiExplorerOperationDetail {
  operation: ApiExplorerOperationSummary;
  requestSchema: unknown;
  responseSchema: unknown;
  schemas: Record<string, unknown>;
  siblingGetPath: string | null;
}

export interface ApiExplorerParsedTarget {
  tenantId: string | null;
  apiHost: string | null;
  path: string | null;
  queryParams: Record<string, string>;
  raw: string;
}

export interface ApiExplorerResolvedTarget extends ApiExplorerParsedTarget {
  parsed: ApiExplorerParsedTarget;
  suggestedEnvironmentId: string | null;
  /** 'host' means the pasted URL decided it — the trustworthy case. */
  reason: 'host' | 'rule' | 'override' | null;
  detail: string | null;
  environments: { id: string; label: string; description: string | null }[];
}

export interface ApiExplorerAppCredential {
  id: string;
  name: string;
}

export interface ApiExplorerSite {
  id: string;
  name: string;
  domain: string | null;
}

export interface ApiExplorerCallResult {
  ok: boolean;
  status: number;
  durationMs: number;
  url: string | null;
  body: unknown;
  error: string | null;
}

export type ApiExplorerAuthMode = 'delegated' | 'app';

export interface ApiExplorerRequestInput {
  method: string;
  path: string;
  queryParams?: Record<string, string>;
  body?: unknown;
  authMode: ApiExplorerAuthMode;
  tenantId: string;
  credentialSetId?: string;
  baseUrl?: string;
  siteId?: string;
}

export interface ApiExplorerIngestRun {
  id: string;
  status: string;
  specsDiscovered: number;
  operationsWritten: number;
  driftNotes: string[];
  error: string | null;
  triggeredById: string | null;
  startedAt: string;
  finishedAt: string | null;
}

// ─── Workspace chat ─────────────────────────────────────────────────────────
// A chat thread backed by a git checkout and the pi coding agent. Mirrors
// src/workspace/workspace-schemas.ts.

export interface WorkspaceSession {
  id: string;
  threadId: string;
  sourceKind: 'public' | 'github_app' | 'none';
  /** Null when the workspace has no repository. */
  sourceUrl: string | null;
  repository: string | null;
  baseBranch: string;
  branch: string;
  persist: 'push' | 'bundle';
  status: 'active' | 'ended';
  lastTurnAt: string | null;
  lastCommit: string | null;
  pullRequestUrl: string | null;
  createdAt: string;
}

export interface WorkspaceChangedFile {
  path: string;
  status: string;
  additions: number;
  deletions: number;
  patch: string | null;
}

export interface WorkspaceSessionDiff {
  aheadBy: number | null;
  files: WorkspaceChangedFile[];
  patch: string | null;
}

export interface WorkspaceRepositoryOption {
  fullName: string;
  defaultBranch: string;
  isPrivate: boolean;
}

/** An empty object starts a workspace with no repository. */
export type WorkspaceSessionSource =
  | { publicUrl: string; repository?: undefined }
  | { repository: string; publicUrl?: undefined }
  | { publicUrl?: undefined; repository?: undefined };

export interface WorkspaceChatConfig {
  runnerUrl: string | null;
  hubBaseUrl: string | null;
  defaultModel: string | null;
  githubAppCredentialSetId: string | null;
  maxConcurrentTurns: number;
  updatedAt: string | null;
}

// ─── Site Editor ─────────────────────────────────────────────────────────────

export interface EditorSite {
  id: string;
  name: string;
  adapter: string;
  repository: string;
  stagingBranch: string;
  prodBranch: string;
  vercelCredentialSetId: string | null;
  vercelProjectId: string | null;
  stagingAlias: string | null;
  hasBypassSecret: boolean;
  previewServiceUrl: string | null;
  liveUrl: string | null;
  updatedAt: string;
}

/** Create or update a site. bypassSecret: a string sets it, null clears it, omitted keeps it. */
export interface EditorSiteUpsert {
  name: string;
  repository: string;
  stagingBranch: string;
  prodBranch: string;
  vercelCredentialSetId: string | null;
  vercelProjectId: string | null;
  stagingAlias: string | null;
  bypassSecret?: string | null;
  previewServiceUrl: string | null;
  liveUrl?: string | null;
}

export interface EditorSiteCheck {
  item: string;
  ok: boolean;
  detail: string;
}

export interface SiteChange {
  id: string;
  title: string;
  summary: string | null;
  kind: 'edit' | 'undo' | 'engineering';
  status: 'pending' | 'staged' | 'live' | 'undone';
  pages: string[];
  author: string | null;
  stagedAt: string;
  liveAt: string | null;
  undoesChangeId: string | null;
  undoneByTitle: string | null;
}

/** Where everything stands, for the hub's pipeline bar (SiteEditorSummary on the server). */
export interface SiteEditorSummary {
  drafts: { mine: number; others: number };
  staged: number;
  release: { id: string; status: 'building' | 'conflict' | 'ready' | 'publishing' } | null;
  live: { publishedAt: string; publishedBy: string | null; changeCount: number } | null;
  liveUrl: string | null;
}

export interface SiteStaging {
  changes: SiteChange[];
  staging: {
    url: string | null;
    commit: string | null;
    deployedCommit: string | null;
    deployState: 'building' | 'ready' | 'error' | 'canceled' | null;
    deployError: string | null;
    updating: boolean;
  };
}

export interface SiteSendProposal {
  title: string;
  summary: string;
  aiWritten: boolean;
  pages: string[];
  fileCount: number;
  check: { ok: boolean; output: string } | null;
  /** The draft commit that was checked; sending names it, and the server refuses any other. */
  headCommit: string;
}

export interface SiteRiskFlag {
  id: string;
  severity: 'blocking' | 'check' | 'note';
  title: string;
  detail: string;
}

export interface SiteRelease {
  id: string;
  /** changes: chosen from staging; production_branch: the production branch as pushed by hand. */
  kind: 'changes' | 'production_branch';
  status: 'building' | 'conflict' | 'ready' | 'publishing' | 'published' | 'stale' | 'rolled_back' | 'failed';
  changes: { id: string; title: string; kind: string; pages: string[] }[];
  summary: string | null;
  riskFlags: SiteRiskFlag[];
  previewUrl: string | null;
  conflict: { changeTitle: string | null; files: string[]; dependsOn: { id: string; title: string }[]; error?: string } | null;
  createdBy: string;
  createdAt: string;
  publishedBy: string | null;
  publishedAt: string | null;
  canRollBack: boolean;
}

/** The production branch against what the Hub last put live: commits pushed outside the Site Editor. */
export interface SiteProduction {
  branch: string;
  head: string | null;
  liveCommit: string | null;
  outsideCommits: { sha: string; title: string }[];
  pendingReleaseId: string | null;
}

/** After a publish, production is merged back into staging; pending until that succeeds. */
export interface SiteMergeBack {
  pending: boolean;
  error: string | null;
}

export type SiteDraftStepKind = 'start' | 'agent_turn' | 'inline_edit' | 'image' | 'restore';

export interface SiteDraftStep {
  id: string;
  kind: SiteDraftStepKind;
  label: string;
  commit: string;
  createdAt: string;
}

/** A kind of new page the site offers under "New…" (its src/content/starters.yaml). */
export interface SiteStarter {
  id: string;
  label: string;
  description: string;
  /** A page of this kind to copy, path from the repository root. */
  example: string;
  /** The AGENTS.md section the assistant follows. */
  guide: string;
}

export interface SiteDraft {
  id: string;
  siteId: string;
  title: string;
  status: 'open' | 'sending' | 'sent' | 'discarded';
  threadId: string | null;
  createdBy: { id: string; name: string };
  mine: boolean;
  createdAt: string;
  updatedAt: string;
  agentWorking: boolean;
  steps: SiteDraftStep[];
  headCommit: string | null;
  /** The parts of the site the draft changes, as of its last step. */
  pages: string[];
  /** No changes from where it started. */
  empty: boolean;
  preview: { url: string | null; error: string | null };
}

/** What resolving one event's tags found (mirrors ticket-deployment-schemas.ts). */
export interface TicketDeploymentResolutionSummary {
  eventId: string;
  tags: Array<{ repo: string; tag: string; status: string; error: string | null; tickets: number }>;
}

/** Rebuild-the-projection result: how many ledger events were folded. */
export interface TicketDeploymentProjectionRebuildResult {
  eventsFolded: number;
  events: number;
  transitions: number;
  chains: number;
  gaps: number;
}

// ─── Security Watch (mirrors src/security-watch/security-watch-schemas.ts) ──

export type SecurityWatchLaneId = 'identity' | 'integrations' | 'data_access' | 'edge' | 'platform' | 'brand';

export type SecurityWatchBrandPhraseSource = 'marketing_index' | 'docs_index';

/** One subject Brand Watch protects (`kibo`, or `tenant:<id>` for a customer's storefronts). */
export interface SecurityWatchBrandWatchEntry {
  subject: string;
  label: string;
  ownedHosts: string[];
  brandTerms: string[];
  phraseSources: SecurityWatchBrandPhraseSource[];
  phrases: string[];
  enabled: boolean;
}
export type SecurityWatchSeverity = 'low' | 'medium' | 'high';
export type SecurityWatchFindingStatus = 'open' | 'acknowledged' | 'resolved' | 'false_positive';
export type SecurityWatchRunStatus = 'collecting' | 'scoring' | 'analysing' | 'done' | 'failed';
export type SecurityWatchCandidateReason = 'ratio' | 'new_pair' | 'tripwire' | 'event' | 'probe_failure' | 'config_change' | 'drilldown';

export interface SecurityWatchClaimView {
  text: string;
  uncited: boolean;
  cites: number[];
}

export interface SecurityWatchCitationView {
  n: number;
  candidateId: string;
  label: string;
  probeTitle: string;
  clusterLabel: string;
  windowStart: string;
  reason: SecurityWatchCandidateReason;
}

export interface SecurityWatchFindingSummaryView {
  id: string;
  lane: SecurityWatchLaneId;
  laneLabel: string;
  severity: SecurityWatchSeverity;
  title: string;
  tripwire: boolean;
  status: SecurityWatchFindingStatus;
  firstSeenWindow: string;
  lastSeenWindow: string;
  day: string;
  windows: string[];
  headline: string | null;
  sourceCount: number;
}

export interface SecurityWatchWindowView {
  windowStart: string;
  label: string;
  runId: string | null;
  status: SecurityWatchRunStatus | 'pending';
  worstSeverity: SecurityWatchSeverity | null;
  tripwire: boolean;
  findingCount: number;
  failingProbes: number;
  finishedAt: string | null;
  error: string | null;
  mailError: string | null;
}

export interface SecurityWatchProbeFailureView {
  probeId: string;
  cluster: string;
  title: string;
  clusterLabel: string;
  status: 'ok' | 'empty' | 'error' | 'skipped';
  error: string | null;
  windows: string[];
}

export interface SecurityWatchDayView {
  day: string;
  label: string;
  windows: SecurityWatchWindowView[];
  verdict: Record<SecurityWatchSeverity, number>;
  probeHealth: { total: number; ok: number; failing: SecurityWatchProbeFailureView[] };
  summary: { source: 'digest' | 'lanes' | 'none'; model: string | null; claims: SecurityWatchClaimView[]; error: string | null };
  citations: SecurityWatchCitationView[];
  findings: SecurityWatchFindingSummaryView[];
  lanes: Array<{ lane: SecurityWatchLaneId; label: string; probes: number; rows: number; candidates: number; findings: number; failedRuns: number }>;
  configChanges: Array<{ candidateId: string; description: string; windowStart: string; weakening: boolean }>;
  digest: { sentAt: string | null; error: string | null } | null;
  previousDay: string | null;
  nextDay: string | null;
}

export interface SecurityWatchFrozenBaseline {
  metric: string;
  median: number;
  mad: number;
  points: number;
  slot: number;
  weekend: boolean;
  days: number;
}

export interface SecurityWatchEvidenceView {
  n: number;
  candidateId: string;
  runId: string;
  probeId: string;
  title: string;
  label: string;
  cluster: string;
  clusterLabel: string;
  lane: SecurityWatchLaneId;
  windowStart: string;
  windowLabel: string;
  day: string;
  reason: SecurityWatchCandidateReason;
  reasonDetail: string;
  metrics: Record<string, number>;
  headline: string | null;
  baseline: SecurityWatchFrozenBaseline | null;
  series: { metric: string; points: Array<{ windowStart: string; value: number }> } | null;
  source: { label: string; url: string | null; description: string };
  reproduce: string;
  tripwireId: string | null;
  suppression: { id: string; title: string } | null;
  drilldown: { tool: string; input: unknown; result: string; error: string | null } | null;
  findings: Array<{ id: string; title: string; severity: SecurityWatchSeverity; day: string }>;
  /** What the probe's source returned for this window, or null when none was kept. */
  answer: SecurityWatchAnswerSummaryView | null;
  /** Who and what the entity is, resolved when scored; null for candidates scored before context existed. */
  context: SecurityWatchCandidateContext | null;
  /** Known actors this candidate matches, as of now. They annotate; they never hide anything. */
  knownActors: SecurityWatchKnownActorMatch[];
}

export type SecurityWatchKnownActorKind = 'namespace' | 'developer_account' | 'appkey' | 'application' | 'tenant' | 'network' | 'ip';

export interface SecurityWatchKnownActorView {
  id: string;
  kind: SecurityWatchKnownActorKind;
  value: string;
  label: string;
  note: string;
  owner: string;
  reviewedOn: string;
  retired: boolean;
  updatedBy: { id: string; displayName: string } | null;
  updatedAt: string;
}

export interface SecurityWatchKnownActorMatch {
  id: string;
  kind: SecurityWatchKnownActorKind;
  value: string;
  label: string;
  note: string;
  matched: string;
}

export interface SecurityWatchKnownActorCreate {
  kind: SecurityWatchKnownActorKind;
  value: string;
  label: string;
  note: string;
  owner: string;
}

export interface SecurityWatchKnownActorUpdate {
  label?: string;
  note?: string;
  owner?: string;
  reviewed?: boolean;
  retired?: boolean;
}

export type SecurityWatchContextKind = 'tenant' | 'app' | 'application' | 'developer_account' | 'ip' | 'network' | 'admin_user';

export interface SecurityWatchContextEntity {
  part: string;
  kind: SecurityWatchContextKind;
  value: string;
  status: 'resolved' | 'unresolved';
  label: string | null;
  reason: string | null;
  facts: Record<string, string | number | boolean | null>;
}

export interface SecurityWatchCandidateContext {
  resolvedAt: string;
  entities: SecurityWatchContextEntity[];
}

export interface SecurityWatchAnswerRow {
  entityKey: string;
  metrics: Record<string, number>;
  detail?: Record<string, string | number | null>;
}

export interface SecurityWatchAnswerSummaryView {
  id: string;
  status: 'ok' | 'empty' | 'error';
  rowCount: number;
  truncated: boolean;
  rawKept: boolean;
  /** The answer's rows whose entity is this candidate's. */
  rows: SecurityWatchAnswerRow[];
}

export interface SecurityWatchAnswerView {
  id: string;
  runId: string;
  probeId: string;
  cluster: string;
  windowStart: string;
  status: 'ok' | 'empty' | 'error';
  error: string | null;
  rowCount: number;
  truncated: boolean;
  rawBytes: number;
  rows: SecurityWatchAnswerRow[];
  raw: unknown;
}

export interface SecurityWatchSuppressionMatch {
  probeIds?: string[];
  clusters?: string[];
  entityKeyPrefixes?: string[];
}

export interface SecurityWatchFindingDetailView extends SecurityWatchFindingSummaryView {
  claims: SecurityWatchClaimView[];
  model: string | null;
  statusNote: string | null;
  statusBy: { id: string; displayName: string } | null;
  statusAt: string | null;
  alertedSeverity: SecurityWatchSeverity | null;
  evidence: SecurityWatchEvidenceView[];
  timeline: Array<{ at: string; kind: string; detail: string; actor: string | null; via: SecurityWatchActionVia | null }>;
  related: Array<{ id: string; title: string; severity: SecurityWatchSeverity; day: string; shared: string }>;
  suppressionDraft: SecurityWatchSuppressionMatch | null;
  canTriage: boolean;
}

export interface SecurityWatchFindingListView {
  items: SecurityWatchFindingSummaryView[];
  total: number;
  page: number;
  totalPages: number;
}

export interface SecurityWatchRunView {
  id: string;
  windowStart: string;
  windowLabel: string;
  day: string;
  status: SecurityWatchRunStatus;
  configVersion: number | null;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  mailError: string | null;
  recipientsSkipped: Array<{ userId: string; displayName: string | null; reason: 'no_role' | 'bot' | 'deactivated' | 'not_found' }>;
  probes: Array<{ probeId: string; cluster: string; status: 'ok' | 'empty' | 'error' | 'skipped'; rows: number; elapsedMs: number; error: string | null; title: string; clusterLabel: string; lane: SecurityWatchLaneId | null }>;
  lanes: Array<{ lane: string; label: string; status: string; model: string; candidateCount: number; inputTokens: number | null; outputTokens: number | null; costUsd: number | null; uncitedCount: number; answeredBy: 'investigation' | 'answer' | 'retry' | null; rejectedLines: number; error: string | null; input: string; output: unknown }>;
  candidates: { total: number; suppressed: number; byReason: Record<string, number> };
}

export interface SecurityWatchSuppressionView {
  id: string;
  title: string;
  reason: string;
  owner: string;
  match: SecurityWatchSuppressionMatch;
  status: 'proposed' | 'active' | 'revoked';
  expiresAt: string;
  expired: boolean;
  approvalSource: 'user' | 'migration' | null;
  proposedBy: { id: string; displayName: string } | null;
  approvedBy: { id: string; displayName: string } | null;
  approvedAt: string | null;
  revokedAt: string | null;
  revokedBy: { id: string; displayName: string } | null;
  proposedVia: SecurityWatchActionVia | null;
  approvedVia: SecurityWatchActionVia | null;
  revokedVia: SecurityWatchActionVia | null;
  createdAt: string;
  matchedLast7Days: number;
}

/** Where a person's Security Watch action came from: the admin pages, their agent over MCP, or their private chat. */
export type SecurityWatchActionVia = 'ui' | 'mcp' | 'chat';

export interface SecurityWatchThresholds {
  investigationEffort: 'default' | 'low' | 'medium' | 'high';
  ratio: number;
  mads: number;
  minHistory: number;
  baselineDays: number;
  bootstrapDays: number;
  continueWindows: number;
  laneInputCharCap: number;
  laneToolCalls: number;
  drilldownMaxHours: number;
}

/** Keyed `<probeId>@<cluster>`. */
export type SecurityWatchProbeOverrides = Record<string, { off?: boolean; floors?: Record<string, number> }>;

export interface SecurityWatchProbeConfigRow {
  key: string;
  id: string;
  cluster: string;
  clusterLabel: string;
  lane: SecurityWatchLaneId;
  title: string;
  /** Code-default floor per ratio metric. */
  floors: Record<string, number>;
}

export interface SecurityWatchConfigView {
  versionId: string | null;
  version: number | null;
  recipients: Array<{ userId: string; displayName: string | null; email: string | null; receives: boolean; reason: 'no_role' | 'bot' | 'deactivated' | 'not_found' | null }>;
  enabledLanes: SecurityWatchLaneId[];
  laneModels: Partial<Record<SecurityWatchLaneId, string>>;
  thresholds: SecurityWatchThresholds;
  probeOverrides: SecurityWatchProbeOverrides;
  brandWatchList: SecurityWatchBrandWatchEntry[];
  probes: SecurityWatchProbeConfigRow[];
  defaults: { model: string; thresholds: SecurityWatchThresholds };
  lanes: Array<{ id: SecurityWatchLaneId; label: string; focus: string }>;
  versions: Array<{ id: string; version: number; note: string | null; createdAt: string; createdBy: { id: string; displayName: string } | null; active: boolean }>;
  deliveryMode: 'capture' | 'deliver' | 'redirect';
  notCovered: string[];
  canEdit: boolean;
}

export interface SecurityWatchConfigUpdate {
  recipientUserIds: string[];
  enabledLanes: SecurityWatchLaneId[];
  laneModels: Partial<Record<SecurityWatchLaneId, string>>;
  thresholds: SecurityWatchThresholds;
  probeOverrides: SecurityWatchProbeOverrides;
  brandWatchList?: SecurityWatchBrandWatchEntry[];
  note?: string | null;
}

export interface SecurityWatchRoleHolder {
  id: string;
  email: string;
  displayName: string;
  tiers: string[];
}

export interface SecurityWatchSuppressionProposal {
  title: string;
  reason: string;
  owner: string;
  match: SecurityWatchSuppressionMatch;
  expiresAt: string;
}

// ─── Effects (/admin/effects) ────────────────────────────────────────────────
// Mirrors src/hub-effects/hub-effect-schemas.ts.

export type HubEffectStatus = 'intended' | 'ok' | 'failed' | 'unknown' | 'settled_happened' | 'settled_resent' | 'settled_dropped';
export type HubEffectKind =
  | 'jira.issue.create'
  | 'hubspot.company.create'
  | 'opsgenie.alert.create'
  | 'google_meet.space.create'
  | 'slack.message.post'
  | 'salesforce.feed_post.create'
  | 'kibo.tenant.write';
export type HubEffectSettlement = 'happened' | 'resend' | 'dropped';

export interface HubEffectView {
  id: string;
  kind: HubEffectKind;
  label: string;
  party: string;
  idempotencyKey: string;
  subjectKind: string | null;
  subjectId: string | null;
  status: HubEffectStatus;
  attempts: number;
  lastError: string | null;
  foundByLookup: boolean;
  canResend: boolean;
  traceId: string | null;
  correlationId: string | null;
  createdAt: string;
  updatedAt: string;
  settledByName: string | null;
  settledAt: string | null;
  settleReason: string | null;
}

export type HubAuditLaw = 'K1' | 'K2' | 'K3' | 'K4' | 'K5' | 'K6';
export type HubAuditFindingStatus = 'open' | 'acknowledged' | 'resolved' | 'false_positive';
export type HubAuditTriageStatus = 'acknowledged' | 'false_positive' | 'open';

/** Something the nightly audit found wrong (src/hub-audit/): ids and counts, never customer text. */
export interface HubAuditFindingView {
  id: string;
  law: HubAuditLaw;
  lawTitle: string;
  checkId: string;
  checkTitle: string;
  subjectKey: string;
  summary: string;
  detail: Record<string, unknown>;
  refs: Array<{ kind: string; id: string }>;
  status: HubAuditFindingStatus;
  raisedCount: number;
  openedAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  statusNote: string | null;
  statusBy: { id: string; displayName: string | null } | null;
  statusAt: string | null;
}

export interface HubAuditCheckOutcome {
  status: 'ok' | 'failed';
  found: number;
  raised: number;
  error?: string;
}

export interface HubAuditRunView {
  id: string;
  workflowId: string;
  trigger: string;
  windowStart: string;
  windowEnd: string;
  status: string;
  checks: Record<string, HubAuditCheckOutcome>;
  raised: number;
  resolved: number;
  alertStatus: string | null;
  alertError: string | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface HubAuditConfigView {
  slackChannelId: string | null;
  slackChannelName: string | null;
  updatedBy: { id: string; displayName: string | null } | null;
  updatedAt: string | null;
}

export interface HubAuditFindingListQuery {
  status?: HubAuditFindingStatus;
  law?: HubAuditLaw;
  checkId?: string;
  page?: number;
  pageSize?: number;
}

export interface HubEffectListQuery {
  status?: HubEffectStatus;
  kind?: HubEffectKind;
  page?: number;
  pageSize?: number;
}

export const api = {
  // API Explorer
  async getApiExplorerCatalog(): Promise<ApiResult<ApiExplorerCatalog>> {
    return fetchApi<ApiExplorerCatalog>('/api/v1/api-explorer/catalog');
  },

  async getApiExplorerOperation(method: string, path: string): Promise<ApiResult<ApiExplorerOperationDetail>> {
    const q = new URLSearchParams({ method, path });
    return fetchApi<ApiExplorerOperationDetail>(`/api/v1/api-explorer/operation?${q.toString()}`);
  },

  async resolveApiExplorerTarget(input: string): Promise<ApiResult<ApiExplorerResolvedTarget>> {
    const q = new URLSearchParams({ input });
    return fetchApi<ApiExplorerResolvedTarget>(`/api/v1/api-explorer/resolve-target?${q.toString()}`);
  },

  async getApiExplorerAppCredentials(): Promise<ApiResult<ApiExplorerAppCredential[]>> {
    return fetchApi<ApiExplorerAppCredential[]>('/api/v1/api-explorer/app-credentials');
  },

  async createApiExplorerAppCredential(
    name: string,
    appKey: string,
    appSecret: string
  ): Promise<ApiResult<ApiExplorerAppCredential>> {
    return fetchApi<ApiExplorerAppCredential>('/api/v1/api-explorer/app-credentials', {
      method: 'POST',
      body: JSON.stringify({ name, appKey, appSecret }),
    });
  },

  async testApiExplorerAppCredential(
    credentialSetId: string,
    baseUrl: string
  ): Promise<ApiResult<{ ok: boolean; message: string }>> {
    return fetchApi<{ ok: boolean; message: string }>('/api/v1/api-explorer/app-credentials/test', {
      method: 'POST',
      body: JSON.stringify({ credentialSetId, baseUrl }),
    });
  },

  async getApiExplorerTenantSites(
    tenantId: string,
    credentialSetId: string,
    baseUrl: string
  ): Promise<ApiResult<ApiExplorerSite[]>> {
    const q = new URLSearchParams({ tenantId, credentialSetId, baseUrl });
    return fetchApi<ApiExplorerSite[]>(`/api/v1/api-explorer/tenant/sites?${q.toString()}`);
  },

  async sendApiExplorerRequest(input: ApiExplorerRequestInput): Promise<ApiResult<ApiExplorerCallResult>> {
    return fetchApi<ApiExplorerCallResult>('/api/v1/api-explorer/request', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async runApiExplorerIngest(): Promise<ApiResult<{ specsDiscovered: number; operationsWritten: number; driftNotes: string[] }>> {
    return fetchApi('/api/v1/admin/api-explorer/ingest', { method: 'POST' });
  },

  async getApiExplorerIngestRuns(): Promise<ApiResult<ApiExplorerIngestRun[]>> {
    return fetchApi<ApiExplorerIngestRun[]>('/api/v1/admin/api-explorer/ingest-runs');
  },

  // Auth
  async getCurrentUser(): Promise<ApiResult<User>> {
    return fetchApi<User>('/auth/me');
  },

  async logout(): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>('/auth/logout', { method: 'POST' });
  },

  // Debug Auth
  async getDebugProfiles(): Promise<ApiResult<DebugAuthStatus>> {
    return fetchApi<DebugAuthStatus>('/auth/debug/profiles');
  },

  async debugLogin(profileId: string): Promise<ApiResult<User>> {
    return fetchApi<User>('/auth/debug/login', {
      method: 'POST',
      body: JSON.stringify({ profileId }),
    });
  },

  // Users
  async getUsers(
    page = 1,
    limit = 20,
    search?: string,
    type?: 'bot' | 'all',
  ): Promise<ApiResult<PaginatedResponse<UserWithGroups>>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    // By default bot (system) users are hidden. `type=bot` returns only bots;
    // `type=all` returns humans and bots together.
    if (type) params.set('type', type);
    return fetchApi<PaginatedResponse<UserWithGroups>>(`/api/v1/users?${params}`);
  },

  // System (bot) users — managed at /admin/bots. Bots are non-authenticatable
  // User rows that satisfy the userId required by system-chat workflows.
  async getBots(page = 1, limit = 20, search?: string): Promise<ApiResult<PaginatedResponse<BotSummary>>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return fetchApi<PaginatedResponse<BotSummary>>(`/api/v1/bots?${params}`);
  },

  async getBot(id: string): Promise<ApiResult<BotDetail>> {
    return fetchApi<BotDetail>(`/api/v1/bots/${id}`);
  },

  async createBot(data: { email: string; displayName: string }): Promise<ApiResult<BotDetail>> {
    return fetchApi<BotDetail>('/api/v1/bots', { method: 'POST', body: JSON.stringify(data) });
  },

  async updateBotGroups(id: string, groupIds: string[]): Promise<ApiResult<BotDetail>> {
    return fetchApi<BotDetail>(`/api/v1/bots/${id}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds }),
    });
  },

  async deleteBot(id: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/bots/${id}`, { method: 'DELETE' });
  },

  // Scheduled chat prompts — user-scoped, at /apps/chat/scheduled. A scheduled
  // prompt is a normal first-message chat that starts later (once or recurring).
  async getScheduledPrompts(
    page = 1,
    limit = 20,
  ): Promise<ApiResult<PaginatedResponse<ScheduledPromptJobSummary>>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return fetchApi<PaginatedResponse<ScheduledPromptJobSummary>>(`/api/v1/scheduled-prompts?${params}`);
  },

  async getScheduledPrompt(id: string): Promise<ApiResult<ScheduledPromptJobDetail>> {
    return fetchApi<ScheduledPromptJobDetail>(`/api/v1/scheduled-prompts/${id}`);
  },

  async createScheduledPrompt(
    data: ScheduledPromptCreateInput,
  ): Promise<ApiResult<ScheduledPromptJobDetail>> {
    return fetchApi<ScheduledPromptJobDetail>('/api/v1/scheduled-prompts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateScheduledPrompt(
    id: string,
    data: ScheduledPromptUpdateInput,
  ): Promise<ApiResult<ScheduledPromptJobDetail>> {
    return fetchApi<ScheduledPromptJobDetail>(`/api/v1/scheduled-prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteScheduledPrompt(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/scheduled-prompts/${id}`, { method: 'DELETE' });
  },

  // Admin view over ALL users' scheduled prompts — at /admin/chat/scheduled-prompts.
  // Admins can see every job with its owner, force-disable (pause) a runaway one, or
  // force-delete. They cannot create or edit the prompt content/cadence — those are
  // the owner's.
  async getAdminScheduledPrompts(
    page = 1,
    limit = 50,
  ): Promise<ApiResult<PaginatedResponse<AdminScheduledPromptJobSummary>>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return fetchApi<PaginatedResponse<AdminScheduledPromptJobSummary>>(`/api/v1/admin/scheduled-prompts?${params}`);
  },

  async getAdminScheduledPrompt(id: string): Promise<ApiResult<AdminScheduledPromptJobDetail>> {
    return fetchApi<AdminScheduledPromptJobDetail>(`/api/v1/admin/scheduled-prompts/${id}`);
  },

  async disableAdminScheduledPrompt(id: string): Promise<ApiResult<{ disabled: boolean }>> {
    return fetchApi<{ disabled: boolean }>(`/api/v1/admin/scheduled-prompts/${id}/disable`, { method: 'PATCH' });
  },

  async deleteAdminScheduledPrompt(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/scheduled-prompts/${id}`, { method: 'DELETE' });
  },

  async getUser(id: string): Promise<ApiResult<UserWithGroups>> {
    return fetchApi<UserWithGroups>(`/api/v1/users/${id}`);
  },

  async updateUserGroups(userId: string, groupIds: string[]): Promise<ApiResult<Group[]>> {
    return fetchApi<Group[]>(`/api/v1/users/${userId}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds }),
    });
  },

  async getUserSessions(userId: string): Promise<ApiResult<Session[]>> {
    return fetchApi<Session[]>(`/api/v1/users/${userId}/sessions`);
  },

  async deleteUserSessions(userId: string): Promise<ApiResult<{ deletedCount: number }>> {
    return fetchApi<{ deletedCount: number }>(`/api/v1/users/${userId}/sessions`, {
      method: 'DELETE',
    });
  },

  async updateUserPreferences(userId: string, preferences: { darkMode?: boolean }): Promise<ApiResult<User>> {
    return fetchApi<User>(`/api/v1/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ preferences }),
    });
  },

  async deleteUser(userId: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/users/${userId}`, {
      method: 'DELETE',
    });
  },

  // Groups
  async getGroups(): Promise<ApiResult<Group[]>> {
    return fetchApi<Group[]>('/api/v1/groups');
  },

  // Tools
  async getTools(): Promise<ApiResult<Tool[]>> {
    return fetchApi<Tool[]>('/api/v1/tools');
  },

  async checkPermission(resource: string, action: string): Promise<ApiResult<{ resource: string; action: string; allowed: boolean }>> {
    return fetchApi<{ resource: string; action: string; allowed: boolean }>(
      `/api/v1/permissions/check?resource=${encodeURIComponent(resource)}&action=${encodeURIComponent(action)}`
    );
  },

  async checkRouteGroupAccess(routeGroup: string): Promise<boolean> {
    const result = await fetchApi<{ object: string; relation: string; allowed: boolean }>(
      `/api/v1/permissions/check?object=${encodeURIComponent(`route-group:${routeGroup}`)}&relation=access`
    );
    return result.success ? result.data.allowed : false;
  },

  // Managed roles (admin-curated, registry-driven roles)
  async getManagedRoles(): Promise<ApiResult<ManagedRoleSummary[]>> {
    return fetchApi<ManagedRoleSummary[]>('/api/v1/admin/managed-roles');
  },

  async getManagedRole(roleId: string): Promise<ApiResult<ManagedRoleDetail>> {
    return fetchApi<ManagedRoleDetail>(`/api/v1/admin/managed-roles/${encodeURIComponent(roleId)}`);
  },

  async grantManagedRole(roleId: string, userId: string, tierId: string): Promise<ApiResult<ManagedRoleGrant>> {
    return fetchApi<ManagedRoleGrant>(`/api/v1/admin/managed-roles/${encodeURIComponent(roleId)}/grants`, {
      method: 'POST',
      body: JSON.stringify({ userId, tierId }),
    });
  },

  async revokeManagedRoleGrant(roleId: string, tupleId: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(
      `/api/v1/admin/managed-roles/${encodeURIComponent(roleId)}/grants/${encodeURIComponent(tupleId)}`,
      { method: 'DELETE' }
    );
  },

  // Debug
  async getHealthStatus(): Promise<{ status: string }> {
    const response = await fetch('/health');
    return response.json() as Promise<{ status: string }>;
  },

  async getReadyStatus(): Promise<{ status: string; checks: Record<string, string> }> {
    const response = await fetch('/ready');
    return response.json() as Promise<{ status: string; checks: Record<string, string> }>;
  },

  // Feature Flags
  async getFeatureFlags(): Promise<ApiResult<FeatureFlags>> {
    return fetchApi<FeatureFlags>('/api/v1/feature-flags');
  },

  async getFeatureFlagsAdmin(): Promise<ApiResult<FeatureFlagDetail[]>> {
    return fetchApi<FeatureFlagDetail[]>('/api/v1/feature-flags/admin');
  },

  async getFeatureFlagAdmin(key: string): Promise<ApiResult<FeatureFlagDetail>> {
    return fetchApi<FeatureFlagDetail>(`/api/v1/feature-flags/admin/${key}`);
  },

  async getFeatureFlagAuditLog(key: string, limit?: number): Promise<ApiResult<FeatureFlagAuditLogEntry[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return fetchApi<FeatureFlagAuditLogEntry[]>(`/api/v1/feature-flags/admin/${key}/audit-log${params}`);
  },

  async updateFeatureFlag(
    key: string,
    enabled: boolean,
    changeReason?: string
  ): Promise<ApiResult<FeatureFlagDetail>> {
    return fetchApi<FeatureFlagDetail>(`/api/v1/feature-flags/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled, changeReason }),
    });
  },

  // GitHub
  async getGitHubConfig(): Promise<ApiResult<GitHubConfig>> {
    return fetchApi<GitHubConfig>('/api/v1/github/config');
  },

  async getGitHubSettings(): Promise<ApiResult<GitHubOAuthSettings | null>> {
    return fetchApi<GitHubOAuthSettings | null>('/api/v1/github/settings');
  },

  async disconnectGitHub(): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>('/api/v1/github/settings', {
      method: 'DELETE',
    });
  },

  async getGitHubRepositories(): Promise<ApiResult<GitHubRepository[]>> {
    return fetchApi<GitHubRepository[]>('/api/v1/github/repositories');
  },

  async selectGitHubRepository(repositoryOwner: string, repositoryName: string): Promise<ApiResult<GitHubOAuthSettings>> {
    return fetchApi<GitHubOAuthSettings>('/api/v1/github/repository', {
      method: 'PUT',
      body: JSON.stringify({ repositoryOwner, repositoryName }),
    });
  },

  async testGitHubConnection(): Promise<ApiResult<GitHubConnectionTestResult>> {
    return fetchApi<GitHubConnectionTestResult>('/api/v1/github/test-connection', {
      method: 'POST',
    });
  },

  // Credential Sets
  async getCredentialSetTypes(): Promise<ApiResult<CredentialSetTypeSummary[]>> {
    return fetchApi<CredentialSetTypeSummary[]>('/api/v1/credential-set-types');
  },

  async getCredentialSets(): Promise<ApiResult<CredentialSetSummary[]>> {
    return fetchApi<CredentialSetSummary[]>('/api/v1/credential-sets');
  },

  async getCredentialSet(id: string): Promise<ApiResult<CredentialSetDetail>> {
    return fetchApi<CredentialSetDetail>(`/api/v1/credential-sets/${id}`);
  },

  async createCredentialSet(input: CreateCredentialSetInput): Promise<ApiResult<CredentialSetDetail>> {
    return fetchApi<CredentialSetDetail>('/api/v1/credential-sets', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateCredentialSet(
    id: string,
    input: { name?: string; description?: string | null; notes?: string | null }
  ): Promise<ApiResult<CredentialSetDetail>> {
    return fetchApi<CredentialSetDetail>(`/api/v1/credential-sets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async deleteCredentialSet(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/credential-sets/${id}`, {
      method: 'DELETE',
    });
  },

  async setCredentialFieldValue(
    credentialSetId: string,
    fieldKey: string,
    value: string,
    note?: string
  ): Promise<ApiResult<FieldDetail>> {
    return fetchApi<FieldDetail>(`/api/v1/credential-sets/${credentialSetId}/fields/${fieldKey}`, {
      method: 'POST',
      body: JSON.stringify({ value, note }),
    });
  },

  async activateCredentialFieldRevision(
    credentialSetId: string,
    fieldKey: string,
    revisionId: string
  ): Promise<ApiResult<FieldDetail>> {
    return fetchApi<FieldDetail>(
      `/api/v1/credential-sets/${credentialSetId}/fields/${fieldKey}/revisions/${revisionId}/activate`,
      { method: 'PUT' }
    );
  },

  async clearCredentialFieldRevision(
    credentialSetId: string,
    fieldKey: string,
    revisionId: string
  ): Promise<ApiResult<FieldDetail>> {
    return fetchApi<FieldDetail>(
      `/api/v1/credential-sets/${credentialSetId}/fields/${fieldKey}/revisions/${revisionId}/value`,
      { method: 'DELETE' }
    );
  },

  async addCredentialSetShare(
    credentialSetId: string,
    input: { businessGroupId?: string; userId?: string; permission?: 'view' | 'edit' }
  ): Promise<ApiResult<ShareRuleDetail>> {
    return fetchApi<ShareRuleDetail>(`/api/v1/credential-sets/${credentialSetId}/shares`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async removeCredentialSetShare(credentialSetId: string, shareId: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/credential-sets/${credentialSetId}/shares/${shareId}`, {
      method: 'DELETE',
    });
  },

  // Temporal
  async getTemporalInfo(): Promise<ApiResult<TemporalClusterInfo>> {
    return fetchApi<TemporalClusterInfo>('/api/v1/temporal/info');
  },

  // Mintlify Shared
  async getMintlifyConfig(): Promise<ApiResult<MintlifyToolsConfig | null>> {
    return fetchApi<MintlifyToolsConfig | null>('/api/v1/admin/mintlify/config');
  },

  async updateMintlifyConfig(data: Partial<MintlifyToolsConfig>): Promise<ApiResult<MintlifyToolsConfig>> {
    return fetchApi<MintlifyToolsConfig>('/api/v1/admin/mintlify/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testMintlifyAgentConnection(): Promise<ApiResult<{ success: boolean; error?: string }>> {
    return fetchApi<{ success: boolean; error?: string }>('/api/v1/admin/mintlify/config/test-agent', {
      method: 'POST',
    });
  },

  async testMintlifyDiscoveryConnection(): Promise<ApiResult<{ success: boolean; error?: string }>> {
    return fetchApi<{ success: boolean; error?: string }>('/api/v1/admin/mintlify/config/test-discovery', {
      method: 'POST',
    });
  },

  // Fireworks AI
  async getFireworksConfig(): Promise<ApiResult<FireworksToolConfig | null>> {
    return fetchApi<FireworksToolConfig | null>('/api/v1/admin/fireworks/config');
  },

  async updateFireworksConfig(data: { credentialSetId: string | null; defaultModel: string | null }): Promise<ApiResult<FireworksToolConfig>> {
    return fetchApi<FireworksToolConfig>('/api/v1/admin/fireworks/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Chat models (admin-curated catalog)
  async getChatModels(): Promise<ApiResult<ChatModelDetail[]>> {
    return fetchApi<ChatModelDetail[]>('/api/v1/admin/chat/models');
  },

  async createChatModel(data: ChatModelInput): Promise<ApiResult<ChatModelDetail>> {
    return fetchApi<ChatModelDetail>('/api/v1/admin/chat/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateChatModel(id: string, data: Partial<ChatModelInput>): Promise<ApiResult<ChatModelDetail>> {
    return fetchApi<ChatModelDetail>(`/api/v1/admin/chat/models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteChatModel(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/chat/models/${id}`, { method: 'DELETE' });
  },

  async getCanaryOverview(): Promise<ApiResult<CanaryOverview>> {
    return fetchApi<CanaryOverview>('/api/v1/canaries');
  },

  async getCanaryRuns(kind: CanaryKind, target: string, limit = 100): Promise<ApiResult<CanaryRunRow[]>> {
    const params = new URLSearchParams({ kind, target, limit: String(limit) });
    return fetchApi<CanaryRunRow[]>(`/api/v1/canaries/runs?${params}`);
  },

  /** Omit the probe to run every enabled probe now. */
  async runCanaries(probe?: { kind: CanaryKind; target: string }): Promise<ApiResult<CanaryProbeResult[]>> {
    return fetchApi<CanaryProbeResult[]>('/api/v1/canaries/run', {
      method: 'POST',
      body: JSON.stringify(probe ?? {}),
    });
  },

  async updateCanaryProbe(probeId: string, data: { enabled: boolean; intervalSeconds: number }): Promise<ApiResult<CanaryOverview>> {
    return fetchApi<CanaryOverview>(`/api/v1/canaries/probes/${encodeURIComponent(probeId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async updateCanaryConfig(data: { systemUserId: string | null }): Promise<ApiResult<CanaryOverview>> {
    return fetchApi<CanaryOverview>('/api/v1/canaries/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async triggerAlertTest(severity: AlertTestSeverity): Promise<ApiResult<AlertTestTriggerResult>> {
    return fetchApi<AlertTestTriggerResult>('/api/v1/admin/alert-tests', {
      method: 'POST',
      body: JSON.stringify({ severity }),
    });
  },

  async testChatModel(data: { provider: ChatProvider; modelId: string }): Promise<ApiResult<ChatModelTestResult>> {
    return fetchApi<ChatModelTestResult>('/api/v1/admin/chat/models/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // User-facing: enabled models for the in-chat picker
  async getEnabledChatModels(): Promise<ApiResult<ChatModelOption[]>> {
    return fetchApi<ChatModelOption[]>('/api/v1/chat/models');
  },

  // Mintlify Agent
  async getMintlifyAgentPrompts(params?: { page?: number; limit?: number; visibility?: string; search?: string }): Promise<ApiResult<PaginatedResponse<MintlifyAgentPrompt>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.visibility) searchParams.set('visibility', params.visibility);
    if (params?.search) searchParams.set('search', params.search);
    return fetchApi<PaginatedResponse<MintlifyAgentPrompt>>(`/api/v1/mintlify-agent/prompts?${searchParams}`);
  },

  async getMintlifyAgentPrompt(id: string): Promise<ApiResult<MintlifyAgentPrompt>> {
    return fetchApi<MintlifyAgentPrompt>(`/api/v1/mintlify-agent/prompts/${id}`);
  },

  async createMintlifyAgentPrompt(input: { name: string; description?: string; visibility?: string; content: string }): Promise<ApiResult<MintlifyAgentPrompt>> {
    return fetchApi<MintlifyAgentPrompt>('/api/v1/mintlify-agent/prompts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateMintlifyAgentPrompt(id: string, input: { name?: string; description?: string | null; visibility?: string; content?: string; changeNote?: string }): Promise<ApiResult<MintlifyAgentPrompt>> {
    return fetchApi<MintlifyAgentPrompt>(`/api/v1/mintlify-agent/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async deleteMintlifyAgentPrompt(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/mintlify-agent/prompts/${id}`, {
      method: 'DELETE',
    });
  },

  async getMintlifyAgentPromptRevisions(promptId: string): Promise<ApiResult<MintlifyAgentPromptRevision[]>> {
    return fetchApi<MintlifyAgentPromptRevision[]>(`/api/v1/mintlify-agent/prompts/${promptId}/revisions`);
  },

  async getMintlifyAgentJobs(params?: { page?: number; limit?: number; status?: string }): Promise<ApiResult<PaginatedResponse<MintlifyAgentJob>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    return fetchApi<PaginatedResponse<MintlifyAgentJob>>(`/api/v1/mintlify-agent/jobs?${searchParams}`);
  },

  async getMintlifyAgentJob(id: string): Promise<ApiResult<MintlifyAgentJobDetail>> {
    return fetchApi<MintlifyAgentJobDetail>(`/api/v1/mintlify-agent/jobs/${id}`);
  },

  async createMintlifyAgentJob(input: { promptContent: string; systemMessage?: string; sourcePromptId?: string; model?: string; asDraft?: boolean; branch?: string }): Promise<ApiResult<MintlifyAgentJob>> {
    return fetchApi<MintlifyAgentJob>('/api/v1/mintlify-agent/jobs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async cancelMintlifyAgentJob(id: string): Promise<ApiResult<{ cancelled: boolean }>> {
    return fetchApi<{ cancelled: boolean }>(`/api/v1/mintlify-agent/jobs/${id}/cancel`, {
      method: 'POST',
    });
  },

  // Documentation
  async getDocs(): Promise<ApiResult<DocSummary[]>> {
    return fetchApi<DocSummary[]>('/api/v1/docs');
  },

  async getDoc(slug: string): Promise<ApiResult<DocDetail>> {
    return fetchApi<DocDetail>(`/api/v1/docs/${encodeURIComponent(slug)}`);
  },

  // Mintlify Doc Index
  async triggerMintlifyDocIndex(): Promise<ApiResult<{ triggered: boolean }>> {
    return fetchApi<{ triggered: boolean }>('/api/v1/admin/mintlify/doc-index', { method: 'POST' });
  },

  async resetMintlifyDocIndex(): Promise<ApiResult<{ reset: boolean }>> {
    return fetchApi<{ reset: boolean }>('/api/v1/admin/mintlify/doc-index/reset', { method: 'POST' });
  },

  async getMintlifyDocIndexStatus(): Promise<ApiResult<DocIndexStatus>> {
    return fetchApi<DocIndexStatus>('/api/v1/admin/mintlify/doc-index/status');
  },

  async getMintlifyDocIndexPages(params?: { page?: number; limit?: number }): Promise<ApiResult<PaginatedResponse<DocIndexPageSummary>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<PaginatedResponse<DocIndexPageSummary>>(`/api/v1/admin/mintlify/doc-index/pages?${searchParams}`);
  },

  async getMintlifyDocIndexPage(id: string): Promise<ApiResult<DocIndexPageDetail>> {
    return fetchApi<DocIndexPageDetail>(`/api/v1/admin/mintlify/doc-index/pages/${id}`);
  },

  async searchMintlifyDocIndex(query: string, pageSize?: number): Promise<ApiResult<{ results: DocIndexSearchResult[]; query: string; totalResults: number }>> {
    return fetchApi<{ results: DocIndexSearchResult[]; query: string; totalResults: number }>('/api/v1/admin/mintlify/doc-index/search', {
      method: 'POST',
      body: JSON.stringify({ query, pageSize: pageSize ?? 5 }),
    });
  },

  // Elasticsearch
  async getElasticsearchEndpoints(): Promise<ApiResult<ElasticsearchEndpointSummary[]>> {
    return fetchApi<ElasticsearchEndpointSummary[]>('/api/v1/elasticsearch/endpoints');
  },

  async getElasticsearchEndpoint(id: string): Promise<ApiResult<ElasticsearchEndpointDetail>> {
    return fetchApi<ElasticsearchEndpointDetail>(`/api/v1/elasticsearch/endpoints/${id}`);
  },

  async createElasticsearchEndpoint(input: { identifier: string; name: string; url: string; credentialSetId: string; rateLimitPerMinute?: number }): Promise<ApiResult<ElasticsearchEndpointDetail>> {
    return fetchApi<ElasticsearchEndpointDetail>('/api/v1/elasticsearch/endpoints', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateElasticsearchEndpoint(id: string, input: { name?: string; url?: string; credentialSetId?: string; isActive?: boolean; rateLimitPerMinute?: number }): Promise<ApiResult<ElasticsearchEndpointDetail>> {
    return fetchApi<ElasticsearchEndpointDetail>(`/api/v1/elasticsearch/endpoints/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async deleteElasticsearchEndpoint(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/elasticsearch/endpoints/${id}`, {
      method: 'DELETE',
    });
  },

  async testElasticsearchConnection(endpointId: string): Promise<ApiResult<ElasticsearchConnectionTestResult>> {
    return fetchApi<ElasticsearchConnectionTestResult>(`/api/v1/elasticsearch/endpoints/${endpointId}/test`, {
      method: 'POST',
    });
  },

  async getElasticsearchConfig(): Promise<ApiResult<ElasticsearchToolConfigDetail>> {
    return fetchApi<ElasticsearchToolConfigDetail>('/api/v1/elasticsearch/config');
  },

  async updateElasticsearchConfig(input: { llmCredentialSetId: string | null; llmModel: string | null; fieldGuide?: string | null; globalEnabled?: boolean; globalRateLimitPerMinute?: number }): Promise<ApiResult<ElasticsearchToolConfigDetail>> {
    return fetchApi<ElasticsearchToolConfigDetail>('/api/v1/elasticsearch/config', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async getElasticsearchOutboundLogs(params?: { endpointId?: string; page?: number; limit?: number }): Promise<ApiResult<ElasticsearchOutboundLogListResponse>> {
    const searchParams = new URLSearchParams();
    if (params?.endpointId) searchParams.set('endpointId', params.endpointId);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<ElasticsearchOutboundLogListResponse>(`/api/v1/elasticsearch/outbound-logs?${searchParams}`);
  },

  // Web Search (Spider.cloud)
  async getWebSearchConfig(): Promise<ApiResult<WebSearchToolConfigDetail>> {
    return fetchApi<WebSearchToolConfigDetail>('/api/v1/web-search/config');
  },

  async updateWebSearchConfig(input: { credentialSetId: string | null; globalEnabled?: boolean }): Promise<ApiResult<WebSearchToolConfigDetail>> {
    return fetchApi<WebSearchToolConfigDetail>('/api/v1/web-search/config', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async getWebSearchOutboundLogs(params?: { requestType?: 'search' | 'scrape'; page?: number; limit?: number }): Promise<ApiResult<WebSearchOutboundLogListResponse>> {
    const searchParams = new URLSearchParams();
    if (params?.requestType) searchParams.set('requestType', params.requestType);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<WebSearchOutboundLogListResponse>(`/api/v1/web-search/outbound-logs?${searchParams}`);
  },

  async testWebSearch(
    input: { requestType: 'search'; query: string; limit?: number } | { requestType: 'scrape'; url: string }
  ): Promise<ApiResult<WebSearchResultsResponse>> {
    return fetchApi<WebSearchResultsResponse>('/api/v1/web-search/test', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Chat
  async getChatThreads(params?: { view?: 'my' | 'all' | 'group'; page?: number; limit?: number }): Promise<ApiResult<PaginatedResponse<ChatThread>>> {
    const searchParams = new URLSearchParams();
    if (params?.view) searchParams.set('view', params.view);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<PaginatedResponse<ChatThread>>(`/api/v1/chat/threads?${searchParams}`);
  },

  async getChatThread(id: string): Promise<ApiResult<ChatThreadDetail>> {
    return fetchApi<ChatThreadDetail>(`/api/v1/chat/threads/${id}`);
  },

  async deleteChatThread(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/chat/threads/${id}`, {
      method: 'DELETE',
    });
  },

  // ─── Workspace chat ─────────────────────────────────────────────────

  async createWorkspaceSession(
    source: WorkspaceSessionSource & { baseBranch?: string; isPrivate?: boolean },
  ): Promise<ApiResult<{ threadId: string; session: WorkspaceSession }>> {
    return fetchApi<{ threadId: string; session: WorkspaceSession }>('/api/v1/workspace-sessions', {
      method: 'POST',
      body: JSON.stringify(source),
    });
  },

  async listWorkspaceRepositories(): Promise<ApiResult<{ configured: boolean; repositories: WorkspaceRepositoryOption[] }>> {
    return fetchApi<{ configured: boolean; repositories: WorkspaceRepositoryOption[] }>('/api/v1/workspace-sessions/repositories');
  },

  async getWorkspaceForThread(threadId: string): Promise<ApiResult<WorkspaceSession>> {
    return fetchApi<WorkspaceSession>(`/api/v1/workspace-sessions/by-thread/${threadId}`);
  },

  async getWorkspaceDiff(sessionId: string): Promise<ApiResult<WorkspaceSessionDiff>> {
    return fetchApi<WorkspaceSessionDiff>(`/api/v1/workspace-sessions/${sessionId}/diff`);
  },

  async openWorkspacePullRequest(sessionId: string, input: { title: string; body?: string }): Promise<ApiResult<{ pullRequestUrl: string }>> {
    return fetchApi<{ pullRequestUrl: string }>(`/api/v1/workspace-sessions/${sessionId}/pull-request`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async endWorkspaceSession(sessionId: string): Promise<ApiResult<{ ended: boolean }>> {
    return fetchApi<{ ended: boolean }>(`/api/v1/workspace-sessions/${sessionId}/end`, { method: 'POST', body: '{}' });
  },

  async getWorkspaceChatConfig(): Promise<ApiResult<WorkspaceChatConfig>> {
    return fetchApi<WorkspaceChatConfig>('/api/v1/admin/workspace-chat/config');
  },

  async updateWorkspaceChatConfig(input: Omit<WorkspaceChatConfig, 'updatedAt'>): Promise<ApiResult<WorkspaceChatConfig>> {
    return fetchApi<WorkspaceChatConfig>('/api/v1/admin/workspace-chat/config', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  // ─── Site Editor ────────────────────────────────────────────────────

  async listEditorSitesAdmin(): Promise<ApiResult<EditorSite[]>> {
    return fetchApi<EditorSite[]>('/api/v1/admin/site-editor/sites');
  },

  async createEditorSite(input: EditorSiteUpsert): Promise<ApiResult<EditorSite>> {
    return fetchApi<EditorSite>('/api/v1/admin/site-editor/sites', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateEditorSite(id: string, input: EditorSiteUpsert): Promise<ApiResult<EditorSite>> {
    return fetchApi<EditorSite>(`/api/v1/admin/site-editor/sites/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  },

  async listEditorSites(): Promise<ApiResult<EditorSite[]>> {
    return fetchApi<EditorSite[]>('/api/v1/site-editor/sites');
  },

  async listSiteDrafts(siteId: string): Promise<ApiResult<SiteDraft[]>> {
    return fetchApi<SiteDraft[]>(`/api/v1/site-editor/sites/${siteId}/drafts`);
  },

  async listSiteStarters(siteId: string): Promise<ApiResult<SiteStarter[]>> {
    return fetchApi<SiteStarter[]>(`/api/v1/site-editor/sites/${siteId}/starters`);
  },

  /** Without a title the draft is named from its first change. */
  async createSiteDraft(siteId: string, title?: string): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/sites/${siteId}/drafts`, { method: 'POST', body: JSON.stringify(title ? { title } : {}) });
  },

  async getSiteDraft(id: string): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/drafts/${id}`);
  },

  async refreshSiteDraft(id: string): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/drafts/${id}/refresh`, { method: 'POST', body: '{}' });
  },

  async restoreSiteDraft(id: string, stepId: string): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/drafts/${id}/restore`, { method: 'POST', body: JSON.stringify({ stepId }) });
  },

  async renameSiteDraft(id: string, title: string): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/drafts/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) });
  },

  /** Answers SITE_SHARED_CONTENT (details.appearsOn) for text other pages show, until sent again with confirmShared. */
  async editSiteDraftField(id: string, ref: string, value: string, confirmShared = false): Promise<ApiResult<SiteDraft>> {
    return fetchApi<SiteDraft>(`/api/v1/site-editor/drafts/${id}/fields`, { method: 'POST', body: JSON.stringify({ ref, value, ...(confirmShared ? { confirmShared } : {}) }) });
  },

  async discardSiteDraft(id: string): Promise<ApiResult<{ discarded: boolean }>> {
    return fetchApi<{ discarded: boolean }>(`/api/v1/site-editor/drafts/${id}/discard`, { method: 'POST', body: '{}' });
  },

  async prepareSendSiteDraft(id: string): Promise<ApiResult<SiteSendProposal>> {
    return fetchApi<SiteSendProposal>(`/api/v1/site-editor/drafts/${id}/send/prepare`, { method: 'POST', body: '{}' });
  },

  async sendSiteDraft(id: string, input: { title: string; summary: string; headCommit: string }): Promise<ApiResult<SiteChange>> {
    return fetchApi<SiteChange>(`/api/v1/site-editor/drafts/${id}/send`, { method: 'POST', body: JSON.stringify(input) });
  },

  async getSiteEditorSummary(siteId: string): Promise<ApiResult<SiteEditorSummary>> {
    return fetchApi<SiteEditorSummary>(`/api/v1/site-editor/sites/${siteId}/summary`);
  },

  async getSiteStaging(siteId: string): Promise<ApiResult<SiteStaging>> {
    return fetchApi<SiteStaging>(`/api/v1/site-editor/sites/${siteId}/staging`);
  },

  /** Record engineers' commits on staging as changes, then the same view as getSiteStaging. */
  async syncSiteStaging(siteId: string): Promise<ApiResult<SiteStaging>> {
    return fetchApi<SiteStaging>(`/api/v1/site-editor/sites/${siteId}/staging/sync`, { method: 'POST', body: '{}' });
  },

  async deploySiteStaging(siteId: string): Promise<ApiResult<SiteStaging>> {
    return fetchApi<SiteStaging>(`/api/v1/site-editor/sites/${siteId}/staging/deploy`, { method: 'POST', body: '{}' });
  },

  async undoSiteChange(id: string): Promise<ApiResult<SiteChange>> {
    return fetchApi<SiteChange>(`/api/v1/site-editor/changes/${id}/undo`, { method: 'POST', body: '{}' });
  },

  async buildSiteRelease(siteId: string, changeIds: string[]): Promise<ApiResult<SiteRelease>> {
    return fetchApi<SiteRelease>(`/api/v1/site-editor/sites/${siteId}/releases`, { method: 'POST', body: JSON.stringify({ changeIds }) });
  },

  async getSiteRelease(id: string): Promise<ApiResult<SiteRelease>> {
    return fetchApi<SiteRelease>(`/api/v1/site-editor/releases/${id}`);
  },

  async getSiteProduction(siteId: string): Promise<ApiResult<SiteProduction>> {
    return fetchApi<SiteProduction>(`/api/v1/site-editor/sites/${siteId}/production`);
  },

  async buildSiteProductionRelease(siteId: string): Promise<ApiResult<SiteRelease>> {
    return fetchApi<SiteRelease>(`/api/v1/site-editor/sites/${siteId}/releases/production-branch`, { method: 'POST', body: '{}' });
  },

  async publishSiteRelease(id: string, acknowledged: string[]): Promise<ApiResult<SiteRelease>> {
    return fetchApi<SiteRelease>(`/api/v1/site-editor/releases/${id}/publish`, { method: 'POST', body: JSON.stringify({ acknowledged }) });
  },

  async rollbackSite(siteId: string): Promise<ApiResult<SiteRelease>> {
    return fetchApi<SiteRelease>(`/api/v1/site-editor/sites/${siteId}/rollback`, { method: 'POST', body: '{}' });
  },

  async unpublishSiteChange(id: string): Promise<ApiResult<SiteChange>> {
    return fetchApi<SiteChange>(`/api/v1/site-editor/changes/${id}/unpublish`, { method: 'POST', body: '{}' });
  },

  async getSiteSchedule(siteId: string): Promise<ApiResult<{ id: string; file: string; publishAt: string }[]>> {
    return fetchApi<{ id: string; file: string; publishAt: string }[]>(`/api/v1/site-editor/sites/${siteId}/schedule`);
  },

  async getSiteMergeBack(siteId: string): Promise<ApiResult<SiteMergeBack>> {
    return fetchApi<SiteMergeBack>(`/api/v1/site-editor/sites/${siteId}/merge-back`);
  },

  async retrySiteMergeBack(siteId: string): Promise<ApiResult<SiteMergeBack>> {
    return fetchApi<SiteMergeBack>(`/api/v1/site-editor/sites/${siteId}/merge-back`, { method: 'POST', body: '{}' });
  },

  async listSiteReleases(siteId: string): Promise<ApiResult<SiteRelease[]>> {
    return fetchApi<SiteRelease[]>(`/api/v1/site-editor/sites/${siteId}/releases`);
  },

  async checkEditorSite(id: string): Promise<ApiResult<EditorSiteCheck[]>> {
    return fetchApi<EditorSiteCheck[]>(`/api/v1/admin/site-editor/sites/${id}/check`, { method: 'POST', body: '{}' });
  },

  // ─── Chat artifacts ─────────────────────────────────────────────────

  // ─── Deck theme ─────────────────────────────────────────────────────

  async getChatDeckTheme(): Promise<ApiResult<ResolvedChatDeckTheme>> {
    return fetchApi<ResolvedChatDeckTheme>('/api/v1/chat/deck-theme');
  },

  async getChatDeckThemeVersions(): Promise<ApiResult<ChatDeckThemeVersionRow[]>> {
    return fetchApi<ChatDeckThemeVersionRow[]>('/api/v1/admin/chat/deck-theme/versions');
  },

  async saveChatDeckTheme(theme: ChatDeckTheme, note?: string | null): Promise<ApiResult<ResolvedChatDeckTheme>> {
    return fetchApi<ResolvedChatDeckTheme>('/api/v1/admin/chat/deck-theme', {
      method: 'PUT',
      body: JSON.stringify({ theme, note: note ?? null }),
    });
  },

  async rollbackChatDeckTheme(version: number): Promise<ApiResult<ResolvedChatDeckTheme>> {
    return fetchApi<ResolvedChatDeckTheme>('/api/v1/admin/chat/deck-theme/rollback', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  },

  async getChatThreadArtifacts(threadId: string): Promise<ApiResult<ChatArtifact[]>> {
    return fetchApi<ChatArtifact[]>(`/api/v1/chat/threads/${threadId}/artifacts`);
  },

  async getChatArtifact(artifactId: string): Promise<ApiResult<ChatArtifactWithContent>> {
    return fetchApi<ChatArtifactWithContent>(`/api/v1/chat/artifacts/${artifactId}`);
  },

  async saveChatArtifact(
    artifactId: string,
    content: string,
    expectedRevision: string | null,
  ): Promise<ApiResult<ChatArtifactWithContent>> {
    return fetchApi<ChatArtifactWithContent>(`/api/v1/chat/artifacts/${artifactId}`, {
      method: 'PUT',
      body: JSON.stringify({ content, expectedRevision }),
    });
  },

  async claimChatArtifact(artifactId: string): Promise<ApiResult<{ heldByOther: boolean; expiresAt: string | null }>> {
    return fetchApi<{ heldByOther: boolean; expiresAt: string | null }>(
      `/api/v1/chat/artifacts/${artifactId}/claim`,
      { method: 'POST' },
    );
  },

  async releaseChatArtifact(artifactId: string): Promise<ApiResult<{ released: boolean }>> {
    return fetchApi<{ released: boolean }>(`/api/v1/chat/artifacts/${artifactId}/release`, { method: 'POST' });
  },

  /**
   * Copy a file between mounts — how a document reaches a library, and later a
   * project or an account folder. Replaced promoteChatArtifact: saving out of a
   * chat is a plain copy, not a flag on the artifact.
   */
  async copyChatFile(
    threadId: string,
    body: { from: string; to: string; expectedHash?: string | null },
  ): Promise<ApiResult<{ path: string; contentHash: string }>> {
    return fetchApi<{ path: string; contentHash: string }>(`/api/v1/chat/threads/${threadId}/files/copy`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getChatArtifactLibrary(): Promise<ApiResult<ChatArtifact[]>> {
    return fetchApi<ChatArtifact[]>('/api/v1/chat/artifact-library');
  },

  async updateChatThreadTitle(id: string, title: string): Promise<ApiResult<{ updated: boolean }>> {
    return fetchApi<{ updated: boolean }>(`/api/v1/chat/threads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },

  /** Open or close a thread to replies from anyone who can read it. Owner only. */
  async setChatThreadCollaborative(id: string, collaborative: boolean): Promise<ApiResult<{ collaborative: boolean }>> {
    return fetchApi<{ collaborative: boolean }>(`/api/v1/chat/threads/${id}/collaborative`, {
      method: 'PATCH',
      body: JSON.stringify({ collaborative }),
    });
  },

  /**
   * Set the caller's own pin on a thread. Any thread the caller can read, including
   * threads they did not author; pinned threads list first, per caller.
   */
  async setChatThreadPinned(id: string, isPinned: boolean): Promise<ApiResult<{ isPinned: boolean }>> {
    return fetchApi<{ isPinned: boolean }>(`/api/v1/chat/threads/${id}/state`, {
      method: 'PUT',
      body: JSON.stringify({ isPinned }),
    });
  },

  async getChatToolCategories(): Promise<ApiResult<ChatToolCategory[]>> {
    return fetchApi<ChatToolCategory[]>('/api/v1/chat/tool-categories');
  },

  /** Threads with a run in flight that the caller can see, and who started each. */
  async getStreamingThreads(): Promise<ApiResult<{ threads: Array<{ threadId: string; startedBy: { id: string; displayName: string } | null }> }>> {
    return fetchApi<{ threads: Array<{ threadId: string; startedBy: { id: string; displayName: string } | null }> }>('/api/v1/chat/threads/streaming');
  },

  async abortChatStream(threadId: string): Promise<ApiResult<{ aborted: boolean }>> {
    return fetchApi<{ aborted: boolean }>(`/api/v1/chat/threads/${threadId}/stream/abort`, {
      method: 'POST',
    });
  },

  async truncateChatMessages(threadId: string, messageId: string): Promise<ApiResult<{ truncated: boolean }>> {
    return fetchApi<{ truncated: boolean }>(`/api/v1/chat/threads/${threadId}/truncate`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    });
  },

  async getChatPendingPrompt(threadId: string): Promise<ApiResult<{ prompts: ChatPendingPrompt[] }>> {
    return fetchApi<{ prompts: ChatPendingPrompt[] }>(
      `/api/v1/chat/threads/${encodeURIComponent(threadId)}/pending-prompt`,
    );
  },

  async submitChatUserInput(
    threadId: string,
    toolCallId: string,
    response: string,
  ): Promise<ApiResult<{ submitted: boolean }>> {
    return fetchApi<{ submitted: boolean }>(
      `/api/v1/chat/threads/${encodeURIComponent(threadId)}/user-input`,
      {
        method: 'POST',
        body: JSON.stringify({ toolCallId, response }),
      },
    );
  },

  // Delegated Kibo tenant access (chat)
  /** Read-only preflight — safe to call on card render; writes no pending session state. */
  async getKiboTenantAccessEnvironments(tenantId: string): Promise<ApiResult<KiboTenantAccessEnvironments>> {
    const params = new URLSearchParams({ tenantId });
    return fetchApi<KiboTenantAccessEnvironments>(`/api/v1/chat/kibo-tenant/environments?${params.toString()}`);
  },
  async getKiboTenantAccess(
    tenantId: string,
    threadId: string,
    toolCallId: string,
    environmentId: string,
  ): Promise<ApiResult<KiboTenantAccessResult>> {
    const params = new URLSearchParams({ tenantId, threadId, toolCallId, environmentId });
    return fetchApi<KiboTenantAccessResult>(`/api/v1/chat/kibo-tenant/access?${params.toString()}`);
  },

  // Kibo login environments (Admin)
  async getKiboLoginEnvironments(): Promise<ApiResult<KiboLoginEnvironment[]>> {
    return fetchApi<KiboLoginEnvironment[]>('/api/v1/admin/kibo/login-environments');
  },
  async saveKiboLoginEnvironment(environment: Partial<KiboLoginEnvironment>): Promise<ApiResult<KiboLoginEnvironment>> {
    return fetchApi<KiboLoginEnvironment>('/api/v1/admin/kibo/login-environments', {
      method: 'PUT',
      body: JSON.stringify(environment),
    });
  },
  async deleteKiboLoginEnvironment(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/kibo/login-environments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // Observed environment mismatches (Admin)
  async getKiboTenantEnvironmentObservations(): Promise<ApiResult<KiboTenantEnvironmentObservation[]>> {
    return fetchApi<KiboTenantEnvironmentObservation[]>('/api/v1/admin/kibo/tenant-environment-observations');
  },
  async applyKiboTenantEnvironmentObservation(id: string): Promise<ApiResult<KiboTenantLoginOverride>> {
    return fetchApi<KiboTenantLoginOverride>(
      `/api/v1/admin/kibo/tenant-environment-observations/${encodeURIComponent(id)}/apply`,
      { method: 'POST' },
    );
  },
  async dismissKiboTenantEnvironmentObservation(id: string): Promise<ApiResult<{ dismissed: boolean }>> {
    return fetchApi<{ dismissed: boolean }>(
      `/api/v1/admin/kibo/tenant-environment-observations/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST' },
    );
  },

  // Kibo tenant login mapping (Admin)
  async getKiboTenantLoginRules(): Promise<ApiResult<KiboTenantLoginRule[]>> {
    return fetchApi<KiboTenantLoginRule[]>('/api/v1/admin/kibo/tenant-login-rules');
  },
  async saveKiboTenantLoginRule(rule: Partial<KiboTenantLoginRule>): Promise<ApiResult<KiboTenantLoginRule>> {
    return fetchApi<KiboTenantLoginRule>('/api/v1/admin/kibo/tenant-login-rules', {
      method: 'PUT',
      body: JSON.stringify(rule),
    });
  },
  async deleteKiboTenantLoginRule(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/kibo/tenant-login-rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  async getKiboTenantLoginOverrides(): Promise<ApiResult<KiboTenantLoginOverride[]>> {
    return fetchApi<KiboTenantLoginOverride[]>('/api/v1/admin/kibo/tenant-login-overrides');
  },
  async saveKiboTenantLoginOverride(override: Partial<KiboTenantLoginOverride>): Promise<ApiResult<KiboTenantLoginOverride>> {
    return fetchApi<KiboTenantLoginOverride>('/api/v1/admin/kibo/tenant-login-overrides', {
      method: 'PUT',
      body: JSON.stringify(override),
    });
  },
  async deleteKiboTenantLoginOverride(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/kibo/tenant-login-overrides/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  async getKiboSafePostPatterns(): Promise<ApiResult<KiboSafePostPattern[]>> {
    return fetchApi<KiboSafePostPattern[]>('/api/v1/admin/kibo/safe-post-patterns');
  },
  async saveKiboSafePostPattern(pattern: Partial<KiboSafePostPattern>): Promise<ApiResult<KiboSafePostPattern>> {
    return fetchApi<KiboSafePostPattern>('/api/v1/admin/kibo/safe-post-patterns', {
      method: 'PUT',
      body: JSON.stringify(pattern),
    });
  },
  async deleteKiboSafePostPattern(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/kibo/safe-post-patterns/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  async exportKiboTenantLoginMapping(): Promise<ApiResult<KiboTenantLoginMappingExport>> {
    return fetchApi<KiboTenantLoginMappingExport>('/api/v1/admin/kibo/tenant-login-mapping/export');
  },
  async importKiboTenantLoginMapping(
    payload: KiboTenantLoginMappingExport,
  ): Promise<ApiResult<KiboTenantLoginMappingImportResult>> {
    return fetchApi<KiboTenantLoginMappingImportResult>(
      '/api/v1/admin/kibo/tenant-login-mapping/import',
      { method: 'POST', body: JSON.stringify(payload) },
    );
  },

  // Chat Tool Config (Admin)
  async getChatConfig(): Promise<ApiResult<ChatToolConfigDetail>> {
    return fetchApi<ChatToolConfigDetail>('/api/v1/admin/chat/config');
  },

  async generateChatExternalResponse(
    threadId: string,
    opts: { regenerate?: boolean } = {},
  ): Promise<ApiResult<ChatExternalResponse>> {
    const qs = opts.regenerate ? '?regenerate=true' : '';
    return fetchApi<ChatExternalResponse>(`/api/v1/chat/threads/${encodeURIComponent(threadId)}/external-response${qs}`, {
      method: 'POST',
    });
  },

  async updateChatConfig(data: {
    credentialSetId: string | null;
    defaultModel: string;
    titleModel: string | null;
    accountAssociationRequiredGroups: string[];
  }): Promise<ApiResult<ChatToolConfigDetail>> {
    return fetchApi<ChatToolConfigDetail>('/api/v1/admin/chat/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Chat-account association
  async getChatPolicy(): Promise<ApiResult<ChatPolicyResponse>> {
    return fetchApi<ChatPolicyResponse>('/api/v1/chat/me/policy');
  },

  async setChatThreadAccount(
    threadId: string,
    update: { accountId: string | null; status: 'set' | 'none' },
  ): Promise<ApiResult<{ account: ChatThreadAccountRef | null; status: 'set' | 'none' }>> {
    return fetchApi<{ account: ChatThreadAccountRef | null; status: 'set' | 'none' }>(
      `/api/v1/chat/threads/${encodeURIComponent(threadId)}/account`,
      { method: 'PATCH', body: JSON.stringify(update) },
    );
  },

  async getAccountChats(
    accountId: string,
    params?: { page?: number; limit?: number; sortBy?: 'updatedAt' | 'createdAt' | 'messageCount'; sortDir?: 'asc' | 'desc' },
  ): Promise<ApiResult<AccountChatsList>> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.sortBy) sp.set('sortBy', params.sortBy);
    if (params?.sortDir) sp.set('sortDir', params.sortDir);
    const qs = sp.toString();
    return fetchApi<AccountChatsList>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/chats${qs ? `?${qs}` : ''}`,
    );
  },

  async searchChats(params: ChatSearchQueryParams): Promise<ApiResult<ChatSearchResult>> {
    const sp = new URLSearchParams();
    sp.set('q', params.q);
    if (params.accountId) sp.set('accountId', params.accountId);
    if (params.ownerId)   sp.set('ownerId', params.ownerId);
    if (params.dateFrom)  sp.set('dateFrom', params.dateFrom);
    if (params.dateTo)    sp.set('dateTo', params.dateTo);
    if (params.page)      sp.set('page', String(params.page));
    if (params.limit)     sp.set('limit', String(params.limit));
    if (params.sortBy)    sp.set('sortBy', params.sortBy);
    if (params.sortDir)   sp.set('sortDir', params.sortDir);
    return fetchApi<ChatSearchResult>(`/api/v1/chat/search?${sp}`);
  },

  async listChatSearchOwners(params?: { q?: string; limit?: number }): Promise<ApiResult<ChatSearchOwner[]>> {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.limit) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return fetchApi<ChatSearchOwner[]>(`/api/v1/chat/search/owners${qs ? `?${qs}` : ''}`);
  },

  async listChatAccountQueue(params?: {
    page?: number;
    limit?: number;
    status?: 'set' | 'none' | 'unknown' | 'all';
    includeSkipped?: boolean;
    userId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ApiResult<ChatAccountQueueList>> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.status) sp.set('status', params.status);
    if (params?.includeSkipped) sp.set('includeSkipped', 'true');
    if (params?.userId) sp.set('userId', params.userId);
    if (params?.dateFrom) sp.set('dateFrom', params.dateFrom);
    if (params?.dateTo) sp.set('dateTo', params.dateTo);
    const qs = sp.toString();
    return fetchApi<ChatAccountQueueList>(`/api/v1/admin/chat/account-queue${qs ? `?${qs}` : ''}`);
  },

  async applyChatAccountQueueAction(
    threadId: string,
    action: ChatAccountQueueAction,
  ): Promise<ApiResult<{ ok: boolean }>> {
    return fetchApi<{ ok: boolean }>(
      `/api/v1/admin/chat/account-queue/${encodeURIComponent(threadId)}`,
      { method: 'PATCH', body: JSON.stringify(action) },
    );
  },

  // MCP Servers (self-serve)
  async getMcpServers(): Promise<ApiResult<McpServerSummary[]>> {
    return fetchApi<McpServerSummary[]>('/api/v1/mcp/servers');
  },

  async getMcpServer(serverId: string): Promise<ApiResult<McpServerDetail>> {
    return fetchApi<McpServerDetail>(`/api/v1/mcp/servers/${encodeURIComponent(serverId)}`);
  },

  async createMcpServer(input: McpServerInput): Promise<ApiResult<McpServerSummary>> {
    return fetchApi<McpServerSummary>('/api/v1/mcp/servers', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateMcpServer(serverId: string, input: McpServerInput): Promise<ApiResult<McpServerSummary>> {
    return fetchApi<McpServerSummary>(`/api/v1/mcp/servers/${encodeURIComponent(serverId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async deleteMcpServer(serverId: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/mcp/servers/${encodeURIComponent(serverId)}`, { method: 'DELETE' });
  },

  async getMcpServerTools(serverId: string): Promise<ApiResult<McpExposedTool[]>> {
    return fetchApi<McpExposedTool[]>(`/api/v1/mcp/servers/${encodeURIComponent(serverId)}/tools`);
  },

  async getMcpCategories(): Promise<ApiResult<McpCategoryStatus[]>> {
    return fetchApi<McpCategoryStatus[]>('/api/v1/mcp/categories');
  },

  async revokeMcpConnection(serverId: string, grantId: string): Promise<ApiResult<{ revoked: boolean }>> {
    return fetchApi<{ revoked: boolean }>(
      `/api/v1/mcp/servers/${encodeURIComponent(serverId)}/connections/${encodeURIComponent(grantId)}`,
      { method: 'DELETE' },
    );
  },

  async getMcpConsentRequest(requestId: string): Promise<ApiResult<McpOAuthConsentRequest>> {
    return fetchApi<McpOAuthConsentRequest>(
      `/api/v1/mcp/oauth/consent/${encodeURIComponent(requestId)}`,
    );
  },

  async decideMcpConsent(requestId: string, approve: boolean): Promise<ApiResult<{ redirectTo: string }>> {
    return fetchApi<{ redirectTo: string }>(
      `/api/v1/mcp/oauth/consent/${encodeURIComponent(requestId)}`,
      { method: 'POST', body: JSON.stringify({ approve }) },
    );
  },

  async getMcpGrants(params?: {
    page?: number;
    limit?: number;
    search?: string;
    includeRevoked?: boolean;
  }): Promise<ApiResult<McpUsagePage<McpOAuthAdminGrantSummary>>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.includeRevoked) query.set('includeRevoked', 'true');
    const qs = query.toString();
    return fetchApi<McpUsagePage<McpOAuthAdminGrantSummary>>(
      `/api/v1/admin/mcp/grants${qs ? `?${qs}` : ''}`,
    );
  },

  async revokeMcpGrant(grantId: string): Promise<ApiResult<{ revoked: boolean }>> {
    return fetchApi<{ revoked: boolean }>(`/api/v1/admin/mcp/grants/${encodeURIComponent(grantId)}`, {
      method: 'DELETE',
    });
  },

  async revokeAllMcpGrants(): Promise<ApiResult<{ revoked: number }>> {
    return fetchApi<{ revoked: number }>('/api/v1/admin/mcp/grants/revoke-all', { method: 'POST' });
  },

  async getMcpOAuthClients(): Promise<ApiResult<McpOAuthClientSummary[]>> {
    return fetchApi<McpOAuthClientSummary[]>('/api/v1/admin/mcp/oauth-clients');
  },

  async setMcpOAuthClientBlocked(id: string, isBlocked: boolean): Promise<ApiResult<{ isBlocked: boolean }>> {
    return fetchApi<{ isBlocked: boolean }>(`/api/v1/admin/mcp/oauth-clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ isBlocked }),
    });
  },

  async getMcpRedirectHosts(): Promise<ApiResult<McpOAuthRedirectHostSummary[]>> {
    return fetchApi<McpOAuthRedirectHostSummary[]>('/api/v1/admin/mcp/redirect-hosts');
  },

  async addMcpRedirectHost(origin: string, label: string): Promise<ApiResult<McpOAuthRedirectHostSummary>> {
    return fetchApi<McpOAuthRedirectHostSummary>('/api/v1/admin/mcp/redirect-hosts', {
      method: 'POST',
      body: JSON.stringify({ origin, label }),
    });
  },

  async removeMcpRedirectHost(id: string): Promise<ApiResult<{ removed: boolean }>> {
    return fetchApi<{ removed: boolean }>(`/api/v1/admin/mcp/redirect-hosts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  async getMcpOAuthConfig(): Promise<ApiResult<McpOAuthConfigView>> {
    return fetchApi<McpOAuthConfigView>('/api/v1/admin/mcp/oauth-config');
  },

  async updateMcpOAuthConfig(input: {
    accessTokenTtlSeconds: number;
    refreshAbsoluteSeconds: number;
    refreshIdleSeconds: number;
  }): Promise<ApiResult<McpOAuthConfigView>> {
    return fetchApi<McpOAuthConfigView>('/api/v1/admin/mcp/oauth-config', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async getMcpServerUsage(
    serverId: string,
    params?: { page?: number; limit?: number; status?: McpCallStatus },
  ): Promise<ApiResult<McpUsagePage<McpUsageEventRow>>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return fetchApi<McpUsagePage<McpUsageEventRow>>(
      `/api/v1/mcp/servers/${encodeURIComponent(serverId)}/usage${qs ? `?${qs}` : ''}`,
    );
  },

  // MCP tool policy (admin)
  async getMcpPolicy(): Promise<ApiResult<McpPolicy>> {
    return fetchApi<McpPolicy>('/api/v1/admin/mcp/policy');
  },

  async updateMcpPolicy(allowedCategories: string[]): Promise<ApiResult<McpPolicy>> {
    return fetchApi<McpPolicy>('/api/v1/admin/mcp/policy', {
      method: 'PUT',
      body: JSON.stringify({ allowedCategories }),
    });
  },

  // MCP usage analytics (admin)
  async getMcpUsageSummary(params?: {
    from?: string;
    to?: string;
  }): Promise<ApiResult<McpUsageSummary>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    const qs = query.toString();
    return fetchApi<McpUsageSummary>(`/api/v1/admin/mcp/usage/summary${qs ? `?${qs}` : ''}`);
  },

  async getMcpUsageByUser(params?: {
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDir?: string;
    search?: string;
  }): Promise<ApiResult<McpUsagePage<McpUsageUserRow>>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<McpUsagePage<McpUsageUserRow>>(
      `/api/v1/admin/mcp/usage/users${qs ? `?${qs}` : ''}`,
    );
  },

  async getMcpUsageByServer(params?: {
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDir?: string;
    search?: string;
  }): Promise<ApiResult<McpUsagePage<McpUsageServerRow>>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<McpUsagePage<McpUsageServerRow>>(
      `/api/v1/admin/mcp/usage/servers${qs ? `?${qs}` : ''}`,
    );
  },

  async getMcpUsageEvents(params?: {
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    userId?: string;
    serverId?: string;
    tool?: string;
    status?: McpCallStatus;
  }): Promise<ApiResult<McpUsagePage<McpUsageEventRow>>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.userId) query.set('userId', params.userId);
    if (params?.serverId) query.set('serverId', params.serverId);
    if (params?.tool) query.set('tool', params.tool);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return fetchApi<McpUsagePage<McpUsageEventRow>>(
      `/api/v1/admin/mcp/usage/events${qs ? `?${qs}` : ''}`,
    );
  },

  // Chat Tool Test Console (Admin)
  async getChatTools(): Promise<ApiResult<ChatToolMetadata[]>> {
    return fetchApi<ChatToolMetadata[]>('/api/v1/admin/chat/tools');
  },

  async getChatToolCategoryAudit(): Promise<ApiResult<ChatToolCategoryAuditReport>> {
    return fetchApi<ChatToolCategoryAuditReport>('/api/v1/admin/chat/tool-categories/audit');
  },

  async executeChatTool(name: string, input: Record<string, unknown>): Promise<ApiResult<ChatToolExecuteResult>> {
    return fetchApi<ChatToolExecuteResult>(`/api/v1/admin/chat/tools/${encodeURIComponent(name)}/execute`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    });
  },

  // Chat Tool Params (Admin)
  async getChatToolParams(): Promise<ApiResult<ChatToolParamsDetail>> {
    return fetchApi<ChatToolParamsDetail>('/api/v1/admin/chat/tool-params');
  },

  async updateChatToolParams(data: ChatToolParamsUpdate | null): Promise<ApiResult<ChatToolParamsDetail>> {
    return fetchApi<ChatToolParamsDetail>('/api/v1/admin/chat/tool-params', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Chat Attachments
  async uploadChatAttachment(file: File): Promise<ApiResult<ChatAttachmentRef>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('/api/v1/chat/attachments/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => 'Upload failed');
      return localApiError('UPLOAD_FAILED', text);
    }
    const json = await response.json() as { data: ChatAttachmentRef };
    return { success: true, data: json.data };
  },

  getChatAttachmentDownloadUrl(attachmentId: string): string {
    return `/api/v1/chat/attachments/${encodeURIComponent(attachmentId)}`;
  },

  // ─── Projects ─────────────────────────────────────────────────────────

  async listChatProjects(params?: { archived?: boolean }): Promise<ApiResult<ChatProject[]>> {
    const sp = new URLSearchParams();
    if (params?.archived) sp.set('archived', 'true');
    return fetchApi<ChatProject[]>(`/api/v1/projects?${sp}`);
  },

  async createChatProject(body: {
    name: string; description: string; openToEveryone: boolean; agentWrites?: 'propose' | 'direct';
  }): Promise<ApiResult<ChatProject>> {
    return fetchApi<ChatProject>('/api/v1/projects', { method: 'POST', body: JSON.stringify(body) });
  },

  async archiveChatProject(projectId: string, archived: boolean): Promise<ApiResult<ChatProject>> {
    return fetchApi<ChatProject>(`/api/v1/projects/${encodeURIComponent(projectId)}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    });
  },

  // ─── Files (the VFS) ──────────────────────────────────────────────────
  //
  // Everything is keyed on an absolute VFS path; the mount identifies the scope.
  // `threadId` is optional context that also mounts /work and /attachments, which
  // is what lets one tree component serve the dashboard and the chat drawer.

  async listVfsFiles(params: { path?: string; threadId?: string; accountId?: string }): Promise<ApiResult<VfsListing>> {
    const sp = new URLSearchParams();
    if (params.path) sp.set('path', params.path);
    if (params.threadId) sp.set('threadId', params.threadId);
    if (params.accountId) sp.set('accountId', params.accountId);
    return fetchApi<VfsListing>(`/api/v1/files?${sp}`);
  },

  async getVfsFileContent(params: { path: string; threadId?: string; accountId?: string }): Promise<ApiResult<VfsFileContent>> {
    const sp = new URLSearchParams({ path: params.path });
    if (params.threadId) sp.set('threadId', params.threadId);
    if (params.accountId) sp.set('accountId', params.accountId);
    return fetchApi<VfsFileContent>(`/api/v1/files/content?${sp}`);
  },

  async saveVfsFileContent(body: {
    path: string; content: string; threadId?: string; accountId?: string; expectedHash?: string | null;
  }): Promise<ApiResult<{ path: string; contentHash: string }>> {
    return fetchApi<{ path: string; contentHash: string }>('/api/v1/files/content', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  getVfsDownloadUrl(path: string, threadId?: string, accountId?: string): string {
    const sp = new URLSearchParams({ path });
    if (threadId) sp.set('threadId', threadId);
    if (accountId) sp.set('accountId', accountId);
    return `/api/v1/files/download?${sp}`;
  },

  async uploadVfsFile(file: File, dir: string, threadId?: string, accountId?: string): Promise<ApiResult<VfsUploadResult>> {
    const formData = new FormData();
    // The destination travels beside the file rather than in the URL so the
    // multipart parser sees one coherent request.
    formData.append('path', dir);
    if (threadId) formData.append('threadId', threadId);
    if (accountId) formData.append('accountId', accountId);
    formData.append('file', file);
    const response = await fetch('/api/v1/files/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => 'Upload failed');
      // The server phrases its rejections for a person ("that file is 14 MB,
      // over the 10 MB limit"), so surface them rather than a generic failure.
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        return localApiError('UPLOAD_FAILED', parsed.error?.message ?? text);
      } catch {
        return localApiError('UPLOAD_FAILED', text);
      }
    }
    const json = await response.json() as { data: VfsUploadResult };
    return { success: true, data: json.data };
  },

  async syncVfsFolder(syncSourceId: string): Promise<ApiResult<{ added: number; updated: number; removed: number; skipped: string[] }>> {
    return fetchApi(`/api/v1/files/sync-sources/${encodeURIComponent(syncSourceId)}/sync`, { method: 'POST' });
  },

  async createVfsFolder(body: { path: string; threadId?: string; accountId?: string }): Promise<ApiResult<{ path: string }>> {
    return fetchApi<{ path: string }>('/api/v1/files/folders', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async deleteVfsEntry(params: {
    path: string; kind: 'file' | 'directory'; threadId?: string; accountId?: string;
  }): Promise<ApiResult<{ deleted: string }>> {
    const sp = new URLSearchParams({ path: params.path, kind: params.kind });
    if (params.threadId) sp.set('threadId', params.threadId);
    if (params.accountId) sp.set('accountId', params.accountId);
    return fetchApi<{ deleted: string }>(`/api/v1/files?${sp}`, { method: 'DELETE' });
  },

  async moveVfsEntry(body: {
    from: string; to: string; threadId?: string; accountId?: string;
  }): Promise<ApiResult<{ path: string }>> {
    return fetchApi<{ path: string }>('/api/v1/files/move', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async renameVfsEntry(body: {
    path: string; name: string; threadId?: string; accountId?: string;
  }): Promise<ApiResult<{ path: string }>> {
    return fetchApi<{ path: string }>('/api/v1/files/rename', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Document export — see ExportMenuComponent and export-actions.ts for the callers.

  /**
   * Render Markdown to a .docx.
   *
   * Deliberately not `fetchApi`: that helper parses every response as JSON, and
   * this one is a binary body on success and JSON only on failure.
   */
  async exportDocumentAsDocx(body: DocumentExportDocxRequest): Promise<ApiResult<Blob>> {
    const response = await fetch('/api/v1/document-export/docx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => 'Export failed');
      // The server phrases its rejections for a person; surface them rather than
      // a generic failure.
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        return localApiError('EXPORT_FAILED', parsed.error?.message ?? text);
      } catch {
        return localApiError('EXPORT_FAILED', text);
      }
    }
    return { success: true, data: await response.blob() };
  },

  async exportDocumentAsXlsx(body: DocumentExportXlsxRequest): Promise<ApiResult<Blob>> {
    const response = await fetch('/api/v1/document-export/xlsx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => 'Export failed');
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        return localApiError('EXPORT_FAILED', parsed.error?.message ?? text);
      } catch {
        return localApiError('EXPORT_FAILED', text);
      }
    }
    return { success: true, data: await response.blob() };
  },

  async exportCsvAsXlsx(body: DocumentExportCsvXlsxRequest): Promise<ApiResult<Blob>> {
    const response = await fetch('/api/v1/document-export/csv-xlsx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => 'Export failed');
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        return localApiError('EXPORT_FAILED', parsed.error?.message ?? text);
      } catch {
        return localApiError('EXPORT_FAILED', text);
      }
    }
    return { success: true, data: await response.blob() };
  },

  async exportDocumentToDrive(body: DocumentExportDriveRequest): Promise<ApiResult<DriveExportResult>> {
    return fetchApi<DriveExportResult>('/api/v1/document-export/google-doc', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getGoogleDriveConnection(): Promise<ApiResult<GoogleDriveConnectionInfo>> {
    return fetchApi<GoogleDriveConnectionInfo>('/api/v1/google-drive/me/connection');
  },

  async disconnectGoogleDrive(): Promise<ApiResult<{ disconnected: boolean }>> {
    return fetchApi<{ disconnected: boolean }>('/api/v1/google-drive/me/connection', { method: 'DELETE' });
  },

  async adminListChatAttachments(params?: { page?: number; pageSize?: number; sortBy?: string; sortDir?: 'asc' | 'desc' }): Promise<ApiResult<ChatAttachmentAdminList>> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.pageSize) sp.set('pageSize', String(params.pageSize));
    if (params?.sortBy) sp.set('sortBy', params.sortBy);
    if (params?.sortDir) sp.set('sortDir', params.sortDir);
    return fetchApi<ChatAttachmentAdminList>(`/api/v1/admin/chat/attachments?${sp}`);
  },

  async adminGetChatAttachment(id: string): Promise<ApiResult<ChatAttachmentAdminDetail>> {
    return fetchApi<ChatAttachmentAdminDetail>(`/api/v1/admin/chat/attachments/${encodeURIComponent(id)}`);
  },

  // Chat Feedback
  async submitMessageFeedback(messageId: string, data: { rating: 1 | 5; content?: string }): Promise<ApiResult<ChatMessageFeedback>> {
    return fetchApi<ChatMessageFeedback>(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteMessageFeedback(messageId: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/feedback`, {
      method: 'DELETE',
    });
  },

  async getThreadFeedback(threadId: string): Promise<ApiResult<{ feedback: ChatMessageFeedback[] }>> {
    return fetchApi<{ feedback: ChatMessageFeedback[] }>(`/api/v1/chat/threads/${encodeURIComponent(threadId)}/feedback`);
  },

  async getChatFeedbackList(params?: { page?: number; limit?: number; search?: string; rating?: '1' | '5' }): Promise<ApiResult<ChatFeedbackList>> {
    const query = new URLSearchParams();
    if (params?.page != null) query.set('page', String(params.page));
    if (params?.limit != null) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.rating) query.set('rating', params.rating);
    return fetchApi<ChatFeedbackList>(`/api/v1/admin/chat/feedback?${query}`);
  },

  // Chat Comments
  async addChatComment(messageId: string, data: { selectedText: string; startOffset: number; endOffset: number; content: string }): Promise<ApiResult<ChatComment>> {
    return fetchApi<ChatComment>(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getThreadComments(threadId: string): Promise<ApiResult<{ comments: ChatComment[] }>> {
    return fetchApi<{ comments: ChatComment[] }>(`/api/v1/chat/threads/${encodeURIComponent(threadId)}/comments`);
  },

  async updateChatComment(commentId: string, data: { content: string }): Promise<ApiResult<ChatComment>> {
    return fetchApi<ChatComment>(`/api/v1/chat/comments/${encodeURIComponent(commentId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteChatComment(commentId: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/chat/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  },

  async getChatAnalytics(params?: {
    from?: string;
    to?: string;
    interval?: ChatAnalyticsInterval;
    metric?: ChatAnalyticsMetric;
    origins?: ChatOrigin[];
  }): Promise<ApiResult<ChatAnalytics>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.interval) query.set('interval', params.interval);
    if (params?.metric) query.set('metric', params.metric);
    if (params?.origins && params.origins.length > 0) query.set('origins', params.origins.join(','));
    const qs = query.toString();
    return fetchApi<ChatAnalytics>(`/api/v1/admin/chat/analytics${qs ? `?${qs}` : ''}`);
  },

  async getChatConversations(params: {
    page?: number; limit?: number;
    sortBy?: string; sortDir?: string;
    from?: string; to?: string;
    dayFilter?: string;
    userSearch?: string; modelFilter?: string; messageSearch?: string;
  }): Promise<ApiResult<ChatConversationList>> {
    const query = new URLSearchParams();
    if (params.page != null) query.set('page', String(params.page));
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.dayFilter) query.set('dayFilter', params.dayFilter);
    if (params.userSearch) query.set('userSearch', params.userSearch);
    if (params.modelFilter) query.set('modelFilter', params.modelFilter);
    if (params.messageSearch) query.set('messageSearch', params.messageSearch);
    return fetchApi<ChatConversationList>(`/api/v1/admin/chat/conversations?${query}`);
  },

  /** The journal's History for one thing (admin only). */
  async getJournalHistory(about: { correlationId?: string; threadId?: string; incidentId?: string }): Promise<ApiResult<{ rows: JournalHistoryRow[] }>> {
    const query = new URLSearchParams();
    if (about.correlationId) query.set('correlationId', about.correlationId);
    if (about.threadId) query.set('threadId', about.threadId);
    if (about.incidentId) query.set('incidentId', about.incidentId);
    return fetchApi<{ rows: JournalHistoryRow[] }>(`/api/v1/admin/journal/history?${query}`);
  },

  // Download a thread's debug trace (Chrome Trace Event Format JSON). Returns the
  // raw Response — the endpoint sends a plain JSON document, not the apiSuccess envelope.
  async downloadChatDebugTrace(threadId: string): Promise<Response> {
    return fetch(`/api/v1/admin/chat/threads/${threadId}/debug-trace`, { credentials: 'include' });
  },

  // Code Repositories
  async getCodeRepoConfig(): Promise<ApiResult<CodeSyncConfigResponse>> {
    return fetchApi<CodeSyncConfigResponse>('/api/v1/admin/code-repos/config');
  },

  async updateCodeRepoConfig(data: { storageBasePath?: string; vmUrl?: string }): Promise<ApiResult<CodeSyncConfigResponse>> {
    return fetchApi<CodeSyncConfigResponse>('/api/v1/admin/code-repos/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getCodeRepos(): Promise<ApiResult<CodeRepositorySummary[]>> {
    return fetchApi<CodeRepositorySummary[]>('/api/v1/admin/code-repos');
  },

  async addCodeRepo(data: { owner: string; name: string; defaultBranch?: string; description?: string }): Promise<ApiResult<CodeRepositorySummary>> {
    return fetchApi<CodeRepositorySummary>('/api/v1/admin/code-repos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateCodeRepo(id: string, data: { defaultBranch: string }): Promise<ApiResult<CodeRepositorySummary>> {
    return fetchApi<CodeRepositorySummary>(`/api/v1/admin/code-repos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async removeCodeRepo(id: string): Promise<ApiResult<{ removed: boolean }>> {
    return fetchApi<{ removed: boolean }>(`/api/v1/admin/code-repos/${id}`, {
      method: 'DELETE',
    });
  },

  async syncCodeRepo(id: string): Promise<ApiResult<CodeRepositorySummary>> {
    return fetchApi<CodeRepositorySummary>(`/api/v1/admin/code-repos/${id}/sync`, {
      method: 'POST',
    });
  },

  async syncAllCodeRepos(): Promise<ApiResult<CodeRepoSyncResult[]>> {
    return fetchApi<CodeRepoSyncResult[]>('/api/v1/admin/code-repos/sync-all', {
      method: 'POST',
    });
  },

  async getAvailableCodeRepos(): Promise<ApiResult<GitHubRepository[]>> {
    return fetchApi<GitHubRepository[]>('/api/v1/admin/code-repos/available');
  },

  async getCodeRepoStorageDebug(): Promise<ApiResult<StorageDebugInfo>> {
    return fetchApi<StorageDebugInfo>('/api/v1/admin/code-repos/storage-debug');
  },

  async scanCodeRepoBucket(): Promise<ApiResult<BucketScanResponse>> {
    return fetchApi<BucketScanResponse>('/api/v1/admin/code-repos/scan-bucket');
  },

  async registerBucketCodeRepo(data: { owner: string; name: string; description?: string }): Promise<ApiResult<CodeRepositorySummary>> {
    return fetchApi<CodeRepositorySummary>('/api/v1/admin/code-repos/register-bucket', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getCodeSearchVmHealth(): Promise<ApiResult<CodeSearchVmHealth>> {
    return fetchApi<CodeSearchVmHealth>('/api/v1/admin/code-repos/vm-health');
  },

  async testCodeSearchVmEndpoint(endpoint: CodeSearchVmEndpoint, params: Record<string, unknown>): Promise<ApiResult<CodeSearchVmTestResult>> {
    return fetchApi<CodeSearchVmTestResult>('/api/v1/admin/code-repos/vm-test', {
      method: 'POST',
      body: JSON.stringify({ endpoint, params }),
    });
  },

  // Salesforce
  async getSalesforceConfig(): Promise<ApiResult<SalesforceConfigResponse>> {
    return fetchApi<SalesforceConfigResponse>('/api/v1/admin/salesforce/config');
  },

  async updateSalesforceConfig(data: {
    credentialSetId?: string | null;
    instanceUrl?: string | null;
    organizationId?: string | null;
    testCredentialSetId?: string | null;
    testInstanceUrl?: string | null;
    testOrganizationId?: string | null;
    unplacedAccountId?: string | null;
    testUnplacedAccountId?: string | null;
    syncDateFrom?: string | null;
    syncDateTo?: string | null;
    summarySystemPrompt?: string | null;
  }): Promise<ApiResult<SalesforceConfigResponse>> {
    return fetchApi<SalesforceConfigResponse>('/api/v1/admin/salesforce/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testSalesforceConnection(connection: 'primary' | 'test' = 'primary'): Promise<ApiResult<SalesforceConnectionTestResult>> {
    const query = connection === 'test' ? '?connection=test' : '';
    return fetchApi<SalesforceConnectionTestResult>(`/api/v1/admin/salesforce/test-connection${query}`, {
      method: 'POST',
    });
  },

  async triggerSalesforceSync(): Promise<ApiResult<SalesforceSyncResult>> {
    return fetchApi<SalesforceSyncResult>('/api/v1/admin/salesforce/sync', {
      method: 'POST',
    });
  },

  async getSalesforceSyncStatus(): Promise<ApiResult<SalesforceSyncStats>> {
    return fetchApi<SalesforceSyncStats>('/api/v1/salesforce/sync-status');
  },

  async getSalesforceCases(params?: { page?: number; limit?: number; search?: string; status?: string; sortBy?: string; sortOrder?: string }): Promise<ApiResult<PaginatedResponse<SalesforceCaseSummary>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    return fetchApi<PaginatedResponse<SalesforceCaseSummary>>(`/api/v1/salesforce/cases?${searchParams}`);
  },

  async getSalesforceCase(id: string): Promise<ApiResult<SalesforceCaseDetail>> {
    return fetchApi<SalesforceCaseDetail>(`/api/v1/salesforce/cases/${id}`);
  },

  async syncSalesforceCase(id: string): Promise<ApiResult<SalesforceSyncResult>> {
    return fetchApi<SalesforceSyncResult>(`/api/v1/salesforce/cases/${id}/sync`, {
      method: 'POST',
    });
  },

  async streamSalesforceCaseSummary(id: string): Promise<Response> {
    return fetch(`/api/v1/salesforce/cases/${id}/summary`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  async fetchRawSalesforceCase(id: string): Promise<ApiResult<Record<string, unknown>>> {
    return fetchApi<Record<string, unknown>>(`/api/v1/salesforce/cases/${id}/raw`);
  },

  async getSalesforceWebhookLogs(): Promise<ApiResult<SalesforceWebhookLogEntry[]>> {
    return fetchApi<SalesforceWebhookLogEntry[]>('/api/v1/admin/salesforce/webhook-logs');
  },

  async getSalesforceDeltaSyncStatus(): Promise<ApiResult<SalesforceDeltaSyncStatus>> {
    return fetchApi<SalesforceDeltaSyncStatus>('/api/v1/admin/salesforce/delta-sync/status');
  },

  async triggerSalesforceDeltaSync(): Promise<ApiResult<{ triggered: boolean }>> {
    return fetchApi<{ triggered: boolean }>('/api/v1/admin/salesforce/delta-sync', {
      method: 'POST',
    });
  },

  async triggerSalesforceAccountSync(): Promise<ApiResult<{ triggered: boolean }>> {
    return fetchApi<{ triggered: boolean }>('/api/v1/admin/salesforce/accounts/sync', {
      method: 'POST',
    });
  },

  async listSalesforceAccounts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResult<PaginatedResponse<SalesforceAccountSummary>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    return fetchApi<PaginatedResponse<SalesforceAccountSummary>>(
      `/api/v1/admin/salesforce/accounts?${searchParams}`
    );
  },

  async getSalesforceAccount(id: string): Promise<ApiResult<SalesforceAccountDetail>> {
    return fetchApi<SalesforceAccountDetail>(`/api/v1/admin/salesforce/accounts/${id}`);
  },

  async getSalesforceAccountSyncRuns(): Promise<ApiResult<SalesforceAccountSyncRun[]>> {
    return fetchApi<SalesforceAccountSyncRun[]>('/api/v1/admin/salesforce/accounts/sync-runs');
  },

  async getSalesforceCrmProperties(): Promise<ApiResult<SalesforceCrmPropertyStatus[]>> {
    return fetchApi<SalesforceCrmPropertyStatus[]>('/api/v1/admin/salesforce/crm-sync/properties');
  },

  async provisionSalesforceCrmProperties(): Promise<ApiResult<SalesforceCrmPropertyStatus[]>> {
    return fetchApi<SalesforceCrmPropertyStatus[]>('/api/v1/admin/salesforce/crm-sync/properties', {
      method: 'POST',
    });
  },

  async triggerSalesforceCrmSync(): Promise<ApiResult<{ triggered: boolean }>> {
    return fetchApi<{ triggered: boolean }>('/api/v1/admin/salesforce/crm-sync', { method: 'POST' });
  },

  async getSalesforceCrmSyncRuns(): Promise<ApiResult<SalesforceCrmSyncRun[]>> {
    return fetchApi<SalesforceCrmSyncRun[]>('/api/v1/admin/salesforce/crm-sync/runs');
  },

  async getSalesforceCutoverReadiness(): Promise<ApiResult<SalesforceCutoverSetupReadiness>> {
    return fetchApi<SalesforceCutoverSetupReadiness>('/api/v1/admin/salesforce/cutover/readiness');
  },

  async getSalesforceCaseStatusAudit(): Promise<ApiResult<SalesforceCaseStatusAudit>> {
    return fetchApi<SalesforceCaseStatusAudit>('/api/v1/admin/salesforce/cutover/status-audit');
  },

  async syncSalesforceMigrationFlags(): Promise<ApiResult<SalesforceMigrationFlagResult>> {
    return fetchApi<SalesforceMigrationFlagResult>('/api/v1/admin/salesforce/cutover/migration-flags', {
      method: 'POST',
    });
  },

  async getSalesforceCaseMigrationPreview(
    accountIds?: string[],
  ): Promise<ApiResult<SalesforceCaseMigrationPreview>> {
    const query = accountIds?.length ? `?accountIds=${encodeURIComponent(accountIds.join(','))}` : '';
    return fetchApi<SalesforceCaseMigrationPreview>(
      `/api/v1/admin/salesforce/cutover/migration-preview${query}`,
    );
  },

  async runSalesforceCaseMigration(input: {
    accountIds?: string[];
    closedStatus?: string;
  }): Promise<ApiResult<{ workflowId: string; alreadyRunning: boolean }>> {
    return fetchApi<{ workflowId: string; alreadyRunning: boolean }>(
      '/api/v1/admin/salesforce/cutover/migrate',
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  async getSalesforceUnlockedCases(): Promise<ApiResult<SalesforceUnlockedCase[]>> {
    return fetchApi<SalesforceUnlockedCase[]>('/api/v1/admin/salesforce/cutover/unlocked');
  },

  async retrySalesforceCaseLocks(): Promise<ApiResult<{ attempted: number; locked: number }>> {
    return fetchApi<{ attempted: number; locked: number }>('/api/v1/admin/salesforce/cutover/retry-locks', {
      method: 'POST',
    });
  },

  async getSalesforceArchiveStatus(): Promise<ApiResult<SalesforceArchiveScopeStatus[]>> {
    return fetchApi<SalesforceArchiveScopeStatus[]>('/api/v1/admin/salesforce/archive');
  },

  async runSalesforceArchive(
    scopeId: string,
  ): Promise<ApiResult<{ workflowId: string; alreadyRunning: boolean }>> {
    return fetchApi<{ workflowId: string; alreadyRunning: boolean }>(
      '/api/v1/admin/salesforce/archive/run',
      { method: 'POST', body: JSON.stringify({ scopeId }) },
    );
  },

  async retrySalesforceArchiveFailures(scopeId: string): Promise<ApiResult<{ cleared: number }>> {
    return fetchApi<{ cleared: number }>('/api/v1/admin/salesforce/archive/retry-failed', {
      method: 'POST',
      body: JSON.stringify({ scopeId }),
    });
  },

  async getSalesforceArchiveFailures(scopeId: string): Promise<ApiResult<SalesforceArchiveFailure[]>> {
    return fetchApi<SalesforceArchiveFailure[]>(
      `/api/v1/admin/salesforce/archive/failures?scopeId=${encodeURIComponent(scopeId)}`,
    );
  },

  async getSalesforceArchiveDownload(
    fileId: string,
  ): Promise<ApiResult<{ url: string; fileName: string; expiresAt: string }>> {
    return fetchApi<{ url: string; fileName: string; expiresAt: string }>(
      `/api/v1/salesforce/archive/files/${fileId}/download`,
    );
  },

  async uploadCodeRepo(file: File, owner: string, name: string, description?: string): Promise<ApiResult<CodeRepositorySummary>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('owner', owner);
    formData.append('name', name);
    if (description) formData.append('description', description);

    const response = await fetch('/api/v1/admin/code-repos/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return response.json() as Promise<ApiResult<CodeRepositorySummary>>;
  },

  // Atlassian (Admin)
  async getAtlassianConfig(): Promise<ApiResult<AtlassianConfigResponse>> {
    return fetchApi<AtlassianConfigResponse>('/api/v1/admin/atlassian/config');
  },

  async updateAtlassianConfig(data: {
    credentialSetId?: string | null;
    confluenceSpaceKeys?: string | null;
    jiraSyncJql?: string | null;
    jiraJqlGuide?: string | null;
    docsJiraProjectKey?: string | null;
    docsJiraIssueType?: string | null;
  }): Promise<ApiResult<AtlassianConfigResponse>> {
    return fetchApi<AtlassianConfigResponse>('/api/v1/admin/atlassian/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testAtlassianConnection(): Promise<ApiResult<AtlassianConnectionTestResult>> {
    return fetchApi<AtlassianConnectionTestResult>('/api/v1/admin/atlassian/test-connection', {
      method: 'POST',
    });
  },

  async testAtlassianConfluenceQuery(query: string, spaceKey?: string): Promise<ApiResult<AtlassianTestQueryConfluenceResultItem[]>> {
    return fetchApi<AtlassianTestQueryConfluenceResultItem[]>('/api/v1/admin/atlassian/test-query/confluence', {
      method: 'POST',
      body: JSON.stringify({ query, spaceKey }),
    });
  },

  async testAtlassianConfluencePageByUrl(url: string): Promise<ApiResult<AtlassianConfluencePageByUrlResult>> {
    return fetchApi<AtlassianConfluencePageByUrlResult>('/api/v1/admin/atlassian/test-query/confluence-page', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  async testAtlassianJiraQuery(jql: string): Promise<ApiResult<AtlassianTestQueryJiraResultItem[]>> {
    return fetchApi<AtlassianTestQueryJiraResultItem[]>('/api/v1/admin/atlassian/test-query/jira', {
      method: 'POST',
      body: JSON.stringify({ jql }),
    });
  },

  async triggerAtlassianConfluenceSync(): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>('/api/v1/admin/atlassian/sync/confluence', {
      method: 'POST',
    });
  },

  async triggerAtlassianJiraSync(): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>('/api/v1/admin/atlassian/sync/jira', {
      method: 'POST',
    });
  },

  // DEV-ONLY: Atlassian token import/export — remove with import/export feature
  async exportAtlassianDevTokens(): Promise<ApiResult<AtlassianDevTokenExport>> {
    return fetchApi<AtlassianDevTokenExport>('/api/v1/admin/atlassian/dev/export-tokens');
  },

  async importAtlassianDevTokens(tokens: Partial<AtlassianDevTokenExport>): Promise<ApiResult<{ imported: number }>> {
    return fetchApi<{ imported: number }>('/api/v1/admin/atlassian/dev/import-tokens', {
      method: 'POST',
      body: JSON.stringify(tokens),
    });
  },
  // END DEV-ONLY

  // Atlassian (User-facing)
  async getAtlassianSyncStatus(): Promise<ApiResult<AtlassianSyncStats>> {
    return fetchApi<AtlassianSyncStats>('/api/v1/atlassian/sync-status');
  },

  async getAtlassianConfluencePages(params?: { page?: number; limit?: number; search?: string; spaceKey?: string }): Promise<ApiResult<PaginatedResponse<AtlassianConfluencePageSummary>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.spaceKey) searchParams.set('spaceKey', params.spaceKey);
    return fetchApi<PaginatedResponse<AtlassianConfluencePageSummary>>(`/api/v1/atlassian/confluence/pages?${searchParams}`);
  },

  async getAtlassianConfluencePage(id: string): Promise<ApiResult<AtlassianConfluencePageDetail>> {
    return fetchApi<AtlassianConfluencePageDetail>(`/api/v1/atlassian/confluence/pages/${id}`);
  },

  async getAtlassianJiraIssues(params?: { page?: number; limit?: number; search?: string; project?: string; status?: string; issueType?: string; priority?: string }): Promise<ApiResult<PaginatedResponse<AtlassianJiraIssueSummary>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.project) searchParams.set('project', params.project);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.issueType) searchParams.set('issueType', params.issueType);
    if (params?.priority) searchParams.set('priority', params.priority);
    return fetchApi<PaginatedResponse<AtlassianJiraIssueSummary>>(`/api/v1/atlassian/jira/issues?${searchParams}`);
  },

  async getAtlassianJiraIssue(key: string): Promise<ApiResult<AtlassianJiraIssueDetail>> {
    return fetchApi<AtlassianJiraIssueDetail>(`/api/v1/atlassian/jira/issues/${encodeURIComponent(key)}`);
  },

  // Slack (Admin)
  async getSlackConfig(): Promise<ApiResult<SlackConfigResponse>> {
    return fetchApi<SlackConfigResponse>('/api/v1/admin/slack/config');
  },

  async updateSlackConfig(data: { credentialSetId?: string | null; syncHistoryDays?: number; hubBaseUrl?: string | null; slackSystemPromptAddition?: string | null }): Promise<ApiResult<SlackConfigResponse>> {
    return fetchApi<SlackConfigResponse>('/api/v1/admin/slack/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testSlackConnection(): Promise<ApiResult<SlackConnectionTestResult>> {
    return fetchApi<SlackConnectionTestResult>('/api/v1/admin/slack/test-connection', {
      method: 'POST',
    });
  },

  async fetchSlackChannels(): Promise<ApiResult<{ channelCount: number }>> {
    return fetchApi<{ channelCount: number }>('/api/v1/admin/slack/channels/fetch', {
      method: 'POST',
    });
  },

  async updateSlackChannelSyncEnabled(channelId: string, syncEnabled: boolean): Promise<ApiResult<SlackChannelSummary>> {
    return fetchApi<SlackChannelSummary>(`/api/v1/admin/slack/channels/${channelId}/sync-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ syncEnabled }),
    });
  },

  async syncSlackChannel(channelId: string): Promise<ApiResult<SlackSyncResult>> {
    return fetchApi<SlackSyncResult>(`/api/v1/admin/slack/channels/${channelId}/sync`, { method: 'POST' });
  },

  async enableAllSlackChannels(): Promise<ApiResult<{ count: number }>> {
    return fetchApi<{ count: number }>('/api/v1/admin/slack/channels/enable-all', {
      method: 'POST',
    });
  },

  async triggerSlackSync(): Promise<ApiResult<SlackSyncResult>> {
    return fetchApi<SlackSyncResult>('/api/v1/admin/slack/sync', {
      method: 'POST',
    });
  },

  async triggerSlackIncrementalSync(): Promise<ApiResult<SlackSyncResult>> {
    return fetchApi<SlackSyncResult>('/api/v1/admin/slack/sync/incremental', {
      method: 'POST',
    });
  },

  async getSlackSyncStatus(): Promise<ApiResult<{ syncStatus: string; syncChannelsTotal: number | null; syncChannelsProcessed: number | null; syncMessagesProcessed: number | null; lastSyncedAt: string | null; lastSyncError: string | null }>> {
    return fetchApi('/api/v1/admin/slack/sync-status');
  },

  // Slack (User-facing)
  async getSlackChannels(params?: { page?: number; limit?: number; search?: string; includeArchived?: boolean }): Promise<ApiResult<{ channels: SlackChannelSummary[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.includeArchived) searchParams.set('includeArchived', 'true');
    return fetchApi<{ channels: SlackChannelSummary[]; total: number }>(`/api/v1/slack/channels?${searchParams}`);
  },

  async getSlackChannelsAdmin(params?: { page?: number; limit?: number; search?: string; includeArchived?: boolean }): Promise<ApiResult<{ channels: SlackChannelSummary[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.includeArchived) searchParams.set('includeArchived', 'true');
    return fetchApi<{ channels: SlackChannelSummary[]; total: number }>(`/api/v1/admin/slack/channels?${searchParams}`);
  },

  async getSlackChannel(id: string): Promise<ApiResult<SlackChannelDetail>> {
    return fetchApi<SlackChannelDetail>(`/api/v1/slack/channels/${id}`);
  },

  async getSlackChannelMessages(channelId: string, params?: { page?: number; limit?: number; search?: string; startDate?: string; endDate?: string; sortOrder?: string; includeThreadReplies?: boolean }): Promise<ApiResult<{ messages: SlackMessageItem[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    // Sent whenever given, false included — the server defaults it to true.
    if (params?.includeThreadReplies !== undefined) {
      searchParams.set('includeThreadReplies', String(params.includeThreadReplies));
    }
    return fetchApi<{ messages: SlackMessageItem[]; total: number }>(`/api/v1/slack/channels/${channelId}/messages?${searchParams}`);
  },

  async getSlackThread(channelId: string, threadTs: string): Promise<ApiResult<{ messages: SlackMessageItem[] }>> {
    return fetchApi<{ messages: SlackMessageItem[] }>(`/api/v1/slack/channels/${channelId}/threads/${encodeURIComponent(threadTs)}`);
  },

  async searchSlackMessages(params: { query: string; channelName?: string; userName?: string; daysBack?: number; limit?: number; page?: number }): Promise<ApiResult<{ results: SlackSearchResult[]; total: number }>> {
    const searchParams = new URLSearchParams();
    searchParams.set('query', params.query);
    if (params.channelName) searchParams.set('channelName', params.channelName);
    if (params.userName) searchParams.set('userName', params.userName);
    if (params.daysBack) searchParams.set('daysBack', String(params.daysBack));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.page) searchParams.set('page', String(params.page));
    return fetchApi<{ results: SlackSearchResult[]; total: number }>(`/api/v1/slack/messages/search?${searchParams}`);
  },

  async searchSlackMessagesLive(params: { query: string; channelName?: string; userName?: string; daysBack?: number; limit?: number; page?: number }): Promise<ApiResult<SlackSearchPage>> {
    const searchParams = new URLSearchParams();
    searchParams.set('query', params.query);
    if (params.channelName) searchParams.set('channelName', params.channelName);
    if (params.userName) searchParams.set('userName', params.userName);
    if (params.daysBack) searchParams.set('daysBack', String(params.daysBack));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.page) searchParams.set('page', String(params.page));
    return fetchApi<SlackSearchPage>(`/api/v1/slack/messages/search/live?${searchParams}`);
  },

  async getSlackMessageContext(params: { channelId: string; ts: string; windowSeconds?: number }): Promise<ApiResult<SlackMessageContext>> {
    const searchParams = new URLSearchParams();
    searchParams.set('channelId', params.channelId);
    searchParams.set('ts', params.ts);
    if (params.windowSeconds) searchParams.set('windowSeconds', String(params.windowSeconds));
    return fetchApi<SlackMessageContext>(`/api/v1/slack/messages/context?${searchParams}`);
  },

  async getSlackUsers(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResult<{ users: SlackUserSummary[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    return fetchApi<{ users: SlackUserSummary[]; total: number }>(`/api/v1/slack/users?${searchParams}`);
  },

  async getSlackStats(): Promise<ApiResult<SlackSyncStats>> {
    return fetchApi<SlackSyncStats>('/api/v1/slack/stats');
  },

  // Callouts (user-facing)
  async getActiveCallouts(page: string): Promise<ApiResult<ActiveCallout[]>> {
    return fetchApi<ActiveCallout[]>(`/api/v1/callouts?page=${encodeURIComponent(page)}`);
  },

  async dismissCallout(id: string): Promise<ApiResult<{ dismissed: boolean }>> {
    return fetchApi<{ dismissed: boolean }>(`/api/v1/callouts/${id}/dismiss`, {
      method: 'POST',
    });
  },

  // Callouts (admin)
  async getCalloutPages(): Promise<ApiResult<CalloutPageOption[]>> {
    return fetchApi<CalloutPageOption[]>('/api/v1/admin/callouts/pages');
  },

  async getCalloutsAdmin(): Promise<ApiResult<CalloutSummary[]>> {
    return fetchApi<CalloutSummary[]>('/api/v1/admin/callouts');
  },

  async getCalloutAdmin(id: string): Promise<ApiResult<CalloutSummary>> {
    return fetchApi<CalloutSummary>(`/api/v1/admin/callouts/${id}`);
  },

  async createCallout(input: CreateCalloutInput): Promise<ApiResult<CalloutSummary>> {
    return fetchApi<CalloutSummary>('/api/v1/admin/callouts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateCallout(id: string, input: UpdateCalloutInput): Promise<ApiResult<CalloutSummary>> {
    return fetchApi<CalloutSummary>(`/api/v1/admin/callouts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async deleteCallout(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/callouts/${id}`, {
      method: 'DELETE',
    });
  },

  // Assistants (admin)
  async getAssistants(): Promise<ApiResult<AssistantSummary[]>> {
    return fetchApi<AssistantSummary[]>('/api/v1/admin/assistants');
  },

  async getAssistant(id: string): Promise<ApiResult<AssistantDetail>> {
    return fetchApi<AssistantDetail>(`/api/v1/admin/assistants/${id}`);
  },

  async createAssistant(input: AssistantCreateInput): Promise<ApiResult<AssistantDetail>> {
    return fetchApi<AssistantDetail>('/api/v1/admin/assistants', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateAssistant(id: string, input: Partial<AssistantCreateInput>): Promise<ApiResult<AssistantDetail>> {
    return fetchApi<AssistantDetail>(`/api/v1/admin/assistants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async deleteAssistant(id: string): Promise<ApiResult<{ deactivated: boolean }>> {
    return fetchApi<{ deactivated: boolean }>(`/api/v1/admin/assistants/${id}`, {
      method: 'DELETE',
    });
  },

  async rotateAssistantKey(id: string): Promise<ApiResult<AssistantDetail>> {
    return fetchApi<AssistantDetail>(`/api/v1/admin/assistants/${id}/rotate-key`, {
      method: 'POST',
    });
  },

  async getAssistantAnalytics(id: string, params?: { from?: string; to?: string }): Promise<ApiResult<AssistantAnalytics>> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    return fetchApi<AssistantAnalytics>(`/api/v1/admin/assistants/${id}/analytics?${searchParams}`);
  },

  async getAssistantThreads(id: string, params?: { page?: number; limit?: number }): Promise<ApiResult<{ threads: AssistantThreadSummary[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    return fetchApi<{ threads: AssistantThreadSummary[]; total: number }>(`/api/v1/admin/assistants/${id}/threads?${searchParams}`);
  },

  async getAssistantThread(id: string, threadId: string): Promise<ApiResult<AssistantThreadDetail>> {
    return fetchApi<AssistantThreadDetail>(`/api/v1/admin/assistants/${id}/threads/${threadId}`);
  },

  async getAssistantFeedback(id: string, params?: { page?: number; limit?: number; search?: string; isResolved?: string; isSpam?: string; source?: string }): Promise<ApiResult<{ feedback: AssistantFeedbackSummary[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.isResolved) searchParams.set('isResolved', params.isResolved);
    if (params?.isSpam) searchParams.set('isSpam', params.isSpam);
    if (params?.source) searchParams.set('source', params.source);
    return fetchApi<{ feedback: AssistantFeedbackSummary[]; total: number }>(`/api/v1/admin/assistants/${id}/feedback?${searchParams}`);
  },

  async getAssistantFeedbackStats(id: string): Promise<ApiResult<AssistantFeedbackStats>> {
    return fetchApi<AssistantFeedbackStats>(`/api/v1/admin/assistants/${id}/feedback/stats`);
  },

  async updateAssistantFeedback(id: string, feedbackId: string, updates: { isResolved?: boolean; resolvedNote?: string; isSpam?: boolean }): Promise<ApiResult<AssistantFeedbackSummary>> {
    return fetchApi<AssistantFeedbackSummary>(`/api/v1/admin/assistants/${id}/feedback/${feedbackId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Google Drive
  async getGoogleDriveConfig(): Promise<ApiResult<GoogleDriveConfig>> {
    return fetchApi<GoogleDriveConfig>('/api/v1/google-drive/config');
  },

  async updateGoogleDriveConfig(credentialSetId: string | null): Promise<ApiResult<GoogleDriveConfig>> {
    return fetchApi<GoogleDriveConfig>('/api/v1/google-drive/config', {
      method: 'PUT',
      body: JSON.stringify({ credentialSetId }),
    });
  },

  async getGoogleDrivePickerToken(): Promise<ApiResult<{ accessToken: string; googleEmail: string; pendingToken: string }>> {
    return fetchApi<{ accessToken: string; googleEmail: string; pendingToken: string }>('/api/v1/google-drive/picker-token');
  },

  async listGoogleDriveFolders(page = 1, pageSize = 20): Promise<ApiResult<{ folders: GoogleDriveFolder[]; total: number }>> {
    return fetchApi<{ folders: GoogleDriveFolder[]; total: number }>(`/api/v1/google-drive/folders?page=${page}&pageSize=${pageSize}`);
  },

  async saveGoogleDriveFolder(data: { folderId: string; folderName: string; folderUrl: string; pendingToken: string }): Promise<ApiResult<GoogleDriveFolder>> {
    return fetchApi<GoogleDriveFolder>('/api/v1/google-drive/folders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getGoogleDriveFolder(id: string): Promise<ApiResult<GoogleDriveFolder>> {
    return fetchApi<GoogleDriveFolder>(`/api/v1/google-drive/folders/${id}`);
  },

  async deleteGoogleDriveFolder(id: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/google-drive/folders/${id}`, { method: 'DELETE' });
  },

  // DEV-ONLY: Google Drive folder import/export — remove with import/export feature
  async exportGoogleDriveDevFolders(): Promise<ApiResult<{ folders: GoogleDriveDevFolderExport[] }>> {
    return fetchApi<{ folders: GoogleDriveDevFolderExport[] }>('/api/v1/google-drive/dev/export-folders');
  },

  async importGoogleDriveDevFolders(folders: GoogleDriveDevFolderExport[]): Promise<ApiResult<{ imported: number; skipped: number }>> {
    return fetchApi<{ imported: number; skipped: number }>('/api/v1/google-drive/dev/import-folders', {
      method: 'POST',
      body: JSON.stringify({ folders }),
    });
  },
  // END DEV-ONLY

  async listGoogleDriveFiles(folderId: string, pageToken?: string, pageSize?: number): Promise<ApiResult<GoogleDriveFileList>> {
    const params = new URLSearchParams();
    if (pageToken) params.set('pageToken', pageToken);
    if (pageSize) params.set('pageSize', String(pageSize));
    return fetchApi<GoogleDriveFileList>(`/api/v1/google-drive/folders/${folderId}/files?${params}`);
  },

  async searchGoogleDriveFiles(folderId: string, q: string, type: 'name' | 'fullText', pageToken?: string): Promise<ApiResult<GoogleDriveFileList>> {
    const params = new URLSearchParams({ q, type });
    if (pageToken) params.set('pageToken', pageToken);
    return fetchApi<GoogleDriveFileList>(`/api/v1/google-drive/folders/${folderId}/search?${params}`);
  },

  async getGoogleDriveFileContent(folderId: string, fileId: string): Promise<ApiResult<GoogleDriveFileContent>> {
    return fetchApi<GoogleDriveFileContent>(`/api/v1/google-drive/folders/${folderId}/files/${fileId}/content`);
  },

  // Cloudflare
  async getCloudflareConfig(): Promise<ApiResult<CloudflareConfigResponse>> {
    return fetchApi<CloudflareConfigResponse>('/api/v1/admin/cloudflare/config');
  },

  async updateCloudflareConfig(data: { credentialSetId?: string | null }): Promise<ApiResult<CloudflareConfigResponse>> {
    return fetchApi<CloudflareConfigResponse>('/api/v1/admin/cloudflare/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testCloudflareSearch(query: string): Promise<ApiResult<unknown>> {
    return fetchApi<unknown>('/api/v1/admin/cloudflare/test-search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  },
  async listCloudflareMcpTools(): Promise<ApiResult<unknown>> {
    return fetchApi<unknown>('/api/v1/admin/cloudflare/mcp-tools');
  },
  async testCloudflareExecute(code: string): Promise<ApiResult<unknown>> {
    return fetchApi<unknown>('/api/v1/admin/cloudflare/test-execute', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  // AI support triage / deflection
  async getSupportDeflectionConfig(): Promise<ApiResult<SupportDeflectionConfigResponse>> {
    return fetchApi<SupportDeflectionConfigResponse>('/api/v1/admin/support-deflection/config');
  },

  async updateSupportDeflectionConfig(
    data: SupportDeflectionConfigUpdate,
  ): Promise<ApiResult<SupportDeflectionConfigResponse>> {
    return fetchApi<SupportDeflectionConfigResponse>('/api/v1/admin/support-deflection/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // ---- AI deflection traces (managed role: support-deflection-trace) ----

  async listSupportDeflectionTraces(
    params?: Partial<Record<string, string | number>>,
  ): Promise<ApiResult<SupportDeflectionTraceList>> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    }
    const qs = query.toString();
    return fetchApi<SupportDeflectionTraceList>(
      `/api/v1/admin/support-deflection/traces${qs ? `?${qs}` : ''}`,
    );
  },

  async getSupportDeflectionTrace(turnId: string): Promise<ApiResult<SupportDeflectionTraceDetail>> {
    return fetchApi<SupportDeflectionTraceDetail>(`/api/v1/admin/support-deflection/traces/${turnId}`);
  },

  async getSupportDeflectionTraceTimeline(
    turnId: string,
  ): Promise<ApiResult<SupportDeflectionStageSpan[]>> {
    return fetchApi<SupportDeflectionStageSpan[]>(
      `/api/v1/admin/support-deflection/traces/${turnId}/timeline`,
    );
  },

  /** One span's full payload — fetched lazily, since a verifier prompt runs to ~70k. */
  async getSupportDeflectionTraceSpan(
    turnId: string,
    spanId: string,
  ): Promise<ApiResult<SupportDeflectionStageSpanDetail>> {
    return fetchApi<SupportDeflectionStageSpanDetail>(
      `/api/v1/admin/support-deflection/traces/${turnId}/spans/${spanId}`,
    );
  },

  /**
   * A whole run as one JSON file: the ticket, its timeline, every effect, every AI
   * turn WITH its full stage payloads, and any lab verdicts.
   *
   * Raw Response — the endpoint sends a JSON document to download, not the apiSuccess
   * envelope. Same shape as downloadChatDebugTrace.
   */
  async exportSupportDeflectionTicketTrace(ticketId: string): Promise<Response> {
    return fetch(
      `/api/v1/admin/support-deflection/tickets/${encodeURIComponent(ticketId)}/trace-export`,
      { credentials: 'include' },
    );
  },

  // ---- Grafana alert triage --------------------------------------------------

  // Google Meet
  async listGoogleMeetings(
    params: GoogleMeetListParams = {},
  ): Promise<ApiResult<PaginatedResponse<GoogleMeetingSummary>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.origin) query.set('origin', params.origin);
    if (params.search) query.set('search', params.search);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return fetchApi<PaginatedResponse<GoogleMeetingSummary>>(`/api/v1/google-meets${suffix}`);
  },

  async getGoogleMeeting(id: string): Promise<ApiResult<GoogleMeetingDetail>> {
    return fetchApi<GoogleMeetingDetail>(`/api/v1/google-meets/${id}`);
  },

  async getGoogleMeetingTranscript(id: string): Promise<ApiResult<GoogleMeetingTranscriptView>> {
    return fetchApi<GoogleMeetingTranscriptView>(`/api/v1/google-meets/${id}/transcript`);
  },

  async startGoogleMeeting(request: GoogleMeetStartRequest): Promise<ApiResult<GoogleMeetStartResult>> {
    return fetchApi<GoogleMeetStartResult>('/api/v1/google-meets', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async endGoogleMeeting(id: string): Promise<ApiResult<{ ended: boolean }>> {
    return fetchApi<{ ended: boolean }>(`/api/v1/google-meets/${id}/end`, { method: 'POST' });
  },

  async recollectGoogleMeetConference(meetingId: string, conferenceId: string): Promise<ApiResult<GoogleMeetRecollectResult>> {
    return fetchApi<GoogleMeetRecollectResult>(`/api/v1/google-meets/${meetingId}/conferences/${conferenceId}/recollect`, {
      method: 'POST',
    });
  },

  async getGoogleMeetConfig(): Promise<ApiResult<GoogleMeetConfig>> {
    return fetchApi<GoogleMeetConfig>('/api/v1/admin/google-meets/config');
  },

  async updateGoogleMeetConfig(config: GoogleMeetConfigUpdate): Promise<ApiResult<GoogleMeetConfig>> {
    return fetchApi<GoogleMeetConfig>('/api/v1/admin/google-meets/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  // Incidents
  async listIncidents(
    params: IncidentListParams = {},
  ): Promise<ApiResult<PaginatedResponse<IncidentSummary>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.stage) query.set('stage', params.stage);
    if (params.severity) query.set('severity', params.severity);
    if (params.accountId) query.set('accountId', params.accountId);
    if (params.search) query.set('search', params.search);
    if (params.includeDismissed) query.set('includeDismissed', 'true');
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return fetchApi<PaginatedResponse<IncidentSummary>>(`/api/v1/incidents${suffix}`);
  },

  async getIncidentMeta(): Promise<ApiResult<IncidentMeta>> {
    return fetchApi<IncidentMeta>('/api/v1/incidents/meta');
  },

  async createIncident(request: IncidentCreateRequest): Promise<ApiResult<IncidentCreateResult>> {
    return fetchApi<IncidentCreateResult>('/api/v1/incidents', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async getIncident(id: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}`);
  },

  async getIncidentAutomation(id: string): Promise<ApiResult<IncidentAutomation>> {
    return fetchApi<IncidentAutomation>(`/api/v1/incidents/${id}/automation`);
  },

  async getIncidentLiveAttendance(id: string): Promise<ApiResult<IncidentLiveAttendance>> {
    return fetchApi<IncidentLiveAttendance>(`/api/v1/incidents/${id}/attendance`);
  },

  // The incident's own bridges. Scoped through the incident rather than read from
  // /api/v1/google-meets, which the google-meets managed role guards.
  async getIncidentMeetings(id: string): Promise<ApiResult<GoogleMeetingDetail[]>> {
    return fetchApi<GoogleMeetingDetail[]>(`/api/v1/incidents/${id}/meetings`);
  },

  async getIncidentMeetingTranscript(
    id: string,
    meetingId: string,
  ): Promise<ApiResult<GoogleMeetingTranscriptView>> {
    return fetchApi<GoogleMeetingTranscriptView>(
      `/api/v1/incidents/${id}/meetings/${meetingId}/transcript`,
    );
  },

  async patchIncident(id: string, patch: IncidentPatchRequest): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async engageIncident(id: string): Promise<ApiResult<{ stage: IncidentStage | null }>> {
    return fetchApi<{ stage: IncidentStage | null }>(`/api/v1/incidents/${id}/engage`, { method: 'POST' });
  },

  // `note` is what actually fixed it. Optional, and omitted entirely when blank so
  // a bare resolve stays a bare POST.
  async resolveIncident(id: string, note?: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
    });
  },

  /** The escape hatch: back to active from resolved or follow-up. */
  async reopenIncident(id: string, reason?: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
    });
  },

  async beginIncidentFollowup(id: string): Promise<ApiResult<{ stage: IncidentStage | null }>> {
    return fetchApi<{ stage: IncidentStage | null }>(`/api/v1/incidents/${id}/followup`, { method: 'POST' });
  },

  /**
   * Ask the war-room thread whether a quiet incident can be closed.
   *
   * Returns the whole incident because the eligibility state moves as a result —
   * the caller applies it rather than waiting for the next poll to notice.
   */
  async nudgeIncident(id: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/nudge`, { method: 'POST' });
  },

  async closeIncident(id: string): Promise<ApiResult<{ stage: IncidentStage | null }>> {
    return fetchApi<{ stage: IncidentStage | null }>(`/api/v1/incidents/${id}/close`, { method: 'POST' });
  },

  async dismissIncident(
    id: string,
    kind: IncidentDismissKind,
    reason?: string,
  ): Promise<ApiResult<{ stage: IncidentStage | null }>> {
    return fetchApi<{ stage: IncidentStage | null }>(`/api/v1/incidents/${id}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ kind, reason }),
    });
  },

  async setIncidentSeverity(
    id: string,
    severity: IncidentSeverity,
  ): Promise<ApiResult<{ changed: boolean }>> {
    return fetchApi<{ changed: boolean }>(`/api/v1/incidents/${id}/severity`, {
      method: 'POST',
      body: JSON.stringify({ severity }),
    });
  },

  async resolveIncidentAccountSuggestion(
    id: string,
    action: 'confirm' | 'reject',
  ): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/account-suggestion`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  },

  // The label is optional: mid-call nobody types one, and the server derives a
  // readable name from the URL when it is absent.
  async postIncidentUpdate(id: string, body: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/updates`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  },

  async editIncidentUpdate(
    id: string,
    updateId: string,
    body: string,
  ): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/updates/${updateId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    });
  },

  async deleteIncidentUpdate(id: string, updateId: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/updates/${updateId}`, {
      method: 'DELETE',
    });
  },

  async addIncidentLink(id: string, url: string, label?: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/links`, {
      method: 'POST',
      body: JSON.stringify(label?.trim() ? { url, label: label.trim() } : { url }),
    });
  },

  async removeIncidentLink(id: string, linkId: string): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/links/${linkId}`, { method: 'DELETE' });
  },

  async linkIncidentJira(
    id: string,
    request: IncidentJiraLinkRequest,
  ): Promise<ApiResult<IncidentDetail>> {
    return fetchApi<IncidentDetail>(`/api/v1/incidents/${id}/jira`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async regenerateIncidentRca(id: string): Promise<ApiResult<IncidentRcaRequestResult>> {
    return fetchApi<IncidentRcaRequestResult>(`/api/v1/incidents/${id}/rca/regenerate`, { method: 'POST' });
  },

  async draftIncidentRcaNow(id: string): Promise<ApiResult<IncidentRcaRun>> {
    return fetchApi<IncidentRcaRun>(`/api/v1/incidents/${id}/rca/draft-now`, { method: 'POST' });
  },

  async getIncidentConfig(): Promise<ApiResult<IncidentConfig>> {
    return fetchApi<IncidentConfig>('/api/v1/admin/incidents/config');
  },

  async updateIncidentConfig(config: IncidentConfigUpdate): Promise<ApiResult<IncidentConfig>> {
    return fetchApi<IncidentConfig>('/api/v1/admin/incidents/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  // Security Watch
  async getSecurityWatchLatestDay(): Promise<ApiResult<{ day: string | null }>> {
    return fetchApi<{ day: string | null }>('/api/v1/security-watch/latest-day');
  },

  async getSecurityWatchDay(day: string): Promise<ApiResult<SecurityWatchDayView>> {
    return fetchApi<SecurityWatchDayView>(`/api/v1/security-watch/days/${encodeURIComponent(day)}`);
  },

  async getSecurityWatchFindings(query: { severity?: string; lane?: string; status?: string; page?: number; limit?: number }): Promise<ApiResult<SecurityWatchFindingListView>> {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') q.set(k, String(v));
    return fetchApi<SecurityWatchFindingListView>(`/api/v1/security-watch/findings?${q.toString()}`);
  },

  async getSecurityWatchFinding(id: string): Promise<ApiResult<SecurityWatchFindingDetailView>> {
    return fetchApi<SecurityWatchFindingDetailView>(`/api/v1/security-watch/findings/${encodeURIComponent(id)}`);
  },

  async updateSecurityWatchFindingStatus(id: string, status: SecurityWatchFindingStatus, note: string | null): Promise<ApiResult<SecurityWatchFindingDetailView>> {
    return fetchApi<SecurityWatchFindingDetailView>(`/api/v1/security-watch/findings/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status, note }) });
  },

  async getSecurityWatchEvidence(candidateId: string): Promise<ApiResult<SecurityWatchEvidenceView>> {
    return fetchApi<SecurityWatchEvidenceView>(`/api/v1/security-watch/evidence/${encodeURIComponent(candidateId)}`);
  },

  async getSecurityWatchAnswer(id: string): Promise<ApiResult<SecurityWatchAnswerView>> {
    return fetchApi<SecurityWatchAnswerView>(`/api/v1/security-watch/answers/${encodeURIComponent(id)}`);
  },

  async getSecurityWatchRun(id: string): Promise<ApiResult<SecurityWatchRunView>> {
    return fetchApi<SecurityWatchRunView>(`/api/v1/security-watch/runs/${encodeURIComponent(id)}`);
  },

  async startSecurityWatchRun(input: { windowStart?: string; force?: boolean }): Promise<ApiResult<{ workflowId: string }>> {
    return fetchApi<{ workflowId: string }>('/api/v1/security-watch/runs', { method: 'POST', body: JSON.stringify(input) });
  },

  async getSecurityWatchKnownActors(): Promise<ApiResult<SecurityWatchKnownActorView[]>> {
    return fetchApi<SecurityWatchKnownActorView[]>('/api/v1/security-watch/known-actors');
  },

  async createSecurityWatchKnownActor(input: SecurityWatchKnownActorCreate): Promise<ApiResult<SecurityWatchKnownActorView>> {
    return fetchApi<SecurityWatchKnownActorView>('/api/v1/security-watch/known-actors', { method: 'POST', body: JSON.stringify(input) });
  },

  async updateSecurityWatchKnownActor(id: string, input: SecurityWatchKnownActorUpdate): Promise<ApiResult<SecurityWatchKnownActorView>> {
    return fetchApi<SecurityWatchKnownActorView>(`/api/v1/security-watch/known-actors/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
  },

  async getSecurityWatchSuppressions(): Promise<ApiResult<SecurityWatchSuppressionView[]>> {
    return fetchApi<SecurityWatchSuppressionView[]>('/api/v1/security-watch/suppressions');
  },

  async proposeSecurityWatchSuppression(input: SecurityWatchSuppressionProposal): Promise<ApiResult<{ id: string }>> {
    return fetchApi<{ id: string }>('/api/v1/security-watch/suppressions', { method: 'POST', body: JSON.stringify(input) });
  },

  async approveSecurityWatchSuppression(id: string): Promise<ApiResult<SecurityWatchSuppressionView[]>> {
    return fetchApi<SecurityWatchSuppressionView[]>(`/api/v1/security-watch/suppressions/${encodeURIComponent(id)}/approve`, { method: 'POST' });
  },

  async revokeSecurityWatchSuppression(id: string): Promise<ApiResult<SecurityWatchSuppressionView[]>> {
    return fetchApi<SecurityWatchSuppressionView[]>(`/api/v1/security-watch/suppressions/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  },

  async getSecurityWatchConfig(): Promise<ApiResult<SecurityWatchConfigView>> {
    return fetchApi<SecurityWatchConfigView>('/api/v1/security-watch/config');
  },

  async updateSecurityWatchConfig(input: SecurityWatchConfigUpdate): Promise<ApiResult<SecurityWatchConfigView>> {
    return fetchApi<SecurityWatchConfigView>('/api/v1/security-watch/config', { method: 'PUT', body: JSON.stringify(input) });
  },

  async activateSecurityWatchConfigVersion(versionId: string): Promise<ApiResult<SecurityWatchConfigView>> {
    return fetchApi<SecurityWatchConfigView>(`/api/v1/security-watch/config/versions/${encodeURIComponent(versionId)}/activate`, { method: 'POST' });
  },

  async getSecurityWatchRecipientCandidates(): Promise<ApiResult<SecurityWatchRoleHolder[]>> {
    return fetchApi<SecurityWatchRoleHolder[]>('/api/v1/security-watch/recipient-candidates');
  },

  async getGrafanaTriageConfig(): Promise<ApiResult<GrafanaTriageConfigResponse>> {
    return fetchApi<GrafanaTriageConfigResponse>('/api/v1/admin/grafana-triage/config');
  },

  async updateGrafanaTriageConfig(
    config: GrafanaTriageConfigUpdate,
    note?: string | null,
  ): Promise<ApiResult<GrafanaTriageConfigResponse>> {
    return fetchApi<GrafanaTriageConfigResponse>('/api/v1/admin/grafana-triage/config', {
      method: 'PUT',
      body: JSON.stringify({ config, note: note ?? null }),
    });
  },

  async listGrafanaTriageConfigVersions(): Promise<ApiResult<GrafanaTriageConfigVersionRow[]>> {
    return fetchApi<GrafanaTriageConfigVersionRow[]>('/api/v1/admin/grafana-triage/config/versions');
  },

  async activateGrafanaTriageConfigVersion(versionId: string): Promise<ApiResult<GrafanaTriageConfigResponse>> {
    return fetchApi<GrafanaTriageConfigResponse>('/api/v1/admin/grafana-triage/config/activate', {
      method: 'POST',
      body: JSON.stringify({ versionId }),
    });
  },

  async listGrafanaWebhookLogs(page = 1, limit = 25): Promise<ApiResult<GrafanaWebhookLogList>> {
    return fetchApi<GrafanaWebhookLogList>(`/api/v1/admin/grafana-triage/webhooks?page=${page}&limit=${limit}`);
  },

  async getGrafanaWebhookLog(logId: string): Promise<ApiResult<GrafanaWebhookLogDetail>> {
    return fetchApi<GrafanaWebhookLogDetail>(`/api/v1/admin/grafana-triage/webhooks/${logId}`);
  },

  async listGrafanaTriageGroups(
    params?: Partial<Record<string, string | number>>,
  ): Promise<ApiResult<GrafanaAlertGroupList>> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    }
    const qs = query.toString();
    return fetchApi<GrafanaAlertGroupList>(`/api/v1/grafana-triage/groups${qs ? `?${qs}` : ''}`);
  },

  async getGrafanaTriageGroup(groupId: string): Promise<ApiResult<GrafanaAlertGroupDetail>> {
    return fetchApi<GrafanaAlertGroupDetail>(`/api/v1/grafana-triage/groups/${groupId}`);
  },

  async getGrafanaTriageGroupTimeline(groupId: string): Promise<ApiResult<GrafanaTriageStageSpan[]>> {
    return fetchApi<GrafanaTriageStageSpan[]>(`/api/v1/grafana-triage/groups/${groupId}/timeline`);
  },

  async getGrafanaTriageGroupSpan(
    groupId: string,
    spanId: string,
  ): Promise<ApiResult<GrafanaTriageStageSpanDetail>> {
    return fetchApi<GrafanaTriageStageSpanDetail>(`/api/v1/grafana-triage/groups/${groupId}/spans/${spanId}`);
  },

  async listSupportDeflectionConfigVersions(): Promise<ApiResult<SupportDeflectionConfigVersionRow[]>> {
    return fetchApi<SupportDeflectionConfigVersionRow[]>('/api/v1/admin/support-deflection/config/versions');
  },

  async activateSupportDeflectionConfigVersion(
    versionId: string,
  ): Promise<ApiResult<SupportDeflectionConfigResponse>> {
    return fetchApi<SupportDeflectionConfigResponse>('/api/v1/admin/support-deflection/config/activate', {
      method: 'POST',
      body: JSON.stringify({ versionId }),
    });
  },

  // Raw Response: these endpoints send a JSON file to download, not the apiSuccess
  // envelope. Same shape as downloadChatDebugTrace / the pricing export.
  async exportSupportDeflectionConfig(): Promise<Response> {
    return fetch('/api/v1/admin/support-deflection/config/export', { credentials: 'include' });
  },

  async downloadSupportDeflectionConfigTemplate(): Promise<Response> {
    return fetch('/api/v1/admin/support-deflection/config/template', { credentials: 'include' });
  },

  async importSupportDeflectionConfig(doc: unknown): Promise<ApiResult<{ imported: true; version: number }>> {
    return fetchApi<{ imported: true; version: number }>('/api/v1/admin/support-deflection/config/import', {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  },

  /** `isTest` probes the Support Lab board for the target instead of the real one. */
  /** Read-only check of every Jira routing target. Creates nothing. */
  async preflightSupportDeflectionJira(): Promise<ApiResult<SupportDeflectionPreflightResponse>> {
    return fetchApi<SupportDeflectionPreflightResponse>('/api/v1/admin/support-deflection/preflight');
  },

  async testSupportDeflectionJira(
    target: JiraTarget,
    isTest = false,
  ): Promise<ApiResult<SupportDeflectionJiraTestResponse>> {
    return fetchApi<SupportDeflectionJiraTestResponse>('/api/v1/admin/support-deflection/jira-test', {
      method: 'POST',
      body: JSON.stringify({ target, isTest }),
    });
  },

  async getSupportDeflectionTurns(ticketId: string): Promise<ApiResult<SupportDeflectionTurn[]>> {
    return fetchApi<SupportDeflectionTurn[]>(
      `/api/v1/admin/support-deflection/tickets/${encodeURIComponent(ticketId)}/turns`,
    );
  },

  async resetSupportDeflection(ticketId: string): Promise<ApiResult<{ ticketId: string; aiDeflectionStatus: string }>> {
    return fetchApi<{ ticketId: string; aiDeflectionStatus: string }>(
      `/api/v1/admin/support-deflection/tickets/${encodeURIComponent(ticketId)}/reset`,
      { method: 'POST' },
    );
  },

  /**
   * File the Jira issue for a case the AI routed but could not file. Refiles from
   * the decision the turn already made — no new model call.
   */
  async fileSupportDeflectionJira(
    ticketId: string,
  ): Promise<ApiResult<{ ticketId: string; jiraKey: string | null; jiraUrl: string | null }>> {
    return fetchApi<{ ticketId: string; jiraKey: string | null; jiraUrl: string | null }>(
      `/api/v1/admin/support-deflection/tickets/${encodeURIComponent(ticketId)}/file-jira`,
      { method: 'POST' },
    );
  },

  // OpsGenie
  async getOpsGenieConfig(): Promise<ApiResult<OpsGenieConfigResponse>> {
    return fetchApi<OpsGenieConfigResponse>('/api/v1/admin/opsgenie/config');
  },

  async updateOpsGenieConfig(data: {
    credentialSetId?: string | null;
    defaultTeam?: string | null;
    enabled?: boolean;
  }): Promise<ApiResult<OpsGenieConfigResponse>> {
    return fetchApi<OpsGenieConfigResponse>('/api/v1/admin/opsgenie/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testOpsGenie(): Promise<ApiResult<OpsGenieTestResponse>> {
    return fetchApi<OpsGenieTestResponse>('/api/v1/admin/opsgenie/test', { method: 'POST' });
  },

  // Outbound mail (Mail Outbox)
  async getSupportNotificationConfig(): Promise<ApiResult<SupportNotificationConfig>> {
    return fetchApi<SupportNotificationConfig>('/api/v1/admin/support-notifications/config');
  },

  async updateSupportNotificationConfig(data: {
    escalationEmails: string[];
    psEscalationEmails: string[];
    slackChannelName?: string | null;
  }): Promise<ApiResult<SupportNotificationConfig>> {
    return fetchApi<SupportNotificationConfig>('/api/v1/admin/support-notifications/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getOutboundMailConfig(): Promise<ApiResult<OutboundMailConfigResponse>> {
    return fetchApi<OutboundMailConfigResponse>('/api/v1/admin/outbound-mail/config');
  },

  async updateOutboundMailConfig(data: OutboundMailConfigUpdateInput): Promise<ApiResult<OutboundMailConfigResponse>> {
    return fetchApi<OutboundMailConfigResponse>('/api/v1/admin/outbound-mail/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** One test message through the seam. A failure answers OUTBOUND_MAIL_FAILED with `details.messageId`. */
  async sendOutboundMailTest(to: string): Promise<ApiResult<OutboundMailMessageDetail>> {
    return fetchApi<OutboundMailMessageDetail>('/api/v1/admin/outbound-mail/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    });
  },

  async listHubEffects(query: HubEffectListQuery): Promise<ApiResult<PaginatedResponse<HubEffectView>>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return fetchApi<PaginatedResponse<HubEffectView>>(`/api/v1/admin/effects${qs ? `?${qs}` : ''}`);
  },

  async getHubEffect(id: string): Promise<ApiResult<HubEffectView>> {
    return fetchApi<HubEffectView>(`/api/v1/admin/effects/${encodeURIComponent(id)}`);
  },

  async settleHubEffect(id: string, settlement: HubEffectSettlement, reason: string): Promise<ApiResult<HubEffectView>> {
    return fetchApi<HubEffectView>(`/api/v1/admin/effects/${encodeURIComponent(id)}/settle`, {
      method: 'POST',
      body: JSON.stringify({ settlement, reason }),
    });
  },

  async listHubAuditFindings(query: HubAuditFindingListQuery): Promise<ApiResult<PaginatedResponse<HubAuditFindingView>>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return fetchApi<PaginatedResponse<HubAuditFindingView>>(`/api/v1/admin/audit/findings${qs ? `?${qs}` : ''}`);
  },

  async getHubAuditFinding(id: string): Promise<ApiResult<HubAuditFindingView>> {
    return fetchApi<HubAuditFindingView>(`/api/v1/admin/audit/findings/${encodeURIComponent(id)}`);
  },

  async triageHubAuditFinding(id: string, status: HubAuditTriageStatus, note: string): Promise<ApiResult<HubAuditFindingView>> {
    return fetchApi<HubAuditFindingView>(`/api/v1/admin/audit/findings/${encodeURIComponent(id)}/triage`, {
      method: 'POST',
      body: JSON.stringify({ status, note }),
    });
  },

  async listHubAuditRuns(page: number, pageSize: number): Promise<ApiResult<PaginatedResponse<HubAuditRunView>>> {
    return fetchApi<PaginatedResponse<HubAuditRunView>>(`/api/v1/admin/audit/runs?page=${page}&pageSize=${pageSize}`);
  },

  async startHubAuditRun(): Promise<ApiResult<{ workflowId: string }>> {
    return fetchApi<{ workflowId: string }>('/api/v1/admin/audit/runs', { method: 'POST', body: JSON.stringify({}) });
  },

  async getHubAuditConfig(): Promise<ApiResult<HubAuditConfigView>> {
    return fetchApi<HubAuditConfigView>('/api/v1/admin/audit/config');
  },

  async updateHubAuditConfig(input: { slackChannelId: string | null; slackChannelName: string | null }): Promise<ApiResult<HubAuditConfigView>> {
    return fetchApi<HubAuditConfigView>('/api/v1/admin/audit/config', { method: 'PUT', body: JSON.stringify(input) });
  },

  async listOutboundMail(query: OutboundMailListQuery): Promise<ApiResult<PaginatedResponse<OutboundMailMessageSummary>>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return fetchApi<PaginatedResponse<OutboundMailMessageSummary>>(
      `/api/v1/admin/outbound-mail/messages${qs ? `?${qs}` : ''}`,
    );
  },

  async getOutboundMail(id: string): Promise<ApiResult<OutboundMailMessageDetail>> {
    return fetchApi<OutboundMailMessageDetail>(`/api/v1/admin/outbound-mail/messages/${encodeURIComponent(id)}`);
  },

  /** Deletes capture-mode rows only. */
  async clearCapturedOutboundMail(): Promise<ApiResult<{ deleted: number }>> {
    return fetchApi<{ deleted: number }>('/api/v1/admin/outbound-mail/messages', { method: 'DELETE' });
  },

  async getOutboundMailCategories(): Promise<ApiResult<OutboundMailCategoryInfo[]>> {
    return fetchApi<OutboundMailCategoryInfo[]>('/api/v1/admin/outbound-mail/categories');
  },

  // Looker
  async getLookerConfig(): Promise<ApiResult<LookerToolConfigResponse>> {
    return fetchApi<LookerToolConfigResponse>('/api/v1/admin/looker/config');
  },

  async updateLookerConfig(data: {
    credentialSetId?: string | null;
    defaultLookbackWeeks?: number;
    cacheTtlSeconds?: number;
    maxConcurrent?: number;
    minTimeMs?: number;
    maxRetries?: number;
  }): Promise<ApiResult<LookerToolConfigResponse>> {
    return fetchApi<LookerToolConfigResponse>('/api/v1/admin/looker/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getWordpressConfig(): Promise<ApiResult<WordpressToolConfigResponse>> {
    return fetchApi<WordpressToolConfigResponse>('/api/v1/admin/wordpress/config');
  },

  async updateWordpressConfig(data: {
    credentialSetId?: string | null;
    baseUrl?: string | null;
    postRecencyMonths?: number;
    indexPagePatterns?: string[];
  }): Promise<ApiResult<WordpressToolConfigResponse>> {
    return fetchApi<WordpressToolConfigResponse>('/api/v1/admin/wordpress/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getWordpressIndexStatus(): Promise<ApiResult<DocIndexStatus>> {
    return fetchApi<DocIndexStatus>('/api/v1/admin/wordpress/index-status');
  },

  async reindexWordpress(): Promise<ApiResult<{ triggered: boolean }>> {
    return fetchApi<{ triggered: boolean }>('/api/v1/admin/wordpress/reindex', { method: 'POST' });
  },

  async prepareWordpressDraft(data: {
    markdown: string;
    title?: string;
  }): Promise<ApiResult<WordpressDraftContent>> {
    return fetchApi<WordpressDraftContent>('/api/v1/wordpress/drafts/prepare', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async createWordpressDraft(data: WordpressDraftContent): Promise<ApiResult<WordpressDraftCreated>> {
    return fetchApi<WordpressDraftCreated>('/api/v1/wordpress/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ─── Company Enrichment ────────────────────────────────────────────────────

  async getCompanyEnrichmentConfig(): Promise<ApiResult<CompanyEnrichmentConfigResponse>> {
    return fetchApi<CompanyEnrichmentConfigResponse>('/api/v1/admin/company-enrichment/config');
  },

  async listEnrichmentApiKeys(): Promise<ApiResult<EnrichmentApiKeyInfo[]>> {
    return fetchApi<EnrichmentApiKeyInfo[]>('/api/v1/admin/company-enrichment/keys');
  },

  async createEnrichmentApiKey(name: string): Promise<ApiResult<EnrichmentApiKeyCreateResult>> {
    return fetchApi<EnrichmentApiKeyCreateResult>('/api/v1/admin/company-enrichment/keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async revokeEnrichmentApiKey(id: string): Promise<ApiResult<{ id: string; revoked: boolean }>> {
    return fetchApi<{ id: string; revoked: boolean }>(`/api/v1/admin/company-enrichment/keys/${id}/revoke`, {
      method: 'POST',
    });
  },

  async listSwanAuditCalls(opts: {
    page?: number;
    limit?: number;
    domain?: string;
    status?: string;
    grade?: string;
  }): Promise<ApiResult<SwanAuditListResult>> {
    const qs = new URLSearchParams();
    if (opts.page) qs.set('page', String(opts.page));
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.domain) qs.set('domain', opts.domain);
    if (opts.status) qs.set('status', opts.status);
    if (opts.grade) qs.set('grade', opts.grade);
    const q = qs.toString();
    return fetchApi<SwanAuditListResult>(`/api/v1/admin/company-enrichment/swan-audit${q ? `?${q}` : ''}`);
  },

  async getSwanAuditCall(id: string): Promise<ApiResult<SwanAuditCallDetail>> {
    return fetchApi<SwanAuditCallDetail>(`/api/v1/admin/company-enrichment/swan-audit/${id}`);
  },

  async updateCompanyEnrichmentConfig(data: {
    spiderCredentialSetId?: string | null;
    fireworksCredentialSetId?: string | null;
    builtwithCredentialSetId?: string | null;
    modelId?: string | null;
    scrapePageLimit?: number;
    contentCharLimit?: number;
    maxOutputTokens?: number;
    activePromptId?: string | null;
    hubspotGradeProperty?: string | null;
    hubspotGroundTruthProperty?: string | null;
    hubspotEnrichmentProperties?: string[];
    swanGradeThreshold?: 'A' | 'A_B';
    swanCacheTtlDays?: number;
    hubspotSwanSourceProperty?: string | null;
    hubspotSwanSourceValue?: string | null;
    hubspotManualSourceProperty?: string | null;
    hubspotManualSourceValue?: string | null;
    swanMaxQueriesPerDay?: number | null;
  }): Promise<ApiResult<CompanyEnrichmentConfigResponse>> {
    return fetchApi<CompanyEnrichmentConfigResponse>('/api/v1/admin/company-enrichment/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async listEnrichmentPrompts(): Promise<ApiResult<EnrichmentPromptResponse[]>> {
    return fetchApi<EnrichmentPromptResponse[]>('/api/v1/company-enrichment/prompts');
  },

  async createEnrichmentPrompt(data: {
    version: string;
    promptText: string;
    description?: string;
    activate?: boolean;
  }): Promise<ApiResult<EnrichmentPromptResponse>> {
    return fetchApi<EnrichmentPromptResponse>('/api/v1/company-enrichment/prompts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async activateEnrichmentPrompt(id: string): Promise<ApiResult<{ activated: boolean }>> {
    return fetchApi<{ activated: boolean }>(`/api/v1/company-enrichment/prompts/${id}/activate`, {
      method: 'POST',
    });
  },

  async testEnrichCompany(data: {
    companyName: string;
    website?: string;
  }): Promise<ApiResult<EnrichOneResult>> {
    return fetchApi<EnrichOneResult>('/api/v1/company-enrichment/test-enrich', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getEnrichmentHubspotProperties(): Promise<ApiResult<EnrichmentHubspotProperty[]>> {
    return fetchApi<EnrichmentHubspotProperty[]>('/api/v1/company-enrichment/hubspot-properties');
  },

  async createEnrichmentBatch(data: {
    name: string;
    source: 'hubspot_list' | 'upload';
    hubspotListId?: string;
    companies?: Array<{ companyName: string; website?: string; hubspotCompanyId?: string }>;
  }): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>('/api/v1/company-enrichment/batches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listEnrichmentBatches(opts?: { includeSwan?: boolean }): Promise<ApiResult<EnrichmentBatchSummary[]>> {
    const qs = opts?.includeSwan ? '?include_swan=true' : '';
    return fetchApi<EnrichmentBatchSummary[]>(`/api/v1/company-enrichment/batches${qs}`);
  },

  async getEnrichmentBatch(id: string): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>(`/api/v1/company-enrichment/batches/${id}`);
  },

  async getEnrichmentBatchProgress(id: string): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>(`/api/v1/company-enrichment/batches/${id}/progress`);
  },

  async getEnrichmentBatchResults(id: string): Promise<ApiResult<EnrichmentResultRow[]>> {
    return fetchApi<EnrichmentResultRow[]>(`/api/v1/company-enrichment/batches/${id}/results`);
  },

  async approveEnrichmentBatch(id: string): Promise<ApiResult<{ approved: boolean }>> {
    return fetchApi<{ approved: boolean }>(`/api/v1/company-enrichment/batches/${id}/approve`, { method: 'POST' });
  },

  /** Re-run a batch: create a fresh batch with the same companies, current config. */
  async rerunEnrichmentBatch(id: string): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>(`/api/v1/company-enrichment/batches/${id}/re-run`, { method: 'POST' });
  },

  /** Re-run only the failed companies in a batch (error set or no score). */
  async rerunFailedEnrichmentBatch(id: string): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>(`/api/v1/company-enrichment/batches/${id}/re-run-failed`, { method: 'POST' });
  },

  /** Stop a running batch: cancels its Temporal workflow and marks it cancelled. */
  async cancelEnrichmentBatch(id: string): Promise<ApiResult<EnrichmentBatchSummary>> {
    return fetchApi<EnrichmentBatchSummary>(`/api/v1/company-enrichment/batches/${id}/cancel`, { method: 'POST' });
  },

  /** Bulk-create HubSpot companies for every graded result in the batch whose
   * grade is in `grades` and not already in HubSpot. */
  async pushEnrichmentBatchGrades(
    id: string,
    grades: Array<'A' | 'B' | 'C' | 'D'>,
  ): Promise<ApiResult<{ pushed: boolean; grades: Array<'A' | 'B' | 'C' | 'D'> }>> {
    return fetchApi<{ pushed: boolean; grades: Array<'A' | 'B' | 'C' | 'D'> }>(
      `/api/v1/company-enrichment/batches/${id}/push-grades`,
      { method: 'POST', body: JSON.stringify({ grades }) },
    );
  },

  /** Manually create a HubSpot company from a graded enrichment result (net-new only). */
  async pushEnrichmentResultToHubspot(
    resultId: string,
  ): Promise<ApiResult<{ created: boolean; companyId: string | null; alreadyExisted: boolean }>> {
    return fetchApi<{ created: boolean; companyId: string | null; alreadyExisted: boolean }>(
      `/api/v1/company-enrichment/results/${resultId}/push-to-hubspot`,
      { method: 'POST' },
    );
  },

  async importVerifiedCompanies(data: {
    source: 'json_import' | 'hubspot_pull';
    companies?: Array<{ companyName: string; domain: string; website?: string; hubspotCompanyId?: string; verifiedGrade: 'A' | 'B' | 'C' | 'D'; notes?: string }>;
    limit?: number;
  }): Promise<ApiResult<{ imported: number }>> {
    return fetchApi<{ imported: number }>('/api/v1/company-enrichment/verified/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getVerifiedCompanies(): Promise<ApiResult<VerifiedCompaniesResponse>> {
    return fetchApi<VerifiedCompaniesResponse>('/api/v1/company-enrichment/verified');
  },

  async createReferenceTest(data: { sampleSize: number; stratified: boolean }): Promise<ApiResult<ReferenceTestSummary>> {
    return fetchApi<ReferenceTestSummary>('/api/v1/company-enrichment/reference-tests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listReferenceTests(): Promise<ApiResult<ReferenceTestSummary[]>> {
    return fetchApi<ReferenceTestSummary[]>('/api/v1/company-enrichment/reference-tests');
  },

  async getReferenceTest(id: string): Promise<ApiResult<ReferenceTestDetail>> {
    return fetchApi<ReferenceTestDetail>(`/api/v1/company-enrichment/reference-tests/${id}`);
  },

  async getOrderMetricsSummary(
    accountId: string,
    tenantId: number,
    range: LookerRange,
    lookback = 1,
  ): Promise<ApiResult<OrderSummaryResponse>> {
    const qs = new URLSearchParams({ tenantId: String(tenantId), range, lookback: String(lookback) });
    return fetchApi<OrderSummaryResponse>(`/api/v1/accounts/${accountId}/order-metrics/summary?${qs}`);
  },

  async getOrderMetricsWeekly(
    accountId: string,
    tenantId: number,
    range: LookerRange,
    lookback = 1,
  ): Promise<ApiResult<WeeklyTrendResponse>> {
    const qs = new URLSearchParams({ tenantId: String(tenantId), range, lookback: String(lookback) });
    return fetchApi<WeeklyTrendResponse>(`/api/v1/accounts/${accountId}/order-metrics/weekly?${qs}`);
  },

  async getOrderMetricsChannel(
    accountId: string,
    tenantId: number,
    range: LookerRange,
    lookback = 1,
  ): Promise<ApiResult<ChannelBreakdownResponse>> {
    const qs = new URLSearchParams({ tenantId: String(tenantId), range, lookback: String(lookback) });
    return fetchApi<ChannelBreakdownResponse>(`/api/v1/accounts/${accountId}/order-metrics/channel?${qs}`);
  },

  async getOrderMetricsItems(
    accountId: string,
    tenantId: number,
    range: LookerRange,
    lookback = 1,
  ): Promise<ApiResult<OrderItemDetailsResponse>> {
    const qs = new URLSearchParams({ tenantId: String(tenantId), range, lookback: String(lookback) });
    return fetchApi<OrderItemDetailsResponse>(`/api/v1/accounts/${accountId}/order-metrics/items?${qs}`);
  },

  // Grafana — global tool config (cache/concurrency/retries)
  async getGrafanaConfig(): Promise<ApiResult<GrafanaToolConfigResponse>> {
    return fetchApi<GrafanaToolConfigResponse>('/api/v1/admin/grafana/config');
  },

  async updateGrafanaConfig(data: {
    defaultRangeHours?: GrafanaRangeHours;
    queryCacheTtlSeconds?: number;
    maxConcurrent?: number;
    minTimeMs?: number;
    maxRetries?: number;
  }): Promise<ApiResult<GrafanaToolConfigResponse>> {
    return fetchApi<GrafanaToolConfigResponse>('/api/v1/admin/grafana/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Grafana — endpoint CRUD
  async listGrafanaEndpoints(): Promise<ApiResult<GrafanaEndpointSummary[]>> {
    return fetchApi<GrafanaEndpointSummary[]>('/api/v1/admin/grafana/endpoints');
  },

  async getGrafanaEndpoint(id: string): Promise<ApiResult<GrafanaEndpointDetail>> {
    return fetchApi<GrafanaEndpointDetail>(`/api/v1/admin/grafana/endpoints/${id}`);
  },

  async createGrafanaEndpoint(data: {
    identifier: string;
    name: string;
    url: string;
    credentialSetId: string;
    datasourceUid: string;
    skipTlsVerify?: boolean;
  }): Promise<ApiResult<GrafanaEndpointDetail>> {
    return fetchApi<GrafanaEndpointDetail>('/api/v1/admin/grafana/endpoints', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateGrafanaEndpoint(
    id: string,
    data: {
      name?: string;
      url?: string;
      credentialSetId?: string;
      datasourceUid?: string;
      isActive?: boolean;
      skipTlsVerify?: boolean;
    },
  ): Promise<ApiResult<GrafanaEndpointDetail>> {
    return fetchApi<GrafanaEndpointDetail>(`/api/v1/admin/grafana/endpoints/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteGrafanaEndpoint(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/grafana/endpoints/${id}`, {
      method: 'DELETE',
    });
  },

  async testGrafanaEndpoint(id: string): Promise<ApiResult<GrafanaConnectionTestResult>> {
    return fetchApi<GrafanaConnectionTestResult>(`/api/v1/admin/grafana/endpoints/${id}/test`, {
      method: 'POST',
    });
  },

  // Grafana — per-endpoint dashboard configs
  async listGrafanaDashboardConfigs(
    endpointId: string,
  ): Promise<ApiResult<GrafanaDashboardConfigResponse[]>> {
    return fetchApi<GrafanaDashboardConfigResponse[]>(
      `/api/v1/admin/grafana/endpoints/${endpointId}/dashboards`,
    );
  },

  async createGrafanaDashboardConfig(
    endpointId: string,
    data: {
      key: string;
      label: string;
      uid: string;
      excludedPanelIds?: number[];
      displayOrder?: number;
      enabled?: boolean;
    },
  ): Promise<ApiResult<GrafanaDashboardConfigResponse>> {
    return fetchApi<GrafanaDashboardConfigResponse>(
      `/api/v1/admin/grafana/endpoints/${endpointId}/dashboards`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  },

  async updateGrafanaDashboardConfig(
    endpointId: string,
    key: string,
    data: {
      label?: string;
      uid?: string;
      excludedPanelIds?: number[];
      displayOrder?: number;
      enabled?: boolean;
    },
  ): Promise<ApiResult<GrafanaDashboardConfigResponse>> {
    return fetchApi<GrafanaDashboardConfigResponse>(
      `/api/v1/admin/grafana/endpoints/${endpointId}/dashboards/${key}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  },

  async deleteGrafanaDashboardConfig(
    endpointId: string,
    key: string,
  ): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(
      `/api/v1/admin/grafana/endpoints/${endpointId}/dashboards/${key}`,
      { method: 'DELETE' },
    );
  },

  async getGrafanaPanelInventory(
    endpointId: string,
    key: string,
  ): Promise<ApiResult<GrafanaPanelInventoryResponse>> {
    return fetchApi<GrafanaPanelInventoryResponse>(
      `/api/v1/admin/grafana/endpoints/${endpointId}/dashboards/${key}/panels`,
    );
  },

  // Grafana — viewer endpoints (operational data tab; tenant resolves the endpoint)
  async listOperationalDataDashboards(
    accountId: string,
    tenantId: number,
  ): Promise<ApiResult<GrafanaDashboardSummary[]>> {
    const qs = new URLSearchParams({ tenantId: String(tenantId) });
    return fetchApi<GrafanaDashboardSummary[]>(
      `/api/v1/accounts/${accountId}/operational-data/dashboards?${qs}`,
    );
  },

  async getOperationalDataDashboard(
    accountId: string,
    key: string,
    tenantId: number,
    rangeHours: GrafanaRangeHours,
  ): Promise<ApiResult<GrafanaDashboardResponse>> {
    const qs = new URLSearchParams({
      tenantId: String(tenantId),
      rangeHours: String(rangeHours),
    });
    return fetchApi<GrafanaDashboardResponse>(
      `/api/v1/accounts/${accountId}/operational-data/${key}?${qs}`,
    );
  },

  // HubSpot
  async getHubSpotConfig(): Promise<ApiResult<HubSpotConfigResponse>> {
    return fetchApi<HubSpotConfigResponse>('/api/v1/admin/hubspot/config');
  },

  async updateHubSpotConfig(data: { credentialSetId?: string | null }): Promise<ApiResult<HubSpotConfigResponse>> {
    return fetchApi<HubSpotConfigResponse>('/api/v1/admin/hubspot/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testHubSpotConnection(): Promise<ApiResult<HubSpotConnectionTestResult>> {
    return fetchApi<HubSpotConnectionTestResult>('/api/v1/admin/hubspot/test-connection', {
      method: 'POST',
    });
  },

  // HubSpot Custom Channel (Support Tickets provider)
  async getHubSpotChannelConfig(): Promise<ApiResult<HubSpotChannelConfigResponse>> {
    return fetchApi<HubSpotChannelConfigResponse>('/api/v1/admin/hubspot-channel/config');
  },

  async updateHubSpotChannelConfig(data: {
    credentialSetId?: string | null;
    statusMap?: HubSpotStatusMap;
    attachmentsBucket?: string | null;
    webhookBaseUrl?: string | null;
  }): Promise<ApiResult<HubSpotChannelConfigResponse>> {
    return fetchApi<HubSpotChannelConfigResponse>('/api/v1/admin/hubspot-channel/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getHubSpotChannelStatus(): Promise<ApiResult<HubSpotChannelStatusResponse>> {
    return fetchApi<HubSpotChannelStatusResponse>('/api/v1/admin/hubspot-channel/status');
  },

  async registerHubSpotChannel(): Promise<ApiResult<{ channelId: string }>> {
    return fetchApi<{ channelId: string }>('/api/v1/admin/hubspot-channel/register-channel', { method: 'POST' });
  },

  async runHubSpotDiagnostics(): Promise<ApiResult<HubSpotDiagnosticsReport>> {
    return fetchApi<HubSpotDiagnosticsReport>('/api/v1/admin/hubspot-channel/diagnostics', { method: 'POST' });
  },

  async syncHubSpotChannelSettings(): Promise<ApiResult<{ channelId: string }>> {
    return fetchApi<{ channelId: string }>('/api/v1/admin/hubspot-channel/sync-settings', { method: 'POST' });
  },

  async getHubSpotChannels(): Promise<ApiResult<HubSpotChannelSummary[]>> {
    return fetchApi<HubSpotChannelSummary[]>('/api/v1/admin/hubspot-channel/channels');
  },

  async deleteHubSpotChannel(channelId: string): Promise<ApiResult<{ channelId: string; deleted: boolean }>> {
    return fetchApi<{ channelId: string; deleted: boolean }>(
      `/api/v1/admin/hubspot-channel/channels/${encodeURIComponent(channelId)}`,
      { method: 'DELETE' },
    );
  },

  async getHubSpotChannelInboxes(): Promise<ApiResult<HubSpotChannelInbox[]>> {
    return fetchApi<HubSpotChannelInbox[]>('/api/v1/admin/hubspot-channel/inboxes');
  },

  async getHubSpotChannelActors(): Promise<ApiResult<HubSpotChannelActor[]>> {
    return fetchApi<HubSpotChannelActor[]>('/api/v1/admin/hubspot-channel/actors');
  },

  async connectHubSpotChannelAccount(data: { inboxId: string; supportEmail: string }): Promise<ApiResult<{ channelAccountId: string }>> {
    return fetchApi<{ channelAccountId: string }>('/api/v1/admin/hubspot-channel/connect-account', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async provisionHubSpotChannelProperties(): Promise<ApiResult<{ ok: boolean }>> {
    return fetchApi<{ ok: boolean }>('/api/v1/admin/hubspot-channel/provision-properties', { method: 'POST' });
  },

  /** `test` provisions the Support Lab pipeline instead of the real one. */
  async provisionHubSpotChannelPipeline(test = false): Promise<ApiResult<HubSpotChannelPipelineResult>> {
    return fetchApi<HubSpotChannelPipelineResult>(
      `/api/v1/admin/hubspot-channel/provision-pipeline${test ? '?test=true' : ''}`,
      { method: 'POST' },
    );
  },

  async activateHubSpotChannel(): Promise<ApiResult<{ ok: boolean }>> {
    return fetchApi<{ ok: boolean }>('/api/v1/admin/hubspot-channel/activate', { method: 'POST' });
  },

  async runHubSpotChannelReconcile(): Promise<ApiResult<{ scanned: number; updated: number }>> {
    return fetchApi<{ scanned: number; updated: number }>('/api/v1/admin/hubspot-channel/reconcile', { method: 'POST' });
  },

  // Kibo
  async getKiboAuthStatus(): Promise<ApiResult<KiboAuthStatus>> {
    return fetchApi<KiboAuthStatus>('/api/v1/kibo/auth/status');
  },

  async testKiboGetTenant(tenantId: string): Promise<ApiResult<KiboTenantResult>> {
    return fetchApi<KiboTenantResult>('/api/v1/kibo/test/get-tenant', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
  },

  async getKiboAuthConfig(): Promise<ApiResult<KiboAuthConfig>> {
    return fetchApi<KiboAuthConfig>('/api/v1/admin/kibo/auth-config');
  },

  async updateKiboAuthConfig(credentialSetId: string | null): Promise<ApiResult<KiboAuthConfig>> {
    return fetchApi<KiboAuthConfig>('/api/v1/admin/kibo/auth-config', {
      method: 'PUT',
      body: JSON.stringify({ credentialSetId }),
    });
  },

  // Doc Feedback
  async generateDocFeedbackSuggestions(threadId: string): Promise<ApiResult<DocFeedbackSuggestions>> {
    return fetchApi<DocFeedbackSuggestions>('/api/v1/doc-feedback/generate-suggestions', {
      method: 'POST',
      body: JSON.stringify({ threadId }),
    });
  },

  async createDocFeedbackTicket(data: { threadId: string; title: string; aspect: string; notes?: string }): Promise<ApiResult<DocFeedbackTicket>> {
    return fetchApi<DocFeedbackTicket>('/api/v1/doc-feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getDocFeedbackTickets(): Promise<ApiResult<DocFeedbackTicketWithStatus[]>> {
    return fetchApi<DocFeedbackTicketWithStatus[]>('/api/v1/doc-feedback');
  },

  async addDocFeedbackComment(id: string, comment: string): Promise<ApiResult<{ success: boolean }>> {
    return fetchApi<{ success: boolean }>(`/api/v1/doc-feedback/${id}/comment`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  },

  async getDocFeedbackTransitions(id: string): Promise<ApiResult<JiraTransition[]>> {
    return fetchApi<JiraTransition[]>(`/api/v1/doc-feedback/${id}/transitions`);
  },

  async transitionDocFeedback(id: string, transitionId: string): Promise<ApiResult<{ success: boolean }>> {
    return fetchApi<{ success: boolean }>(`/api/v1/doc-feedback/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ transitionId }),
    });
  },

  // RFP Qualifier v2
  async getRfpConfig(): Promise<ApiResult<RfpConfig>> {
    return fetchApi<RfpConfig>('/api/v1/admin/rfp-qualifier/config');
  },

  async updateRfpConfig(data: Partial<RfpConfig>): Promise<ApiResult<RfpConfig>> {
    return fetchApi<RfpConfig>('/api/v1/admin/rfp-qualifier/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getRfpKbStatus(): Promise<ApiResult<RfpKbStatus>> {
    return fetchApi<RfpKbStatus>('/api/v1/admin/rfp-qualifier/status');
  },

  async clearRfpKb(): Promise<ApiResult<{ cleared: boolean }>> {
    return fetchApi<{ cleared: boolean }>('/api/v1/admin/rfp-qualifier/kb', { method: 'DELETE' });
  },

  async getRfpKbSources(): Promise<ApiResult<RfpKbSource[]>> {
    return fetchApi<RfpKbSource[]>('/api/v1/rfp-qualifier/kb/sources');
  },

  async deleteRfpKbSource(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/rfp-qualifier/kb/sources/${id}`, { method: 'DELETE' });
  },

  async moveRfpKbSource(id: string, groupId: string | null): Promise<ApiResult<RfpKbSource>> {
    return fetchApi<RfpKbSource>(`/api/v1/rfp-qualifier/kb/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ groupId }),
    });
  },

  async reindexRfpKbSource(id: string): Promise<ApiResult<{ workflowId: string }>> {
    return fetchApi<{ workflowId: string }>(`/api/v1/rfp-qualifier/kb/sources/${id}/reindex`, {
      method: 'POST',
    });
  },

  async deleteRfpQaPair(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/rfp-qualifier/kb/qa-pairs/${id}`, { method: 'DELETE' });
  },

  async updateRfpQaPair(id: string, patch: { question?: string; answer?: string }): Promise<ApiResult<{ updated: boolean }>> {
    return fetchApi<{ updated: boolean }>(`/api/v1/rfp-qualifier/kb/qa-pairs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  async deleteRfpDocChunk(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/rfp-qualifier/kb/doc-chunks/${id}`, { method: 'DELETE' });
  },

  async getRfpSourceChunks(id: string): Promise<ApiResult<RfpSourceChunks>> {
    return fetchApi<RfpSourceChunks>(`/api/v1/rfp-qualifier/kb/sources/${id}/chunks`);
  },

  async importRfpQaExcel(formData: FormData): Promise<ApiResult<{ jobs: Array<{ sourceId: string; filename: string; workflowId: string }> }>> {
    return fetchApi<{ jobs: Array<{ sourceId: string; filename: string; workflowId: string }> }>('/api/v1/rfp-qualifier/kb/qa', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type — browser sets multipart boundary automatically
    });
  },

  async getRfpKbSourceById(id: string): Promise<ApiResult<RfpKbSource>> {
    return fetchApi<RfpKbSource>(`/api/v1/rfp-qualifier/kb/sources/${id}`);
  },

  async importRfpDocument(formData: FormData): Promise<ApiResult<{ sourceId: string; count: number }>> {
    return fetchApi<{ sourceId: string; count: number }>('/api/v1/rfp-qualifier/kb/docs', {
      method: 'POST',
      body: formData,
    });
  },

  async importRfpUrl(url: string, groupId?: string): Promise<ApiResult<{ sourceId: string; count: number }>> {
    return fetchApi<{ sourceId: string; count: number }>('/api/v1/rfp-qualifier/kb/url', {
      method: 'POST',
      body: JSON.stringify({ url, groupId }),
    });
  },

  async getRfpSessions(): Promise<ApiResult<RfpSession[]>> {
    return fetchApi<RfpSession[]>('/api/v1/rfp-qualifier/sessions');
  },

  async createRfpSession(formData: FormData): Promise<ApiResult<{ sessionId: string; excelInfo: unknown }>> {
    return fetchApi<{ sessionId: string; excelInfo: unknown }>('/api/v1/rfp-qualifier/sessions', {
      method: 'POST',
      body: formData,
    });
  },

  async processRfpSession(sessionId: string, data: { profileId?: string }): Promise<ApiResult<{ started: boolean }>> {
    return fetchApi<{ started: boolean }>(`/api/v1/rfp-qualifier/sessions/${sessionId}/process`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async cancelRfpSession(sessionId: string): Promise<ApiResult<{ cancelled: boolean }>> {
    return fetchApi<{ cancelled: boolean }>(`/api/v1/rfp-qualifier/sessions/${sessionId}/cancel`, {
      method: 'POST',
    });
  },

  async getRfpSessionProgress(sessionId: string, cursor?: string): Promise<ApiResult<{ events: RfpProgressEvent[]; nextCursor: string; done: boolean }>> {
    const q = cursor !== undefined ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return fetchApi<{ events: RfpProgressEvent[]; nextCursor: string; done: boolean }>(`/api/v1/rfp-qualifier/sessions/${sessionId}/stream${q}`);
  },

  async getRfpResults(sessionId: string): Promise<ApiResult<RfpResultSummary[]>> {
    return fetchApi<RfpResultSummary[]>(`/api/v1/rfp-qualifier/sessions/${sessionId}/results`);
  },

  /** One full row, including `sources` and `toolTrace`. */
  async getRfpResult(sessionId: string, resultId: string): Promise<ApiResult<RfpResult>> {
    return fetchApi<RfpResult>(`/api/v1/rfp-qualifier/sessions/${sessionId}/results/${resultId}`);
  },

  async updateRfpResult(
    sessionId: string,
    resultId: string,
    patch: { editedAnswer?: string; question?: string; verificationStatus?: 'verified' | 'needs_review' },
  ): Promise<ApiResult<RfpResult>> {
    return fetchApi<RfpResult>(`/api/v1/rfp-qualifier/sessions/${sessionId}/results/${resultId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  async regenerateRfpResult(sessionId: string, resultId: string): Promise<ApiResult<RfpResult>> {
    return fetchApi<RfpResult>(`/api/v1/rfp-qualifier/sessions/${sessionId}/results/${resultId}/regenerate`, {
      method: 'POST',
    });
  },

  async deleteRfpResult(sessionId: string, resultId: string): Promise<ApiResult<null>> {
    return fetchApi<null>(`/api/v1/rfp-qualifier/sessions/${sessionId}/results/${resultId}`, {
      method: 'DELETE',
    });
  },

  async getRfpExport(sessionId: string): Promise<Response> {
    return fetch(`/api/v1/rfp-qualifier/sessions/${sessionId}/export`, { credentials: 'include' });
  },

  async getRfpExportFilled(sessionId: string): Promise<Response> {
    return fetch(`/api/v1/rfp-qualifier/sessions/${sessionId}/export-filled`, { credentials: 'include' });
  },

  async downloadRfpKbSource(sourceId: string): Promise<Response> {
    return fetch(`/api/v1/rfp-qualifier/kb/sources/${sourceId}/download`, { credentials: 'include' });
  },

  async exportRfpKbSource(sourceId: string): Promise<Response> {
    return fetch(`/api/v1/rfp-qualifier/kb/sources/${sourceId}/export`, { credentials: 'include' });
  },

  async exportAllRfpKbSources(groupId?: string): Promise<Response> {
    const qs = groupId ? `?groupId=${encodeURIComponent(groupId)}` : '';
    return fetch(`/api/v1/rfp-qualifier/kb/sources/export${qs}`, { credentials: 'include' });
  },

  async importRfpKbSources(file: File): Promise<ApiResult<RfpKbImportResult>> {
    const form = new FormData();
    form.append('file', file);
    return fetchApi<RfpKbImportResult>('/api/v1/rfp-qualifier/kb/sources/import', {
      method: 'POST',
      body: form,
    });
  },

  async replaceRfpKbSource(sourceId: string, file: File): Promise<ApiResult<{ replaced: true }>> {
    const form = new FormData();
    form.append('file', file);
    return fetchApi<{ replaced: true }>(`/api/v1/rfp-qualifier/kb/sources/${sourceId}/replace`, {
      method: 'PUT',
      body: form,
    });
  },

  async downloadRfpSessionOriginal(sessionId: string): Promise<Response> {
    return fetch(`/api/v1/rfp-qualifier/sessions/${sessionId}/download-original`, { credentials: 'include' });
  },

  async testRfpQuestion(question: string, profileId?: string): Promise<ApiResult<RfpPipelineTestResult>> {
    return fetchApi<RfpPipelineTestResult>('/api/v1/rfp-qualifier/test-question', {
      method: 'POST',
      body: JSON.stringify({ question, profileId }),
    });
  },

  async testRfpSemanticSearch(query: string, limit?: number, profileId?: string): Promise<ApiResult<RfpSearchToolTestResult>> {
    return fetchApi<RfpSearchToolTestResult>('/api/v1/rfp-qualifier/test/semantic-search', {
      method: 'POST',
      body: JSON.stringify({ query, limit, profileId }),
    });
  },

  async testRfpKeywordSearch(query: string, limit?: number, profileId?: string): Promise<ApiResult<RfpSearchToolTestResult>> {
    return fetchApi<RfpSearchToolTestResult>('/api/v1/rfp-qualifier/test/keyword-search', {
      method: 'POST',
      body: JSON.stringify({ query, limit, profileId }),
    });
  },

  async testRfpDocSearch(query: string): Promise<ApiResult<RfpSearchToolTestResult>> {
    return fetchApi<RfpSearchToolTestResult>('/api/v1/rfp-qualifier/test/doc-search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  },

  async getRfpProfiles(): Promise<ApiResult<RfpResponseProfile[]>> {
    return fetchApi<RfpResponseProfile[]>('/api/v1/rfp-qualifier/profiles');
  },

  async createRfpProfile(data: Omit<RfpResponseProfile, 'id' | 'kbGroups'> & { kbGroupIds: string[] }): Promise<ApiResult<RfpResponseProfile>> {
    return fetchApi<RfpResponseProfile>('/api/v1/rfp-qualifier/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRfpProfile(id: string, data: Partial<Omit<RfpResponseProfile, 'kbGroups'>> & { kbGroupIds?: string[] }): Promise<ApiResult<RfpResponseProfile>> {
    return fetchApi<RfpResponseProfile>(`/api/v1/rfp-qualifier/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteRfpProfile(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/rfp-qualifier/profiles/${id}`, { method: 'DELETE' });
  },

  async listRfpKbGroups(): Promise<ApiResult<RfpKbGroup[]>> {
    return fetchApi<RfpKbGroup[]>('/api/v1/rfp-qualifier/kb/groups');
  },

  async createRfpKbGroup(data: { name: string; description?: string }): Promise<ApiResult<RfpKbGroup>> {
    return fetchApi<RfpKbGroup>('/api/v1/rfp-qualifier/kb/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRfpKbGroup(id: string, data: { name?: string; description?: string }): Promise<ApiResult<RfpKbGroup>> {
    return fetchApi<RfpKbGroup>(`/api/v1/rfp-qualifier/kb/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteRfpKbGroup(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/rfp-qualifier/kb/groups/${id}`, { method: 'DELETE' });
  },

  async getRfpKbSourcesByGroup(groupId?: string): Promise<ApiResult<RfpKbSource[]>> {
    const url = groupId ? `/api/v1/rfp-qualifier/kb/sources?group=${groupId}` : '/api/v1/rfp-qualifier/kb/sources';
    return fetchApi<RfpKbSource[]>(url);
  },

  async getExcelFileInfo(formData: FormData): Promise<ApiResult<RfpExcelFileInfo>> {
    return fetchApi<RfpExcelFileInfo>('/api/v1/rfp-qualifier/excel-info', {
      method: 'POST',
      body: formData,
    });
  },

  // ─── Pricing Calculator ─────────────────────────────────────────────────────

  async calculatePricing(inputs: PricingCalculateInput): Promise<ApiResult<PricingResult>> {
    return fetchApi<PricingResult>('/api/v1/pricing/calculate', {
      method: 'POST',
      body: JSON.stringify(inputs),
    });
  },

  // Quotes
  async listMyPricingQuotes(): Promise<ApiResult<PricingQuote[]>> {
    return fetchApi<PricingQuote[]>('/api/v1/pricing/quotes');
  },
  async createPricingQuote(data: PricingQuoteCreate): Promise<ApiResult<PricingQuote>> {
    return fetchApi<PricingQuote>('/api/v1/pricing/quotes', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingQuote(id: string | number, data: Partial<PricingQuoteCreate>): Promise<ApiResult<PricingQuote>> {
    return fetchApi<PricingQuote>(`/api/v1/pricing/quotes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async getPricingQuote(id: string | number): Promise<ApiResult<PricingQuote>> {
    return fetchApi<PricingQuote>(`/api/v1/pricing/quotes/${id}`);
  },
  async deletePricingQuote(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/pricing/quotes/${id}`, { method: 'DELETE' });
  },
  async exportPricingQuoteExcel(id: number): Promise<ApiResult<Blob>> {
    const res = await fetch(`/api/v1/pricing/quotes/${id}/export`);
    if (!res.ok) return localApiError('EXPORT_FAILED', 'Export failed');
    return { success: true, data: await res.blob() };
  },

  // Deals
  async listMyPricingDeals(): Promise<ApiResult<PricingDeal[]>> {
    return fetchApi<PricingDeal[]>('/api/v1/pricing/deals');
  },
  async getPricingDeal(id: string | number): Promise<ApiResult<PricingDeal>> {
    return fetchApi<PricingDeal>(`/api/v1/pricing/deals/${id}`);
  },
  async createPricingDeal(data: PricingDealCreate): Promise<ApiResult<PricingDeal>> {
    return fetchApi<PricingDeal>('/api/v1/pricing/deals', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingDeal(id: number, data: PricingDealUpdate): Promise<ApiResult<PricingDeal>> {
    return fetchApi<PricingDeal>(`/api/v1/pricing/deals/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingDeal(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/pricing/deals/${id}`, { method: 'DELETE' });
  },
  async setPricingDealArchived(id: string | number, archived: boolean): Promise<ApiResult<PricingDeal>> {
    return fetchApi<PricingDeal>(`/api/v1/pricing/deals/${id}/archive`, { method: 'POST', body: JSON.stringify({ archived }) });
  },

  // All Quotes — Quote Access viewer/manager (or admin)
  async listAllPricingQuotes(): Promise<ApiResult<PricingQuote[]>> {
    return fetchApi<PricingQuote[]>('/api/v1/pricing/all-quotes');
  },

  // Admin — Global Settings
  async getPricingGlobalSettings(): Promise<ApiResult<PricingGlobalSettings>> {
    return fetchApi<PricingGlobalSettings>('/api/v1/admin/pricing/global-settings');
  },
  async updatePricingGlobalSettings(data: Partial<PricingGlobalSettings>): Promise<ApiResult<PricingGlobalSettings>> {
    return fetchApi<PricingGlobalSettings>('/api/v1/admin/pricing/global-settings', { method: 'PUT', body: JSON.stringify(data) });
  },

  // Admin — Price Tiers
  async getPricingPriceTiers(product?: string): Promise<ApiResult<PricingPriceTier[]>> {
    const qs = product ? `?product=${encodeURIComponent(product)}` : '';
    return fetchApi<PricingPriceTier[]>(`/api/v1/admin/pricing/price-tiers${qs}`);
  },
  async createPricingPriceTier(data: Omit<PricingPriceTier, 'id'>): Promise<ApiResult<PricingPriceTier>> {
    return fetchApi<PricingPriceTier>('/api/v1/admin/pricing/price-tiers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingPriceTier(id: number, data: Partial<Omit<PricingPriceTier, 'id'>>): Promise<ApiResult<PricingPriceTier>> {
    return fetchApi<PricingPriceTier>(`/api/v1/admin/pricing/price-tiers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingPriceTier(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/price-tiers/${id}`, { method: 'DELETE' });
  },

  // Admin — Tier Multipliers
  async getPricingTierMultipliers(product?: string): Promise<ApiResult<PricingTierMultiplier[]>> {
    const qs = product ? `?product=${encodeURIComponent(product)}` : '';
    return fetchApi<PricingTierMultiplier[]>(`/api/v1/admin/pricing/tier-multipliers${qs}`);
  },
  async updatePricingTierMultiplier(data: { product: string; tier: string; multiplier: number }): Promise<ApiResult<PricingTierMultiplier>> {
    return fetchApi<PricingTierMultiplier>('/api/v1/admin/pricing/tier-multipliers', { method: 'PUT', body: JSON.stringify(data) });
  },

  // Admin — Rules
  async getPricingRules(product?: string): Promise<ApiResult<PricingRule[]>> {
    const qs = product ? `?product=${encodeURIComponent(product)}` : '';
    return fetchApi<PricingRule[]>(`/api/v1/admin/pricing/rules${qs}`);
  },
  async createPricingRule(data: Omit<PricingRule, 'id'>): Promise<ApiResult<PricingRule>> {
    return fetchApi<PricingRule>('/api/v1/admin/pricing/rules', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingRule(id: number, data: Partial<Omit<PricingRule, 'id'>>): Promise<ApiResult<PricingRule>> {
    return fetchApi<PricingRule>(`/api/v1/admin/pricing/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingRule(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/rules/${id}`, { method: 'DELETE' });
  },

  // Admin — NFR Options
  async getPricingNfrOptions(): Promise<ApiResult<PricingNfrOption[]>> {
    return fetchApi<PricingNfrOption[]>('/api/v1/admin/pricing/nfr-options');
  },
  async createPricingNfrOption(data: Omit<PricingNfrOption, 'id'>): Promise<ApiResult<PricingNfrOption>> {
    return fetchApi<PricingNfrOption>('/api/v1/admin/pricing/nfr-options', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingNfrOption(id: number, data: Partial<Omit<PricingNfrOption, 'id'>>): Promise<ApiResult<PricingNfrOption>> {
    return fetchApi<PricingNfrOption>(`/api/v1/admin/pricing/nfr-options/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingNfrOption(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/nfr-options/${id}`, { method: 'DELETE' });
  },

  // Admin — SLA Options
  async getPricingSlaOptions(): Promise<ApiResult<PricingSlaOption[]>> {
    return fetchApi<PricingSlaOption[]>('/api/v1/admin/pricing/sla-options');
  },
  async createPricingSlaOption(data: Omit<PricingSlaOption, 'id'>): Promise<ApiResult<PricingSlaOption>> {
    return fetchApi<PricingSlaOption>('/api/v1/admin/pricing/sla-options', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingSlaOption(id: number, data: Partial<Omit<PricingSlaOption, 'id'>>): Promise<ApiResult<PricingSlaOption>> {
    return fetchApi<PricingSlaOption>(`/api/v1/admin/pricing/sla-options/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingSlaOption(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/sla-options/${id}`, { method: 'DELETE' });
  },

  // Admin — DR Options
  async getPricingDrOptions(): Promise<ApiResult<PricingDrOption[]>> {
    return fetchApi<PricingDrOption[]>('/api/v1/admin/pricing/dr-options');
  },
  async createPricingDrOption(data: Omit<PricingDrOption, 'id'>): Promise<ApiResult<PricingDrOption>> {
    return fetchApi<PricingDrOption>('/api/v1/admin/pricing/dr-options', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingDrOption(id: number, data: Partial<Omit<PricingDrOption, 'id'>>): Promise<ApiResult<PricingDrOption>> {
    return fetchApi<PricingDrOption>(`/api/v1/admin/pricing/dr-options/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingDrOption(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/dr-options/${id}`, { method: 'DELETE' });
  },

  // Admin — Uplift Options (Support tiers + Agentic addons)
  async getPricingUpliftOptions(): Promise<ApiResult<PricingUpliftOption[]>> {
    return fetchApi<PricingUpliftOption[]>('/api/v1/admin/pricing/uplift-options');
  },
  async createPricingUpliftOption(data: Omit<PricingUpliftOption, 'id'>): Promise<ApiResult<PricingUpliftOption>> {
    return fetchApi<PricingUpliftOption>('/api/v1/admin/pricing/uplift-options', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingUpliftOption(id: number, data: Partial<Omit<PricingUpliftOption, 'id'>>): Promise<ApiResult<PricingUpliftOption>> {
    return fetchApi<PricingUpliftOption>(`/api/v1/admin/pricing/uplift-options/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingUpliftOption(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/uplift-options/${id}`, { method: 'DELETE' });
  },

  // Admin — Infra Costs
  async getPricingInfraCosts(): Promise<ApiResult<PricingInfraCost[]>> {
    return fetchApi<PricingInfraCost[]>('/api/v1/admin/pricing/infra-costs');
  },
  async updatePricingInfraCost(key: string, price: number): Promise<ApiResult<PricingInfraCost>> {
    return fetchApi<PricingInfraCost>(`/api/v1/admin/pricing/infra-costs/${key}`, { method: 'PUT', body: JSON.stringify({ price }) });
  },

  // Admin — Dropship Settings
  async getPricingDropshipSettings(): Promise<ApiResult<PricingKeyValue[]>> {
    return fetchApi<PricingKeyValue[]>('/api/v1/admin/pricing/dropship-settings');
  },
  async updatePricingDropshipSettings(items: PricingKeyValue[]): Promise<ApiResult<PricingKeyValue[]>> {
    const results = await Promise.all(
      items.map((item) =>
        fetchApi<PricingKeyValue>(`/api/v1/admin/pricing/dropship-settings/${item.key}`, {
          method: 'PUT',
          body: JSON.stringify({ value: item.value }),
        })
      )
    );
    const failed = results.find((r): r is ApiError => !r.success);
    if (failed) return failed;
    return { success: true, data: items };
  },

  // Admin — Dropship Tiers
  async getPricingDropshipTiers(): Promise<ApiResult<PricingDropshipTier[]>> {
    return fetchApi<PricingDropshipTier[]>('/api/v1/admin/pricing/dropship-tiers');
  },
  async createPricingDropshipTier(data: Omit<PricingDropshipTier, 'id'>): Promise<ApiResult<PricingDropshipTier>> {
    return fetchApi<PricingDropshipTier>('/api/v1/admin/pricing/dropship-tiers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingDropshipTier(id: number, data: Partial<Omit<PricingDropshipTier, 'id'>>): Promise<ApiResult<PricingDropshipTier>> {
    return fetchApi<PricingDropshipTier>(`/api/v1/admin/pricing/dropship-tiers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingDropshipTier(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/dropship-tiers/${id}`, { method: 'DELETE' });
  },

  // Admin — Convo Settings
  async getPricingConvoSettings(): Promise<ApiResult<PricingKeyValue[]>> {
    return fetchApi<PricingKeyValue[]>('/api/v1/admin/pricing/convo-settings');
  },
  async updatePricingConvoSettings(items: PricingKeyValue[]): Promise<ApiResult<PricingKeyValue[]>> {
    const results = await Promise.all(
      items.map((item) =>
        fetchApi<PricingKeyValue>(`/api/v1/admin/pricing/convo-settings/${item.key}`, {
          method: 'PUT',
          body: JSON.stringify({ value: item.value }),
        })
      )
    );
    const failed = results.find((r): r is ApiError => !r.success);
    if (failed) return failed;
    return { success: true, data: items };
  },

  // Admin — Convo Tiers
  async getPricingConvoTiers(): Promise<ApiResult<PricingConvoTier[]>> {
    return fetchApi<PricingConvoTier[]>('/api/v1/admin/pricing/convo-tiers');
  },
  async createPricingConvoTier(data: Omit<PricingConvoTier, 'id'>): Promise<ApiResult<PricingConvoTier>> {
    return fetchApi<PricingConvoTier>('/api/v1/admin/pricing/convo-tiers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingConvoTier(id: number, data: Partial<Omit<PricingConvoTier, 'id'>>): Promise<ApiResult<PricingConvoTier>> {
    return fetchApi<PricingConvoTier>(`/api/v1/admin/pricing/convo-tiers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingConvoTier(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/convo-tiers/${id}`, { method: 'DELETE' });
  },

  // Admin — Deal Discount Tiers
  async getPricingDealDiscountTiers(): Promise<ApiResult<PricingDealDiscountTier[]>> {
    return fetchApi<PricingDealDiscountTier[]>('/api/v1/admin/pricing/deal-discount-tiers');
  },
  async createPricingDealDiscountTier(data: Omit<PricingDealDiscountTier, 'id'>): Promise<ApiResult<PricingDealDiscountTier>> {
    return fetchApi<PricingDealDiscountTier>('/api/v1/admin/pricing/deal-discount-tiers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updatePricingDealDiscountTier(id: number, data: Partial<Omit<PricingDealDiscountTier, 'id'>>): Promise<ApiResult<PricingDealDiscountTier>> {
    return fetchApi<PricingDealDiscountTier>(`/api/v1/admin/pricing/deal-discount-tiers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deletePricingDealDiscountTier(id: number): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/pricing/deal-discount-tiers/${id}`, { method: 'DELETE' });
  },

  // Admin — Config Import / Export / Template
  async exportPricingConfig(): Promise<ApiResult<unknown>> {
    const res = await fetch('/api/v1/admin/pricing/export-config');
    if (!res.ok) return localApiError('EXPORT_FAILED', 'Export failed');
    return { success: true, data: (await res.json()) as unknown };
  },
  async getPricingConfigTemplate(): Promise<ApiResult<unknown>> {
    const res = await fetch('/api/v1/admin/pricing/config-template');
    if (!res.ok) return localApiError('TEMPLATE_FAILED', 'Failed to get template');
    return { success: true, data: (await res.json()) as unknown };
  },
  async importPricingConfig(data: unknown): Promise<ApiResult<PricingImportResult>> {
    return fetchApi<PricingImportResult>('/api/v1/admin/pricing/import-config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async clearPricingConfig(): Promise<ApiResult<PricingClearResult>> {
    return fetchApi<PricingClearResult>('/api/v1/admin/pricing/clear-config', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  // Admin — Test Runner
  async runPricingTests(file?: File): Promise<ApiResult<PricingTestResult[]>> {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      return fetchApi<PricingTestResult[]>('/api/v1/admin/pricing/run-tests', { method: 'POST', body: formData });
    }
    return fetchApi<PricingTestResult[]>('/api/v1/admin/pricing/run-tests', { method: 'POST' });
  },

  // ── Signals ──────────────────────────────────────────────────────────────

  async logPageViewSignal(page: string, resourceId?: string): Promise<void> {
    await fetch('/api/v1/signals/page-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, resourceId }),
    }).catch(() => {});
  },

  async logChatEngagementSignal(threadId: string, dwellSeconds: number): Promise<void> {
    await fetch('/api/v1/signals/chat-engagement', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId, dwellSeconds }),
    }).catch(() => {});
  },

  async getSignalAnalytics(params?: { from?: string; to?: string }): Promise<ApiResult<SignalAnalyticsSummary>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    const qs = query.toString();
    return fetchApi<SignalAnalyticsSummary>(`/api/v1/admin/signals/analytics${qs ? `?${qs}` : ''}`);
  },

  async getSignalUsers(params?: { from?: string; to?: string; page?: number; limit?: number; sortBy?: string; sortDir?: string }): Promise<ApiResult<SignalUserList>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return fetchApi<SignalUserList>(`/api/v1/admin/signals/users${qs ? `?${qs}` : ''}`);
  },

  async getSignalAllEvents(params?: { from?: string; to?: string; page?: number; limit?: number; type?: string; userSearch?: string; sortBy?: string; sortDir?: string }): Promise<ApiResult<SignalAllEventList>> {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.type) query.set('type', params.type);
    if (params?.userSearch) query.set('userSearch', params.userSearch);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return fetchApi<SignalAllEventList>(`/api/v1/admin/signals/events${qs ? `?${qs}` : ''}`);
  },

  async getSignalUserEvents(userId: string, params?: { page?: number; limit?: number }): Promise<ApiResult<SignalUserEventList>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchApi<SignalUserEventList>(`/api/v1/admin/signals/users/${encodeURIComponent(userId)}/events${qs ? `?${qs}` : ''}`);
  },

  // ── Persona Chat ────────────────────────────────────────────────────────────

  async getChatInit(): Promise<ApiResult<ChatInitData>> {
    return fetchApi<ChatInitData>('/api/v1/chat/init');
  },

  async fillTaskFields(text: string, fields: PersonaFieldDefinition[]): Promise<ApiResult<{ values: Record<string, unknown> }>> {
    return fetchApi<{ values: Record<string, unknown> }>('/api/v1/chat/fill-fields', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, fields }),
    });
  },

  async listMyPersonaTasks(): Promise<ApiResult<UserPersonaTaskSummary[]>> {
    return fetchApi<UserPersonaTaskSummary[]>('/api/v1/chat/my-tasks');
  },

  async getMyPersonaTask(taskId: string): Promise<ApiResult<UserPersonaTaskSummary>> {
    return fetchApi<UserPersonaTaskSummary>(`/api/v1/chat/my-tasks/${taskId}`);
  },

  async createMyPersonaTask(input: UserPersonaTaskCreate): Promise<ApiResult<UserPersonaTaskSummary>> {
    return fetchApi<UserPersonaTaskSummary>('/api/v1/chat/my-tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async updateMyPersonaTask(taskId: string, input: UserPersonaTaskCreate): Promise<ApiResult<UserPersonaTaskSummary>> {
    return fetchApi<UserPersonaTaskSummary>(`/api/v1/chat/my-tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async deleteMyPersonaTask(taskId: string): Promise<void> {
    await fetch(`/api/v1/chat/my-tasks/${taskId}`, { method: 'DELETE' });
  },

  async listAdminPersonas(): Promise<ApiResult<PersonaSummary[]>> {
    return fetchApi<PersonaSummary[]>('/api/v1/admin/personas');
  },

  async getAdminPersona(id: string): Promise<ApiResult<PersonaSummary>> {
    return fetchApi<PersonaSummary>(`/api/v1/admin/personas/${id}`);
  },

  async createAdminPersona(input: PersonaCreate): Promise<ApiResult<PersonaSummary>> {
    return fetchApi<PersonaSummary>('/api/v1/admin/personas', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async updateAdminPersona(id: string, input: PersonaCreate): Promise<ApiResult<PersonaSummary>> {
    return fetchApi<PersonaSummary>(`/api/v1/admin/personas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async deleteAdminPersona(id: string): Promise<void> {
    await fetch(`/api/v1/admin/personas/${id}`, { method: 'DELETE' });
  },

  async clearAllAdminPersonaData(): Promise<void> {
    await fetch('/api/v1/admin/personas', { method: 'DELETE' });
  },

  // Global task library
  async listAdminTasks(): Promise<ApiResult<AdminTaskSummary[]>> {
    return fetchApi<AdminTaskSummary[]>('/api/v1/admin/tasks');
  },

  async getAdminTask(id: string): Promise<ApiResult<AdminTaskDetail>> {
    return fetchApi<AdminTaskDetail>(`/api/v1/admin/tasks/${id}`);
  },

  async createAdminTask(input: PersonaTaskCreate): Promise<ApiResult<AdminTaskDetail>> {
    return fetchApi<AdminTaskDetail>('/api/v1/admin/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async updateAdminTask(id: string, input: PersonaTaskCreate): Promise<ApiResult<AdminTaskDetail>> {
    return fetchApi<AdminTaskDetail>(`/api/v1/admin/tasks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async deleteAdminTask(id: string): Promise<void> {
    await fetch(`/api/v1/admin/tasks/${id}`, { method: 'DELETE' });
  },

  // Persona ↔ Task junction
  async assignAdminPersonaTask(personaId: string, taskId: string, sortOrder: number): Promise<ApiResult<PersonaSummary>> {
    return fetchApi<PersonaSummary>(`/api/v1/admin/personas/${personaId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId, sortOrder }),
    });
  },

  async updateAdminPersonaTaskSortOrder(personaId: string, taskId: string, sortOrder: number): Promise<ApiResult<AdminTaskDetail>> {
    return fetchApi<AdminTaskDetail>(`/api/v1/admin/personas/${personaId}/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sortOrder }),
    });
  },

  async removeAdminPersonaTask(personaId: string, taskId: string): Promise<void> {
    await fetch(`/api/v1/admin/personas/${personaId}/tasks/${taskId}`, { method: 'DELETE' });
  },

  async exportAdminPersonas(): Promise<void> {
    const resp = await fetch('/api/v1/admin/personas/export');
    if (!resp.ok) return;
    const date = new Date().toISOString().slice(0, 10);
    downloadBlob(`personas-export-${date}.yaml`, await resp.blob());
  },

  // The uploaded file is posted verbatim so the server owns YAML parsing and
  // reports syntax errors with line/column detail. YAML is a superset of JSON,
  // so an older .json export still imports through the same path.
  async previewAdminPersonaImport(yaml: string): Promise<ApiResult<PersonaImportPreview>> {
    return fetchApi<PersonaImportPreview>('/api/v1/admin/personas/import/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/yaml' },
      body: yaml,
    });
  },

  async importAdminPersonas(yaml: string): Promise<ApiResult<PersonaImportPreview>> {
    return fetchApi<PersonaImportPreview>('/api/v1/admin/personas/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/yaml' },
      body: yaml,
    });
  },

  async listAdminUserPersonaTasks(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResult<AdminUserPersonaTaskList>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<AdminUserPersonaTaskList>(`/api/v1/admin/user-tasks${qs ? `?${qs}` : ''}`);
  },

  async promoteUserPersonaTask(userTaskId: string, targetPersonaId: string): Promise<ApiResult<AdminTaskDetail>> {
    return fetchApi<AdminTaskDetail>(`/api/v1/admin/user-tasks/${userTaskId}/promote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetPersonaId }),
    });
  },

  async getAdminUserPersonaTask(id: string): Promise<ApiResult<UserPersonaTaskSummary>> {
    return fetchApi<UserPersonaTaskSummary>(`/api/v1/admin/user-tasks/${id}`);
  },

  async updateAdminUserPersonaTask(id: string, input: UserPersonaTaskCreate): Promise<ApiResult<UserPersonaTaskSummary>> {
    return fetchApi<UserPersonaTaskSummary>(`/api/v1/admin/user-tasks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  async deleteAdminUserPersonaTask(id: string): Promise<void> {
    await fetch(`/api/v1/admin/user-tasks/${id}`, { method: 'DELETE' });
  },

  async listAdminAllToolCategories(): Promise<ApiResult<string[]>> {
    return fetchApi<string[]>('/api/v1/admin/user-tasks/tool-categories');
  },

  // ── A2A Configs ────────────────────────────────────────────────────────────
  async listA2aConfigs(): Promise<ApiResult<A2aConfigSummary[]>> {
    return fetchApi<A2aConfigSummary[]>('/api/v1/admin/a2a/configs');
  },

  async createA2aConfig(data: A2aConfigCreateInput): Promise<ApiResult<A2aConfigDetail>> {
    return fetchApi<A2aConfigDetail>('/api/v1/admin/a2a/configs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async getA2aConfig(id: string): Promise<ApiResult<A2aConfigDetail>> {
    return fetchApi<A2aConfigDetail>(`/api/v1/admin/a2a/configs/${id}`);
  },

  async updateA2aConfig(id: string, data: A2aConfigUpdateInput): Promise<ApiResult<A2aConfigDetail>> {
    return fetchApi<A2aConfigDetail>(`/api/v1/admin/a2a/configs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async deleteA2aConfig(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/a2a/configs/${id}`, { method: 'DELETE' });
  },

  async rotateA2aKey(configId: string): Promise<ApiResult<A2aKeyRotateResult>> {
    return fetchApi<A2aKeyRotateResult>(`/api/v1/admin/a2a/configs/${configId}/keys`, { method: 'POST' });
  },

  async revokeA2aKey(configId: string, keyId: string): Promise<ApiResult<{ revoked: boolean }>> {
    return fetchApi<{ revoked: boolean }>(`/api/v1/admin/a2a/configs/${configId}/keys/${keyId}`, { method: 'DELETE' });
  },

  async getA2aAnalytics(configId: string): Promise<ApiResult<A2aAnalytics>> {
    return fetchApi<A2aAnalytics>(`/api/v1/admin/a2a/configs/${configId}/analytics`);
  },

  // ---- Support ticket numbering ----
  async getSupportTicketNumber(): Promise<ApiResult<TicketNumberInfo>> {
    return fetchApi<TicketNumberInfo>('/api/v1/admin/support-tickets/number');
  },

  async setSupportTicketNumberSeed(seed: string): Promise<ApiResult<TicketNumberInfo>> {
    return fetchApi<TicketNumberInfo>('/api/v1/admin/support-tickets/number', {
      method: 'PUT',
      body: JSON.stringify({ seed }),
    });
  },

  // ---- Support Lab ----
  // Reads and writes go to /api/v1/support-lab with the ordinary session cookie.
  // The lab's API key never reaches the browser; the server injects it when it
  // dispatches into the public support routes.

  async getSupportLabReadiness(): Promise<ApiResult<SupportLabReadiness>> {
    return fetchApi<SupportLabReadiness>('/api/v1/support-lab/readiness');
  },

  async getSupportLabTargets(): Promise<ApiResult<SupportLabTarget[]>> {
    return fetchApi<SupportLabTarget[]>('/api/v1/support-lab/targets');
  },

  async getSupportLabVerdictOptions(): Promise<ApiResult<SupportLabVerdictOption[]>> {
    return fetchApi<SupportLabVerdictOption[]>('/api/v1/support-lab/verdict-options');
  },

  /** The lab's own run list — who ran what. Not the client's ticket list. */
  async listSupportLabTickets(params: SupportLabListParams = {}): Promise<ApiResult<SupportLabTicketListResponse>> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.sort) qs.set('sort', params.sort);
    if (params.dir) qs.set('dir', params.dir);
    if (params.createdById) qs.set('createdById', params.createdById);
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.tenantId) qs.set('tenantId', params.tenantId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<SupportLabTicketListResponse>(`/api/v1/support-lab/tickets${suffix}`);
  },

  /** Who has runs, for the list's "whose runs?" filter. Spans every page. */
  async listSupportLabRunCreators(
    params: SupportLabRunCreatorParams = {},
  ): Promise<ApiResult<SupportLabRunCreator[]>> {
    const qs = new URLSearchParams();
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.tenantId) qs.set('tenantId', params.tenantId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<SupportLabRunCreator[]>(`/api/v1/support-lab/tickets/creators${suffix}`);
  },

  /**
   * The mirrored Salesforce cases, each annotated with the lab target it
   * resolves to. Searches the case body as well as its headline fields — the
   * picker is used to hunt for a case *about* something.
   */
  async listSupportLabSalesforceCases(
    params: { q?: string; page?: number; limit?: number } = {},
  ): Promise<ApiResult<SupportLabCaseListResponse>> {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<SupportLabCaseListResponse>(`/api/v1/support-lab/salesforce-cases${suffix}`);
  },

  async getSupportLabSalesforceCasePrefill(caseId: string): Promise<ApiResult<SupportLabCasePrefill>> {
    return fetchApi<SupportLabCasePrefill>(
      `/api/v1/support-lab/salesforce-cases/${encodeURIComponent(caseId)}/prefill`,
    );
  },

  async getSupportLabPipeline(ticketId: string): Promise<ApiResult<SupportLabPipelineState>> {
    return fetchApi<SupportLabPipelineState>(
      `/api/v1/support-lab/tickets/${encodeURIComponent(ticketId)}/pipeline`,
    );
  },

  async listSupportLabTurns(ticketId: string): Promise<ApiResult<SupportLabTurn[]>> {
    return fetchApi<SupportLabTurn[]>(`/api/v1/support-lab/tickets/${encodeURIComponent(ticketId)}/turns`);
  },

  async listSupportLabPersonas(): Promise<ApiResult<SupportLabPersona[]>> {
    return fetchApi<SupportLabPersona[]>('/api/v1/support-lab/personas');
  },

  /** Resolves (creating on first use) the persona this operator acts as on a tenant. */
  async getSupportLabCurrentPersona(tenantId: string): Promise<ApiResult<SupportLabPersona>> {
    return fetchApi<SupportLabPersona>(
      `/api/v1/support-lab/personas/current?tenantId=${encodeURIComponent(tenantId)}`,
    );
  },

  async renameSupportLabPersona(id: string, displayName: string): Promise<ApiResult<SupportLabPersona>> {
    return fetchApi<SupportLabPersona>(`/api/v1/support-lab/personas/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ displayName }),
    });
  },

  /** Where this run lives in HubSpot. `url` and `reason` are exclusive. */
  async getSupportLabHubSpotLink(ticketId: string): Promise<ApiResult<SupportLabHubSpotLink>> {
    return fetchApi<SupportLabHubSpotLink>(
      `/api/v1/support-lab/tickets/${encodeURIComponent(ticketId)}/hubspot-link`,
    );
  },

  async listSupportLabVerdicts(ticketId: string): Promise<ApiResult<SupportLabVerdict[]>> {
    return fetchApi<SupportLabVerdict[]>(`/api/v1/support-lab/tickets/${encodeURIComponent(ticketId)}/verdicts`);
  },

  /** Exactly one of messageId / turnId — a sent reply, or a suppressed draft. */
  async recordSupportLabVerdict(
    ticketId: string,
    input: {
      messageId?: string;
      turnId?: string;
      verdict: string;
      correctedText?: string | null;
      note?: string | null;
    },
  ): Promise<ApiResult<SupportLabVerdict>> {
    return fetchApi<SupportLabVerdict>(`/api/v1/support-lab/tickets/${encodeURIComponent(ticketId)}/verdicts`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // ---- The portal: the client's own eleven routes, proxied ----
  // Each mirrors a /public/api/v1/support path one-for-one. The lab frame calls
  // these and nothing else, so what the frame exercises is what a client
  // exercises — including the failure modes.

  /**
   * Categories and client-reported severities, from the support API's own
   * reference data rather than a copy. Needs a tenant + dev account because the
   * public endpoint is actor-scoped — a hardcoded copy drifts from the registry
   * and is rejected at create time with nothing but "Invalid input" to go on.
   */
  async getSupportLabReferenceData(
    tenantId: string,
    devAccountId: string,
  ): Promise<ApiResult<SupportReferenceData>> {
    const qs = new URLSearchParams({ tenantId, devAccountId });
    return fetchApi<SupportReferenceData>(`/api/v1/support-lab/portal/reference-data?${qs.toString()}`);
  },

  /**
   * The client's ticket list for this target.
   *
   * Scoped by dev account, so it returns every lab ticket on it whoever opened
   * it — which is the real portal's semantic, where a dev account is a
   * customer's team and you see colleagues' cases beside your own.
   */
  async listSupportLabPortalTickets(
    tenantId: string,
    devAccountId: string,
    params: { page?: number; limit?: number } = {},
  ): Promise<ApiResult<SupportTicketListResponse>> {
    const qs = new URLSearchParams({ tenantId, devAccountId });
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi<SupportTicketListResponse>(`/api/v1/support-lab/portal/tickets?${qs.toString()}`);
  },

  async getSupportLabPortalChanges(
    tenantId: string,
    devAccountId: string,
    since?: string,
  ): Promise<ApiResult<SupportTicketChanges>> {
    const qs = new URLSearchParams({ tenantId, devAccountId });
    if (since) qs.set('since', since);
    return fetchApi<SupportTicketChanges>(`/api/v1/support-lab/portal/tickets/changes?${qs.toString()}`);
  },

  /** `idempotencyKey` is the draft's, resent on a retry so the retry is the same write. */
  async createSupportLabTicket(
    input: SupportLabCreateTicketInput,
    idempotencyKey?: string,
  ): Promise<ApiResult<{ ticketId: string }>> {
    return fetchApi<{ ticketId: string }>('/api/v1/support-lab/portal/tickets', {
      method: 'POST',
      body: JSON.stringify(input),
      ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    });
  },

  async getSupportLabTicket(ticketId: string): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(`/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}`);
  },

  async replySupportLabTicket(
    ticketId: string,
    body: string,
    attachmentIds?: string[],
    idempotencyKey?: string,
  ): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(attachmentIds?.length ? { body, attachmentIds } : { body }),
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
      },
    );
  },

  async setSupportLabResolution(ticketId: string, confirmed: boolean): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/resolution`,
      { method: 'POST', body: JSON.stringify({ confirmed }) },
    );
  },

  /** Contract 4.0: the client closes their own ticket, with an optional reason. */
  async closeSupportLabTicket(ticketId: string, reason?: string): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/close`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    );
  },

  /** Contract 4.0: the client escalates their ticket, once, saying why. */
  async escalateSupportLabTicket(ticketId: string, reason: string): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/escalate`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    );
  },

  async setSupportLabPriority(ticketId: string, priority: string): Promise<ApiResult<SupportTicketDetail>> {
    return fetchApi<SupportTicketDetail>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/priority`,
      { method: 'POST', body: JSON.stringify({ priority }) },
    );
  },

  async markSupportLabTicketRead(
    ticketId: string,
  ): Promise<ApiResult<{ ticketId: string; unread: boolean }>> {
    return fetchApi<{ ticketId: string; unread: boolean }>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/read`,
      { method: 'POST' },
    );
  },

  /**
   * `target` is required for `ticketId: 'new'` (an intake upload, before the ticket
   * exists): the server has no run to take the tenant and persona from yet.
   */
  async getSupportLabUploadTarget(
    ticketId: string,
    file: { fileName: string; mimeType: string; size: number },
    target?: { tenantId: string; devAccountId: string },
  ): Promise<ApiResult<SupportUploadTarget>> {
    const query = target
      ? `?${new URLSearchParams({ tenantId: target.tenantId, devAccountId: target.devAccountId }).toString()}`
      : '';
    return fetchApi<SupportUploadTarget>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/attachments/upload-target${query}`,
      { method: 'POST', body: JSON.stringify(file) },
    );
  },

  /**
   * Signed download URL. Scoped through the owning ticket, which is the only
   * handle the lab has on which run — and so which persona — an attachment
   * belongs to. Fails with 403 while the file is still being scanned.
   */
  async getSupportLabAttachmentDownload(
    ticketId: string,
    attachmentId: string,
  ): Promise<ApiResult<SupportAttachmentDownload>> {
    return fetchApi<SupportAttachmentDownload>(
      `/api/v1/support-lab/portal/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}/download`,
    );
  },

  async getSupportLabConfig(): Promise<ApiResult<SupportLabConfigResponse>> {
    return fetchApi<SupportLabConfigResponse>('/api/v1/support-lab/config');
  },

  async updateSupportLabConfig(input: {
    apiKey?: string | null;
    personaDomain?: string | null;
  }): Promise<ApiResult<SupportLabConfigResponse>> {
    return fetchApi<SupportLabConfigResponse>('/api/v1/support-lab/config', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  // ---- Support Tickets API key management ----
  async listSupportKeys(): Promise<ApiResult<SupportKeyInfo[]>> {
    return fetchApi<SupportKeyInfo[]>('/api/v1/admin/support-tickets/keys');
  },

  async createSupportKey(name: string, isTest = false): Promise<ApiResult<SupportKeyCreateResult>> {
    return fetchApi<SupportKeyCreateResult>('/api/v1/admin/support-tickets/keys', {
      method: 'POST',
      body: JSON.stringify({ name, isTest }),
    });
  },

  async revokeSupportKey(keyId: string): Promise<ApiResult<{ id: string; revoked: boolean }>> {
    return fetchApi<{ id: string; revoked: boolean }>(`/api/v1/admin/support-tickets/keys/${keyId}/revoke`, {
      method: 'POST',
    });
  },

  downloadSupportOpenApiSpec(): Promise<Response> {
    return fetch('/api/v1/admin/support-tickets/openapi/download', { credentials: 'include' });
  },

  async listSupportTickets(params: SupportTicketListParams = {}): Promise<ApiResult<AdminSupportTicketListResponse>> {
    const qs = new URLSearchParams();
    if (params.stage) qs.set('stage', params.stage);
    if (params.devAccountId) qs.set('devAccountId', params.devAccountId);
    if (params.q) qs.set('q', params.q);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    // Sent as the literal string the server's enum expects. Omitted = both.
    if (params.isTest !== undefined) qs.set('isTest', params.isTest ? 'true' : 'false');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<AdminSupportTicketListResponse>(`/api/v1/admin/support-tickets/tickets${suffix}`);
  },

  async getSupportTicket(id: string): Promise<ApiResult<AdminSupportTicketDetail>> {
    return fetchApi<AdminSupportTicketDetail>(`/api/v1/admin/support-tickets/tickets/${encodeURIComponent(id)}`);
  },

  // ---- PS draft approval (decision 3) ----
  async getSupportDesk(): Promise<ApiResult<SupportDeskSummary>> {
    return fetchApi<SupportDeskSummary>('/api/v1/admin/support-tickets/desk');
  },

  async activateSalesforceDesk(): Promise<ApiResult<SupportDeskSummary>> {
    return fetchApi<SupportDeskSummary>('/api/v1/admin/support-tickets/desk/salesforce/activate', { method: 'POST' });
  },

  /** Where Support Lab-key tickets go; null follows the main desk. */
  async setTestSupportDesk(provider: 'hub-db' | 'hubspot' | 'salesforce' | null): Promise<ApiResult<SupportDeskSummary>> {
    return fetchApi<SupportDeskSummary>('/api/v1/admin/support-tickets/desk/test', {
      method: 'PUT',
      body: JSON.stringify({ provider }),
    });
  },

  async listSupportDraftQueue(): Promise<ApiResult<SupportDraftQueueRow[]>> {
    return fetchApi<SupportDraftQueueRow[]>('/api/v1/admin/support-tickets/drafts');
  },

  async approveSupportDraft(ticketId: string, text?: string): Promise<ApiResult<{ approved: true }>> {
    return fetchApi<{ approved: true }>(
      `/api/v1/admin/support-tickets/tickets/${encodeURIComponent(ticketId)}/draft/approve`,
      { method: 'POST', body: JSON.stringify(text ? { text } : {}) },
    );
  },

  async rejectSupportDraft(ticketId: string, reason?: string): Promise<ApiResult<{ rejected: true }>> {
    return fetchApi<{ rejected: true }>(
      `/api/v1/admin/support-tickets/tickets/${encodeURIComponent(ticketId)}/draft/reject`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    );
  },

  async getSupportReferenceData(): Promise<ApiResult<SupportReferenceData>> {
    return fetchApi<SupportReferenceData>('/api/v1/admin/support-tickets/reference-data');
  },

  // ---- Support automation workflows (rules / actions / invocations) ----
  async listSupportRules(): Promise<ApiResult<SupportRuleCatalogEntry[]>> {
    return fetchApi<SupportRuleCatalogEntry[]>('/api/v1/admin/support-tickets/workflows/rules');
  },

  async updateSupportRule(
    ruleId: string,
    body: { enabled?: boolean; params?: Record<string, unknown> },
  ): Promise<ApiResult<SupportRuleCatalogEntry>> {
    return fetchApi<SupportRuleCatalogEntry>(`/api/v1/admin/support-tickets/workflows/rules/${encodeURIComponent(ruleId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async listSupportActions(): Promise<ApiResult<SupportActionCatalogEntry[]>> {
    return fetchApi<SupportActionCatalogEntry[]>('/api/v1/admin/support-tickets/workflows/actions');
  },

  async getSupportVocabularies(): Promise<ApiResult<SupportVocabularies>> {
    return fetchApi<SupportVocabularies>('/api/v1/admin/support-tickets/vocabularies');
  },

  async listSupportActionInvocations(
    params: SupportInvocationListParams = {},
  ): Promise<ApiResult<SupportActionInvocationList>> {
    const qs = new URLSearchParams();
    if (params.page != null) qs.set('page', String(params.page));
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.ticketId) qs.set('ticketId', params.ticketId);
    if (params.ruleId) qs.set('ruleId', params.ruleId);
    if (params.actionId) qs.set('actionId', params.actionId);
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<SupportActionInvocationList>(`/api/v1/admin/support-tickets/workflows/invocations${suffix}`);
  },

  async listSupportPortalChats(params: SupportPortalChatListParams = {}): Promise<ApiResult<SupportPortalChatList>> {
    const qs = new URLSearchParams();
    if (params.page != null) qs.set('page', String(params.page));
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<SupportPortalChatList>(`/api/v1/admin/support-tickets/chats${suffix}`);
  },

  // Kibo Verification Tenants
  async getKiboTenants(): Promise<ApiResult<KiboVerificationTenant[]>> {
    return fetchApi<KiboVerificationTenant[]>('/api/v1/admin/kibo/tenants');
  },

  async createKiboTenant(data: { credentialSetId: string; baseUrl?: string; displayOrder?: number }): Promise<ApiResult<KiboVerificationTenant>> {
    return fetchApi<KiboVerificationTenant>('/api/v1/admin/kibo/tenants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async updateKiboTenant(id: string, data: { displayName?: string; baseUrl?: string; enabled?: boolean; notes?: string; notesMode?: 'append' | 'replace'; displayOrder?: number }): Promise<ApiResult<KiboVerificationTenant>> {
    return fetchApi<KiboVerificationTenant>(`/api/v1/admin/kibo/tenants/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async deleteKiboTenant(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/admin/kibo/tenants/${id}`, { method: 'DELETE' });
  },

  async getKiboTenantNotesHistory(id: string): Promise<ApiResult<KiboTenantNotesRevision[]>> {
    return fetchApi<KiboTenantNotesRevision[]>(`/api/v1/admin/kibo/tenants/${id}/notes-history`);
  },

  // ===== Fireworks Proxy (Kibo AI Gateway) =====

  // Self-serve
  async getFireworksProxyKeys(): Promise<ApiResult<FireworksProxyKeySummary[]>> {
    return fetchApi<FireworksProxyKeySummary[]>('/api/v1/fireworks-proxy/keys');
  },
  async createFireworksProxyKey(name: string): Promise<ApiResult<FireworksProxyKeyCreated>> {
    return fetchApi<FireworksProxyKeyCreated>('/api/v1/fireworks-proxy/keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },
  async revokeFireworksProxyKey(id: string): Promise<ApiResult<{ id: string; revoked: boolean }>> {
    return fetchApi<{ id: string; revoked: boolean }>(`/api/v1/fireworks-proxy/keys/${id}`, { method: 'DELETE' });
  },
  async getFireworksProxyMyUsage(): Promise<ApiResult<FireworksUserUsageSummary>> {
    return fetchApi<FireworksUserUsageSummary>('/api/v1/fireworks-proxy/me/usage');
  },

  // Admin / managed role
  async getFireworksProxyConfig(): Promise<ApiResult<FireworksProxyConfigResponse>> {
    return fetchApi<FireworksProxyConfigResponse>('/api/v1/admin/fireworks-proxy/config');
  },
  async updateFireworksProxyConfig(credentialSetId: string | null): Promise<ApiResult<FireworksProxyConfigResponse>> {
    return fetchApi<FireworksProxyConfigResponse>('/api/v1/admin/fireworks-proxy/config', {
      method: 'PUT',
      body: JSON.stringify({ credentialSetId }),
    });
  },
  async getFireworksProxyBudgetConfig(): Promise<ApiResult<FireworksBudgetConfigResponse>> {
    return fetchApi<FireworksBudgetConfigResponse>('/api/v1/admin/fireworks-proxy/budget');
  },
  async updateFireworksProxyBudgetConfig(
    input: { defaultMonthlyBudgetUsd?: number; enforce?: boolean },
  ): Promise<ApiResult<FireworksBudgetConfigResponse>> {
    return fetchApi<FireworksBudgetConfigResponse>('/api/v1/admin/fireworks-proxy/budget', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  async getFireworksProxyUserBudgets(month?: string): Promise<ApiResult<FireworksUserBudgetRow[]>> {
    const q = month ? `?month=${encodeURIComponent(month)}` : '';
    return fetchApi<FireworksUserBudgetRow[]>(`/api/v1/admin/fireworks-proxy/budgets${q}`);
  },
  async setFireworksProxyUserBudget(
    userId: string,
    monthlyBudgetUsd: number,
    note?: string,
  ): Promise<ApiResult<{ userId: string; updated: boolean }>> {
    return fetchApi<{ userId: string; updated: boolean }>(`/api/v1/admin/fireworks-proxy/budgets/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ monthlyBudgetUsd, note }),
    });
  },
  async clearFireworksProxyUserBudget(userId: string): Promise<ApiResult<{ userId: string; cleared: boolean }>> {
    return fetchApi<{ userId: string; cleared: boolean }>(`/api/v1/admin/fireworks-proxy/budgets/${userId}`, {
      method: 'DELETE',
    });
  },
  async getFireworksProxyModels(): Promise<ApiResult<FireworksProxyModelResponse[]>> {
    return fetchApi<FireworksProxyModelResponse[]>('/api/v1/admin/fireworks-proxy/models');
  },
  async createFireworksProxyModel(input: FireworksProxyModelInput): Promise<ApiResult<FireworksProxyModelResponse>> {
    return fetchApi<FireworksProxyModelResponse>('/api/v1/admin/fireworks-proxy/models', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  async updateFireworksProxyModel(
    id: string,
    input: Partial<Omit<FireworksProxyModelInput, 'modelPath'>>,
  ): Promise<ApiResult<FireworksProxyModelResponse>> {
    return fetchApi<FireworksProxyModelResponse>(`/api/v1/admin/fireworks-proxy/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  async deleteFireworksProxyModel(id: string): Promise<ApiResult<{ id: string; deleted: boolean }>> {
    return fetchApi<{ id: string; deleted: boolean }>(`/api/v1/admin/fireworks-proxy/models/${id}`, {
      method: 'DELETE',
    });
  },
  async getFireworksProxyUsage(
    params: { month?: string; page?: number; limit?: number } = {},
  ): Promise<ApiResult<{ items: FireworksProxyUsageEvent[]; pagination: { total: number; page: number; limit: number; totalPages: number } }>> {
    const qs = new URLSearchParams();
    if (params.month) qs.set('month', params.month);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return fetchApi(`/api/v1/admin/fireworks-proxy/usage${q ? `?${q}` : ''}`);
  },

  // LinkedIn Post Generator
  async getLinkedInAssets(params?: { search?: string; pillar?: string; product?: string; tier?: number; model?: string; limit?: number }): Promise<ApiResult<LinkedInAsset[]>> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.pillar) qs.set('pillar', params.pillar);
    if (params?.product) qs.set('product', params.product);
    if (params?.tier) qs.set('tier', String(params.tier));
    if (params?.model) qs.set('model', params.model);
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<LinkedInAsset[]>(`/api/v1/linkedin-post-generator/assets${suffix}`);
  },

  async getRandomLinkedInAsset(): Promise<ApiResult<LinkedInAsset>> {
    return fetchApi<LinkedInAsset>('/api/v1/linkedin-post-generator/assets/random');
  },

  // Streams 3 variants as newline-delimited JSON. Returns the parsed variants
  // and an async iterator over partial results as each variant lands.
  generateLinkedInPosts(
    body: { product?: string; tone?: string; targetAudience?: string; seoKeywords?: string; authorContext?: string; assetId?: string },
    onVariant: (variant: LinkedInVariant) => void,
    onVariantError?: (error: { variantIndex: number; tone: string; error: string }) => void,
  ): { done: Promise<void> } {
    const done = (async (): Promise<void> => {
      const res = await fetch('/api/v1/linkedin-post-generator/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      // The stream emits one object per line: either a LinkedInVariant or a
      // LinkedInVariantError marker. Both are surfaced to the caller via the
      // matching callback; the caller decides how to render failures.
      await readNdjsonStream<LinkedInVariant | { variantIndex: number; tone: string; error: string }>(
        res,
        (parsed) => {
          if ('error' in parsed) onVariantError?.(parsed);
          else onVariant(parsed);
        },
      );
    })();
    return { done };
  },

  async getLinkedInHistory(): Promise<ApiResult<LinkedInPost[]>> {
    return fetchApi<LinkedInPost[]>('/api/v1/linkedin-post-generator/history');
  },

  async markLinkedInPostPosted(postId: string): Promise<ApiResult<{ ok: true }>> {
    return fetchApi<{ ok: true }>(`/api/v1/linkedin-post-generator/posts/${postId}/posted`, { method: 'POST' });
  },

  async submitLinkedInFeedback(postId: string, humanityScore: number, note?: string): Promise<ApiResult<{ ok: true }>> {
    return fetchApi<{ ok: true }>(`/api/v1/linkedin-post-generator/posts/${postId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ humanityScore, note }),
    });
  },

  async getLinkedInConfig(): Promise<ApiResult<LinkedInGeneratorConfig>> {
    return fetchApi<LinkedInGeneratorConfig>('/api/v1/admin/linkedin-post-generator/config');
  },

  async updateLinkedInConfig(data: { bannedPhrases: string[]; bannedPatterns: string[]; guidancePrompt: string; defaultTones: string[]; hotNowBoosts: string[] }): Promise<ApiResult<LinkedInGeneratorConfig>> {
    return fetchApi<LinkedInGeneratorConfig>('/api/v1/admin/linkedin-post-generator/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getLinkedInFeedback(params?: { includeDiscarded?: boolean; limit?: number }): Promise<ApiResult<LinkedInFeedbackRow[]>> {
    const qs = new URLSearchParams();
    if (params?.includeDiscarded) qs.set('includeDiscarded', 'true');
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return fetchApi<LinkedInFeedbackRow[]>(`/api/v1/admin/linkedin-post-generator/config/feedback${suffix}`);
  },

  async getLinkedInKnowledgeBase(): Promise<ApiResult<LinkedInKnowledgeBaseRule[]>> {
    return fetchApi<LinkedInKnowledgeBaseRule[]>('/api/v1/admin/linkedin-post-generator/config/knowledge-base');
  },

  async discardLinkedInFeedback(feedbackId: string): Promise<ApiResult<{ ok: true }>> {
    return fetchApi<{ ok: true }>(`/api/v1/admin/linkedin-post-generator/config/feedback/${feedbackId}/discard`, { method: 'POST' });
  },

  async restoreLinkedInFeedback(feedbackId: string): Promise<ApiResult<{ ok: true }>> {
    return fetchApi<{ ok: true }>(`/api/v1/admin/linkedin-post-generator/config/feedback/${feedbackId}/restore`, { method: 'POST' });
  },

  async deleteLinkedInKnowledgeBaseRule(ruleId: string): Promise<ApiResult<{ ok: true }>> {
    return fetchApi<{ ok: true }>(`/api/v1/admin/linkedin-post-generator/config/knowledge-base/${ruleId}`, { method: 'DELETE' });
  },

  async getLinkedInUsage(): Promise<ApiResult<LinkedInUsageRow[]>> {
    return fetchApi<LinkedInUsageRow[]>('/api/v1/admin/linkedin-post-generator/usage');
  },

  // ─── Ticket Deployments (BA-155) ────────────────────────────────────────────
  async getTicketDeploymentEvents(params?: {
    limit?: number;
    page?: number;
    environment?: string;
    kind?: string;
    gitRepo?: string;
    service?: string;
    search?: string;
    occurredFrom?: string;
    occurredTo?: string;
  }): Promise<ApiResult<TicketDeploymentEventListResult>> {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.page) q.set('page', String(params.page));
    if (params?.environment) q.set('environment', params.environment);
    if (params?.kind) q.set('kind', params.kind);
    if (params?.gitRepo) q.set('gitRepo', params.gitRepo);
    if (params?.service) q.set('service', params.service);
    if (params?.search) q.set('search', params.search);
    if (params?.occurredFrom) q.set('occurredFrom', params.occurredFrom);
    if (params?.occurredTo) q.set('occurredTo', params.occurredTo);
    const qs = q.toString();
    return fetchApi<TicketDeploymentEventListResult>(
      qs ? `/api/v1/ticket-deployments/events?${qs}` : '/api/v1/ticket-deployments/events',
    );
  },

  async getTicketDeploymentEvent(id: string): Promise<ApiResult<TicketDeploymentEventDetail>> {
    return fetchApi<TicketDeploymentEventDetail>(`/api/v1/ticket-deployments/events/${id}`);
  },

  // The containment views (database reads; nothing calls GitHub or Jira).
  async listTicketDeploymentTickets(params: {
    search?: string;
    environment?: string;
    sort?: 'lastChange' | 'key' | 'environments';
    direction?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
  } = {}): Promise<ApiResult<TicketDeploymentTicketPage>> {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') q.set(key, String(value));
    }
    const qs = q.toString();
    return fetchApi<TicketDeploymentTicketPage>(qs ? `/api/v1/ticket-deployments/tickets?${qs}` : '/api/v1/ticket-deployments/tickets');
  },

  async getTicketDeploymentTicket(key: string): Promise<ApiResult<TicketDeploymentTicketView>> {
    return fetchApi<TicketDeploymentTicketView>(`/api/v1/ticket-deployments/tickets/${encodeURIComponent(key)}`);
  },

  async getTicketDeploymentEnvironments(): Promise<ApiResult<TicketDeploymentEnvironmentGrid>> {
    return fetchApi<TicketDeploymentEnvironmentGrid>('/api/v1/ticket-deployments/environments');
  },

  // One base environment against any number of targets; none is the one-environment view.
  async compareTicketDeploymentEnvironments(base: string, targets: string[]): Promise<ApiResult<TicketDeploymentEnvironmentComparison>> {
    const q = new URLSearchParams({ base });
    if (targets.length > 0) q.set('targets', targets.join(','));
    return fetchApi<TicketDeploymentEnvironmentComparison>(`/api/v1/ticket-deployments/environments/compare?${q.toString()}`);
  },

  async getTicketDeploymentEventTransitions(id: string): Promise<ApiResult<TicketDeploymentEventTransitions>> {
    return fetchApi<TicketDeploymentEventTransitions>(`/api/v1/ticket-deployments/events/${id}/transitions`);
  },

  async getTicketDeploymentStatus(): Promise<ApiResult<TicketDeploymentStatus>> {
    return fetchApi<TicketDeploymentStatus>('/api/v1/admin/ticket-deployments/status');
  },

  // Resolve one event's tags again (key managers), after fixing config.
  async resolveTicketDeploymentEvent(id: string): Promise<ApiResult<TicketDeploymentResolutionSummary>> {
    return fetchApi<TicketDeploymentResolutionSummary>(`/api/v1/admin/ticket-deployments/events/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  // Rebuild every transition and service state from the ledger (key managers).
  async rebuildTicketDeploymentProjection(): Promise<ApiResult<TicketDeploymentProjectionRebuildResult>> {
    return fetchApi<TicketDeploymentProjectionRebuildResult>('/api/v1/admin/ticket-deployments/rebuild-projection', {
      method: 'POST',
    });
  },

  // Run the ticket-deployment sweep now (key managers): re-fold, follow gitops PRs,
  // resolve pending tags. Starts a Temporal workflow and returns at once.
  async resolvePendingTicketDeploymentTags(): Promise<ApiResult<{ started: boolean; workflowId: string }>> {
    return fetchApi<{ started: boolean; workflowId: string }>('/api/v1/admin/ticket-deployments/resolve-pending', {
      method: 'POST',
    });
  },

  async getTicketDeploymentKeys(): Promise<ApiResult<TicketDeploymentKeyInfo[]>> {
    return fetchApi<TicketDeploymentKeyInfo[]>('/api/v1/admin/ticket-deployments/keys');
  },

  async createTicketDeploymentKey(name: string): Promise<ApiResult<TicketDeploymentKeyCreateResult>> {
    return fetchApi<TicketDeploymentKeyCreateResult>('/api/v1/admin/ticket-deployments/keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async revokeTicketDeploymentKey(keyId: string): Promise<ApiResult<{ revoked: boolean }>> {
    return fetchApi<{ revoked: boolean }>(`/api/v1/admin/ticket-deployments/keys/${keyId}/revoke`, {
      method: 'POST',
    });
  },

  async getTicketDeploymentConfig(): Promise<ApiResult<TicketDeploymentToolConfig>> {
    return fetchApi<TicketDeploymentToolConfig>('/api/v1/admin/ticket-deployments/config');
  },

  async updateTicketDeploymentConfig(githubAppCredentialSetId: string | null): Promise<ApiResult<TicketDeploymentToolConfig>> {
    return fetchApi<TicketDeploymentToolConfig>('/api/v1/admin/ticket-deployments/config', {
      method: 'PUT',
      body: JSON.stringify({ githubAppCredentialSetId }),
    });
  },
};

// ============================================================================
// ACCOUNT DATABASE TYPES
// ============================================================================

// Organizations
export interface OrgSummary {
  id: string;
  name: string;
  orgType: 'Customer' | 'Partner' | 'Internal';
  status: 'Active' | 'Implementation' | 'Churned' | 'Prospect';
  hubspotCompanyId: string | null;
  parentOrgId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  _count: { accounts: number };
}

export interface OrgDetail {
  id: string;
  name: string;
  orgType: 'Customer' | 'Partner' | 'Internal';
  status: 'Active' | 'Implementation' | 'Churned' | 'Prospect';
  hubspotCompanyId: string | null;
  parentOrgId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  parentOrg: { id: string; name: string; orgType: string } | null;
  childOrgs: Array<{ id: string; name: string; orgType: string; status: string }>;
  accounts: Array<{ id: string; ref: string | null; hubspotAccountId: string | null; salesforceAccountId: string | null }>;
}

// Accounts
export type TicketSource = 'SalesforceOnly' | 'Migrating' | 'HubSpotOnly';
export type OrgStatus = 'Active' | 'Implementation' | 'Churned' | 'Prospect';

export interface AccountStatusHistoryInfo {
  id: string;
  status: OrgStatus;
  effectiveFrom: string;
  recordedAt: string;
  recordedByUserId: string;
  notes: string | null;
}

export type SlackChannelPurpose = 'AccountSupport' | 'Sales';

export interface SlackChannelInfo {
  id: string;
  channel: string;
  purpose: SlackChannelPurpose;
  createdAt: string;
}

export interface JiraLabelInfo {
  id: string;
  label: string;
  createdAt: string;
}

export interface JiraIssue {
  id: string;
  key: string;
  summary: string;
  status: string;
  statusCategory: string | null;
  priority: string | null;
  issueType: string | null;
  assignee: string | null;
  url: string | null;
}

/** The PS sub-tier a client is entitled to. Null means nobody has recorded one. */
export type SupportTier = 'Premium' | 'Regular';

export interface AccountSummary {
  id: string;
  ref: string | null;
  orgId: string;
  orgName: string;
  parentOrgName: string | null;
  hubspotAccountId: string | null;
  salesforceAccountId: string | null;
  ticketSource: TicketSource;
  supportTier: SupportTier | null;
  slackChannels: SlackChannelInfo[];
  jiraLabels: JiraLabelInfo[];
  createdAt: string;
  updatedAt: string;
  currentStatus: OrgStatus | null;
  deliveryPod: AccountDeliveryPodInfo | null;
  tenantCount: number;
  partners: Array<{ id: string; orgName: string }>;
}

export interface AccountDetail {
  id: string;
  ref: string | null;
  orgId: string;
  org: { id: string; name: string; orgType: string; status: string; parentOrgId: string | null; parentOrgName: string | null };
  name: string | null;
  logoUrl: string | null;
  description: string | null;
  hubspotAccountId: string | null;
  salesforceAccountId: string | null;
  ticketSource: TicketSource;
  supportTier: SupportTier | null;
  ticketMigrationValidatedAt: string | null;
  ticketMigrationValidatedById: string | null;
  deliveryPod: AccountDeliveryPodInfo | null;
  /** One entry per role shown on an account (director, CDM); empty when there is no pod. */
  deliveryLeads: AccountDeliveryLead[];
  slackChannels: SlackChannelInfo[];
  jiraLabels: JiraLabelInfo[];
  createdAt: string;
  updatedAt: string;
  currentStatus: OrgStatus | null;
  statusHistory: AccountStatusHistoryInfo[];
  partnerAccess: Array<{ id: string; partnerOrgId: string; partnerOrgName: string; grantedAt: string; revokedAt: string | null; notes: string | null }>;
  tenants: Array<{ id: string; kiboTenantId: number; displayName: string; tenantType: string; tenantRole: string; status: string; tenantPodName: string; tenantPodTenantUrlTemplate: string | null; notes: string | null; devAccountId: string | null }>;
  // One per Kibo environment the client runs in; each tenant's devAccountId is one of these.
  devAccounts: AccountDevAccount[];
}

export interface AccountListStats {
  total: number;
  active: number;
  implementation: number;
  withPartnerAccess: number;
}

export interface AccountListResponse {
  items: AccountSummary[];
  stats: AccountListStats;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface PartnerAccessInfo {
  id: string;
  partnerOrgId: string;
  partnerOrgName: string;
  grantedAt: string;
  revokedAt: string | null;
  notes: string | null;
}

export type ActivityEventType = 'status_changed' | 'tenant_added' | 'partner_revoked' | 'note_added';

export interface ActivityEvent {
  type: ActivityEventType;
  timestamp: string;
  title: string;
  meta: string;
  actorDisplayName: string | null;
}

export interface ActivityFeedResponse {
  events: ActivityEvent[];
  stats: {
    openTickets: number;
    activeTenants: number;
    partnersWithAccess: number;
    daysSinceLastNote: number | null;
  };
}

export interface NoteInfo {
  id: string;
  accountId: string;
  authorId: string;
  authorDisplayName: string;
  body: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

// Tenants
export type TenantType = 'EcommOnly' | 'OmsOnly' | 'EcommPlusOms' | 'CurbsideOnly';
export type TenantRole = 'Production' | 'Sandbox' | 'Staging' | 'Performance';
export type TenantStatus = 'Active' | 'Decommissioned' | 'Paused';

export interface TenantDomainInfo {
  id: string;
  tenantId: string;
  domain: string;
  domainType: 'SystemAssigned' | 'Internal' | 'Custom';
  cloudflareZoneId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantStatusHistoryInfo {
  id: string;
  status: TenantStatus;
  effectiveFrom: string;
  recordedAt: string;
  recordedByUserId: string;
  notes: string | null;
}

export interface TenantSummary {
  id: string;
  accountId: string;
  orgName: string;
  tenantPodId: string;
  tenantPodName: string;
  tenantPodTenantUrlTemplate: string | null;
  kiboTenantId: number;
  displayName: string;
  tenantType: TenantType;
  tenantRole: TenantRole;
  status: TenantStatus;
  mozuInstanceId: string | null;
  kubeNamespace: string | null;
  apiTransform: boolean;
  createdAt: string;
  updatedAt: string;
  primaryDomain: string | null;
  notes: string | null;
}

export interface TenantDetail {
  id: string;
  accountId: string;
  account: { id: string; orgId: string; orgName: string };
  devAccountId: string | null;
  devAccount: { id: string; kiboDevAccountId: string; label: string } | null;
  tenantPodId: string;
  tenantPod: { id: string; name: string; tenantPodType: string; status: string; tenantUrlTemplate: string | null; environment: { id: string; name: string; cloudProvider: string; region: string } | null };
  kiboTenantId: number;
  displayName: string;
  tenantType: TenantType;
  tenantRole: TenantRole;
  status: TenantStatus;
  mozuInstanceId: string | null;
  kubeNamespace: string | null;
  apiTransform: boolean;
  createdAt: string;
  updatedAt: string;
  notes: string | null;
  domains: TenantDomainInfo[];
  statusHistory: TenantStatusHistoryInfo[];
}

export interface TenantListStats {
  total: number;
  active: number;
  decommissioned: number;
  tenantPods: number;
}

export interface TenantListResponse {
  items: TenantSummary[];
  stats: TenantListStats;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface OrgListResponse {
  items: OrgSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ─── Account Database Admin Types ────────────────────────────────────────────

// ─── Delivery Pods ────────────────────────────────────────────────────────────
// Mirrors src/delivery-pods/delivery-pod-schemas.ts. Roles are fetched from
// /api/v1/delivery-pod-roles rather than duplicated here, so a retitle in the
// backend registry does not need a matching frontend edit.

export interface DeliveryPodRole {
  id: string;
  label: string;
}

export interface DeliveryPodMemberInfo {
  id: string;
  userId: string;
  userDisplayName: string;
  userEmail: string;
  role: string;
  roleLabel: string;
  createdAt: string;
}

export interface DeliveryPodSummary {
  id: string;
  name: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  accountCount: number;
}

export interface DeliveryPodDetail {
  id: string;
  name: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  members: DeliveryPodMemberInfo[];
  accounts: Array<{ id: string; name: string | null; orgName: string }>;
}

export interface AccountDeliveryPodInfo {
  id: string;
  name: string;
}

/** One `showOnAccount` role on an account's pod and who holds it. */
export interface AccountDeliveryLead {
  role: string;
  roleLabel: string;
  people: Array<{ userId: string; displayName: string; email: string }>;
}

export type CloudProvider = 'AWS' | 'GCP';
export type TenantPodType = 'Production' | 'Staging' | 'Performance';
export type TenantPodStatus = 'Active' | 'Paused';

export interface EnvironmentSummary {
  id: string; name: string; cloudProvider: CloudProvider; region: string;
  createdAt: string; _count: { tenantPods: number };
}
export interface EnvironmentDetail {
  id: string; name: string; cloudProvider: CloudProvider; region: string; createdAt: string;
  tenantPods: Array<{ id: string; name: string; tenantPodType: TenantPodType; status: TenantPodStatus }>;
}
export interface TenantPodEndpointRef {
  id: string; identifier: string; name: string;
}
export interface TenantPodSummary {
  id: string; environmentId: string; environmentName: string; name: string;
  tenantPodType: TenantPodType; status: TenantPodStatus; tenantUrlTemplate: string | null;
  grafanaEndpoint: TenantPodEndpointRef | null;
  elasticsearchEndpoint: TenantPodEndpointRef | null;
  createdAt: string; updatedAt: string;
  _count: { tenants: number };
}
export interface TenantPodDetail {
  id: string; environmentId: string;
  environment: { id: string; name: string; cloudProvider: CloudProvider; region: string };
  name: string; tenantPodType: TenantPodType; status: TenantPodStatus; tenantUrlTemplate: string | null;
  grafanaEndpoint: TenantPodEndpointRef | null;
  elasticsearchEndpoint: TenantPodEndpointRef | null;
  createdAt: string; updatedAt: string;
  tenants: Array<{ id: string; kiboTenantId: number; displayName: string; tenantType: string; tenantRole: string; status: string }>;
}
export interface AccountDevAccount {
  id: string; kiboDevAccountId: string; label: string; notes: string | null;
  environment: { id: string; name: string } | null; tenantCount: number;
}
/** A dev account whose tickets' Cases sit on the holding Account ("Kibo Admin - Unplaced"). */
export interface DevAccountUnplacedEntry {
  kiboDevAccountId: string;
  /** Null when Hub has never had a row for this dev account. */
  devAccount: {
    id: string;
    label: string;
    account: { id: string; name: string; salesforceAccountId: string | null } | null;
  } | null;
  tenantIds: string[];
  openerDomains: string[];
  oldestUnplacedAt: string | null;
  /** Accounts agents put this dev account's Cases on in Salesforce; an admin confirms one. */
  suggestions: {
    sfAccountId: string;
    /** The Hub account with that Salesforce id; null when Hub has none. */
    account: { id: string; name: string } | null;
    ticketId: string;
    salesforceCaseId: string | null;
    suggestedAt: string;
  }[];
  tickets: {
    id: string;
    isTest: boolean;
    salesforceCaseId: string | null;
    salesforceCaseNumber: string | null;
    unplacedAt: string | null;
    reason: string | null;
  }[];
}

export interface DevAccountSummary {
  id: string; kiboDevAccountId: string; label: string; notes: string | null;
  /** Null only for a legacy row the 2026-09-30 backfill could not place. */
  account: { id: string; name: string } | null;
  environment: { id: string; name: string } | null;
  createdAt: string; updatedAt: string; _count: { tenants: number };
}
export interface DevAccountDetail {
  id: string; kiboDevAccountId: string; label: string; notes: string | null;
  account: { id: string; name: string } | null;
  environment: { id: string; name: string } | null;
  createdAt: string; updatedAt: string;
  tenants: Array<{ id: string; kiboTenantId: number; displayName: string }>;
}
export interface PartnerContactInfo {
  id: string; partnerOrgId: string; email: string; displayName: string | null;
  notes: string | null; createdAt: string; updatedAt: string;
}
// Tenant Pod Distribution
export interface TenantPodDistributionTenant {
  id: string;
  kiboTenantId: number;
  displayName: string;
  tenantType: string;
  tenantRole: string;
  status: string;
  accountId: string;
  accountOrgName: string;
  domains: Array<{ domain: string; domainType: string }>;
}

export interface TenantPodDistributionPod {
  id: string;
  name: string;
  tenantPodType: string;
  status: string;
  tenantCount: number;
  tenants: TenantPodDistributionTenant[];
}

export interface TenantPodDistributionEnvironment {
  id: string;
  name: string;
  cloudProvider: string;
  region: string;
  tenantPodCount: number;
  tenantCount: number;
  tenantPods: TenantPodDistributionPod[];
}

export interface TenantPodDistributionResponse {
  stats: {
    environments: number;
    tenantPods: number;
    accounts: number;
    tenants: number;
  };
  environments: TenantPodDistributionEnvironment[];
}

export interface ImportSectionCounts {
  created: number;
  updated: number;
  unchanged: number;
  /** Dropped because a ref did not resolve; each one has a matching warning. */
  skipped: number;
}

export interface ImportSummary {
  environments:  ImportSectionCounts;
  tenantPods:    ImportSectionCounts;
  organizations: ImportSectionCounts;
  accounts:      ImportSectionCounts;
  partners:      ImportSectionCounts;
  devAccounts:   ImportSectionCounts;
  tenants:       ImportSectionCounts;
  tenantDomains: ImportSectionCounts;
  slackChannels: ImportSectionCounts;
  jiraLabels:    ImportSectionCounts;
  partnerAccess: ImportSectionCounts;
  warnings: string[];
}

// ===== Fireworks Proxy (Kibo AI Gateway) types =====

export interface FireworksProxyKeySummary {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface FireworksProxyKeyCreated extends FireworksProxyKeySummary {
  secret: string;
}

export interface FireworksUsageByModel {
  modelPath: string;
  displayName: string | null;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  requestCount: number;
}

export interface FireworksUsageByDay {
  day: string;
  estimatedCost: number;
  requestCount: number;
}

export interface FireworksUserUsageSummary {
  month: string;
  budgetUsd: number;
  spendUsd: number;
  remainingUsd: number;
  overBudget: boolean;
  enforced: boolean;
  byModel: FireworksUsageByModel[];
  byDay: FireworksUsageByDay[];
}

export interface FireworksProxyConfigResponse {
  credentialSetId: string | null;
  credentialSetName: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface FireworksBudgetConfigResponse {
  defaultMonthlyBudgetUsd: number;
  enforce: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface FireworksUserBudgetRow {
  userId: string;
  email: string;
  displayName: string;
  monthlyBudgetUsd: number;
  isOverride: boolean;
  note: string | null;
  monthSpendUsd: number;
  updatedAt: string | null;
}

export interface FireworksProxyModelResponse {
  id: string;
  modelPath: string;
  displayName: string;
  inputPer1M: number;
  cachedInputPer1M: number;
  outputPer1M: number;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedBy: { id: string; displayName: string } | null;
}

export interface FireworksProxyModelInput {
  modelPath: string;
  displayName: string;
  inputPer1M: number;
  cachedInputPer1M: number;
  outputPer1M: number;
  enabled?: boolean;
  sortOrder?: number;
}

export interface FireworksProxyUsageEvent {
  id: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  month: string;
  harness: string | null;
  modelPath: string;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  streamed: boolean;
  createdAt: string;
}

// ─── Ticket Deployments (BA-155) ──────────────────────────────────────────────

/** observed and pending come only with schemaVersion 2 events. */
export type TicketDeploymentKind = 'rollback' | 'deploy' | 'observed' | 'pending';

export interface TicketDeploymentServiceEntry {
  project: string;
  kubeServiceName: string;
  dockerImage?: string | null;
  fromVersion?: string | null;
  toVersion: string;
  changeType: 'rollback' | 'deploy';
}

export interface TicketDeploymentEventSummary {
  id: string;
  eventId: string;
  kind: TicketDeploymentKind;
  occurredAt: string;
  actor: string | null;
  environmentClass: string;
  environments: string[];
  gitOrg: string | null;
  gitRepo: string | null;
  services: TicketDeploymentServiceEntry[];
}

export interface TicketDeploymentEventDetail extends TicketDeploymentEventSummary {
  schemaVersion: number;
  region: string | null;
  buildUrl: string | null;
  jobName: string | null;
  payload: unknown;
  createdAt: string;
}

// Containment read API (mirrors src/ticket-deployments/ticket-deployment-schemas.ts).

export interface TicketDeploymentTicketRow {
  key: string;
  summary: string | null;
  status: string | null;
  url: string | null;
  repos: string[];
  environmentsNow: string[];
  lastChange: { change: string; environment: string; at: string } | null;
}

export interface TicketDeploymentTicketPage {
  items: TicketDeploymentTicketRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TicketDeploymentTicketPlacement {
  environment: string;
  environmentClass: string;
  kubeServiceName: string;
  repo: string | null;
  version: string;
  state: 'deployed' | 'reverted' | 'not-deployed' | 'pending' | 'unknown';
  since: string;
  eventId: string;
}

export interface TicketDeploymentTicketChangeRow {
  at: string;
  environment: string;
  environmentClass: string;
  kubeServiceName: string;
  change: 'arrived' | 'updated' | 'reverted' | 'left';
  fromVersion: string | null;
  toVersion: string;
  eventId: string;
  kind: string;
  repoUrl: string | null;
  commitSha: string | null;
}

export interface TicketDeploymentTicketView {
  key: string;
  summary: string | null;
  status: string | null;
  url: string | null;
  known: boolean;
  placements: TicketDeploymentTicketPlacement[];
  changes: TicketDeploymentTicketChangeRow[];
}

export interface TicketDeploymentEnvironmentCell {
  environment: string;
  environmentClass: string;
  kubeServiceName: string;
  repo: string | null;
  version: string;
  since: string;
  eventId: string;
  gap: boolean;
  tagStatus: string | null;
}

export interface TicketDeploymentEnvironmentGrid {
  cells: TicketDeploymentEnvironmentCell[];
}

export interface TicketDeploymentTransitionView {
  id: string;
  environment: string;
  environmentClass: string;
  kubeServiceName: string;
  repo: string | null;
  fromVersion: string | null;
  fromSource: string;
  reportedFromVersion: string | null;
  toVersion: string;
  occurredAt: string;
  gap: boolean;
  repoUrl: string | null;
  fromTag: { tag: string; status: string; error: string | null; commitSha: string | null } | null;
  toTag: { tag: string; status: string; error: string | null; commitSha: string | null } | null;
  ticketCommits: Record<string, string | null>;
  arrived: string[];
  updated: string[];
  reverted: string[];
  left: string[];
}

export interface TicketDeploymentEventTransitions {
  transitions: TicketDeploymentTransitionView[];
}

export interface TicketDeploymentStatus {
  tags: { pending: number; resolved: number; missing: number; failed: number };
  problems: Array<{ repo: string; tag: string; status: string; error: string | null; attempts: number; updatedAt: string }>;
  gitopsPullsOpen: number;
  unfoldedEvents: number;
  gaps: number;
  newestEventAt: string | null;
}

// Environment comparison (mirrors src/ticket-deployments/ticket-deployment-schemas.ts).

export interface TicketDeploymentComparisonSide {
  version: string;
  since: string;
  eventId: string;
  tagStatus: string | null;
}

export type TicketDeploymentComparisonPresence = 'present' | 'partly' | 'missing' | 'reverted' | 'unknown' | 'absent';

export interface TicketDeploymentComparisonService {
  kubeServiceName: string;
  repo: string | null;
  base: TicketDeploymentComparisonSide | null;
  targets: Record<string, TicketDeploymentComparisonSide | null>;
  missing: Record<string, number | null>;
  extra: Record<string, number | null>;
}

export interface TicketDeploymentComparisonTicket {
  key: string;
  summary: string | null;
  services: string[];
  byTarget: Record<string, TicketDeploymentComparisonPresence>;
}

export interface TicketDeploymentEnvironmentComparison {
  base: string;
  targets: string[];
  services: TicketDeploymentComparisonService[];
  tickets: TicketDeploymentComparisonTicket[];
  extraTickets: TicketDeploymentComparisonTicket[];
}

export interface TicketDeploymentEventListResult {
  items: TicketDeploymentEventSummary[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export interface TicketDeploymentKeyInfo {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface TicketDeploymentKeyCreateResult {
  id: string;
  name: string;
  fullKey: string;
  keyPrefix: string;
  createdAt: string;
}

export interface TicketDeploymentToolConfig {
  githubAppCredentialSetId: string | null;
}

// ─── Ticket join (Jira fixVersion) ───────────────────────────────────────────

export interface TicketJiraIssue {
  id: string;
  key: string;
  summary: string;
  status: string;
  statusCategory: string | null;
  priority: string | null;
  issueType: string | null;
  assignee: string | null;
  fixVersions: string[];
  resolution: string | null;
  url: string | null;
}

export const apiClient = api;

// ─── Sales Intelligence — call transcription ────────────────────────────────

export type CallTranscriptStatus = 'pending' | 'transcribing' | 'completed' | 'failed' | 'screened_out';
export type CallTranscriptSttMode = 'dynamic_batch' | 'standard';

export interface TranscribeCallResult {
  callTranscriptId: string;
  workflowId: string;
}

/** Result of cancelling one call transcription. `result` is the Temporal
 * workflow status: 'cancelled' (a RUNNING workflow received the cancel),
 * 'not_running' (workflow already terminal), or 'not_found' (no workflow with
 * this id). In all three cases the row is reconciled to `failed` so a stuck
 * `transcribing`/`pending` row is un-stuck even with no live workflow. */
export interface CancelTranscriptionResult {
  hubspotCallId: string;
  result: 'cancelled' | 'not_running' | 'not_found';
  reconciled: boolean;
}

/** Result of bulk-retrying every failed transcript row in the STT inbox. */
export interface RetryFailedTranscriptsResult {
  /** HubSpot call ids a fresh transcription workflow was started for. */
  retried: string[];
  /** HubSpot call ids skipped because a workflow is already running for them. */
  inFlight: string[];
  /** Per-call failures — the call id plus the error message that stopped it. */
  errors: Array<{ hubspotCallId: string; error: string }>;
  /** Total failed rows the retry considered (the 24h STT-inbox window). */
  scanned: number;
}

/** Stuck transcript rows (24h+ without movement in a non-terminal state) —
 *  the pipeline health banner's data. Mirrors the backend
 *  StuckTranscriptCounts (sales-intelligence-schemas.ts). */
export interface StuckTranscriptCounts {
  sttPending: number;
  sttTranscribing: number;
  sttFailedStale: number;
  gradingPending: number;
  gradingFailed: number;
}

export interface CallTranscriptSummary {
  id: string;
  hubspotCallId: string;
  hubspotOwnerId: string | null;
  status: CallTranscriptStatus;
  sttMode: CallTranscriptSttMode;
  model: string;
  transcribedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CallTranscriptDetail extends CallTranscriptSummary {
  gcsRecordingUri: string;
  utterances: unknown;
  fullText: string | null;
  repSpeakerLabel: string | null;
}

// ─── Sales Intelligence — messaging documents ────────────────────────────

export type MessagingDocCategory =
  | 'b2b'
  | 'b2c_unified'
  | 'b2c_oms'
  | 'b2c_ecommerce'
  | 'dropship'
  | 'agentic'
  | 'subscriptions';

export type MessagingDocKind = 'messaging' | 'selling_guide' | 'positioning_guide';

// ─── Sales Intelligence — call grading rubric ───────────────────────────────

export interface CallGradingWeighting {
  coverage: number;
  fidelity: number;
  outcome: number;
}

export type CallType = 'discovery' | 'demo' | 'commercial' | 'other';

export type GradingCriterion =
  | 'general_messaging_alignment'
  | 'demo_technical_review'
  | 'meddpicc'
  | 'commercial_acumen';

export const GRADING_CRITERION_LABELS: Record<GradingCriterion, string> = {
  general_messaging_alignment: 'General Messaging Alignment',
  demo_technical_review: 'Demo / Technical Review',
  meddpicc: 'MEDDPICC',
  commercial_acumen: 'Commercial acumen & preparedness',
};

export const CALL_TYPE_LABELS: Record<CallType, string> = {
  discovery: 'Discovery',
  demo: 'Demo',
  commercial: 'Commercial',
  other: 'Other',
};

export interface RubricCriterion {
  criterion: GradingCriterion;
  label: string;
  appliesToCallTypes: CallType[];
  prompt: string;
}

export interface CallGradingRubricPayload {
  promptTemplate: string;
  chunkMinutes: number;
  modelId: string;
  weighting: CallGradingWeighting;
  minCallDurationMs: number;
  criteria: RubricCriterion[];
  recentCallWindow: number;
}

export interface CallGradingRubricVersionSummary {
  id: string;
  version: number;
  note: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CallGradingRubricVersionDetail extends CallGradingRubricVersionSummary {
  payload: CallGradingRubricPayload;
  createdById: string | null;
}

export interface UpdateCallGradingRubricBody {
  promptTemplate?: string;
  chunkMinutes?: number;
  modelId?: string;
  weighting?: CallGradingWeighting;
  minCallDurationMs?: number;
  criteria?: RubricCriterion[];
  recentCallWindow?: number;
  note?: string | null;
}

// ─── Sales Intelligence — call screening ────────────────────────────────────

export interface CallScreeningPayload {
  enabled: boolean;
  modelId: string;
  promptTemplate: string;
  exemptHubspotOwnerIds: string[];
}

export interface CallScreeningVersionSummary {
  id: string;
  version: number;
  note: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CallScreeningVersionDetail extends CallScreeningVersionSummary {
  payload: CallScreeningPayload;
  createdById: string | null;
}

export interface UpdateCallScreeningBody {
  enabled?: boolean;
  modelId?: string;
  promptTemplate?: string;
  exemptHubspotOwnerIds?: string[];
  note?: string | null;
}

// ─── Sales Intelligence — sales reps + call grades ────────────────────────────

export type SalesRepRole = 'AE' | 'BDE' | 'SE';

export interface SalesRepSummary {
  id: string;
  slug: string;
  name: string;
  role: SalesRepRole;
  hubspotOwnerId: string | null;
  graded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RepCategoryAverage {
  repId: string;
  category: MessagingDocCategory;
  mode: 'B2C' | 'B2B' | null;
  averageScore: number | null;
  callCount: number;
}

export interface GradesSummary {
  reps: Array<{
    id: string;
    slug: string;
    name: string;
    role: SalesRepRole;
    hubspotOwnerId: string | null;
    averages: RepCategoryAverage[];
  }>;
}

export interface RepCallCriterionGrade {
  criterion: GradingCriterion;
  score: number | null;
  rationale: string | null;
  strengths: string[];
  improvements: string[];
  coachingContinue: string[];
  coachingStop: string[];
  coachingStart: string[];
}

export interface RepCriterionAverage {
  repId: string;
  criterion: GradingCriterion;
  averageScore: number | null;
  callCount: number;
}

export interface RepCallGrade {
  callTranscriptId: string;
  hubspotCallId: string;
  /** Deep link to the HubSpot call record (their player trims silence). Null
   * when the HubSpot integration's portal id isn't configured. */
  hubspotCallUrl: string | null;
  callTitle: string | null;
  callDirection: string | null;
  companyName: string | null;
  /** When the call actually happened (HubSpot `hs_timestamp`). Null on rows
   *  backfilled before the column shipped — render a process date then. */
  hsTimestamp: string | null;
  transcribedAt: string | null;
  gradedAt: string;
  category: MessagingDocCategory;
  mode: 'B2C' | 'B2B' | null;
  score: number | null;
  rationale: string | null;
  strengths: string[];
  improvements: string[];
  callType: CallType | null;
  criteria: RepCallCriterionGrade[];
}

export interface RepGrades {
  rep: {
    id: string;
    slug: string;
    name: string;
    role: SalesRepRole;
    hubspotOwnerId: string | null;
  };
  criterionAverages: RepCriterionAverage[];
  calls: RepCallGrade[];
}

export interface MessagingDocumentSummary {
  id: string;
  category: MessagingDocCategory;
  kind: MessagingDocKind;
  title: string;
  buyerSegment: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MessagingDocumentDetail extends MessagingDocumentSummary {
  content: string;
}

// ─── Sales Intelligence — HubSpot OAuth connection ──────────────────────────

export interface SalesIntelligenceHubSpotConfigResponse {
  id: string | null;
  credentialSetId: string | null;
  credentialSetName: string | null;
  portalId: string | null;
  connected: boolean;
  accessTokenExpiresAt: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; displayName: string } | null;
}

export interface SalesIntelligenceHubSpotTestConnectionResult {
  portalId: string | null;
}

// ─── Sales Intelligence — knowledge base ─────────────────────────────────────

export type GradingStatus = 'pending' | 'graded' | 'skipped' | 'failed';

export type TranscriptStatus = 'pending' | 'transcribing' | 'completed' | 'failed' | 'screened_out';

export interface KnowledgeBaseRep {
  id: string;
  slug: string;
  name: string;
  role: SalesRepRole;
}

export interface KnowledgeBaseGradePreview {
  category: MessagingDocCategory;
  mode: 'B2C' | 'B2B' | null;
  score: number | null;
  rationalePreview: string | null;
}

export interface KnowledgeBaseCriterionGradePreview {
  criterion: GradingCriterion;
  score: number | null;
}

// ── Qualification scorecard evaluations (shadow) ─────────────────────────────

export type ScorecardReadinessBand = 'qualified_to_advance' | 'needs_follow_up' | 'not_yet_qualified';
export type ScorecardStageKeyUi = 'lead_new' | 'lead_qualified' | 's0_create' | 's1_qualify' | 's2_sales_accepted';

export interface ScorecardCategoryScoreUi {
  categoryId: string;
  score: number;
  rationale: string;
}

export interface ScorecardContradictionUi {
  dataPoint: string;
  crmValue: string;
  transcriptClaim: string;
  note?: string;
}

export interface ScorecardEvaluationListEntry {
  id: string;
  hubspotCallId: string;
  callTranscriptId: string;
  stageKey: string;
  scope: string;
  dealId: string | null;
  contactId: string | null;
  compositeScore: number | null;
  readinessBand: string;
  hasCoaching: boolean;
  contradictionCount: number;
  scoredAt: string;
}

export interface ScorecardEvaluationDetail {
  id: string;
  scorecardId: string;
  hubspotCallId: string;
  callTranscriptId: string;
  scope: string;
  stageKey: string;
  dealId: string | null;
  contactId: string | null;
  configVersionId: string | null;
  modelId: string;
  categoryScores: ScorecardCategoryScoreUi[];
  compositeScore: number | null;
  readinessBand: string;
  contradictions: ScorecardContradictionUi[];
  crmContext: {
    confirmedDataPointIds?: string[];
    dataPoints?: Array<{ dataPoint: string; value: string | null; sourceProperty: string | null; confirmedInCrm: boolean; daysSinceEdit: number | null }>;
    buyingRoles?: Array<{ name: string | null; email: string | null; associationRoleLabels: string[]; propertyRoleSignals: string[] }>;
    capdbGrade?: { grade: string; sourceProperty: string } | null;
    priorCalls?: { count: number; firstCallAt: string | null; lastCallAt: string | null; daysSinceLastCall: number | null; lastSummary: string | null };
  };
  coaching: {
    summary: string;
    start: string[];
    stop: string[];
    continue: string[];
    recommendedNextStep: string;
  } | null;
  scoredAt: string;
  coachedAt: string | null;
  callTranscript: {
    id: string;
    callTitle: string | null;
    companyName: string | null;
    companyDomain: string | null;
    hsTimestamp: string | null;
    hubspotCallUrl: string | null;
    hubspotOwnerId: string | null;
  } | null;
}

export const scorecardApi = {
  async evaluateCall(body: { hubspotCallId: string; internalEmailDomains?: string[]; staleAfterDays?: number }): Promise<ApiResult<{ started: boolean; workflowId: string }>> {
    return fetchApi<{ started: boolean; workflowId: string }>('/api/v1/admin/sales-intelligence/scorecard/evaluate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async listEvaluations(query: { offset?: number; limit?: number; readinessBand?: string; stageKey?: string; dealId?: string }): Promise<ApiResult<{ entries: ScorecardEvaluationListEntry[]; total: number }>> {
    const params = new URLSearchParams();
    if (query.offset !== undefined) params.set('offset', String(query.offset));
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.readinessBand) params.set('readinessBand', query.readinessBand);
    if (query.stageKey) params.set('stageKey', query.stageKey);
    if (query.dealId) params.set('dealId', query.dealId);
    const qs = params.toString();
    return fetchApi<{ entries: ScorecardEvaluationListEntry[]; total: number }>(`/api/v1/admin/sales-intelligence/scorecard/evaluations${qs ? `?${qs}` : ''}`);
  },

  async getEvaluation(id: string): Promise<ApiResult<{ evaluation: ScorecardEvaluationDetail }>> {
    return fetchApi<{ evaluation: ScorecardEvaluationDetail }>(`/api/v1/admin/sales-intelligence/scorecard/evaluations/${encodeURIComponent(id)}`);
  },
};

export interface KnowledgeBaseEntry {
  callTranscriptId: string;
  hubspotCallId: string;
  callTitle: string | null;
  callDirection: string | null;
  companyName: string | null;
  hubspotOwnerId: string | null;
  rep: KnowledgeBaseRep | null;
  /** When the call actually happened (HubSpot `hs_timestamp`). Null on rows
   *  backfilled before the column shipped — render a process date then. */
  hsTimestamp: string | null;
  transcribedAt: string | null;
  gradedAt: string | null;
  /** When the call entered the KB (status → completed/screened_out); null pre-KB. */
  addedToKnowledgeBaseAt: string | null;
  gradingStatus: GradingStatus;
  gradingError: string | null;
  overallScore: number | null;
  gradingModel: string | null;
  rubricVersionId: string | null;
  rubricVersion: number | null;
  transcriptStatus: TranscriptStatus;
  screenReason: string | null;
  callType: CallType | null;
  criterionGrades: KnowledgeBaseCriterionGradePreview[];
  grades: KnowledgeBaseGradePreview[];
}

export interface KnowledgeBaseListResponse {
  items: KnowledgeBaseEntry[];
  nextOffset: number | null;
  total: number;
}

export interface KnowledgeBaseGradeDetail {
  id: string;
  category: MessagingDocCategory;
  mode: 'B2C' | 'B2B' | null;
  score: number | null;
  rationale: string | null;
  strengths: string[];
  improvements: string[];
  gradedAt: string;
}

export interface KnowledgeBaseCriterionGradeDetail {
  id: string;
  criterion: GradingCriterion;
  score: number | null;
  rationale: string | null;
  strengths: string[];
  improvements: string[];
  gradedAt: string;
}

export interface KnowledgeBaseEntryDetail {
  callTranscriptId: string;
  hubspotCallId: string;
  callTitle: string | null;
  callDirection: string | null;
  callSummary: string | null;
  companyName: string | null;
  companyDomain: string | null;
  hubspotOwnerId: string | null;
  rep: KnowledgeBaseRep | null;
  sttModel: string;
  sttMode: 'dynamic_batch' | 'standard';
  repSpeakerLabel: string | null;
  /** When the call actually happened (HubSpot `hs_timestamp`). Null on rows
   *  backfilled before the column shipped — render a process date then. */
  hsTimestamp: string | null;
  transcribedAt: string | null;
  gradedAt: string | null;
  /** When the call entered the KB (status → completed/screened_out); null pre-KB. */
  addedToKnowledgeBaseAt: string | null;
  gradingStatus: GradingStatus;
  gradingError: string | null;
  overallScore: number | null;
  gradingModel: string | null;
  rubricVersionId: string | null;
  rubricVersion: number | null;
  transcriptStatus: TranscriptStatus;
  screenReason: string | null;
  callType: CallType | null;
  grades: KnowledgeBaseGradeDetail[];
  criterionGrades: KnowledgeBaseCriterionGradeDetail[];
  utterances: unknown;
  fullText: string | null;
}

export interface GradingStatusResponse {
  callTranscriptId: string;
  gradingStatus: GradingStatus;
  gradingError: string | null;
  running: boolean;
}

export interface KnowledgeBaseFilters {
  repId?: string;
  category?: MessagingDocCategory;
  mode?: 'B2C' | 'B2B';
  status?: GradingStatus;
  gradedOnly?: boolean;
  screenedOnly?: boolean;
  callType?: CallType;
  minScore?: number;
  maxScore?: number;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

// ── Deal Health ─────────────────────────────────────────────────────────────
// Sibling axis to the call-grading scorecard: scores reps' deal hygiene
// (MEDDPIC capture, record freshness, contact coverage) from HubSpot deal
// records, not call recordings. Forecast Stability reserved (v1: null).

export type DealHealthCategory =
  | 'meddpic_completeness'
  | 'meddpic_data_quality'
  | 'next_steps_freshness'
  | 'overview_freshness'
  | 'contact_coverage'
  | 'next_steps_quality'
  | 'forecast_stability';

export type DealHealthBand = 'healthy' | 'at_risk' | 'unhealthy';

export const DEAL_HEALTH_CATEGORY_LABELS: Record<DealHealthCategory, string> = {
  meddpic_completeness: 'MEDDPIC Completeness',
  meddpic_data_quality: 'MEDDPIC Data Quality',
  next_steps_freshness: 'Next Steps Freshness',
  overview_freshness: 'Overview Tab Freshness',
  contact_coverage: 'Contact Coverage',
  next_steps_quality: 'Next Steps Quality',
  forecast_stability: 'Forecast / Close-Date Stability',
};

export const DEAL_HEALTH_BAND_LABELS: Record<DealHealthBand, string> = {
  healthy: 'Healthy',
  at_risk: 'At Risk',
  unhealthy: 'Unhealthy',
};

export interface DealHealthWeights {
  meddpic_completeness: number;
  meddpic_data_quality: number;
  next_steps_freshness: number;
  overview_freshness: number;
  contact_coverage: number;
  next_steps_quality: number;
  forecast_stability: number;
}

export interface DealHealthConfigPayload {
  weights: DealHealthWeights;
  modelId: string;
  meddpicFieldAllowlist?: Partial<Record<'metrics' | 'economic_buyer' | 'decision_criteria' | 'decision_process' | 'paper_process' | 'identify_pain' | 'champion', string>>;
}

export interface DealHealthFieldEvidence {
  /** The 5 MEDDPIC field values as the rep entered them (empty string = blank).
   *  Champion is deliberately absent — the contacts panel shows the association
   *  labels instead (the Yes/No flag is the unreliable source). */
  meddpicFields: Record<string, string>;
  /** How many of the 5 MEDDPIC fields are populated (non-blank, non-placeholder). */
  meddpicPopulatedCount: number;
  /** The Next Steps text (hs_next_step), or empty string. */
  nextSteps: string;
  /** ISO timestamp of the deal's last-modified date, or null. */
  lastModified: string | null;
  /** Count of contacts associated to the deal, or 0. */
  contactCount: number;
}

/** One named hygiene check behind a deal's band — backend-computed from the
 *  same rawDealProperties snapshot + category scores the backend scored
 *  against. The UI renders these verbatim and never re-derives its own. */
export interface DealHealthHygieneCheck {
  id: string;
  label: string;
  status: 'pass' | 'fail';
  detail: string;
}

/** A contact label found on the deal's associations during the last scoring
 *  run, with how many contacts carry it. Display-only for Neutral — the
 *  Contact Coverage score counts the four active roles only. */
export interface DealHealthContactLabelCount {
  label: string;
  count: number;
}

export interface DealHealthScoreDetail {
  id: string;
  dealId: string;
  dealName: string | null;
  stage: string | null;
  amount: number | null;
  closeDate: string | null;
  meddpicCompleteness: number | null;
  meddpicDataQuality: number | null;
  nextStepsFreshness: number | null;
  overviewFreshness: number | null;
  contactCoverage: number | null;
  nextStepsQuality: number | null;
  forecastStability: number | null;
  totalScore: number | null;
  healthBand: DealHealthBand | null;
  scoredAt: string;
  fieldEvidence: DealHealthFieldEvidence;
  /** Why this deal landed in its band — backend-derived pass/fail checks. */
  hygieneChecks: DealHealthHygieneCheck[];
  /** Contact label counts from the last scoring run (Champion / Decision
   *  Maker / Influencer / Enemy / Neutral + any others). Empty array on rows
   *  scored before the column existed or when the portal had no labels. */
  contactLabels: DealHealthContactLabelCount[];
}

/** Coaching summary (Start / Stop / Continue) for a rep. Cached server-side
 *  in CoachingSummaryCache: regenerated only when the rep's inputs changed
 *  (new graded call, deal change/rescore, config edit), so repeated views are
 *  instant and stable. `generatedAt` is when the LLM produced this exact
 *  text; `cached` is false on the request that regenerated it. */
export interface CoachingSummary {
  continue: string[];
  stop: string[];
  start: string[];
  insufficientSignal: boolean;
  generatedAt: string;
  cached: boolean;
}

export interface RepDealHealthAverage {
  repId: string;
  meddpicCompleteness: number | null;
  meddpicDataQuality: number | null;
  nextStepsFreshness: number | null;
  overviewFreshness: number | null;
  contactCoverage: number | null;
  nextStepsQuality: number | null;
  forecastStability: number | null;
  totalScore: number | null;
  healthBand: DealHealthBand | null;
  dealCount: number;
  healthyCount: number;
  atRiskCount: number;
  unhealthyCount: number;
}

export interface DealHealthSummary {
  reps: Array<{
    id: string;
    slug: string;
    name: string;
    role: SalesRepRole;
    hubspotOwnerId: string | null;
    average: RepDealHealthAverage;
  }>;
}

export interface RepDealHealth {
  rep: {
    id: string;
    slug: string;
    name: string;
    role: SalesRepRole;
    hubspotOwnerId: string | null;
  };
  average: RepDealHealthAverage;
  deals: DealHealthScoreDetail[];
}

export const dealHealthApi = {
  async getSummary(): Promise<ApiResult<DealHealthSummary>> {
    return fetchApi<DealHealthSummary>('/api/v1/sales-intelligence/deals/summary');
  },

  async getRepDeals(repId: string): Promise<ApiResult<RepDealHealth>> {
    return fetchApi<RepDealHealth>(`/api/v1/sales-intelligence/deals/reps/${encodeURIComponent(repId)}`);
  },

  async getConfig(): Promise<ApiResult<DealHealthConfigPayload>> {
    return fetchApi<DealHealthConfigPayload>('/api/v1/admin/sales-intelligence/deal-health/config');
  },

  async updateConfig(body: Partial<{ weights: DealHealthWeights; modelId: string; meddpicFieldAllowlist: DealHealthConfigPayload['meddpicFieldAllowlist'] }>): Promise<ApiResult<DealHealthConfigPayload>> {
    return fetchApi<DealHealthConfigPayload>('/api/v1/admin/sales-intelligence/deal-health/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async rescore(limit?: number): Promise<ApiResult<{ started: boolean }>> {
    const query = limit && limit > 0 ? `?limit=${encodeURIComponent(limit)}` : '';
    return fetchApi<{ started: boolean }>(`/api/v1/admin/sales-intelligence/deal-health/rescore${query}`, { method: 'POST' });
  },

  // ── Coaching summary (cached, deal-health) ──────────────────────
  // force=true bypasses the server-side fingerprint check and re-runs the
  // LLM; the default just re-reads (and re-uses) the cache.
  async getDealHealthCoachingSummary(repId: string, opts: { force?: boolean } = {}): Promise<ApiResult<CoachingSummary>> {
    const qs = opts.force ? '?force=true' : '';
    return fetchApi<CoachingSummary>(`/api/v1/sales-intelligence/deals/reps/${encodeURIComponent(repId)}/coaching-summary${qs}`, { method: 'POST' });
  },
};

export const salesIntelligenceApi = {
  async transcribeCall(body: { hubspotCallId: string; sttMode?: CallTranscriptSttMode }): Promise<ApiResult<TranscribeCallResult>> {
    return fetchApi<TranscribeCallResult>('/api/v1/admin/sales-intelligence/call-transcripts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async listCallTranscripts(): Promise<ApiResult<CallTranscriptSummary[]>> {
    return fetchApi<CallTranscriptSummary[]>('/api/v1/admin/sales-intelligence/call-transcripts');
  },

  async getCallTranscript(hubspotCallId: string): Promise<ApiResult<CallTranscriptDetail>> {
    return fetchApi<CallTranscriptDetail>(`/api/v1/admin/sales-intelligence/call-transcripts/${encodeURIComponent(hubspotCallId)}`);
  },

  async getStuckTranscriptCounts(): Promise<ApiResult<StuckTranscriptCounts>> {
    return fetchApi<StuckTranscriptCounts>('/api/v1/admin/sales-intelligence/call-transcripts/stuck');
  },

  async retryFailedTranscripts(): Promise<ApiResult<RetryFailedTranscriptsResult>> {
    return fetchApi<RetryFailedTranscriptsResult>('/api/v1/admin/sales-intelligence/call-transcripts/retry-failed', {
      method: 'POST',
    });
  },

  async cancelCallTranscript(hubspotCallId: string): Promise<ApiResult<CancelTranscriptionResult>> {
    return fetchApi<CancelTranscriptionResult>(`/api/v1/admin/sales-intelligence/call-transcripts/${encodeURIComponent(hubspotCallId)}/cancel`, {
      method: 'POST',
    });
  },

  async getSalesIntelligenceHubSpotConfig(): Promise<ApiResult<SalesIntelligenceHubSpotConfigResponse>> {
    return fetchApi<SalesIntelligenceHubSpotConfigResponse>('/api/v1/admin/sales-intelligence/hubspot/config');
  },

  async updateSalesIntelligenceHubSpotConfig(data: {
    credentialSetId?: string | null;
  }): Promise<ApiResult<SalesIntelligenceHubSpotConfigResponse>> {
    return fetchApi<SalesIntelligenceHubSpotConfigResponse>('/api/v1/admin/sales-intelligence/hubspot/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async testSalesIntelligenceHubSpotConnection(): Promise<ApiResult<SalesIntelligenceHubSpotTestConnectionResult>> {
    return fetchApi<SalesIntelligenceHubSpotTestConnectionResult>(
      '/api/v1/admin/sales-intelligence/hubspot/test-connection',
      { method: 'POST' },
    );
  },

  // OAuth authorize is a browser redirect (not a JSON API call); the page calls
  // window.location.href = this URL to start the grant flow.
  get salesIntelligenceHubSpotAuthorizeUrl(): string {
    return '/api/v1/admin/sales-intelligence/hubspot/oauth/authorize';
  },

  async listMessagingDocuments(): Promise<ApiResult<MessagingDocumentSummary[]>> {
    return fetchApi<MessagingDocumentSummary[]>('/api/v1/admin/sales-intelligence/messaging-docs');
  },

  async getMessagingDocument(id: string): Promise<ApiResult<MessagingDocumentDetail>> {
    return fetchApi<MessagingDocumentDetail>(`/api/v1/admin/sales-intelligence/messaging-docs/${encodeURIComponent(id)}`);
  },

  async createMessagingDocument(body: {
    category: MessagingDocCategory;
    kind: MessagingDocKind;
    title: string;
    content: string;
    buyerSegment?: string | null;
  }): Promise<ApiResult<MessagingDocumentDetail>> {
    return fetchApi<MessagingDocumentDetail>('/api/v1/admin/sales-intelligence/messaging-docs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async updateMessagingDocument(id: string, body: {
    category?: MessagingDocCategory;
    kind?: MessagingDocKind;
    title?: string;
    content?: string;
    buyerSegment?: string | null;
  }): Promise<ApiResult<MessagingDocumentDetail>> {
    return fetchApi<MessagingDocumentDetail>(`/api/v1/admin/sales-intelligence/messaging-docs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async deleteMessagingDocument(id: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/admin/sales-intelligence/messaging-docs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ── Call grading rubric (versioned) ─────────────────────────────────────
  async getGradingRubric(): Promise<ApiResult<CallGradingRubricVersionDetail>> {
    return fetchApi<CallGradingRubricVersionDetail>('/api/v1/admin/sales-intelligence/grading-rubric');
  },

  async listGradingRubricVersions(): Promise<ApiResult<CallGradingRubricVersionSummary[]>> {
    return fetchApi<CallGradingRubricVersionSummary[]>('/api/v1/admin/sales-intelligence/grading-rubric/versions');
  },

  async updateGradingRubric(body: UpdateCallGradingRubricBody): Promise<ApiResult<CallGradingRubricVersionDetail>> {
    return fetchApi<CallGradingRubricVersionDetail>('/api/v1/admin/sales-intelligence/grading-rubric', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async activateGradingRubricVersion(versionId: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/admin/sales-intelligence/grading-rubric/activate/${encodeURIComponent(versionId)}`, {
      method: 'POST',
    });
  },

  // ── Call screening (versioned, admin-editable) ───────────────────────
  async getCallScreening(): Promise<ApiResult<CallScreeningVersionDetail>> {
    return fetchApi<CallScreeningVersionDetail>('/api/v1/admin/sales-intelligence/screening');
  },

  async listCallScreeningVersions(): Promise<ApiResult<CallScreeningVersionSummary[]>> {
    return fetchApi<CallScreeningVersionSummary[]>('/api/v1/admin/sales-intelligence/screening/versions');
  },

  async updateCallScreening(body: UpdateCallScreeningBody): Promise<ApiResult<CallScreeningVersionDetail>> {
    return fetchApi<CallScreeningVersionDetail>('/api/v1/admin/sales-intelligence/screening', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async activateCallScreeningVersion(versionId: string): Promise<ApiResult<{ message: string }>> {
    return fetchApi<{ message: string }>(`/api/v1/admin/sales-intelligence/screening/activate/${encodeURIComponent(versionId)}`, {
      method: 'POST',
    });
  },

  // ── Roster + grades (read by the matrix + personal scorecard) ──────────
  async listSalesReps(): Promise<ApiResult<SalesRepSummary[]>> {
    return fetchApi<SalesRepSummary[]>('/api/v1/sales-intelligence/reps');
  },

  async getGradesSummary(
    range?: { since?: string; until?: string; recentCallWindow?: number },
  ): Promise<ApiResult<GradesSummary>> {
    const params = new URLSearchParams();
    if (range?.since) params.set('since', range.since);
    if (range?.until) params.set('until', range.until);
    if (range?.recentCallWindow) params.set('recentCallWindow', String(range.recentCallWindow));
    const qs = params.toString();
    return fetchApi<GradesSummary>(`/api/v1/sales-intelligence/grades/summary${qs ? `?${qs}` : ''}`);
  },

  async getRepGrades(repId: string, range?: { since?: string; until?: string }): Promise<ApiResult<RepGrades>> {
    const params = new URLSearchParams();
    if (range?.since) params.set('since', range.since);
    if (range?.until) params.set('until', range.until);
    const qs = params.toString();
    return fetchApi<RepGrades>(`/api/v1/sales-intelligence/reps/${encodeURIComponent(repId)}/grades${qs ? `?${qs}` : ''}`);
  },

  // ── Coaching summary (cached, cross-tool) ───────────────────────
  // force=true bypasses the server-side fingerprint check and re-runs the
  // LLM; the default just re-reads (and re-uses) the cache.
  async getRepCoachingSummary(repId: string, opts: { force?: boolean } = {}): Promise<ApiResult<CoachingSummary>> {
    const qs = opts.force ? '?force=true' : '';
    return fetchApi<CoachingSummary>(`/api/v1/sales-intelligence/reps/${encodeURIComponent(repId)}/coaching-summary${qs}`, { method: 'POST' });
  },

  // ── Batch grading (admin) ─────────────────────────────────────────────
  async startBatchGrading(body: {
    limit?: number;
    since?: string;
    /** Exclusive upper bound on hs_timestamp — page backward through history. */
    before?: string;
  }): Promise<ApiResult<{
    started: string[];
    gradedOnly: string[];
    alreadyRunning: string[];
    alreadyDone: string[];
    alreadyScreened: string[];
    scanned: number;
    newestScannedTimestamp: string | null;
    oldestScannedTimestamp: string | null;
    hasMore: boolean;
  }>> {
    return fetchApi<{
      started: string[];
      gradedOnly: string[];
      alreadyRunning: string[];
      alreadyDone: string[];
      alreadyScreened: string[];
      scanned: number;
      newestScannedTimestamp: string | null;
      oldestScannedTimestamp: string | null;
      hasMore: boolean;
    }>('/api/v1/admin/sales-intelligence/grade-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async gradeCallTranscript(callTranscriptId: string): Promise<ApiResult<{ workflowId: string }>> {
    return fetchApi<{ workflowId: string }>(`/api/v1/admin/sales-intelligence/grade/${encodeURIComponent(callTranscriptId)}`, {
      method: 'POST',
    });
  },

  // Re-grade every already-graded call in an optional (call-time) window.
  // `since`/`before` are ISO bounds on when the call happened (hs_timestamp,
  // falling back to ingest createdAt for legacy rows). Omit both to regrade
  // every graded call. Use after a rubric/criteria change.
  async regradeGradedCalls(body: {
    since?: string;
    before?: string;
  }): Promise<ApiResult<{
    started: string[];
    alreadyRunning: string[];
    scanned: number;
    refreshed: number;
    refreshFailures: number;
  }>> {
    return fetchApi<{
      started: string[];
      alreadyRunning: string[];
      scanned: number;
      refreshed: number;
      refreshFailures: number;
    }>('/api/v1/admin/sales-intelligence/regrade', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  // ── Knowledge base (admin) ─────────────────────────────────────────────
  async listKnowledgeBase(filters: KnowledgeBaseFilters): Promise<ApiResult<KnowledgeBaseListResponse>> {
    const params = new URLSearchParams();
    if (filters.repId) params.set('repId', filters.repId);
    if (filters.category) params.set('category', filters.category);
    if (filters.mode) params.set('mode', filters.mode);
    if (filters.status) params.set('status', filters.status);
    if (filters.gradedOnly) params.set('gradedOnly', 'true');
    if (filters.screenedOnly) params.set('screenedOnly', 'true');
    if (filters.callType) params.set('callType', filters.callType);
    if (filters.minScore !== undefined) params.set('minScore', String(filters.minScore));
    if (filters.maxScore !== undefined) params.set('maxScore', String(filters.maxScore));
    if (filters.since) params.set('since', filters.since);
    if (filters.until) params.set('until', filters.until);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return fetchApi<KnowledgeBaseListResponse>(`/api/v1/admin/sales-intelligence/knowledge-base${qs ? `?${qs}` : ''}`);
  },

  async getKnowledgeBaseEntry(callTranscriptId: string): Promise<ApiResult<KnowledgeBaseEntryDetail>> {
    return fetchApi<KnowledgeBaseEntryDetail>(`/api/v1/admin/sales-intelligence/knowledge-base/${encodeURIComponent(callTranscriptId)}`);
  },

  async updateCallType(callTranscriptId: string, callType: CallType | null): Promise<ApiResult<{ callTranscriptId: string; callType: CallType | null }>> {
    return fetchApi<{ callTranscriptId: string; callType: CallType | null }>(`/api/v1/admin/sales-intelligence/call-transcripts/${encodeURIComponent(callTranscriptId)}/call-type`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callType }),
    });
  },

  async getGradingStatus(callTranscriptId: string): Promise<ApiResult<GradingStatusResponse>> {
    return fetchApi<GradingStatusResponse>(`/api/v1/admin/sales-intelligence/grade/${encodeURIComponent(callTranscriptId)}/status`);
  },
};

export const accountDbApi = {
  // Organizations
  async getOrganizations(params?: { page?: number; limit?: number; orgType?: string; status?: string; search?: string }): Promise<ApiResult<OrgListResponse>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.orgType) query.set('orgType', params.orgType);
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<OrgListResponse>(`/api/v1/organizations${qs ? `?${qs}` : ''}`);
  },

  async getOrganization(id: string): Promise<ApiResult<OrgDetail>> {
    return fetchApi<OrgDetail>(`/api/v1/organizations/${id}`);
  },

  async createOrganization(data: { name: string; orgType: string; parentOrgId?: string | null; hubspotCompanyId?: string | null; status?: string; notes?: string | null }): Promise<ApiResult<OrgDetail>> {
    return fetchApi<OrgDetail>('/api/v1/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateOrganization(id: string, data: Partial<{ name: string; orgType: string; parentOrgId: string | null; hubspotCompanyId: string | null; status: string; notes: string | null }>): Promise<ApiResult<OrgDetail>> {
    return fetchApi<OrgDetail>(`/api/v1/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Accounts
  async getAccounts(params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string; status?: string; deliveryPodId?: string; unassignedDeliveryPod?: boolean; ticketSource?: string; search?: string }): Promise<ApiResult<AccountListResponse>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params?.status) query.set('status', params.status);
    if (params?.deliveryPodId) query.set('deliveryPodId', params.deliveryPodId);
    if (params?.unassignedDeliveryPod) query.set('unassignedDeliveryPod', 'true');
    if (params?.ticketSource) query.set('ticketSource', params.ticketSource);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<AccountListResponse>(`/api/v1/accounts${qs ? `?${qs}` : ''}`);
  },

  async getAccount(id: string): Promise<ApiResult<AccountDetail>> {
    return fetchApi<AccountDetail>(`/api/v1/accounts/${id}`);
  },

  async createAccount(data: { orgId: string; ref?: string | null; ticketSource: string; supportTier?: SupportTier | null; hubspotAccountId?: string | null; salesforceAccountId?: string | null; deliveryPodId?: string | null }): Promise<ApiResult<AccountDetail>> {
    return fetchApi<AccountDetail>('/api/v1/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAccount(id: string, data: Partial<{ name: string | null; ref: string | null; description: string | null; ticketSource: string; supportTier: SupportTier | null; hubspotAccountId: string | null; salesforceAccountId: string | null; deliveryPodId: string | null }>): Promise<ApiResult<AccountDetail>> {
    return fetchApi<AccountDetail>(`/api/v1/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async uploadAccountLogo(accountId: string, file: File): Promise<ApiResult<AccountDetail>> {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<AccountDetail>(`/api/v1/accounts/${accountId}/logo`, {
      method: 'POST',
      body: formData,
    });
  },

  async addSlackChannel(accountId: string, data: { channel: string; purpose: SlackChannelPurpose }): Promise<ApiResult<SlackChannelInfo>> {
    return fetchApi<SlackChannelInfo>(`/api/v1/accounts/${accountId}/slack-channels`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async removeSlackChannel(accountId: string, channelId: string): Promise<ApiResult<void>> {
    return fetchApi<void>(`/api/v1/accounts/${accountId}/slack-channels/${channelId}`, {
      method: 'DELETE',
    });
  },

  async addJiraLabel(accountId: string, data: { label: string }): Promise<ApiResult<JiraLabelInfo>> {
    return fetchApi<JiraLabelInfo>(`/api/v1/accounts/${accountId}/jira-labels`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async removeJiraLabel(accountId: string, labelId: string): Promise<ApiResult<void>> {
    return fetchApi<void>(`/api/v1/accounts/${accountId}/jira-labels/${labelId}`, {
      method: 'DELETE',
    });
  },

  async listAccountTickets(accountId: string): Promise<ApiResult<{ items: JiraIssue[] }>> {
    return fetchApi<{ items: JiraIssue[] }>(`/api/v1/accounts/${accountId}/tickets`);
  },

  async listAccountSalesforceCases(accountId: string): Promise<ApiResult<{ items: SalesforceCaseSummary[] }>> {
    return fetchApi<{ items: SalesforceCaseSummary[] }>(`/api/v1/accounts/${accountId}/salesforce-cases`);
  },

  async addAccountStatusHistory(accountId: string, data: { status: string; effectiveFrom: string; notes?: string | null }): Promise<ApiResult<AccountStatusHistoryInfo>> {
    return fetchApi<AccountStatusHistoryInfo>(`/api/v1/accounts/${accountId}/status-history`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getAccountPartnerAccess(accountId: string): Promise<ApiResult<PartnerAccessInfo[]>> {
    return fetchApi<PartnerAccessInfo[]>(`/api/v1/accounts/${accountId}/partner-access`);
  },

  async grantPartnerAccess(accountId: string, data: { partnerOrgId: string; notes?: string | null }): Promise<ApiResult<PartnerAccessInfo>> {
    return fetchApi<PartnerAccessInfo>(`/api/v1/accounts/${accountId}/partner-access`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updatePartnerAccessNotes(accountId: string, accessId: string, notes: string | null): Promise<ApiResult<PartnerAccessInfo>> {
    return fetchApi<PartnerAccessInfo>(`/api/v1/accounts/${accountId}/partner-access/${accessId}`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },

  async revokePartnerAccess(accountId: string, accessId: string): Promise<ApiResult<PartnerAccessInfo>> {
    return fetchApi<PartnerAccessInfo>(`/api/v1/accounts/${accountId}/partner-access/${accessId}/revoke`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  },

  async getAccountActivityFeed(accountId: string): Promise<ApiResult<ActivityFeedResponse>> {
    return fetchApi<ActivityFeedResponse>(`/api/v1/accounts/${accountId}/activity-feed`);
  },

  async listAccountNotes(accountId: string): Promise<ApiResult<NoteInfo[]>> {
    return fetchApi<NoteInfo[]>(`/api/v1/accounts/${accountId}/notes`);
  },

  async createAccountNote(accountId: string, body: string): Promise<ApiResult<NoteInfo>> {
    return fetchApi<NoteInfo>(`/api/v1/accounts/${accountId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  },

  // Delivery Pods
  async getDeliveryPodRoles(): Promise<ApiResult<{ items: DeliveryPodRole[] }>> {
    return fetchApi<{ items: DeliveryPodRole[] }>('/api/v1/delivery-pod-roles');
  },

  async getDeliveryPods(): Promise<ApiResult<{ items: DeliveryPodSummary[] }>> {
    return fetchApi<{ items: DeliveryPodSummary[] }>('/api/v1/delivery-pods');
  },

  async getDeliveryPod(id: string): Promise<ApiResult<DeliveryPodDetail>> {
    return fetchApi<DeliveryPodDetail>(`/api/v1/delivery-pods/${id}`);
  },

  async createDeliveryPod(data: { name: string; notes?: string | null }): Promise<ApiResult<DeliveryPodDetail>> {
    return fetchApi<DeliveryPodDetail>('/api/v1/delivery-pods', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateDeliveryPod(id: string, data: Partial<{ name: string; notes: string | null }>): Promise<ApiResult<DeliveryPodDetail>> {
    return fetchApi<DeliveryPodDetail>(`/api/v1/delivery-pods/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteDeliveryPod(id: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/delivery-pods/${id}`, { method: 'DELETE' });
  },

  async addDeliveryPodMember(id: string, data: { userId: string; role: string }): Promise<ApiResult<DeliveryPodMemberInfo>> {
    return fetchApi<DeliveryPodMemberInfo>(`/api/v1/delivery-pods/${id}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async removeDeliveryPodMember(id: string, membershipId: string): Promise<ApiResult<{ deleted: boolean }>> {
    return fetchApi<{ deleted: boolean }>(`/api/v1/delivery-pods/${id}/members/${membershipId}`, { method: 'DELETE' });
  },

  async updateAccountNote(accountId: string, noteId: string, patch: { body?: string; isPinned?: boolean }): Promise<ApiResult<NoteInfo>> {
    return fetchApi<NoteInfo>(`/api/v1/accounts/${accountId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  async deleteAccountNote(accountId: string, noteId: string): Promise<ApiResult<void>> {
    return fetchApi<void>(`/api/v1/accounts/${accountId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  },

  async getTenantPodDistribution(): Promise<ApiResult<TenantPodDistributionResponse>> {
    return fetchApi<TenantPodDistributionResponse>('/api/v1/tenant-pod-distribution');
  },

  // Tenants
  async getTenants(params?: { page?: number; limit?: number; tenantPodId?: string; tenantType?: string; tenantRole?: string; status?: string }): Promise<ApiResult<TenantListResponse>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.tenantPodId) query.set('tenantPodId', params.tenantPodId);
    if (params?.tenantType) query.set('tenantType', params.tenantType);
    if (params?.tenantRole) query.set('tenantRole', params.tenantRole);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return fetchApi<TenantListResponse>(`/api/v1/tenants${qs ? `?${qs}` : ''}`);
  },

  async getTenant(id: string): Promise<ApiResult<TenantDetail>> {
    return fetchApi<TenantDetail>(`/api/v1/tenants/${id}`);
  },

  async createTenant(data: { accountId: string; tenantPodId: string; kiboTenantId: number; displayName: string; tenantType: string; tenantRole: string; status?: string; mozuInstanceId?: string | null; kubeNamespace?: string | null }): Promise<ApiResult<TenantDetail>> {
    return fetchApi<TenantDetail>('/api/v1/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenant(id: string, data: Partial<{ accountId: string; devAccountId: string | null; tenantPodId: string; displayName: string; tenantType: string; tenantRole: string; status: string; mozuInstanceId: string | null; kubeNamespace: string | null; apiTransform: boolean; notes: string | null }>): Promise<ApiResult<TenantDetail>> {
    return fetchApi<TenantDetail>(`/api/v1/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async addTenantStatusHistory(tenantId: string, data: { status: string; effectiveFrom: string; notes?: string | null }): Promise<ApiResult<TenantStatusHistoryInfo>> {
    return fetchApi<TenantStatusHistoryInfo>(`/api/v1/tenants/${tenantId}/status-history`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async addTenantDomain(tenantId: string, data: { domain: string; domainType: string; cloudflareZoneId?: string | null }): Promise<ApiResult<TenantDomainInfo>> {
    return fetchApi<TenantDomainInfo>(`/api/v1/tenants/${tenantId}/domains`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenantDomain(tenantId: string, domainId: string, data: Partial<{ domain: string; domainType: string; cloudflareZoneId: string | null }>): Promise<ApiResult<TenantDomainInfo>> {
    return fetchApi<TenantDomainInfo>(`/api/v1/tenants/${tenantId}/domains/${domainId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteTenantDomain(tenantId: string, domainId: string): Promise<ApiResult<{ success: true }>> {
    return fetchApi<{ success: true }>(`/api/v1/tenants/${tenantId}/domains/${domainId}`, {
      method: 'DELETE',
    });
  },

  // Environments
  async getEnvironments(): Promise<ApiResult<{ items: EnvironmentSummary[] }>> {
    return fetchApi<{ items: EnvironmentSummary[] }>('/api/v1/environments');
  },

  async getEnvironment(id: string): Promise<ApiResult<EnvironmentDetail>> {
    return fetchApi<EnvironmentDetail>(`/api/v1/environments/${id}`);
  },

  async createEnvironment(data: { name: string; cloudProvider: string; region: string }): Promise<ApiResult<EnvironmentDetail>> {
    return fetchApi<EnvironmentDetail>('/api/v1/admin/environments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateEnvironment(id: string, data: Partial<{ name: string; cloudProvider: string; region: string }>): Promise<ApiResult<EnvironmentDetail>> {
    return fetchApi<EnvironmentDetail>(`/api/v1/admin/environments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Tenant Pods
  async getTenantPods(params?: { page?: number; limit?: number; status?: string }): Promise<ApiResult<{ items: TenantPodSummary[]; pagination: PaginatedResponse<TenantPodSummary>['pagination'] }>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return fetchApi<{ items: TenantPodSummary[]; pagination: PaginatedResponse<TenantPodSummary>['pagination'] }>(`/api/v1/tenant-pods${qs ? `?${qs}` : ''}`);
  },

  async getTenantPod(id: string): Promise<ApiResult<TenantPodDetail>> {
    return fetchApi<TenantPodDetail>(`/api/v1/tenant-pods/${id}`);
  },

  async createTenantPod(data: {
    environmentId: string;
    name: string;
    tenantPodType: string;
    status?: string;
    grafanaEndpointId?: string | null;
    elasticsearchEndpointId?: string | null;
  }): Promise<ApiResult<TenantPodDetail>> {
    return fetchApi<TenantPodDetail>('/api/v1/admin/tenant-pods', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenantPod(id: string, data: Partial<{
    name: string;
    tenantPodType: string;
    status: string;
    tenantUrlTemplate: string | null;
    grafanaEndpointId: string | null;
    elasticsearchEndpointId: string | null;
  }>): Promise<ApiResult<TenantPodDetail>> {
    return fetchApi<TenantPodDetail>(`/api/v1/admin/tenant-pods/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Dev Accounts
  // Unpaginated: the route returns every dev account as a bare array.
  async getDevAccounts(): Promise<ApiResult<DevAccountSummary[]>> {
    return fetchApi<DevAccountSummary[]>('/api/v1/dev-accounts');
  },

  async getDevAccount(id: string): Promise<ApiResult<DevAccountDetail>> {
    return fetchApi<DevAccountDetail>(`/api/v1/dev-accounts/${id}`);
  },

  async createDevAccount(data: { kiboDevAccountId: string; label: string; notes?: string | null; accountId: string; environmentId?: string | null }): Promise<ApiResult<DevAccountDetail>> {
    return fetchApi<DevAccountDetail>('/api/v1/admin/dev-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateDevAccount(id: string, data: Partial<{ kiboDevAccountId: string; label: string; notes: string | null; accountId: string; environmentId: string | null }>): Promise<ApiResult<DevAccountDetail>> {
    return fetchApi<DevAccountDetail>(`/api/v1/admin/dev-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async getUnplacedDevAccounts(): Promise<ApiResult<DevAccountUnplacedEntry[]>> {
    return fetchApi<DevAccountUnplacedEntry[]>('/api/v1/admin/dev-accounts/unplaced');
  },

  async mapUnplacedDevAccount(kiboDevAccountId: string, data: { accountId: string; label?: string }): Promise<ApiResult<{ devAccountId: string }>> {
    return fetchApi<{ devAccountId: string }>(`/api/v1/admin/dev-accounts/unplaced/${encodeURIComponent(kiboDevAccountId)}/map`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteDevAccount(id: string): Promise<ApiResult<{ deleted: true }>> {
    return fetchApi<{ deleted: true }>(`/api/v1/admin/dev-accounts/${id}`, {
      method: 'DELETE',
    });
  },

  // Partner Contacts
  async getPartnerContacts(partnerOrgId?: string): Promise<ApiResult<{ items: PartnerContactInfo[] }>> {
    const qs = partnerOrgId ? `?partnerOrgId=${encodeURIComponent(partnerOrgId)}` : '';
    return fetchApi<{ items: PartnerContactInfo[] }>(`/api/v1/partner-contacts${qs}`);
  },

  async createPartnerContact(data: { partnerOrgId: string; email: string; displayName?: string | null; notes?: string | null }): Promise<ApiResult<PartnerContactInfo>> {
    return fetchApi<PartnerContactInfo>('/api/v1/partner-contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updatePartnerContact(id: string, data: Partial<{ email: string; displayName: string | null; notes: string | null }>): Promise<ApiResult<PartnerContactInfo>> {
    return fetchApi<PartnerContactInfo>(`/api/v1/partner-contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deletePartnerContact(id: string): Promise<ApiResult<{ success: true }>> {
    return fetchApi<{ success: true }>(`/api/v1/partner-contacts/${id}`, {
      method: 'DELETE',
    });
  },

  // Import / Export
  async importAccounts(payload: unknown): Promise<ApiResult<ImportSummary>> {
    return fetchApi<ImportSummary>('/api/v1/admin/accounts/import', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async exportAccounts(): Promise<Response> {
    return fetch('/api/v1/admin/accounts/export');
  },
};

// ============================================================================
// CLIENT COMMUNICATIONS
// ============================================================================
//
// The workspace that replaces Salesforce mass email and the CCQ Jira process.
// A campaign is the change being communicated; a send is one message in its
// sequence; a recipient row is the per-address delivery record.

export type CommunicationNoticeTypeId =
  | 'Change'
  | 'Maintenance'
  | 'Release'
  | 'IncidentOngoing'
  | 'IncidentResolved';

export type CommunicationCampaignStatusId =
  | 'Draft'
  | 'InReview'
  | 'Approved'
  | 'Sending'
  | 'Complete'
  | 'Cancelled';

export type CommunicationSendStatusId =
  | 'Draft'
  | 'AwaitingApproval'
  | 'Scheduled'
  | 'Sending'
  | 'Sent'
  | 'Failed'
  | 'Cancelled';

export type CommunicationReviewRoleId = 'Engineering' | 'Product' | 'IncidentCommander';
export type CommunicationReviewStatusId = 'Pending' | 'Approved' | 'Rejected';
export type CommunicationAudienceKindId = 'all' | 'tenantPod' | 'accounts' | 'adhoc';

export interface CommunicationNoticeTypeDefinition {
  id: CommunicationNoticeTypeId;
  label: string;
  description: string;
  defaultCadenceKey: string;
  clientActionCadenceKey?: string;
}

export interface CommunicationGateRule {
  noticeType: CommunicationNoticeTypeId;
  accuracyRoles: CommunicationReviewRoleId[];
  secondApproverOverOneAccount: boolean;
  rationale: string;
}

export interface CommunicationCadenceStep {
  key: string;
  label: string;
  offsetDays: number;
  noticeTypeOverride?: CommunicationNoticeTypeId;
  onEvent?: boolean;
}

export interface CommunicationCadence {
  key: string;
  label: string;
  rationale: string;
  steps: CommunicationCadenceStep[];
}

export interface CommunicationRegistry {
  noticeTypes: CommunicationNoticeTypeDefinition[];
  gateRules: CommunicationGateRule[];
  cadences: CommunicationCadence[];
  leadTimeDays: { shortOutage: number; longOutage: number; clientAction: number };
}

export interface CommunicationUserRef {
  id: string;
  displayName: string;
  email?: string;
}

export interface CommunicationReview {
  id: string;
  userId: string;
  role: CommunicationReviewRoleId;
  status: CommunicationReviewStatusId;
  note: string | null;
  requestedAt: string;
  decidedAt: string | null;
  user: CommunicationUserRef;
}

export interface CommunicationLink {
  id: string;
  kind: 'ReleaseNotes' | 'PortalKba' | 'PublicDocs' | 'Other';
  label: string;
  url: string;
}

/** One address that need not be a SPOC or a known contact. */
export interface CommunicationAdHocAddress {
  email: string;
  accountId?: string | null;
  note?: string | null;
}

export interface CommunicationAudienceSpec {
  kind: CommunicationAudienceKindId;
  tenantPodIds?: string[];
  accountIds?: string[];
  adHoc?: CommunicationAdHocAddress[];
  excludeSandboxOnly?: boolean;
}

export interface CommunicationSend {
  id: string;
  campaignId: string;
  ordinal: number;
  label: string;
  stepKey: string | null;
  noticeTypeOverride: CommunicationNoticeTypeId | null;
  offsetDays: number | null;
  scheduledFor: string | null;
  status: CommunicationSendStatusId;
  audience: CommunicationAudienceSpec | null;
  audiencePreviewedAt: string | null;
  audiencePreviewedBy: CommunicationUserRef | null;
  approvedAt: string | null;
  approvedBy: CommunicationUserRef | null;
  resolvedCountAtApproval: number | null;
  dryRunAt: string | null;
  dryRunToEmail: string | null;
  sentAt: string | null;
  failureReason: string | null;
  _count: { recipients: number };
}

export interface CommunicationCampaign {
  id: string;
  seq: number;
  /** "CC-14" — the reference people say out loud. */
  ref: string;
  title: string;
  noticeType: CommunicationNoticeTypeId;
  status: CommunicationCampaignStatusId;
  subject: string;
  bodyMarkdown: string;
  ccqRef: string | null;
  dateType: 'FixedDate' | 'DateRange' | 'ToBeConfirmed' | null;
  timelineEnforced: boolean;
  sandboxRelease: string | null;
  sandboxDate: string | null;
  productionRelease: string | null;
  productionDate: string | null;
  rolloutPlanAgreed: boolean;
  requiresClientAction: boolean;
  slackChannel: string | null;
  engineeringOwnerId: string | null;
  productOwnerId: string | null;
  engineeringOwner: CommunicationUserRef | null;
  productOwner: CommunicationUserRef | null;
  cadenceKey: string | null;
  anchorField: 'ProductionDate' | 'SandboxDate' | 'Custom' | null;
  anchorDate: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: CommunicationUserRef;
  createdById: string;
  reviews: CommunicationReview[];
  links: CommunicationLink[];
  sends: CommunicationSend[];
  /** Present on an update that reset the review because the copy changed. */
  reviewsReset?: number;
  approvalsCleared?: number;
}

export interface CommunicationResolvedRecipient {
  email: string;
  name: string | null;
  clientContactId: string | null;
  accountId: string | null;
  accountName: string | null;
  isSpoc: boolean;
  isAdHoc: boolean;
}

export interface CommunicationAudiencePreview {
  resolvedAt: string;
  recipients: CommunicationResolvedRecipient[];
  accountCount: number;
  recipientCount: number;
  adHocCount: number;
  exclusions: {
    accountId: string;
    accountName: string;
    reason: 'excluded-account' | 'no-contactable-spoc' | 'sandbox-only' | 'not-live';
  }[];
  thinSpocAccounts: { accountId: string; accountName: string; spocCount: number }[];
}

export interface CommunicationGateStatus {
  gate: {
    rule: CommunicationGateRule;
    outstandingRoles: CommunicationReviewRoleId[];
    secondApproverRequired: boolean;
    blockers: { code: string; message: string }[];
    canSend: boolean;
  };
  accountCount: number;
  recipientCount: number;
}

export interface CommunicationAudienceOptions {
  tenantPods: { id: string; name: string; type: string; environment: string }[];
  accounts: { id: string; name: string }[];
}

export interface CommunicationDeliveryLogRow {
  id: string;
  sentAt: string | null;
  status: string;
  failureReason: string | null;
  email: string;
  isAdHoc: boolean;
  contactName: string | null;
  sendLabel: string;
  campaignId: string;
  campaignTitle: string;
  campaignSeq: number;
  noticeType: CommunicationNoticeTypeId;
}

export interface CommunicationTemplate {
  id: string;
  name: string;
  subject: string | null;
  bodyMarkdown: string;
  noticeType: CommunicationNoticeTypeId | null;
  salesforceTemplateId: string | null;
  salesforceFolder: string | null;
  salesforceUses: number | null;
  isActive: boolean;
}

/** A ticket Kibo opened at a client, as the campaign's fan-out view shows it. */
export interface CommunicationFanOutTicket {
  id: string;
  subject: string;
  stage: string;
  stageLabel: string;
  createdAt: string;
  updatedAt: string;
  openerEmail: string;
  hubspotThreadId: string | null;
  account: { id: string; name: string } | null;
}

export interface CommunicationFanOutOutcome {
  accountId: string | null;
  accountName: string;
  ticketId: string | null;
  skipReason: 'already-open' | 'no-tenant' | 'no-contactable-spoc' | 'no-account' | 'failed' | null;
  error?: string;
}

export interface CommunicationOpenedTicket {
  ticketId: string;
  hubspotThreadId: string | null;
  hubspotTicketObjectId: string | null;
  recipientEmail: string;
  accountId: string;
  accountName: string;
  notified: boolean;
  notifyError: string | null;
}

export interface CommunicationConfig {
  id: string;
  fromName: string | null;
  fromEmail: string | null;
  replyToEmail: string | null;
  dryRunEmail: string | null;
  sendingEnabled: boolean;
  updatedAt: string;
}

export const communicationsApi = {
  async getRegistry(): Promise<ApiResult<CommunicationRegistry>> {
    return fetchApi<CommunicationRegistry>('/api/v1/communications/registry');
  },

  async getAudienceOptions(): Promise<ApiResult<CommunicationAudienceOptions>> {
    return fetchApi<CommunicationAudienceOptions>('/api/v1/communications/audience-options');
  },

  async getPeople(q?: string): Promise<ApiResult<{ people: CommunicationUserRef[] }>> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return fetchApi<{ people: CommunicationUserRef[] }>(`/api/v1/communications/people${qs}`);
  },

  async renderPreview(input: { subject: string; bodyMarkdown: string }): Promise<ApiResult<{ html: string }>> {
    return fetchApi<{ html: string }>('/api/v1/communications/preview', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async listCampaigns(params?: {
    page?: number;
    limit?: number;
    status?: string;
    noticeType?: string;
    createdById?: string;
    search?: string;
    awaitingMyReview?: boolean;
  }): Promise<ApiResult<{ campaigns: CommunicationCampaign[]; total: number; page: number; limit: number }>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.noticeType) query.set('noticeType', params.noticeType);
    if (params?.createdById) query.set('createdById', params.createdById);
    if (params?.search) query.set('search', params.search);
    if (params?.awaitingMyReview) query.set('awaitingMyReview', 'true');
    const qs = query.toString();
    return fetchApi(`/api/v1/communications/campaigns${qs ? `?${qs}` : ''}`);
  },

  async getCampaign(id: string): Promise<ApiResult<CommunicationCampaign>> {
    return fetchApi<CommunicationCampaign>(`/api/v1/communications/campaigns/${id}`);
  },

  async listTemplates(includeRetired = false): Promise<ApiResult<{ templates: CommunicationTemplate[] }>> {
    return fetchApi<{ templates: CommunicationTemplate[] }>(
      `/api/v1/communications/templates${includeRetired ? '?includeRetired=true' : ''}`,
    );
  },

  async setTemplateActive(templateId: string, isActive: boolean): Promise<ApiResult<CommunicationTemplate>> {
    return fetchApi<CommunicationTemplate>(`/api/v1/communications/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
  },

  async createCampaign(data: {
    title: string;
    noticeType: CommunicationNoticeTypeId;
    subject?: string;
    bodyMarkdown?: string;
    requiresClientAction?: boolean;
    ccqRef?: string | null;
    templateId?: string | null;
  }): Promise<ApiResult<CommunicationCampaign>> {
    return fetchApi<CommunicationCampaign>('/api/v1/communications/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateCampaign(
    id: string,
    data: Partial<{
      title: string;
      noticeType: CommunicationNoticeTypeId;
      subject: string;
      bodyMarkdown: string;
      ccqRef: string | null;
      dateType: string | null;
      timelineEnforced: boolean;
      sandboxRelease: string | null;
      sandboxDate: string | null;
      productionRelease: string | null;
      productionDate: string | null;
      rolloutPlanAgreed: boolean;
      requiresClientAction: boolean;
      slackChannel: string | null;
      engineeringOwnerId: string | null;
      productOwnerId: string | null;
      anchorField: string | null;
      anchorDate: string | null;
    }>,
  ): Promise<ApiResult<CommunicationCampaign>> {
    return fetchApi<CommunicationCampaign>(`/api/v1/communications/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async cancelCampaign(id: string): Promise<ApiResult<CommunicationCampaign>> {
    return fetchApi<CommunicationCampaign>(`/api/v1/communications/campaigns/${id}/cancel`, { method: 'POST' });
  },

  async addLink(
    campaignId: string,
    data: { kind: string; label: string; url: string },
  ): Promise<ApiResult<{ added: true }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/links`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async removeLink(campaignId: string, linkId: string): Promise<ApiResult<{ removed: true }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/links/${linkId}`, { method: 'DELETE' });
  },

  async requestReview(
    campaignId: string,
    data: { userId: string; role: CommunicationReviewRoleId },
  ): Promise<ApiResult<{ requested: true }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async removeReview(campaignId: string, reviewId: string): Promise<ApiResult<{ removed: true }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/reviews/${reviewId}`, { method: 'DELETE' });
  },

  async decideReview(
    campaignId: string,
    reviewId: string,
    data: { decision: 'Approved' | 'Rejected'; note?: string | null },
  ): Promise<ApiResult<{ recorded: true }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/reviews/${reviewId}/decision`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async applyCadence(
    campaignId: string,
    data: {
      cadenceKey: string | null;
      anchorField?: string | null;
      anchorDate?: string | null;
      discardApprovedSends?: boolean;
    },
  ): Promise<ApiResult<{ created: number; updated: number; deleted: number }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/cadence`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async createSend(
    campaignId: string,
    data: { label: string; offsetDays?: number | null; scheduledFor?: string | null },
  ): Promise<ApiResult<{ sendId: string }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/sends`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateSend(
    sendId: string,
    data: Partial<{
      label: string;
      offsetDays: number | null;
      scheduledFor: string | null;
      audience: CommunicationAudienceSpec;
    }>,
  ): Promise<ApiResult<{ updated: true }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteSend(sendId: string): Promise<ApiResult<{ deleted: true }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}`, { method: 'DELETE' });
  },

  /** `record` marks the preview as seen, which is what unlocks approval. */
  async getSendAudience(sendId: string, record = false): Promise<ApiResult<CommunicationAudiencePreview>> {
    return fetchApi<CommunicationAudiencePreview>(
      `/api/v1/communications/sends/${sendId}/audience${record ? '?record=true' : ''}`,
    );
  },

  async resolveAudience(spec: CommunicationAudienceSpec): Promise<ApiResult<CommunicationAudiencePreview>> {
    return fetchApi<CommunicationAudiencePreview>('/api/v1/communications/audience/resolve', {
      method: 'POST',
      body: JSON.stringify(spec),
    });
  },

  async getSendGate(sendId: string): Promise<ApiResult<CommunicationGateStatus>> {
    return fetchApi<CommunicationGateStatus>(`/api/v1/communications/sends/${sendId}/gate`);
  },

  async approveSend(sendId: string): Promise<ApiResult<{ resolvedCount: number }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}/approve`, { method: 'POST' });
  },

  async scheduleSend(sendId: string, scheduledFor: string): Promise<ApiResult<{ scheduled: true }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledFor }),
    });
  },

  async cancelSend(sendId: string): Promise<ApiResult<{ cancelled: true }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}/cancel`, { method: 'POST' });
  },

  async sendNow(sendId: string): Promise<ApiResult<{ workflowId: string; alreadyRunning: boolean }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}/send`, { method: 'POST' });
  },

  async dryRun(sendId: string, toEmail?: string): Promise<ApiResult<{ to: string; sendResult: string }>> {
    return fetchApi(`/api/v1/communications/sends/${sendId}/dry-run`, {
      method: 'POST',
      body: JSON.stringify(toEmail ? { toEmail } : {}),
    });
  },

  /** Open a support conversation at one client under Kibo's name. */
  async openTicketAtClient(
    accountId: string,
    data: { subject: string; body: string; clientContactId?: string | null },
  ): Promise<ApiResult<CommunicationOpenedTicket>> {
    return fetchApi<CommunicationOpenedTicket>(`/api/v1/communications/accounts/${accountId}/open-ticket`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** One ticket per client across a campaign's audience. */
  async fanOut(
    campaignId: string,
    sendId?: string | null,
  ): Promise<ApiResult<{ opened: number; skipped: number; outcomes: CommunicationFanOutOutcome[] }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/fan-out`, {
      method: 'POST',
      body: JSON.stringify(sendId ? { sendId } : {}),
    });
  },

  async listCampaignTickets(
    campaignId: string,
  ): Promise<ApiResult<{ tickets: CommunicationFanOutTicket[] }>> {
    return fetchApi(`/api/v1/communications/campaigns/${campaignId}/tickets`);
  },

  async getAccountLog(
    accountId: string,
    params?: { page?: number; limit?: number; recipient?: string; status?: string; sinceDays?: number },
  ): Promise<ApiResult<{ rows: CommunicationDeliveryLogRow[]; total: number; page: number; limit: number }>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.recipient) query.set('recipient', params.recipient);
    if (params?.status) query.set('status', params.status);
    if (params?.sinceDays) query.set('sinceDays', String(params.sinceDays));
    const qs = query.toString();
    return fetchApi(`/api/v1/communications/accounts/${accountId}/log${qs ? `?${qs}` : ''}`);
  },

  async getConfig(): Promise<ApiResult<{ config: CommunicationConfig | null }>> {
    return fetchApi('/api/v1/admin/communications/config');
  },

  async updateConfig(data: Partial<CommunicationConfig>): Promise<ApiResult<CommunicationConfig>> {
    return fetchApi<CommunicationConfig>('/api/v1/admin/communications/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ─── Implementation Portal (code: client-hub) ──────────────────────────────────────────────────────────────
// Per-client lifecycle workspace: artefacts, classification gate, derived state.
// Backend source of truth: src/client-hub/client-hub-schemas.ts — these types
// mirror the API's wire shapes.

export type ClientHubArtefactTypeWire = 'recording' | 'transcript' | 'document' | 'deck' | 'spreadsheet' | 'decision' | 'link';
export type ClientHubStageWire = 'pre_sales' | 'handoff' | 'design' | 'plan' | 'test' | 'implementation' | 'support';
export type ClientHubClassificationWire = 'unclassified' | 'open' | 'internal' | 'restricted' | 'quarantined';
export type ClientHubConsentStateWire = 'pending' | 'granted' | 'refused';

export interface ClientHubCompletenessView {
  pct: number;
  stage: ClientHubStageWire;
  missing: { key: string; label: string; present: boolean }[];
}

export interface ClientHubAccountSummaryWire {
  accountId: string;
  name: string | null;
  ref: string | null;
  lifecycleStage: ClientHubStageWire;
  completeness: ClientHubCompletenessView;
  artefactCount: number;
  quarantinedCount: number;
  lastActivityAt: string | null;
}

export interface ClientHubTimelineEventWire {
  id: string;
  action: string;
  objectType: string;
  objectId: string | null;
  detail: string | null;
  actorId: string;
  at: string;
}

export interface ClientHubWorkspaceOverviewWire {
  account: ClientHubAccountSummaryWire;
  lifecycleStage: ClientHubStageWire;
  artefactCounts: { total: number; indexed: number; quarantined: number; unclassified: number };
  timeline: ClientHubTimelineEventWire[];
}

export interface ClientHubArtefactWire {
  id: string;
  accountId: string;
  title: string;
  type: ClientHubArtefactTypeWire;
  stage: ClientHubStageWire;
  classification: ClientHubClassificationWire;
  indexed: boolean;
  consentState: ClientHubConsentStateWire | null;
  scanReason: string | null;
  scanMatches: unknown;
  supersedesId: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  classifiedBy: string | null;
  classifiedAt: string | null;
  classificationReason: string | null;
  vfsPath: string | null;
}

export interface ClientHubUploadResultWire {
  artefact: ClientHubArtefactWire | null;
  scan: { quarantined: boolean; reason: string | null; matches: unknown };
  warnings: string[];
}

export const clientHubApi = {
  async getClientHubAccounts(): Promise<ApiResult<{ accounts: ClientHubAccountSummaryWire[] }>> {
    return fetchApi('/api/v1/client-hub/accounts');
  },

  async getClientHubWorkspace(accountId: string): Promise<ApiResult<ClientHubWorkspaceOverviewWire>> {
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}`);
  },

  async getClientHubArtefacts(accountId: string): Promise<ApiResult<{ artefacts: ClientHubArtefactWire[] }>> {
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}/artefacts`);
  },

  async uploadClientHubArtefact(
    accountId: string,
    file: File,
    fields: {
      title?: string;
      type: ClientHubArtefactTypeWire;
      stage: ClientHubStageWire;
      consentState?: ClientHubConsentStateWire;
      supersedesId?: string;
    },
  ): Promise<ApiResult<ClientHubUploadResultWire>> {
    const formData = new FormData();
    formData.append('file', file);
    if (fields.title) formData.append('title', fields.title);
    formData.append('type', fields.type);
    formData.append('stage', fields.stage);
    if (fields.consentState) formData.append('consentState', fields.consentState);
    if (fields.supersedesId) formData.append('supersedesId', fields.supersedesId);
    // fetchApi leaves FormData's Content-Type to the browser — same as the
    // account-logo upload, since the boundary must be generated per request.
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}/artefacts`, {
      method: 'POST',
      body: formData,
    });
  },

  async classifyClientHubArtefact(
    artefactId: string,
    classification: 'open' | 'internal' | 'restricted',
    reason: string,
  ): Promise<ApiResult<{ artefact: ClientHubArtefactWire; indexed: boolean }>> {
    return fetchApi(`/api/v1/client-hub/artefacts/${artefactId}/classify`, {
      method: 'POST',
      body: JSON.stringify({ classification, reason }),
    });
  },

  async rescanClientHubArtefact(
    artefactId: string,
  ): Promise<ApiResult<{ quarantined: boolean; reason: string | null; matches: unknown }>> {
    return fetchApi(`/api/v1/client-hub/artefacts/${artefactId}/rescan`, { method: 'POST' });
  },

  async getClientHubAudit(
    accountId: string,
    limit?: number,
  ): Promise<ApiResult<{ events: ClientHubTimelineEventWire[] }>> {
    const qs = limit ? `?limit=${limit}` : '';
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}/audit${qs}`);
  },
};

// ─── Implementation Portal — handoff agent ──────────────────────────────
// Agent-layer additions on top of the client-hub client section above; the
// endpoints live under the kept /api/v1/client-hub prefix.

export type ImplementationPortalAgentTypeWire = 'handoff';
export type ImplementationPortalReviewOutcomeWire = 'accept_as_is' | 'accept_with_edits' | 'request_changes' | 'reject';
export type ImplementationPortalDocumentStateWire = 'in_review' | 'accepted' | 'superseded' | 'rejected';

export interface ImplementationPortalPackCitationWire {
  artefactId: string;
  locator: string;
  excerpt: string;
}

export interface ImplementationPortalPackSectionWire {
  id: string;
  heading: string;
  body: string;
  citations: ImplementationPortalPackCitationWire[];
  gap?: { assignTo: string };
}

export interface ImplementationPortalHandoffPackWire {
  sections: ImplementationPortalPackSectionWire[];
  openQuestions: { question: string; reason: string }[];
}

export interface ImplementationPortalRunWire {
  id: string;
  agentType: ImplementationPortalAgentTypeWire;
  status: 'pending' | 'running' | 'completed' | 'failed';
  definitionVersion: number | null;
  modelId: string;
  requestedBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  groundingPct: number | null;
  errorMessage: string | null;
  inputArtefactIds: string[];
  excludedInputs: { artefactId: string; title: string; reason: string }[];
  documentId: string | null;
}

export interface ImplementationPortalRunStageEventWire {
  stage: string;
  at: string;
  units?: number;
  details?: Record<string, number>;
}

export interface ImplementationPortalHandoffDocumentWire {
  id: string;
  runId: string;
  agentType: ImplementationPortalAgentTypeWire;
  version: number;
  state: ImplementationPortalDocumentStateWire;
  sections: ImplementationPortalPackSectionWire[];
  openQuestions: ImplementationPortalHandoffPackWire['openQuestions'];
  uncitedSectionIds: string[];
  reviewedSections: { sectionId: string; disposition: 'confirmed' | 'deleted' }[] | null;
  acceptedBy: string | null;
  acceptedAt: string | null;
  acceptedOutcome: ImplementationPortalReviewOutcomeWire | null;
  acceptReason: string | null;
  editDistancePct: number | null;
  createdAt: string;
  deliverables?: ImplementationPortalDeliverablesWire;
}

export interface ImplementationPortalDeliverablesWire {
  clientConfirmation: string;
  clientConfirmationSend: string;
  safetyReport: string;
  discoveryAgenda: string;
  internalHandoff: string;
}

export interface ImplementationPortalHandoffStateWire {
  readiness: { pct: number; missing: { key: string; label: string; present: boolean }[] };
  document: ImplementationPortalHandoffDocumentWire | null;
  run: ImplementationPortalRunWire | null;
  runStages?: ImplementationPortalRunStageEventWire[];
}

export const implementationPortalAgentApi = {
  async getImplementationPortalHandoff(accountId: string): Promise<ApiResult<ImplementationPortalHandoffStateWire>> {
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}/handoff`);
  },

  async runImplementationPortalHandoffAgent(accountId: string): Promise<ApiResult<{ run: ImplementationPortalRunWire; document: ImplementationPortalHandoffDocumentWire | null }>> {
    return fetchApi(`/api/v1/client-hub/accounts/${accountId}/agents/handoff/run`, { method: 'POST' });
  },

  async reviewImplementationPortalDocument(
    documentId: string,
    input: {
      outcome: ImplementationPortalReviewOutcomeWire;
      reason?: string;
      editedSections?: { id: string; body: string }[];
      uncitedDispositions?: { sectionId: string; disposition: 'confirmed' | 'deleted' }[];
    },
  ): Promise<ApiResult<{ result: { outcome: string; documentId: string; editDistancePct?: number }; document: ImplementationPortalHandoffDocumentWire | null }>> {
    return fetchApi(`/api/v1/client-hub/documents/${documentId}/review`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
